/**
 * Advanced Trip Plan Component
 * Main page for advanced trip planning with Mapbox GL JS
 */

import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import mapboxgl from 'mapbox-gl';
import 'mapbox-gl/dist/mapbox-gl.css';
import { MAPBOX_API_KEY_ADVANCED, API_BASE_URL } from '../../config';
import { useAuth } from '../../contexts/AuthContext';
import { useToast } from '../../contexts/ToastContext';
import { ArrowLeft, ArrowRight, Calendar, CalendarDays, MapPin, Navigation, ChevronDown, X, Filter, Bookmark, Grid3x3, List, Plus, Check, Eye, ChevronUp, MoveUp, MoveDown, ChevronLeft as MoveLeft, ChevronRight as MoveRight, ChevronLeft, ChevronRight, Users, Sparkles, Route, MapPinned, MapPinCheck, Car, Bus, TrainTrack, ExternalLink, Flag, Save, BookOpen, Edit2, Mail, MessageSquare, Zap } from 'lucide-react';
import { updatePageMetadata, pageMetadata } from '../../utils/pageMetadata';
import { getPlaceTypeConfig, formatDistance, calculateDistance, type PlaceType } from '../../utils/advancedTrip/placeUtils';
import { generatePlacePopupHTML } from '../../utils/advancedTrip/markerUtils';
import { getImageVariants } from '../../utils/imageUtils';
import PlaceDetailPanel from '../PlaceDetailPanel';
import { PlaceItem } from '../../types/explore';
import { exploreService } from '../../services/exploreService';
import { fetchDrivingRoute } from '../../utils/mapboxDirections';
import { getHolidayForDate, getHolidaysInRange } from '../../utils/sriLankaHolidays';
import { useTripEndLocation } from './hooks/useTripEndLocation';
import { useRouteCalculation } from './hooks/useRouteCalculation';
import { useNearbyPlaces } from './hooks/useNearbyPlaces';
import { useSavedTrips } from './hooks/useSavedTrips';
import { usePlaceManagement } from './hooks/usePlaceManagement';
import { useAIReview } from './hooks/useAIReview';
import { useDrawerSheet } from '../../hooks/useDrawerSheet';
import RegistrationPrompt from '../RegistrationPrompt';
import { tripService } from '../../services/tripService';
import { SavedTrip } from '../../types/trip';
import TripSummaryModal from './TripSummaryModal';
import OptimizationDialog from './OptimizationDialog';
import QuoteRequestForm from './QuoteRequestForm';
import QuickQuoteDetail from './QuickQuoteDetail';
import { Timestamp } from 'firebase/firestore';
import { useAsyncSummaryGeneration } from './hooks/useAsyncSummaryGeneration';
import SummaryGenerationModal from './SummaryGenerationModal';

// Custom styles for Mapbox popups
const popupStyles = `
  .mapboxgl-popup-content {
    padding: 0 !important;
    border-radius: 12px !important;
    box-shadow: 0 4px 16px rgba(0,0,0,0.12) !important;
  }
  
  .mapboxgl-popup-close-button {
    width: 28px !important;
    height: 28px !important;
    font-size: 18px !important;
    line-height: 1 !important;
    color: #6b7280 !important;
    padding: 0 !important;
    background: rgba(255, 255, 255, 0.95) !important;
    border-radius: 50% !important;
    margin: 8px !important;
    display: flex !important;
    align-items: center !important;
    justify-content: center !important;
    text-align: center !important;
    transition: all 0.2s ease !important;
    z-index: 10 !important;
    right: 0 !important;
    top: 0 !important;
  }
  
  .mapboxgl-popup-close-button:hover {
    background: rgba(255, 255, 255, 1) !important;
    color: #111827 !important;
    transform: scale(1.1) !important;
  }
  
  .mapboxgl-popup-tip {
    border-top-color: white !important;
  }
`;

// Sri Lanka boundaries for map bounds
const SRI_LANKA_BOUNDS = {
  minLat: 5.9,
  maxLat: 9.9,
  minLng: 79.5,
  maxLng: 82.0
};

type DrawerPosition = 'collapsed' | 'half' | 'expanded';
type DrawerView = 'home' | 'trip-options' | 'day-trip' | 'day-trip-destinations' | 'my-day-trip' | 'multi-day-trip' | 'multi-day-trip-details' | 'multi-day-trip-itinerary' | 'multi-day-trip-day-location' | 'multi-day-trip-day-destinations' | 'multi-day-trip-plan-day' | 'saved-day-trips' | 'saved-multi-day-trips' | 'quick-quotes';

type TransportMode = 'car' | 'bus' | 'train';

interface RouteSegment {
  fromIndex: number;
  toIndex: number;
  distance: number; // in km
  duration: number; // in minutes
  isApproximate: boolean;
  geometry: [number, number][]; // coordinates for line
  trainRoute?: {
    geometry: [number, number][];
    distance: number;
    stations: {
      from: {
        name: string;
        coordinates: [number, number];
        distanceFromPlace: number;
      };
      to: {
        name: string;
        coordinates: [number, number];
        distanceFromPlace: number;
      };
    };
  };
}

interface QuickLocation {
  name: string;
  coordinates: [number, number]; // [lng, lat]
}

const QUICK_LOCATIONS: QuickLocation[] = [
  { name: 'Airport', coordinates: [79.8841, 7.1807] },
  { name: 'Colombo', coordinates: [79.8612, 6.9271] },
  { name: 'Galle', coordinates: [80.2170, 6.0535] },
  { name: 'Ella', coordinates: [81.0467, 6.8667] },
  { name: 'Sigiriya', coordinates: [80.7597, 7.9569] },
  { name: 'Mirissa', coordinates: [80.4686, 5.9450] },
  { name: 'Kandy', coordinates: [80.6350, 7.2906] },
];

export default function Plan() {
  const mapContainerDesktopRef = useRef<HTMLDivElement>(null);
  const mapContainerMobileRef = useRef<HTMLDivElement>(null);
  const mapRef = useRef<mapboxgl.Map | null>(null);
  const drawerRef = useRef<HTMLDivElement>(null);
  const drawerContentRef = useRef<HTMLDivElement>(null);
  const startMarkerRef = useRef<mapboxgl.Marker | null>(null);
  const placeMarkersRef = useRef<mapboxgl.Marker[]>([]); // Store place markers
  const currentPopupRef = useRef<mapboxgl.Popup | null>(null); // Track currently open popup
  const [mapLoaded, setMapLoaded] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const { authState } = useAuth();
  const { showToast } = useToast();
  const [isDesktop, setIsDesktop] = useState(window.innerWidth >= 768);
  
  // Update page metadata on mount
  useEffect(() => {
    updatePageMetadata(pageMetadata.plan.title, pageMetadata.plan.description);
  }, []);
  
  // Mobile drawer state - using useDrawerSheet hook
  const {
    drawerPosition,
    drawerHeight,
    isDragging,
    expand,
    collapse,
    setHalf,
    dragHandlers,
  } = useDrawerSheet('collapsed');
  
  // Drawer navigation state
  const [currentView, setCurrentView] = useState<DrawerView>('home');
  
  // Day trip state
  const [isMapSelectionMode, setIsMapSelectionMode] = useState(false);
  const [startLocation, setStartLocation] = useState<{ name: string; coordinates: [number, number] } | null>(null);
  const [locationLoading, setLocationLoading] = useState(false);
  
  // Destination picking state
  const [selectedTypes, setSelectedTypes] = useState<PlaceType[]>(['attraction', 'food', 'experience', 'hotel']);
  const [maxDistance, setMaxDistance] = useState<number>(20); // km
  const [showOnlySaved, setShowOnlySaved] = useState(false);
  const [viewMode, setViewMode] = useState<'list' | 'grid'>('list');
  
  // Trip places management - extracted to custom hook
  const {
    tripPlaces,
    setTripPlaces,
    isPlaceInTrip,
    addToTrip,
    removeFromTrip,
    toggleTripPlace,
    movePlace,
    categorizePlaces,
  } = usePlaceManagement();
  
  // AI Review state (modal states stay in main component)
  const [isTripFinalized, setIsTripFinalized] = useState(false);
  const [showFinalizeModal, setShowFinalizeModal] = useState(false);
  
  const {
    aiReview,
    setAiReview,
    isGeneratingReview,
    aiReviewRef,
    generateAIReview: hookGenerateAIReview,
    handleFinalizeConfirm: hookHandleFinalizeConfirm,
  } = useAIReview({
    startLocation,
    tripPlaces,
    apiBaseUrl: API_BASE_URL,
  });
  
  // Place detail panel state
  const [selectedPlaceForDetail, setSelectedPlaceForDetail] = useState<PlaceItem | null>(null);
  const [isLoadingPlaceDetails, setIsLoadingPlaceDetails] = useState(false);
  
  // Mobile card carousel state
  const [activeCardIndex, setActiveCardIndex] = useState(0);
  const [cardSwipeStart, setCardSwipeStart] = useState(0);
  const [cardSwipeOffset, setCardSwipeOffset] = useState(0);
  const [isCardSwiping, setIsCardSwiping] = useState(false);
  const cardContainerRef = useRef<HTMLDivElement>(null);
  
  // Multi-day trip state
  const [multiDayStartDate, setMultiDayStartDate] = useState<Date | null>(null);
  const [multiDayEndDate, setMultiDayEndDate] = useState<Date | null>(null);
  const [multiDayAdults, setMultiDayAdults] = useState(2);
  const [multiDayChildren, setMultiDayChildren] = useState(0);
  const [multiDayInfants, setMultiDayInfants] = useState(0);
  const [currentMonth, setCurrentMonth] = useState(new Date());
  const [isEditingTripDetails, setIsEditingTripDetails] = useState(false);
  const [multiDayStartLocation, setMultiDayStartLocation] = useState<{ name: string; coordinates: [number, number] } | null>(null);
  const [showQuickLocationsDropdown, setShowQuickLocationsDropdown] = useState(false);
  const [showCalendarPicker, setShowCalendarPicker] = useState(false);
  const [showTravelerPicker, setShowTravelerPicker] = useState(false);
  const [isSelectingStartLocation, setIsSelectingStartLocation] = useState(false);
  const [currentDayTripId, setCurrentDayTripId] = useState<string | null>(null);
  const tempStartMarkerRef = useRef<mapboxgl.Marker | null>(null);
  const permanentStartMarkerRef = useRef<mapboxgl.Marker | null>(null);
  const [selectedDayIndex, setSelectedDayIndex] = useState<number | null>(null);
  const [selectedDayLocation, setSelectedDayLocation] = useState<{ name: string; coordinates: [number, number] } | null>(null);
  // Changed to array to support multiple primary locations per day
  const [dayLocations, setDayLocations] = useState<Map<number, Array<{ name: string; coordinates: [number, number] }>>>(new Map());
  // Track which primary location index user is currently adding places to
  const [currentPrimaryLocationIndex, setCurrentPrimaryLocationIndex] = useState<number>(0);
  
  // Nearby places fetching - extracted to custom hook
  const {
    nearbyPlaces,
    setNearbyPlaces,
    placesLoading,
    setPlacesLoading,
  } = useNearbyPlaces({
    currentView,
    startLocation,
    selectedDayLocation,
    userId: authState.user?.userId,
    maxDistance,
    selectedTypes,
    showOnlySaved,
    apiBaseUrl: API_BASE_URL,
  });
  const [showDayLocationSlider, setShowDayLocationSlider] = useState(false);
  const [showAddLocationSlider, setShowAddLocationSlider] = useState(false);  // NEW: for adding additional locations
  const [showRecommendedDropdown, setShowRecommendedDropdown] = useState(false);
  const [showAddMorePlacesDropdown, setShowAddMorePlacesDropdown] = useState(false);  // NEW: for selecting location to add more places
  const [isSelectingDayLocation, setIsSelectingDayLocation] = useState(false);
  const tempDayMarkerRef = useRef<mapboxgl.Marker | null>(null);
  const tempTripEndMarkerRef = useRef<mapboxgl.Marker | null>(null);
  // Changed to array to support multiple markers per day (one per primary location)
  const permanentDayMarkersRef = useRef<Map<number, mapboxgl.Marker[]>>(new Map());
  const [dayTransportation, setDayTransportation] = useState<Map<number, Map<number, TransportMode>>>(new Map()); // dayIndex -> (segmentIndex -> transport mode)
  const [expandedTransportIndex, setExpandedTransportIndex] = useState<number | null>(null);
  const [dayStartLocations, setDayStartLocations] = useState<Map<number, QuickLocation>>(new Map());
  const [activeTrainRoutes, setActiveTrainRoutes] = useState<Map<number, any>>(new Map());
  const [dayPlaces, setDayPlaces] = useState<Map<number, any[]>>(new Map()); // Store places for each day
  
  // Finalized days data (saved when user clicks Continue from Plan Day view)
  interface FinalizedDayData {
    places: any[];  // Each place now has primaryLocationIndex: number
    transportation: Map<number, TransportMode>;
    routeSegments: RouteSegment[];
    primaryLocations: Array<{ name: string; coordinates: [number, number] }>;  // NEW: array of primary locations
    startLocation?: QuickLocation;  // DEPRECATED: keep for backwards compatibility
    endLocation?: QuickLocation;  // for trip end
    endTransportMode?: TransportMode;  // for trip end
    endRouteSegment?: RouteSegment;  // for trip end
    isMultiDayBlock?: boolean;  // for multi-day blocks
    blockDayIndices?: number[];  // array of combined day indices
    blockStartDate?: Date;
    blockEndDate?: Date;
  }
  const [finalizedDays, setFinalizedDays] = useState<Map<number, FinalizedDayData>>(new Map());
  const [expandedDays, setExpandedDays] = useState<Set<number>>(new Set());
  const [combinedDayBlocks, setCombinedDayBlocks] = useState<Map<number, number[]>>(new Map()); // Maps first day index to array of combined day indices
  const [isHolidayAlertExpanded, setIsHolidayAlertExpanded] = useState(false);
  
  // Helper function to calculate distance between two coordinates
  const calculateDistance = useCallback((lat1: number, lon1: number, lat2: number, lon2: number): number => {
    const R = 6371; // Earth's radius in km
    const dLat = (lat2 - lat1) * Math.PI / 180;
    const dLon = (lon2 - lon1) * Math.PI / 180;
    const a = 
      Math.sin(dLat / 2) * Math.sin(dLat / 2) +
      Math.cos(lat1 * Math.PI / 180) * Math.cos(lat2 * Math.PI / 180) *
      Math.sin(dLon / 2) * Math.sin(dLon / 2);
    const c = 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));
    return R * c; // Distance in km
  }, []); // No dependencies - pure calculation function
  
  // Route calculation - extracted to custom hook
  const {
    dayRouteSegments,
    setDayRouteSegments,
    trainAvailability,
    setTrainAvailability,
  } = useRouteCalculation({
    currentView,
    selectedDayIndex,
    selectedDayLocation,
    multiDayStartLocation,
    tripPlaces,
    dayPlaces,
    combinedDayBlocks,
    finalizedDays,
    calculateDistance,
    fetchDrivingRoute,
    apiBaseUrl: API_BASE_URL,
  });
  
  // Memoize the "from location" for trip end route to avoid unnecessary recalculations
  // This only changes when the actual starting point changes, not just when Maps get new references
  const tripEndFromLocation = useMemo(() => {
    // Determine the "from" location
    let fromLocation: QuickLocation | null = null;
    
    // Get the last day index
    const lastDayIndex = Array.from(dayLocations.keys()).reduce((max, idx) => Math.max(max, idx), -1);
    
    if (lastDayIndex >= 0) {
      // Check if we're currently planning the last day (use tripPlaces)
      if (selectedDayIndex === lastDayIndex && tripPlaces.length > 0) {
        // Use last place from current planning session
        const lastPlace = tripPlaces[tripPlaces.length - 1];
        fromLocation = {
          name: lastPlace.title || lastPlace.name || 'Last Place',
          coordinates: [
            lastPlace.geo_point?.lon || lastPlace.geo_point?.lng || lastPlace.geo_point?.longitude || 0,
            lastPlace.geo_point?.lat || lastPlace.geo_point?.latitude || 0
          ]
        };
      } else {
        // Find the actual last day's data, accounting for merged blocks
        let lastDayPlaces: PlaceItem[] | undefined;
        let lastDayFinalizedData: FinalizedDayData | undefined;
        let foundInBlock = false;
        
        // Check if the last day is part of a merged block
        for (const [blockStartIndex, blockIndices] of Array.from(combinedDayBlocks.entries())) {
          if (blockIndices.includes(lastDayIndex)) {
            // Last day is in this merged block
            lastDayFinalizedData = finalizedDays.get(blockStartIndex);
            lastDayPlaces = lastDayFinalizedData?.places;
            foundInBlock = true;
            break;
          }
        }
        
        // If not in a merged block, check regular dayPlaces and finalizedDays
        if (!foundInBlock) {
          lastDayPlaces = dayPlaces.get(lastDayIndex);
          if (!lastDayPlaces) {
            lastDayFinalizedData = finalizedDays.get(lastDayIndex);
            lastDayPlaces = lastDayFinalizedData?.places;
          }
        }
        
        if (lastDayPlaces && lastDayPlaces.length > 0) {
          // Use last place of the day (or merged block)
          const lastPlace = lastDayPlaces[lastDayPlaces.length - 1];
          fromLocation = {
            name: lastPlace.title || lastPlace.name || 'Last Place',
            coordinates: [
              lastPlace.geo_point?.lon || lastPlace.geo_point?.lng || 0,
              lastPlace.geo_point?.lat || 0
            ]
          };
        } else {
          // No places in last day - look backward to find the previous day's last place
          let foundPreviousPlace = false;
          
          // Start from the day before last and work backward
          for (let dayIdx = lastDayIndex - 1; dayIdx >= 0; dayIdx--) {
            // Check if this day is in a merged block
            let prevDayPlaces: PlaceItem[] | undefined;
            
            // Check merged blocks first
            for (const [blockStartIndex, blockIndices] of Array.from(combinedDayBlocks.entries())) {
              if (blockIndices.includes(dayIdx)) {
                const blockData = finalizedDays.get(blockStartIndex);
                prevDayPlaces = blockData?.places;
                break;
              }
            }
            
            // If not in a merged block, check regular places
            if (!prevDayPlaces) {
              prevDayPlaces = dayPlaces.get(dayIdx);
              if (!prevDayPlaces) {
                const dayData = finalizedDays.get(dayIdx);
                prevDayPlaces = dayData?.places;
              }
            }
            
            // If this day has places, use the last one
            if (prevDayPlaces && prevDayPlaces.length > 0) {
              const lastPlace = prevDayPlaces[prevDayPlaces.length - 1];
              fromLocation = {
                name: lastPlace.title || lastPlace.name || 'Last Place',
                coordinates: [
                  lastPlace.geo_point?.lon || lastPlace.geo_point?.lng || 0,
                  lastPlace.geo_point?.lat || 0
                ]
              };
              foundPreviousPlace = true;
              break;
            }
          }
          
          // If still no place found, use last day's first primary location as fallback
          if (!foundPreviousPlace) {
            const lastDayLocs = dayLocations.get(lastDayIndex);
            fromLocation = lastDayLocs?.[0] || null;
          }
        }
      }
    }
    
    return fromLocation;
  }, [dayLocations, dayPlaces, finalizedDays, tripPlaces, selectedDayIndex, combinedDayBlocks]);
  
  // Trip end state - extracted to custom hook
  const {
    isTripEnded,
    setIsTripEnded,
    tripEndLocation,
    setTripEndLocation,
    tripEndRouteSegment,
    setTripEndRouteSegment,
    tripEndTransportMode,
    setTripEndTransportMode,
    tripEndTrainAvailable,
    setTripEndTrainAvailable,
    tripEndMarkerRef,
    addTripEndMarker,
    removeTripEndMarker,
  } = useTripEndLocation({
    mapRef,
    mapLoaded,
    tripEndFromLocation,
    calculateDistance,
    fetchDrivingRoute,
    apiBaseUrl: API_BASE_URL,
  });
  
  const [showTripEndLocationPicker, setShowTripEndLocationPicker] = useState(false);
  const [isSelectingTripEndLocation, setIsSelectingTripEndLocation] = useState(false);
  const [isResettingDayLocation, setIsResettingDayLocation] = useState(false);
  const [showResetItineraryModal, setShowResetItineraryModal] = useState(false);
  const [tripOptionsMode, setTripOptionsMode] = useState<'create' | 'saved'>('create'); // Track which mode to show in trip-options
  
  // Trip save/load state
  const [showSavePrompt, setShowSavePrompt] = useState(false);
  const [pendingNavigation, setPendingNavigation] = useState<DrawerView | null>(null);
  const [dayTripName, setDayTripName] = useState('');
  const [multiDayTripName, setMultiDayTripName] = useState('');
  const [loadingTrips, setLoadingTrips] = useState(false);

  // Email itinerary states (kept for auth prompt)
  const [showEmailAuthPrompt, setShowEmailAuthPrompt] = useState(false);
  const [showWhatsAppVerifyPrompt, setShowWhatsAppVerifyPrompt] = useState(false);
  const [additionalEmail, setAdditionalEmail] = useState('');
  const [showTripDetailsModal, setShowTripDetailsModal] = useState(false);
  
  // Rate limit states
  const [showRateLimitPrompt, setShowRateLimitPrompt] = useState(false);
  const [rateLimitWaitMinutes, setRateLimitWaitMinutes] = useState<number | undefined>(undefined);
  const [registrationVariant, setRegistrationVariant] = useState<'google_auth' | 'whatsapp_verify' | 'wait_only'>('google_auth');

  // Trip summary states
  const [showSummaryModal, setShowSummaryModal] = useState(false);
  const [showOptimizationDialog, setShowOptimizationDialog] = useState(false);
  const [showAsyncSummaryModal, setShowAsyncSummaryModal] = useState(false);
  const [showLazyQuoteForm, setShowLazyQuoteForm] = useState(false);
  const [selectedQuickQuote, setSelectedQuickQuote] = useState<any>(null);

  // Helper to convert route segments to Firestore-safe format
  const convertRouteSegmentForFirestore = (segment: RouteSegment): any => {
    const converted: any = {
      fromIndex: segment.fromIndex,
      toIndex: segment.toIndex,
      distance: segment.distance,
      duration: segment.duration,
      isApproximate: segment.isApproximate,
      geometry: segment.geometry.map(coord => ({ lat: coord[1], lng: coord[0] }))
    };
    
    if (segment.trainRoute) {
      converted.trainRoute = {
        geometry: segment.trainRoute.geometry.map(coord => ({ lat: coord[1], lng: coord[0] })),
        distance: segment.trainRoute.distance,
        stations: {
          from: {
            name: segment.trainRoute.stations.from.name,
            coordinates: { lat: segment.trainRoute.stations.from.coordinates[1], lng: segment.trainRoute.stations.from.coordinates[0] },
            distanceFromPlace: segment.trainRoute.stations.from.distanceFromPlace
          },
          to: {
            name: segment.trainRoute.stations.to.name,
            coordinates: { lat: segment.trainRoute.stations.to.coordinates[1], lng: segment.trainRoute.stations.to.coordinates[0] },
            distanceFromPlace: segment.trainRoute.stations.to.distanceFromPlace
          }
        }
      };
    }
    
    return converted;
  };

  // Helper to convert from Firestore format back to app format
  const convertRouteSegmentFromFirestore = (segment: any): RouteSegment => {
    const converted: RouteSegment = {
      fromIndex: segment.fromIndex,
      toIndex: segment.toIndex,
      distance: segment.distance,
      duration: segment.duration,
      isApproximate: segment.isApproximate,
      geometry: segment.geometry.map((coord: any) => [coord.lng, coord.lat] as [number, number])
    };
    
    if (segment.trainRoute) {
      converted.trainRoute = {
        geometry: segment.trainRoute.geometry.map((coord: any) => [coord.lng, coord.lat] as [number, number]),
        distance: segment.trainRoute.distance,
        stations: {
          from: {
            name: segment.trainRoute.stations.from.name,
            coordinates: [segment.trainRoute.stations.from.coordinates.lng, segment.trainRoute.stations.from.coordinates.lat] as [number, number],
            distanceFromPlace: segment.trainRoute.stations.from.distanceFromPlace
          },
          to: {
            name: segment.trainRoute.stations.to.name,
            coordinates: [segment.trainRoute.stations.to.coordinates.lng, segment.trainRoute.stations.to.coordinates.lat] as [number, number],
            distanceFromPlace: segment.trainRoute.stations.to.distanceFromPlace
          }
        }
      };
    }
    
    return converted;
  };

  // Saved trips management - extracted to custom hook
  const {
    savedTrips,
    setSavedTrips,
    currentTripId,
    setCurrentTripId,
    hasUnsavedChanges,
    setHasUnsavedChanges,
    isSendingEmail,
    isGeneratingSummary,
    tripSummary,
    setTripSummary,
    placePhotos,
    setPlacePhotos,
    summaryError,
    handleSaveTrip: hookHandleSaveTrip,
    handleLoadTrip: hookHandleLoadTrip,
    handleDeleteTrip: hookHandleDeleteTrip,
    handleEmailItinerary: hookHandleEmailItinerary,
    handleGenerateSummary,
    clearSummary,
    handleRequestQuote,
  } = useSavedTrips({
    userId: authState.user?.userId,
    apiBaseUrl: API_BASE_URL,
    convertRouteSegmentForFirestore,
    convertRouteSegmentFromFirestore,
  });

  // Async summary generation hook for long trips (>5 days)
  const asyncSummary = useAsyncSummaryGeneration({
    onComplete: (result) => {
      // Load the generated summary into state (modal will be shown when user clicks "View Summary")
      if (currentTripId && result) {
        handleGenerateSummary(currentTripId, result.optimizeOrder, undefined);
      }
    },
    onError: (error) => {
      console.error('[Plan] Async summary generation failed:', error);
      showToast(error || 'Failed to generate trip summary', 'error');
    }
  });

  // Get drawer height based on position (as percentage of viewport)
  const getDrawerHeightPercent = () => {
    switch (drawerPosition) {
      case 'collapsed':
        return 10; // 10vh - just show header
      case 'half':
        return 50; // 50vh - half screen
      case 'expanded':
        return 85; // 85vh - almost full (account for bottom nav)
      default:
        return 10;
    }
  };

  // Get drawer height in pixels (for content calculations)
  const getDrawerHeight = () => {
    return (window.innerHeight * getDrawerHeightPercent()) / 100;
  };

  // Calculate map offset for mobile view to keep places visible above drawer
  const getMapOffsetForDrawer = (): [number, number] => {
    if (!isDesktop && mapRef.current) {
      const screenHeight = window.innerHeight;
      
      // On mobile, always center marker at 30% from top (which is 60% into the top 50% of screen)
      // Screen center is at 50% (screenHeight / 2)
      // We want it at 30% from top (screenHeight * 0.3)
      // Offset needed: (screenHeight * 0.3) - (screenHeight * 0.5) = -screenHeight * 0.2
      // Negative offset moves the view up
      const pixelOffset = -screenHeight * 0.2;
      
      return [0, pixelOffset];
    }
    
    return [0, 0];
  };

  // Calculate map offset based on drawer position
  // For day-trip and other views:
  // - Drawer collapsed: center at 50% (natural center)
  // - Drawer half: center at 30% from top
  // - Drawer expanded: center at 10% from top
  //
  // For multi-day-trip-itinerary (large content):
  // - Drawer collapsed: center at 50% (natural center)
  // - Drawer half: center at 40-45% from top (moved down)
  // - Drawer expanded: center at 20-25% from top (moved down)
  const getDrawerAdjustedOffset = (): [number, number] => {
    if (!isDesktop) {
      const screenHeight = window.innerHeight;
      
      // Determine target position based on drawer state and current view
      let targetPercent = 0.5; // Default: center (50%)
      
      const isMultiDayView = currentView === 'multi-day-trip-itinerary' || currentView === 'multi-day-trip-day-destinations';
      
      if (isMultiDayView) {
        // Multi-day view specific positions (itinerary and destination picker)
        if (drawerPosition === 'collapsed') {
          targetPercent = 0.7; // Collapsed drawer: 70% from top
        } else if (drawerPosition === 'half') {
          targetPercent = 0.525; // Half drawer: 52.5% from top
        } else if (drawerPosition === 'expanded') {
          targetPercent = 0.31; // Expanded drawer: 31% from top
        }
      } else {
        // Default positions for other views
        if (drawerPosition === 'half') {
          targetPercent = 0.3; // Half drawer: 30% from top
        } else if (drawerPosition === 'expanded') {
          targetPercent = 0.1; // Expanded drawer: 10% from top
        }
      }
      
      // Mapbox natural center is at 50% of screen
      const mapboxNaturalCenter = screenHeight * 0.5;
      
      // Target position in pixels
      const targetPosition = screenHeight * targetPercent;
      
      // Offset to move from natural center to target position
      const pixelOffset = targetPosition - mapboxNaturalCenter;
      
      return [0, pixelOffset];
    }
    
    return [0, 0];
  };


  // Helper function: Check if any days have places added
  const hasAnyDayWithPlaces = useCallback(() => {
    // Check finalized days
    if (finalizedDays.size > 0) {
      for (const [, dayData] of Array.from(finalizedDays.entries())) {
        if (dayData.places && dayData.places.length > 0) {
          return true;
        }
      }
    }
    // Check day places (in-progress days)
    if (dayPlaces.size > 0) {
      for (const [, places] of Array.from(dayPlaces.entries())) {
        if (places && places.length > 0) {
          return true;
        }
      }
    }
    return false;
  }, [finalizedDays, dayPlaces]);

  // Helper function: Check which day should be planned next
  const getNextDayToPlan = useCallback(() => {
    // If no days finalized WITH places, Day 0 (Day 1) is next
    if (finalizedDays.size === 0) {
      return 0;
    }
    
    // Find the highest finalized day index that actually HAS places
    const finalizedIndices = Array.from(finalizedDays.keys()).sort((a, b) => a - b);
    
    // Filter to only include days/blocks that have places
    const finalizedWithPlaces = finalizedIndices.filter(idx => {
      const day = finalizedDays.get(idx);
      return day && day.places && day.places.length > 0;
    });
    
    // If no days have places yet, start from Day 0
    if (finalizedWithPlaces.length === 0) {
      return 0;
    }
    
    const lastFinalizedIndex = finalizedWithPlaces[finalizedWithPlaces.length - 1];
    const lastFinalizedDay = finalizedDays.get(lastFinalizedIndex);
    
    // Check if it's a merged block
    if (lastFinalizedDay?.isMultiDayBlock && lastFinalizedDay.blockDayIndices) {
      // Next day is after the last day of the merged block
      const lastDayOfBlock = lastFinalizedDay.blockDayIndices[lastFinalizedDay.blockDayIndices.length - 1];
      return lastDayOfBlock + 1;
    }
    
    // Next day is directly after the last finalized day
    return lastFinalizedIndex + 1;
  }, [finalizedDays]);

  // Detect screen size changes
  useEffect(() => {
    const handleResize = () => {
      setIsDesktop(window.innerWidth >= 768);
    };

    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  // Prevent scroll propagation from drawer content to map
  useEffect(() => {
    const contentEl = drawerContentRef.current;
    if (!contentEl) return;

    const handleTouchStart = (e: TouchEvent) => {
      const touch = e.touches[0];
      contentEl.dataset.touchStartY = touch.clientY.toString();
      contentEl.dataset.scrollTopStart = contentEl.scrollTop.toString();
    };

    const handleTouchMove = (e: TouchEvent) => {
      const touch = e.touches[0];
      const startY = parseFloat(contentEl.dataset.touchStartY || '0');
      const deltaY = touch.clientY - startY;
      
      const scrollTop = contentEl.scrollTop;
      const scrollHeight = contentEl.scrollHeight;
      const clientHeight = contentEl.clientHeight;
      const isScrollable = scrollHeight > clientHeight;

      if (!isScrollable) {
        return;
      }

      const isAtTop = scrollTop <= 0;
      const isAtBottom = scrollTop + clientHeight >= scrollHeight - 1;

      // Prevent default to stop map scrolling when content is scrollable
      if (!isAtTop && !isAtBottom) {
        e.stopPropagation();
      } else if (isAtTop && deltaY > 0) {
        e.stopPropagation();
      } else if (isAtBottom && deltaY < 0) {
        e.stopPropagation();
      }
    };

    contentEl.addEventListener('touchstart', handleTouchStart, { passive: true });
    contentEl.addEventListener('touchmove', handleTouchMove, { passive: false });

    return () => {
      contentEl.removeEventListener('touchstart', handleTouchStart);
      contentEl.removeEventListener('touchmove', handleTouchMove);
    };
  }, [drawerPosition]);

  // Initialize Mapbox
  useEffect(() => {
    // Check if Mapbox API key is configured
    if (!MAPBOX_API_KEY_ADVANCED) {
      setError('Mapbox API key is not configured');
      return;
    }

    // Get the correct container based on screen size
    const containerRef = isDesktop ? mapContainerDesktopRef.current : mapContainerMobileRef.current;
    if (!containerRef) return;

    // Clean up existing map if switching between desktop/mobile
    if (mapRef.current) {
      mapRef.current.remove();
      mapRef.current = null;
      setMapLoaded(false);
    }

    try {
      // Set the access token
      mapboxgl.accessToken = MAPBOX_API_KEY_ADVANCED;

      // Create the map
      const map = new mapboxgl.Map({
        container: containerRef,
        style: 'mapbox://styles/mapbox/streets-v12',
        center: [80.7718, 7.8731], // Sri Lanka
        zoom: 7,
      });

      // Add navigation controls
      map.addControl(new mapboxgl.NavigationControl(), 'top-right');

      // Add scale control
      map.addControl(new mapboxgl.ScaleControl(), 'bottom-right');

      // Handle map load
      map.on('load', () => {
        setMapLoaded(true);
      });

      // Handle map errors
      map.on('error', (e) => {
        setError('Failed to load map');
      });

      // Store map reference
      mapRef.current = map;

      // Cleanup
      return () => {
        if (mapRef.current) {
          mapRef.current.remove();
          mapRef.current = null;
        }
      };
    } catch (err) {
      setError('Failed to initialize map');
    }
  }, [isDesktop]);

  // Resize map when drawer position changes
  useEffect(() => {
    if (mapRef.current) {
      // Small delay to let DOM update
      setTimeout(() => {
        mapRef.current?.resize();
      }, 300);
    }
  }, [drawerPosition]);

  // Close "Add More Places" dropdown when clicking outside
  useEffect(() => {
    if (!showAddMorePlacesDropdown) return;

    const handleClickOutside = (event: MouseEvent) => {
      const target = event.target as HTMLElement;
      // Check if click is outside the dropdown
      if (!target.closest('.add-more-places-dropdown-container')) {
        setShowAddMorePlacesDropdown(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [showAddMorePlacesDropdown]);

  // Navigation functions
  const navigateToView = (view: DrawerView) => {
    setCurrentView(view);
    // Auto-expand drawer when navigating to new view
    if (view !== 'home' && drawerPosition === 'collapsed') {
      setHalf();
    }
    // Exit map selection mode when navigating away from day-trip
    if (view !== 'day-trip') {
      setIsMapSelectionMode(false);
    }
  };

  const navigateBack = () => {
    if (currentView === 'saved-day-trips' || currentView === 'saved-multi-day-trips' || currentView === 'quick-quotes') {
      setTripOptionsMode('saved');
      handleNavigation('trip-options');
    } else if (currentView === 'trip-options') {
      handleNavigation('home');
    } else if (currentView === 'my-day-trip') {
      handleNavigation('day-trip-destinations');
    } else if (currentView === 'day-trip-destinations') {
      // Reset places and map markers when going back to day trip planning
      setNearbyPlaces([]);
      setTripPlaces([]);  // Clear previously selected destinations
      // Clear map markers
      placeMarkersRef.current.forEach(marker => marker.remove());
      placeMarkersRef.current = [];
      handleNavigation('day-trip');
    } else if (currentView === 'multi-day-trip-plan-day') {
      handleNavigation('multi-day-trip-day-destinations');
    } else if (currentView === 'multi-day-trip-day-destinations') {
      // Reset places and map markers when going back to itinerary
      setNearbyPlaces([]);
      // Clear map markers
      placeMarkersRef.current.forEach(marker => marker.remove());
      placeMarkersRef.current = [];
      
      // Check if the current location has any places selected
      // If not, remove it from dayLocations (user backed out without selecting places)
      if (selectedDayIndex !== null) {
        const placesForCurrentLocation = tripPlaces.filter(p => (p.primaryLocationIndex || 0) === currentPrimaryLocationIndex);
        if (placesForCurrentLocation.length === 0) {
          // No places selected for this location - remove it from dayLocations
          setDayLocations(prev => {
            const newMap = new Map(prev);
            const locations = newMap.get(selectedDayIndex) || [];
            // Only remove if this is the location we just added (last one in the array)
            if (locations.length > 0 && currentPrimaryLocationIndex === locations.length - 1) {
              const updatedLocations = locations.slice(0, -1); // Remove last location
              if (updatedLocations.length > 0) {
                newMap.set(selectedDayIndex, updatedLocations);
              } else {
                newMap.delete(selectedDayIndex);
              }
            }
            return newMap;
          });
        }
      }
      
      handleNavigation('multi-day-trip-itinerary');
      setSelectedDayIndex(null);
      setSelectedDayLocation(null);
    } else if (currentView === 'multi-day-trip-itinerary') {
      // Clear finalized days and related state
      setFinalizedDays(new Map());
      setCombinedDayBlocks(new Map());
      setDayPlaces(new Map());
      setDayTransportation(new Map());
      setDayRouteSegments(new Map());
      setActiveTrainRoutes(new Map());
      setDayLocations(new Map());
      
      // Reset trip end state
      setIsTripEnded(false);
      setTripEndLocation(null);
      setTripEndRouteSegment(null);
      setTripEndTransportMode('car');
      removeTripEndMarker();
      setTripEndTrainAvailable(false);
      
      // Reset selections
      setSelectedDayIndex(null);
      setSelectedDayLocation(null);
      
      // Clear nearby places to prevent markers from being re-added
      setNearbyPlaces([]);
      
      // Clear map markers
      placeMarkersRef.current.forEach(marker => marker.remove());
      placeMarkersRef.current = [];
      
      // Clear start location marker
      if (startMarkerRef.current) {
        startMarkerRef.current.remove();
        startMarkerRef.current = null;
      }
      
      // Reset multi-day start location (keep dates and travelers)
      setMultiDayStartLocation(null);
      
      // Use handleNavigation so the save prompt shows if there are unsaved changes
      handleNavigation('multi-day-trip-details');
    } else if (currentView === 'multi-day-trip-details') {
      handleNavigation('multi-day-trip');
    } else if (currentView === 'day-trip' || currentView === 'multi-day-trip') {
      // Reset everything when going back to trip type selection
      // Reset day trip data
      setStartLocation(null);
      setDayTripName('');
      setNearbyPlaces([]);
      setTripPlaces([]);
      // Clear map markers
      placeMarkersRef.current.forEach(marker => marker.remove());
      placeMarkersRef.current = [];
      // Remove start location marker if exists
      if (startMarkerRef.current) {
        startMarkerRef.current.remove();
        startMarkerRef.current = null;
      }
      
      // Reset multi-day trip data
      setMultiDayTripName('');
      setMultiDayStartDate(null);
      setMultiDayEndDate(null);
      setMultiDayAdults(2);
      setMultiDayChildren(0);
      setMultiDayInfants(0);
      setDayLocations(new Map());
      setDayPlaces(new Map());
      setFinalizedDays(new Map());
      setSelectedDayIndex(null);
      setSelectedDayLocation(null);
      setDayTransportation(new Map());
      setDayRouteSegments(new Map());
      setActiveTrainRoutes(new Map());
      setExpandedDays(new Set());
      setCombinedDayBlocks(new Map());
      
      // Reset trip end data
      setIsTripEnded(false);
      setTripEndLocation(null);
      setTripEndTransportMode('car');
      setTripEndRouteSegment(null);
      setTripEndTrainAvailable(false);
      
      // Reset filters
      setSelectedTypes([]);
      setMaxDistance(20);
      setShowOnlySaved(false);
      
      handleNavigation('trip-options');
    }
    // Exit map selection mode when going back
    setIsMapSelectionMode(false);
  };

  const handleContinueToDestinations = () => {
    // Validate trip name
    if (!dayTripName.trim()) {
      const roasts = [
        "Hold up! 🤔 Even your GPS needs a trip name to know where it's going...",
        "Whoa there! 🛑 A trip without a name is like a destination without a map!",
        "Not so fast! ✋ Give your adventure a name it deserves!",
        "Easy tiger! 🐯 Name your trip first - it's not a secret mission!",
        "Pump the brakes! 🚗 Every great journey starts with a great name!"
      ];
      const randomRoast = roasts[Math.floor(Math.random() * roasts.length)];
      showToast(randomRoast, 'warning', 4000);
      return;
    }
    setCurrentView('day-trip-destinations');
    // Places will be fetched automatically by useEffect when view changes
  };

  // Location selection functions
  const handleUseCurrentLocation = async () => {
    setLocationLoading(true);
    try {
      if (!navigator.geolocation) {
        alert('Geolocation is not supported by your browser');
        setLocationLoading(false);
        return;
      }

      navigator.geolocation.getCurrentPosition(
        (position) => {
          const coords: [number, number] = [position.coords.longitude, position.coords.latitude];
          setStartLocation({ name: 'Current Location', coordinates: coords });
          setTripPlaces([]);  // Clear any previously selected destinations
          // Map centering will be handled by useEffect based on drawer position
          setLocationLoading(false);
        },
        (error) => {
          alert('Unable to get your location. Please try another method.');
          setLocationLoading(false);
        }
      );
    } catch (err) {
      setLocationLoading(false);
    }
  };

  const handleQuickLocation = (location: QuickLocation) => {
    setStartLocation({ name: location.name, coordinates: location.coordinates });
    setTripPlaces([]);  // Clear any previously selected destinations
    // Map centering will be handled by useEffect based on drawer position
  };

  const handleSelectOnMap = () => {
    setIsMapSelectionMode(true);
    // Collapse drawer on mobile
    if (!isDesktop) {
      collapse();
    }
  };

  const handleClearLocation = () => {
    setStartLocation(null);
    // Remove marker from map
    if (startMarkerRef.current) {
      startMarkerRef.current.remove();
      startMarkerRef.current = null;
    }
    // Reset map view to Sri Lanka
    if (mapRef.current) {
      const offset = getMapOffsetForDrawer();
      mapRef.current.flyTo({ center: [80.7718, 7.8731], zoom: 7, duration: 2000, offset: offset });
    }
  };

  // Trip end marker functions and management now handled by useTripEndLocation hook

  // Toggle type filter
  const toggleTypeFilter = (type: PlaceType) => {
    setSelectedTypes(prev => 
      prev.includes(type) 
        ? prev.filter(t => t !== type)
        : [...prev, type]
    );
  };

  // Mobile card swipe handlers
  const handleCardTouchStart = (e: React.TouchEvent) => {
    setCardSwipeStart(e.touches[0].clientX);
    setIsCardSwiping(true);
  };

  const handleCardTouchMove = (e: React.TouchEvent) => {
    if (!isCardSwiping) return;
    const currentX = e.touches[0].clientX;
    const diff = currentX - cardSwipeStart;
    setCardSwipeOffset(diff);
  };

  const handleCardTouchEnd = () => {
    if (!isCardSwiping) return;
    setIsCardSwiping(false);

    const threshold = 50; // Minimum swipe distance to trigger card change
    
    if (cardSwipeOffset > threshold) {
      // Swipe right - go to previous card (with loop)
      setActiveCardIndex(prev => prev === 0 ? nearbyPlaces.length - 1 : prev - 1);
    } else if (cardSwipeOffset < -threshold) {
      // Swipe left - go to next card (with loop)
      setActiveCardIndex(prev => prev === nearbyPlaces.length - 1 ? 0 : prev + 1);
    }
    
    setCardSwipeOffset(0);
  };

  // Navigate to specific card (from list click)
  const navigateToCard = (index: number) => {
    setActiveCardIndex(index);
    // Center map on the selected place
    const place = nearbyPlaces[index];
    if (place?.geo_point && mapRef.current) {
      const lng = place.geo_point.lon || place.geo_point.lng || place.geo_point.longitude;
      const lat = place.geo_point.lat || place.geo_point.latitude;
      if (lng && lat) {
        const offset = getMapOffsetForDrawer();
        mapRef.current.flyTo({
          center: [lng, lat],
          zoom: 14,
          duration: 1000,
          offset: offset
        });
      }
    }
  };

  // Reset card index only when switching views or when array becomes empty
  useEffect(() => {
    // Only reset to 0 if current index is out of bounds
    const maxIndex = currentView === 'day-trip-destinations' 
      ? nearbyPlaces.length - 1 
      : tripPlaces.length - 1;
    
    if (maxIndex < 0 || activeCardIndex > maxIndex) {
    setActiveCardIndex(0);
    }
  }, [nearbyPlaces.length, tripPlaces.length, currentView]);

  // Track unsaved changes
  useEffect(() => {
    // Mark as unsaved when trip data changes (but not on initial load)
    if (currentView === 'my-day-trip' && tripPlaces.length > 0 && !currentTripId) {
      setHasUnsavedChanges(true);
    }
    if ((currentView === 'multi-day-trip-itinerary' || currentView === 'multi-day-trip-plan-day') && 
        (finalizedDays.size > 0 || dayLocations.size > 0) && !currentTripId) {
      setHasUnsavedChanges(true);
    }
  }, [tripPlaces, finalizedDays, dayLocations, multiDayStartDate, multiDayEndDate, currentView, currentTripId]);

  // Browser refresh/close detection (NOT for internal navigation)
  useEffect(() => {
    const handleBeforeUnload = (e: BeforeUnloadEvent) => {
      if (hasUnsavedChanges) {
        e.preventDefault();
        e.returnValue = '';
      }
    };
    
    window.addEventListener('beforeunload', handleBeforeUnload);
    return () => window.removeEventListener('beforeunload', handleBeforeUnload);
  }, [hasUnsavedChanges]);

  // Load saved trips when entering saved trips views
  useEffect(() => {
    const loadSavedTrips = async () => {
      if (!authState.user?.userId) return;
      if (currentView !== 'saved-day-trips' && currentView !== 'saved-multi-day-trips' && currentView !== 'quick-quotes') return;
      
      setLoadingTrips(true);
      try {
        const trips = await tripService.getUserTrips(authState.user.userId);
        setSavedTrips(trips);
      } catch (error) {
      } finally {
        setLoadingTrips(false);
      }
    };
    
    loadSavedTrips();
  }, [currentView, authState.user?.userId]);

  // Center map on active card in mobile carousel/list/grid with drawer-aware offset
  useEffect(() => {
    if (!mapRef.current || isDesktop) return;

    // Skip in plan-day view - map should maintain its current view showing all day places
    if (currentView === 'multi-day-trip-plan-day') return;

    let placeToCenter = null;

    // Determine which place to center based on current view
    if (currentView === 'day-trip-destinations' && nearbyPlaces.length > 0) {
      placeToCenter = nearbyPlaces[activeCardIndex];
    } else if (currentView === 'multi-day-trip-day-destinations') {
      if (nearbyPlaces.length > 0) {
        placeToCenter = nearbyPlaces[activeCardIndex];
      }
    }

    if (placeToCenter?.geo_point) {
      const lng = placeToCenter.geo_point.lon || placeToCenter.geo_point.lng || placeToCenter.geo_point.longitude;
      const lat = placeToCenter.geo_point.lat || placeToCenter.geo_point.latitude;
      
      if (lng && lat) {
        const offset = getDrawerAdjustedOffset();
        mapRef.current.flyTo({
          center: [lng, lat],
          zoom: 14,
          duration: 1000,
          offset: offset
        });
      }
    }
  }, [activeCardIndex, currentView, nearbyPlaces, tripPlaces, isDesktop, drawerPosition]);

  // Reset finalized state when trip places change (but keep review visible for reference)
  useEffect(() => {
    setIsTripFinalized(false); // Reset finalized state when places change
    // Keep aiReview visible - it will be cleared when AI Review is clicked again
  }, [tripPlaces]);

  // Draw route lines on map for "my-day-trip" view
  useEffect(() => {
    if (!mapRef.current || !mapLoaded || currentView !== 'my-day-trip' || !startLocation || tripPlaces.length === 0) {
      // Remove route layers if they exist
      if (mapRef.current) {
        if (mapRef.current.getLayer('route-line')) {
          mapRef.current.removeLayer('route-line');
        }
        if (mapRef.current.getLayer('route-arrows')) {
          mapRef.current.removeLayer('route-arrows');
        }
        if (mapRef.current.getSource('route')) {
          mapRef.current.removeSource('route');
        }
        if (mapRef.current.getSource('route-arrows')) {
          mapRef.current.removeSource('route-arrows');
        }
      }
      // Remove start marker if view changes away from my-day-trip
      if (startMarkerRef.current && currentView !== 'my-day-trip') {
        startMarkerRef.current.remove();
        startMarkerRef.current = null;
      }
      return;
    }

    // Add start location marker for my-day-trip view
    if (startMarkerRef.current) {
      startMarkerRef.current.remove();
    }
    startMarkerRef.current = new mapboxgl.Marker({ color: '#22c55e' })
      .setLngLat(startLocation.coordinates)
      .addTo(mapRef.current);

    // Build route coordinates: start → place1 → place2 → ...
    const coordinates: [number, number][] = [startLocation.coordinates];
    
    tripPlaces.forEach(place => {
      const lng = place.geo_point?.lon || place.geo_point?.lng || place.geo_point?.longitude;
      const lat = place.geo_point?.lat || place.geo_point?.latitude;
      if (lng && lat) {
        coordinates.push([lng, lat]);
      }
    });

    // Create GeoJSON for the route line
    const routeGeoJSON = {
      type: 'Feature' as const,
      properties: {},
      geometry: {
        type: 'LineString' as const,
        coordinates
      }
    };

    // Calculate midpoints for arrows
    const arrowPoints: any[] = [];
    for (let i = 0; i < coordinates.length - 1; i++) {
      const [lng1, lat1] = coordinates[i];
      const [lng2, lat2] = coordinates[i + 1];
      
      // Calculate midpoint
      const midLng = (lng1 + lng2) / 2;
      const midLat = (lat1 + lat2) / 2;
      
      // Calculate bearing (angle) from point 1 to point 2
      // Using atan2 to get the angle, then adding 180 to point in the correct direction
      const dLng = lng2 - lng1;
      const dLat = lat2 - lat1;
      const bearing = (Math.atan2(dLng, dLat) * (180 / Math.PI) + 180) % 360;
      
      arrowPoints.push({
        type: 'Feature' as const,
        properties: {
          bearing: bearing
        },
        geometry: {
          type: 'Point' as const,
          coordinates: [midLng, midLat]
        }
      });
    }

    const arrowsGeoJSON = {
      type: 'FeatureCollection' as const,
      features: arrowPoints
    };

    // Remove existing route layers if they exist
    if (mapRef.current.getLayer('route-line')) {
      mapRef.current.removeLayer('route-line');
    }
    if (mapRef.current.getLayer('route-arrows')) {
      mapRef.current.removeLayer('route-arrows');
    }
    if (mapRef.current.getSource('route')) {
      mapRef.current.removeSource('route');
    }
    if (mapRef.current.getSource('route-arrows')) {
      mapRef.current.removeSource('route-arrows');
    }

    // Add route line source
    mapRef.current.addSource('route', {
      type: 'geojson',
      data: routeGeoJSON
    });

    // Add arrows source
    mapRef.current.addSource('route-arrows', {
      type: 'geojson',
      data: arrowsGeoJSON
    });

    // Add line layer
    mapRef.current.addLayer({
      id: 'route-line',
      type: 'line',
      source: 'route',
      layout: {
        'line-join': 'round',
        'line-cap': 'round'
      },
      paint: {
        'line-color': '#3b82f6',
        'line-width': 4
      }
    });

    // Load arrow icon if not already loaded
    if (!mapRef.current.hasImage('route-arrow')) {
      // Create arrow SVG as data URL
      const arrowSvg = `
        <svg width="24" height="24" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
          <path d="M12 2 L12 18 M12 18 L6 12 M12 18 L18 12" 
                stroke="#3b82f6" 
                stroke-width="3" 
                fill="none" 
                stroke-linecap="round" 
                stroke-linejoin="round"/>
        </svg>
      `;
      const img = new Image(24, 24);
      img.onload = () => {
        if (mapRef.current && !mapRef.current.hasImage('route-arrow')) {
          mapRef.current.addImage('route-arrow', img);
          
          // Add arrow symbols layer after image is loaded
          if (!mapRef.current.getLayer('route-arrows')) {
            mapRef.current.addLayer({
              id: 'route-arrows',
              type: 'symbol',
              source: 'route-arrows',
              layout: {
                'icon-image': 'route-arrow',
                'icon-size': 0.8,
                'icon-rotate': ['get', 'bearing'],
                'icon-rotation-alignment': 'map',
                'icon-allow-overlap': true,
                'icon-ignore-placement': true
              }
            });
          }
        }
      };
      img.src = 'data:image/svg+xml;base64,' + btoa(arrowSvg);
    } else {
      // Arrow image already exists, just add the layer
      mapRef.current.addLayer({
        id: 'route-arrows',
        type: 'symbol',
        source: 'route-arrows',
        layout: {
          'icon-image': 'route-arrow',
          'icon-size': 0.8,
          'icon-rotate': ['get', 'bearing'],
          'icon-rotation-alignment': 'map',
          'icon-allow-overlap': true,
          'icon-ignore-placement': true
        }
      });
    }

    // Fit map bounds to show all places
    // On mobile, use more bottom padding to keep content visible above drawer
    const bounds = new mapboxgl.LngLatBounds();
    
    // Validate and extend start location coordinates
    if (startLocation.coordinates && Array.isArray(startLocation.coordinates) && startLocation.coordinates.length === 2) {
      const [lng, lat] = startLocation.coordinates;
      if (typeof lng === 'number' && typeof lat === 'number' && lng !== 0 && lat !== 0) {
        bounds.extend([lng, lat]);
      }
    }
    
    tripPlaces.forEach(place => {
      const lng = place.geo_point?.lon || place.geo_point?.lng || place.geo_point?.longitude;
      const lat = place.geo_point?.lat || place.geo_point?.latitude;
      if (lng && lat && lng !== 0 && lat !== 0) {
        bounds.extend([lng, lat]);
      }
    });
    
    // Apply larger bottom padding on mobile to shift view up (same as 30% from top)
    const screenHeight = window.innerHeight;
    const bottomPadding = isDesktop ? 100 : screenHeight * 0.4; // 40% bottom padding = 30% from top center
    
    // Only fit bounds if we have valid coordinates
    if (!bounds.isEmpty()) {
      mapRef.current.fitBounds(bounds, {
        padding: { 
          top: 100, 
          bottom: bottomPadding, 
          left: 50, 
          right: 50 
        },
        duration: 1000,
        maxZoom: 14
      });
    }

    // Cleanup function
    return () => {
      if (mapRef.current) {
        if (mapRef.current.getLayer('route-line')) {
          mapRef.current.removeLayer('route-line');
        }
        if (mapRef.current.getLayer('route-arrows')) {
          mapRef.current.removeLayer('route-arrows');
        }
        if (mapRef.current.getSource('route')) {
          mapRef.current.removeSource('route');
        }
        if (mapRef.current.getSource('route-arrows')) {
          mapRef.current.removeSource('route-arrows');
        }
      }
    };
  }, [currentView, tripPlaces, startLocation, mapLoaded]);

  // NOTE: Route calculation moved to useRouteCalculation hook
  
  // Store current day's places when they change
  useEffect(() => {
    if (currentView !== 'multi-day-trip-plan-day' || selectedDayIndex === null) {
      return;
    }

    setDayPlaces(prev => {
      const currentPlaces = prev.get(selectedDayIndex);
      const placesChanged = !currentPlaces || 
        currentPlaces.length !== tripPlaces.length ||
        currentPlaces.some((place, idx) => place.id !== tripPlaces[idx]?.id);
      
      if (placesChanged) {
        const newMap = new Map(prev);
        newMap.set(selectedDayIndex, tripPlaces);
        return newMap;
      }
      return prev;
    });
  }, [currentView, selectedDayIndex, tripPlaces]);

  // Adjust map bounds to show all places in day planner view
  useEffect(() => {
    if (currentView !== 'multi-day-trip-plan-day' || !mapRef.current || !mapLoaded || selectedDayIndex === null) {
      return;
    }

    // Get all coordinates to include in bounds
    const coordinates: [number, number][] = [];
    
    // Determine start location (same logic as calculateRoutes)
    let dayStartLocation: QuickLocation | null = null;
    
    if (selectedDayIndex === 0) {
      dayStartLocation = multiDayStartLocation;
    } else {
      // Find the actual previous day, accounting for merged blocks
      let previousDayPlaces: PlaceItem[] | undefined;
      const previousDayIndex = selectedDayIndex - 1;
      let foundInBlock = false;
      
      for (const [blockStartIndex, blockIndices] of Array.from(combinedDayBlocks.entries())) {
        if (blockIndices.includes(previousDayIndex)) {
          const previousDayData = finalizedDays.get(blockStartIndex);
          previousDayPlaces = previousDayData?.places;
          foundInBlock = true;
          break;
        }
      }
      
      if (!foundInBlock) {
        previousDayPlaces = dayPlaces.get(previousDayIndex);
        if (!previousDayPlaces) {
          const previousDayData = finalizedDays.get(previousDayIndex);
          previousDayPlaces = previousDayData?.places;
        }
      }
      
      if (previousDayPlaces && previousDayPlaces.length > 0) {
        const lastPlace = previousDayPlaces[previousDayPlaces.length - 1];
        dayStartLocation = {
          name: lastPlace.name,
          coordinates: [
            lastPlace.geo_point?.lon || lastPlace.geo_point?.lng || 0,
            lastPlace.geo_point?.lat || 0
          ]
        };
      }
    }
    
    // Add start location (with validation)
    if (dayStartLocation?.coordinates && Array.isArray(dayStartLocation.coordinates) && dayStartLocation.coordinates.length === 2) {
      const [lng, lat] = dayStartLocation.coordinates;
      if (typeof lng === 'number' && typeof lat === 'number' && lng !== 0 && lat !== 0) {
        coordinates.push([lng, lat]);
      }
    }
    
    // Add all trip places
    tripPlaces.forEach(place => {
      const lng = place.geo_point?.lon || place.geo_point?.lng || 0;
      const lat = place.geo_point?.lat || 0;
      if (lng && lat && lng !== 0 && lat !== 0) {
        coordinates.push([lng, lat]);
      }
    });
    
    // If we have at least 2 coordinates, fit bounds
    if (coordinates.length >= 2) {
      const bounds = new mapboxgl.LngLatBounds();
      coordinates.forEach(coord => bounds.extend(coord));
      
      mapRef.current.fitBounds(bounds, {
        padding: { top: 100, bottom: 100, left: 100, right: isDesktop ? 450 : 100 }, // More padding on right for desktop panel
        maxZoom: 13,
        duration: 1000
      });
    } else if (coordinates.length === 1) {
      // Just one coordinate, center on it
      const offset = getMapOffsetForDrawer();
      mapRef.current.flyTo({
        center: coordinates[0],
        zoom: 12,
        duration: 1000,
        offset: offset
      });
    }
  }, [currentView, selectedDayIndex, tripPlaces, mapLoaded, multiDayStartLocation, dayPlaces, combinedDayBlocks, finalizedDays]);

  // Clean up map layers when all places are removed from a day
  useEffect(() => {
    if (currentView !== 'multi-day-trip-plan-day' || selectedDayIndex === null || !mapRef.current || !mapLoaded) {
      return;
    }

    // If all places have been removed, clean up map layers for this day
    if (tripPlaces.length === 0) {
      const map = mapRef.current;
      const segments = dayRouteSegments.get(selectedDayIndex) || [];
      
      
      // Remove all route layers for this day
      segments.forEach((_, segmentIndex) => {
        const layerId = `day-route-line-${selectedDayIndex}-seg${segmentIndex}`;
        const sourceId = `day-route-segment-${selectedDayIndex}-seg${segmentIndex}`;
        const trainLayerPrefix = `day-train-${selectedDayIndex}-seg${segmentIndex}`;
        
        try {
          // Remove car/bus layers
          if (map.getLayer(layerId)) map.removeLayer(layerId);
          if (map.getSource(sourceId)) map.removeSource(sourceId);
          
          // Remove train layers
          if (map.getLayer(`${trainLayerPrefix}-to-station`)) map.removeLayer(`${trainLayerPrefix}-to-station`);
          if (map.getSource(`${trainLayerPrefix}-to-station-source`)) map.removeSource(`${trainLayerPrefix}-to-station-source`);
          if (map.getLayer(`${trainLayerPrefix}-train-rail`)) map.removeLayer(`${trainLayerPrefix}-train-rail`);
          if (map.getSource(`${trainLayerPrefix}-train-rail-source`)) map.removeSource(`${trainLayerPrefix}-train-rail-source`);
          if (map.getLayer(`${trainLayerPrefix}-from-station`)) map.removeLayer(`${trainLayerPrefix}-from-station`);
          if (map.getSource(`${trainLayerPrefix}-from-station-source`)) map.removeSource(`${trainLayerPrefix}-from-station-source`);
        } catch (e) {
        }
      });
      
      // Also remove from finalizedDays if it was finalized
      setFinalizedDays(prev => {
        const newMap = new Map(prev);
        if (newMap.has(selectedDayIndex)) {
          newMap.delete(selectedDayIndex);
        }
        return newMap;
      });
      
      // Mark as having unsaved changes
      setHasUnsavedChanges(true);
    }
  }, [currentView, selectedDayIndex, tripPlaces.length, dayRouteSegments, mapLoaded, setHasUnsavedChanges]);

  // NOTE: Route calculation moved to useTripEndLocation hook
  
  // Add/remove trip end route to dayRouteSegments when trip end is set/cleared in day planner view
  useEffect(() => {
    // Only apply in day planner view
    if (currentView !== 'multi-day-trip-plan-day' || selectedDayIndex === null) {
      return;
    }

    // Find the last day index
    const lastDayIndex = Array.from(dayLocations.keys()).reduce((max, idx) => Math.max(max, idx), -1);
    
    // Only process if we're in the last day
    if (selectedDayIndex !== lastDayIndex) {
      return;
    }

    setDayRouteSegments(prev => {
      const newMap = new Map(prev);
      let currentSegments = newMap.get(selectedDayIndex) || [];
      
      if (isTripEnded && tripEndRouteSegment) {
        // Remove the last segment if it exists (old trip end route)
        // Trip end is always the last segment, so we remove it first before adding the new one
        if (currentSegments.length > 0) {
          // Check if last segment has the same start point as the current trip end segment
          const lastSegment = currentSegments[currentSegments.length - 1];
          const lastPlaces = dayPlaces.get(selectedDayIndex) || [];
          
          // If we have places, the trip end should start from the last place
          if (lastPlaces.length > 0) {
            const lastPlace = lastPlaces[lastPlaces.length - 1];
            const lastPlaceCoords = [
              lastPlace.geo_point?.lon || lastPlace.geo_point?.lng || 0,
              lastPlace.geo_point?.lat || 0
            ];
            
            // Check if the last segment starts from the last place (indicating it's a trip end segment)
            const lastSegmentStart = lastSegment.geometry[0];
            const coordsMatch = 
              Math.abs(lastSegmentStart[0] - lastPlaceCoords[0]) < 0.0001 &&
              Math.abs(lastSegmentStart[1] - lastPlaceCoords[1]) < 0.0001;
            
            if (coordsMatch) {
              currentSegments = currentSegments.slice(0, -1);
            }
          }
        }
        
        // Add the new trip end route
        const updatedSegments = [...currentSegments, tripEndRouteSegment];
        newMap.set(selectedDayIndex, updatedSegments);
      } else if (!isTripEnded) {
        // Remove trip end route (last segment) when trip end is cleared
        if (currentSegments.length > 0) {
          const lastPlaces = dayPlaces.get(selectedDayIndex) || [];
          
          if (lastPlaces.length > 0) {
            const lastSegment = currentSegments[currentSegments.length - 1];
            const lastPlace = lastPlaces[lastPlaces.length - 1];
            const lastPlaceCoords = [
              lastPlace.geo_point?.lon || lastPlace.geo_point?.lng || 0,
              lastPlace.geo_point?.lat || 0
            ];
            
            // Check if the last segment starts from the last place (indicating it's a trip end segment)
            const lastSegmentStart = lastSegment.geometry[0];
            const coordsMatch = 
              Math.abs(lastSegmentStart[0] - lastPlaceCoords[0]) < 0.0001 &&
              Math.abs(lastSegmentStart[1] - lastPlaceCoords[1]) < 0.0001;
            
            if (coordsMatch) {
              const updatedSegments = currentSegments.slice(0, -1);
              newMap.set(selectedDayIndex, updatedSegments);
            }
          }
        }
      }
      
      return newMap;
    });

    // Also update transportation mode for trip end segment
    // Wait for next tick to ensure dayRouteSegments has been updated
    setTimeout(() => {
      setDayTransportation(prev => {
        const newMap = new Map(prev);
        const currentTransport = newMap.get(selectedDayIndex) || new Map();
        const newTransport = new Map(currentTransport);
        
        if (isTripEnded && tripEndRouteSegment) {
          // Add transport mode for trip end as the last segment
          const currentSegments = dayRouteSegments.get(selectedDayIndex) || [];
          const lastSegmentIndex = currentSegments.length - 1; // Last segment (just added)
          
          newTransport.set(lastSegmentIndex, tripEndTransportMode);
        } else if (!isTripEnded) {
          // Remove transport mode for the last segment when trip end is cleared
          const currentSegments = dayRouteSegments.get(selectedDayIndex) || [];
          if (currentSegments.length > 0) {
            const lastSegmentIndex = currentSegments.length; // One past current length (old trip end index)
            newTransport.delete(lastSegmentIndex);
          }
        }
        
        newMap.set(selectedDayIndex, newTransport);
        return newMap;
      });
    }, 0);
  }, [isTripEnded, tripEndRouteSegment, tripEndTransportMode, selectedDayIndex, currentView, dayLocations]);

  // NOTE: Removed sync effect that was syncing trip end to finalizedDays
  // This was causing infinite loops because:
  // 1. Route calculation updates tripEndRouteSegment
  // 2. Sync effect runs (tripEndRouteSegment in deps)
  // 3. Updates finalizedDays
  // 4. Drawing effect runs
  // 5. Back to step 1
  //
  // Trip end is intentionally separate from finalizedDays and stored separately in Firestore
  // as the tripEnd property

  // Center map to show full Sri Lanka when trip end location is selected
  useEffect(() => {
    if (!mapRef.current || !mapLoaded || !tripEndLocation) return;
    
    const map = mapRef.current;
    
    // Zoom out to show full Sri Lanka
    // Sri Lanka bounds: approximately 5.9°N to 9.9°N, 79.5°E to 81.9°E
    map.fitBounds(
      [
        [79.5, 5.9],  // Southwest coordinates [lng, lat]
        [81.9, 9.9]   // Northeast coordinates [lng, lat]
      ],
      {
        padding: { top: 50, bottom: 50, left: 50, right: 50 },
        duration: 1500,
        essential: true
      }
    );
  }, [tripEndLocation, mapLoaded]);

  // Zoom map to show full Sri Lanka when viewing itinerary
  useEffect(() => {
    if (!mapRef.current || !mapLoaded || currentView !== 'multi-day-trip-itinerary') return;
    
    const map = mapRef.current;
    const isDesktop = window.innerWidth >= 768;
    const screenHeight = window.innerHeight;
    
    // Add a small delay to ensure state updates have settled and map is ready
    const timeoutId = setTimeout(() => {
    // Calculate padding to center map in top 50% on mobile
    const bottomPadding = isDesktop ? 200 : screenHeight * 0.5; // 50% from bottom for mobile
    
    // Zoom out to show full Sri Lanka
    map.fitBounds(
      [
        [SRI_LANKA_BOUNDS.minLng, SRI_LANKA_BOUNDS.minLat],  // Southwest coordinates [lng, lat]
        [SRI_LANKA_BOUNDS.maxLng, SRI_LANKA_BOUNDS.maxLat]   // Northeast coordinates [lng, lat]
      ],
      {
        padding: { 
          top: isDesktop ? 100 : 50, 
          bottom: bottomPadding, 
          left: 50, 
          right: 50 
        },
        duration: 1000,
        essential: true
      }
    );
    }, 300);
    
    return () => clearTimeout(timeoutId);
  }, [currentView, mapLoaded]);

  // NOTE: Train availability checking moved to useRouteCalculation hook

  // Draw route lines on map for multi-day trip plan view
  useEffect(() => {
    if (!mapRef.current || !mapLoaded || currentView !== 'multi-day-trip-plan-day' || selectedDayIndex === null) {
      // Clean up any existing route layers
      if (mapRef.current) {
        try {
          // Check if map style is loaded
          mapRef.current.getStyle();
          const segments = dayRouteSegments.get(selectedDayIndex || 0) || [];
          segments.forEach((_, index) => {
            try {
              if (mapRef.current!.getLayer(`route-line-${index}`)) {
                mapRef.current!.removeLayer(`route-line-${index}`);
              }
              if (mapRef.current!.getLayer(`route-arrows-${index}`)) {
                mapRef.current!.removeLayer(`route-arrows-${index}`);
              }
              if (mapRef.current!.getSource(`route-segment-${index}`)) {
                mapRef.current!.removeSource(`route-segment-${index}`);
              }
              if (mapRef.current!.getSource(`route-arrows-${index}`)) {
                mapRef.current!.removeSource(`route-arrows-${index}`);
              }

              // Clean up train route layers
              ['to-station', 'train-rail', 'from-station'].forEach(suffix => {
                const layerId = `train-${suffix}-${index}`;
                const sourceId = `train-${suffix}-source-${index}`;
                try {
                  if (mapRef.current!.getLayer(layerId)) {
                    mapRef.current!.removeLayer(layerId);
                  }
                  if (mapRef.current!.getSource(sourceId)) {
                    mapRef.current!.removeSource(sourceId);
                  }
                } catch (e) {
                  // Ignore cleanup errors
                }
              });
            } catch (e) {
              // Map might be removed or not ready, ignore
            }
          });
        } catch (e) {
          // Map might be removed or style not loaded, ignore
        }
      }
      return;
    }

    const segments = dayRouteSegments.get(selectedDayIndex);
    if (!segments || segments.length === 0) return;

    const map = mapRef.current;

    // Load arrow icon if not already loaded
    if (!map.hasImage('route-arrow')) {
      const arrowSvg = `
        <svg width="24" height="24" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
          <path d="M12 2 L12 18 M12 18 L6 12 M12 18 L18 12" 
                stroke="#3b82f6" 
                stroke-width="3" 
                fill="none" 
                stroke-linecap="round" 
                stroke-linejoin="round"/>
        </svg>
      `;
      const img = new Image(24, 24);
      img.onload = () => {
        if (map && !map.hasImage('route-arrow')) {
          map.addImage('route-arrow', img);
        }
      };
      img.src = 'data:image/svg+xml;base64,' + btoa(arrowSvg);
    }

    // Get current day's transportation modes
    const currentDayTransport = dayTransportation.get(selectedDayIndex) || new Map();

    // Draw each route segment
    segments.forEach((segment, index) => {
      const transportMode = currentDayTransport.get(index) || 'car';
      
      // Draw car route if mode is 'car' OR if train is selected but no train route data
      const trainRoute = activeTrainRoutes.get(index);
      const shouldDrawCarRoute = transportMode === 'car' || (transportMode === 'train' && !trainRoute);
      
      if (shouldDrawCarRoute) {
        // Create GeoJSON for the route line
        const routeGeoJSON = {
          type: 'Feature' as const,
          properties: {},
          geometry: {
            type: 'LineString' as const,
            coordinates: segment.geometry
          }
        };

        // Calculate arrow positions (1-2 arrows per segment)
      const arrowPoints: any[] = [];
      const coords = segment.geometry;
      
      if (coords.length >= 2) {
        // Add arrow at midpoint
        const midIndex = Math.floor(coords.length / 2);
        const [lng1, lat1] = coords[midIndex - 1] || coords[0];
        const [lng2, lat2] = coords[midIndex] || coords[coords.length - 1];
        
        const midLng = (lng1 + lng2) / 2;
        const midLat = (lat1 + lat2) / 2;
        
        const dLng = lng2 - lng1;
        const dLat = lat2 - lat1;
        const bearing = (Math.atan2(dLng, dLat) * (180 / Math.PI) + 180) % 360;
        
        arrowPoints.push({
          type: 'Feature' as const,
          properties: { bearing },
          geometry: {
            type: 'Point' as const,
            coordinates: [midLng, midLat]
          }
        });

        // For longer routes, add second arrow at 75% point
        if (coords.length > 4) {
          const quarterIndex = Math.floor(coords.length * 0.75);
          const [lng3, lat3] = coords[quarterIndex - 1];
          const [lng4, lat4] = coords[quarterIndex];
          
          const quarterLng = (lng3 + lng4) / 2;
          const quarterLat = (lat3 + lat4) / 2;
          
          const dLng2 = lng4 - lng3;
          const dLat2 = lat4 - lat3;
          const bearing2 = (Math.atan2(dLng2, dLat2) * (180 / Math.PI) + 180) % 360;
          
          arrowPoints.push({
            type: 'Feature' as const,
            properties: { bearing: bearing2 },
            geometry: {
              type: 'Point' as const,
              coordinates: [quarterLng, quarterLat]
            }
          });
        }
      }

      const arrowsGeoJSON = {
        type: 'FeatureCollection' as const,
        features: arrowPoints
      };

      // Remove existing layers if they exist
      if (map.getLayer(`route-line-${index}`)) {
        map.removeLayer(`route-line-${index}`);
      }
      if (map.getLayer(`route-arrows-${index}`)) {
        map.removeLayer(`route-arrows-${index}`);
      }
      if (map.getSource(`route-segment-${index}`)) {
        map.removeSource(`route-segment-${index}`);
      }
      if (map.getSource(`route-arrows-${index}`)) {
        map.removeSource(`route-arrows-${index}`);
      }

      // Add route line source
      map.addSource(`route-segment-${index}`, {
        type: 'geojson',
        data: routeGeoJSON
      });

      // Add arrows source
      map.addSource(`route-arrows-${index}`, {
        type: 'geojson',
        data: arrowsGeoJSON
      });

      // Add line layer
      map.addLayer({
        id: `route-line-${index}`,
        type: 'line',
        source: `route-segment-${index}`,
        layout: {
          'line-join': 'round',
          'line-cap': 'round'
        },
        paint: {
          'line-color': '#3b82f6',
          'line-width': 4
        }
      });

      // Add arrow symbols layer
      if (map.hasImage('route-arrow')) {
        map.addLayer({
          id: `route-arrows-${index}`,
          type: 'symbol',
          source: `route-arrows-${index}`,
          layout: {
            'icon-image': 'route-arrow',
            'icon-size': 0.8,
            'icon-rotate': ['get', 'bearing'],
            'icon-rotation-alignment': 'map',
            'icon-allow-overlap': true,
            'icon-ignore-placement': true
          }
        });
      }
      } // End of car route drawing

      // Draw bus route if mode is 'bus' (purple dashed straight line)
      if (transportMode === 'bus') {
        // Create GeoJSON for bus route (straight line)
        const busRouteGeoJSON = {
          type: 'Feature' as const,
          properties: {},
          geometry: {
            type: 'LineString' as const,
            coordinates: segment.geometry
          }
        };

        // Remove existing layers if they exist
        if (map.getLayer(`route-line-${index}`)) {
          map.removeLayer(`route-line-${index}`);
        }
        if (map.getSource(`route-segment-${index}`)) {
          map.removeSource(`route-segment-${index}`);
        }

        // Add bus route line source
        map.addSource(`route-segment-${index}`, {
          type: 'geojson',
          data: busRouteGeoJSON
        });

        // Add bus line layer (purple dashed)
        map.addLayer({
          id: `route-line-${index}`,
          type: 'line',
          source: `route-segment-${index}`,
          layout: {
            'line-join': 'round',
            'line-cap': 'round'
          },
          paint: {
            'line-color': '#9333ea',
            'line-width': 3,
            'line-dasharray': [2, 2],
            'line-opacity': 0.7
          }
        });
      } // End of bus route drawing

      // Only draw train route if mode is 'train' AND we have train route data
      if (transportMode === 'train' && trainRoute && selectedDayIndex !== null) {
        // Get the itinerary to find place coordinates
        // For Day 1, use trip's start location; for Day 2+, use previous day's last place
        let dayStartLocation: QuickLocation | null = null;
        
        if (selectedDayIndex === 0) {
          dayStartLocation = multiDayStartLocation;
        } else {
          // Find the actual previous day, accounting for merged blocks
          let previousDayPlaces: PlaceItem[] | undefined;
          const previousDayIndex = selectedDayIndex - 1;
          let foundInBlock = false;
          
          for (const [blockStartIndex, blockIndices] of Array.from(combinedDayBlocks.entries())) {
            if (blockIndices.includes(previousDayIndex)) {
              const previousDayData = finalizedDays.get(blockStartIndex);
              previousDayPlaces = previousDayData?.places;
              foundInBlock = true;
              break;
            }
          }
          
          if (!foundInBlock) {
            previousDayPlaces = dayPlaces.get(previousDayIndex);
            if (!previousDayPlaces) {
              const previousDayData = finalizedDays.get(previousDayIndex);
              previousDayPlaces = previousDayData?.places;
            }
          }
          
          if (previousDayPlaces && previousDayPlaces.length > 0) {
            const lastPlace = previousDayPlaces[previousDayPlaces.length - 1];
            dayStartLocation = {
              name: lastPlace.name,
              coordinates: [
                lastPlace.geo_point?.lon || lastPlace.geo_point?.lng || 0,
                lastPlace.geo_point?.lat || 0
              ]
            };
          }
        }
        
        if (dayStartLocation) {
          const itinerary = [
            dayStartLocation,
            ...tripPlaces.map(place => ({
              coordinates: [
                place.geo_point?.lon || place.geo_point?.lng || place.geo_point?.longitude || 0,
                place.geo_point?.lat || place.geo_point?.latitude || 0
              ]
            }))
          ];

          const fromPlace = itinerary[index];
          const toPlace = itinerary[index + 1];
          
          if (fromPlace && toPlace) {

        // Draw three segments for train route:
        // 1. Blue line from place to start station
        const toStationGeoJSON = {
          type: 'Feature' as const,
          properties: {},
          geometry: {
            type: 'LineString' as const,
            coordinates: [fromPlace.coordinates, trainRoute.stations.from.coordinates]
          }
        };

        // 2. Red dashed line for train route
        const trainRouteGeoJSON = {
          type: 'Feature' as const,
          properties: {},
          geometry: {
            type: 'LineString' as const,
            coordinates: trainRoute.geometry
          }
        };

        // 3. Blue line from end station to place
        const fromStationGeoJSON = {
          type: 'Feature' as const,
          properties: {},
          geometry: {
            type: 'LineString' as const,
            coordinates: [trainRoute.stations.to.coordinates, toPlace.coordinates]
          }
        };

        // Remove existing train layers if they exist
        ['to-station', 'train-rail', 'from-station'].forEach(suffix => {
          const layerId = `train-${suffix}-${index}`;
          const sourceId = `train-${suffix}-source-${index}`;
          if (map.getLayer(layerId)) {
            map.removeLayer(layerId);
          }
          if (map.getSource(sourceId)) {
            map.removeSource(sourceId);
          }
        });

        // Add to-station line
        map.addSource(`train-to-station-source-${index}`, {
          type: 'geojson',
          data: toStationGeoJSON
        });
        map.addLayer({
          id: `train-to-station-${index}`,
          type: 'line',
          source: `train-to-station-source-${index}`,
          layout: {
            'line-join': 'round',
            'line-cap': 'round'
          },
          paint: {
            'line-color': '#3b82f6',
            'line-width': 3
          }
        });

        // Add train rail line (red dashed)
        map.addSource(`train-train-rail-source-${index}`, {
          type: 'geojson',
          data: trainRouteGeoJSON
        });
        map.addLayer({
          id: `train-train-rail-${index}`,
          type: 'line',
          source: `train-train-rail-source-${index}`,
          layout: {
            'line-join': 'round',
            'line-cap': 'round'
          },
          paint: {
            'line-color': '#ef4444',
            'line-width': 4,
            'line-dasharray': [4, 2]
          }
        });

        // Add from-station line
        map.addSource(`train-from-station-source-${index}`, {
          type: 'geojson',
          data: fromStationGeoJSON
        });
        map.addLayer({
          id: `train-from-station-${index}`,
          type: 'line',
          source: `train-from-station-source-${index}`,
          layout: {
            'line-join': 'round',
            'line-cap': 'round'
          },
          paint: {
            'line-color': '#3b82f6',
            'line-width': 3
          }
        });
          }
        }
      }
    });

    // Cleanup function
    return () => {
      if (map) {
        try {
          // Check if map style is loaded
          map.getStyle();
          segments.forEach((_, index) => {
            try {
              if (map.getLayer(`route-line-${index}`)) {
                map.removeLayer(`route-line-${index}`);
              }
              if (map.getLayer(`route-arrows-${index}`)) {
                map.removeLayer(`route-arrows-${index}`);
              }
              if (map.getSource(`route-segment-${index}`)) {
                map.removeSource(`route-segment-${index}`);
              }
              if (map.getSource(`route-arrows-${index}`)) {
                map.removeSource(`route-arrows-${index}`);
              }

              // Clean up train route layers
              ['to-station', 'train-rail', 'from-station'].forEach(suffix => {
                const layerId = `train-${suffix}-${index}`;
                const sourceId = `train-${suffix}-source-${index}`;
                try {
                  if (map.getLayer(layerId)) {
                    map.removeLayer(layerId);
                  }
                  if (map.getSource(sourceId)) {
                    map.removeSource(sourceId);
                  }
                } catch (e) {
                  // Ignore cleanup errors
                }
              });
            } catch (e) {
              // Map might be removed or not ready, ignore
            }
          });
        } catch (e) {
          // Map might be removed or style not loaded, ignore
        }
      }
    };
  }, [currentView, selectedDayIndex, dayRouteSegments, activeTrainRoutes, multiDayStartLocation, selectedDayLocation, tripPlaces, mapLoaded, dayPlaces, dayTransportation]);

  // Clean up trip end route layers when location/state changes (runs BEFORE drawing)
  useEffect(() => {
    const map = mapRef.current;
    if (!map || !mapLoaded) return;

    const layerId = 'trip-end-route-line';
    const sourceId = 'trip-end-route-segment';
    const trainLayerPrefix = 'trip-end-train';


    // Always clean up trip-end-specific layers (used in itinerary view)
    try {
      // Remove layers first
      if (map.getLayer(layerId)) {
        map.removeLayer(layerId);
      }
      ['to-station', 'train-rail', 'from-station'].forEach(suffix => {
        const trainLayerId = `${trainLayerPrefix}-${suffix}`;
        if (map.getLayer(trainLayerId)) {
          map.removeLayer(trainLayerId);
        }
      });

      // Then remove sources
      if (map.getSource(sourceId)) {
        map.removeSource(sourceId);
      }
      ['to-station', 'train-rail', 'from-station'].forEach(suffix => {
        const trainSourceId = `${trainLayerPrefix}-${suffix}-source`;
        if (map.getSource(trainSourceId)) {
          map.removeSource(trainSourceId);
        }
      });
    } catch (e) {
    }
  }, [mapLoaded, isTripEnded, tripEndLocation, tripEndRouteSegment, tripEndTransportMode]);

  // Clean up empty locations (2nd, 3rd, etc.) when all places for that location are removed
  // Location 0 (first primary location) is NOT automatically removed - user must use Reset Location button
  useEffect(() => {
    if (selectedDayIndex === null || currentView !== 'multi-day-trip-plan-day') return;
    
    const locations = dayLocations.get(selectedDayIndex) || [];
    if (locations.length <= 1) return; // Only check if there are 2+ locations
    
    // Check each location (except Location 0) to see if it has any places
    let hasChanges = false;
    const locationsToKeep: Array<{ name: string; coordinates: [number, number] }> = [];
    
    locations.forEach((location, locIndex) => {
      if (locIndex === 0) {
        // Always keep Location 0
        locationsToKeep.push(location);
      } else {
        // Check if there are any places for this location
        const placesForLocation = tripPlaces.filter(p => (p.primaryLocationIndex || 0) === locIndex);
        if (placesForLocation.length > 0) {
          locationsToKeep.push(location);
        } else {
          // This location has no places - will be removed
          hasChanges = true;
        }
      }
    });
    
    if (hasChanges) {
      // Update dayLocations with only the locations that have places (or are Location 0)
      setDayLocations(prev => {
        const newMap = new Map(prev);
        newMap.set(selectedDayIndex, locationsToKeep);
        return newMap;
      });
      
      // Update primaryLocationIndex for remaining places to reflect new indices
      // This is needed because removing a middle location shifts indices
      if (locationsToKeep.length < locations.length) {
        setTripPlaces(prev => prev.map(place => {
          const oldIndex = place.primaryLocationIndex || 0;
          // Count how many locations before this one were removed
          let removedBefore = 0;
          for (let i = 1; i < oldIndex; i++) {
            const placesForLoc = prev.filter(p => (p.primaryLocationIndex || 0) === i);
            if (placesForLoc.length === 0) {
              removedBefore++;
            }
          }
          return {
            ...place,
            primaryLocationIndex: oldIndex > 0 ? oldIndex - removedBefore : 0
          };
        }));
      }
    }
  }, [tripPlaces, selectedDayIndex, currentView, dayLocations]);

  // Draw route lines on map for itinerary view (all finalized days)
  useEffect(() => {
    if (!mapRef.current || !mapLoaded || currentView !== 'multi-day-trip-itinerary') {
      return;
    }

    const map = mapRef.current;
    
    // Clean up any existing itinerary route layers
    try {
      map.getStyle();
      Array.from(finalizedDays.keys()).forEach(dayIndex => {
        const dayData = finalizedDays.get(dayIndex);
        if (!dayData) return;
        
        dayData.routeSegments.forEach((_, segmentIndex) => {
          const layerId = `itinerary-route-line-day${dayIndex}-seg${segmentIndex}`;
          const sourceId = `itinerary-route-segment-day${dayIndex}-seg${segmentIndex}`;
          const trainLayerPrefix = `itinerary-train-day${dayIndex}-seg${segmentIndex}`;
          
          try {
            if (map.getLayer(layerId)) map.removeLayer(layerId);
            if (map.getSource(sourceId)) map.removeSource(sourceId);
            
            // Clean up train route layers
            ['to-station', 'train-rail', 'from-station'].forEach(suffix => {
              const trainLayerId = `${trainLayerPrefix}-${suffix}`;
              const trainSourceId = `${trainLayerPrefix}-${suffix}-source`;
              try {
                if (map.getLayer(trainLayerId)) map.removeLayer(trainLayerId);
                if (map.getSource(trainSourceId)) map.removeSource(trainSourceId);
              } catch (e) {
                // Ignore
              }
            });
          } catch (e) {
            // Ignore
          }
        });
      });
    } catch (e) {
      // Map style not loaded
    }

    // Clean up trip end route layers (if any exist from previous state)
    try {
      const tripEndLayerId = 'trip-end-route-line';
      const tripEndSourceId = 'trip-end-route-segment';
      const tripEndTrainPrefix = 'trip-end-train';
      
      if (map.getLayer(tripEndLayerId)) {
        map.removeLayer(tripEndLayerId);
      }
      if (map.getSource(tripEndSourceId)) {
        map.removeSource(tripEndSourceId);
      }
      
      ['to-station', 'train-rail', 'from-station'].forEach(suffix => {
        const trainLayerId = `${tripEndTrainPrefix}-${suffix}`;
        const trainSourceId = `${tripEndTrainPrefix}-${suffix}-source`;
        try {
          if (map.getLayer(trainLayerId)) {
            map.removeLayer(trainLayerId);
          }
          if (map.getSource(trainSourceId)) {
            map.removeSource(trainSourceId);
          }
        } catch (e) {
          // Ignore
        }
      });
    } catch (e) {
    }

    if (finalizedDays.size === 0) return;

    // Draw routes for each finalized day
    Array.from(finalizedDays.entries()).forEach(([dayIndex, dayData]) => {
      const { routeSegments, transportation, startLocation } = dayData;
      
      routeSegments.forEach((segment, segmentIndex) => {
        const transportMode = transportation.get(segmentIndex) || 'car';
        const layerId = `itinerary-route-line-day${dayIndex}-seg${segmentIndex}`;
        const sourceId = `itinerary-route-segment-day${dayIndex}-seg${segmentIndex}`;
        
        // If train is selected but no train route data, fall back to car route
        const shouldDrawCarRoute = transportMode === 'car' || (transportMode === 'train' && !segment.trainRoute);
        
        if (shouldDrawCarRoute) {
          // Draw car route (blue solid line)
          const routeGeoJSON = {
            type: 'Feature' as const,
            properties: {},
            geometry: {
              type: 'LineString' as const,
              coordinates: segment.geometry
            }
          };

          try {
            map.addSource(sourceId, {
              type: 'geojson',
              data: routeGeoJSON
            });

            map.addLayer({
              id: layerId,
              type: 'line',
              source: sourceId,
              layout: {
                'line-join': 'round',
                'line-cap': 'round'
              },
              paint: {
                'line-color': '#3b82f6',
                'line-width': 3,
                'line-opacity': 0.7
              }
            });
          } catch (e) {
            // Layer might already exist
          }
        } else if (transportMode === 'bus') {
          // Draw bus route (purple dashed straight line)
          const busGeoJSON = {
            type: 'Feature' as const,
            properties: {},
            geometry: {
              type: 'LineString' as const,
              coordinates: segment.geometry
            }
          };

          try {
            map.addSource(sourceId, {
              type: 'geojson',
              data: busGeoJSON
            });

            map.addLayer({
              id: layerId,
              type: 'line',
              source: sourceId,
              layout: {
                'line-join': 'round',
                'line-cap': 'round'
              },
              paint: {
                'line-color': '#9333ea',
                'line-width': 3,
                'line-dasharray': [2, 2],
                'line-opacity': 0.7
              }
            });
          } catch (e) {
            // Layer might already exist
          }
        } else if (transportMode === 'train' && segment.trainRoute) {
          // Draw train route (red dashed line with blue commute lines)
          const trainRoute = segment.trainRoute;
          const trainLayerPrefix = `itinerary-train-day${dayIndex}-seg${segmentIndex}`;
          
          try {
            // To-station commute line
            map.addSource(`${trainLayerPrefix}-to-station-source`, {
              type: 'geojson',
              data: {
                type: 'Feature' as const,
                properties: {},
                geometry: {
                  type: 'LineString' as const,
                  coordinates: [
                    segment.geometry[0], // Start point (place A)
                    trainRoute.stations.from.coordinates
                  ]
                }
              }
            });
            map.addLayer({
              id: `${trainLayerPrefix}-to-station`,
              type: 'line',
              source: `${trainLayerPrefix}-to-station-source`,
              layout: { 'line-join': 'round', 'line-cap': 'round' },
              paint: { 'line-color': '#3b82f6', 'line-width': 2, 'line-opacity': 0.7 }
            });

            // Train rail line
            map.addSource(`${trainLayerPrefix}-train-rail-source`, {
              type: 'geojson',
              data: {
                type: 'Feature' as const,
                properties: {},
                geometry: {
                  type: 'LineString' as const,
                  coordinates: trainRoute.geometry
                }
              }
            });
            map.addLayer({
              id: `${trainLayerPrefix}-train-rail`,
              type: 'line',
              source: `${trainLayerPrefix}-train-rail-source`,
              layout: { 'line-join': 'round', 'line-cap': 'round' },
              paint: { 'line-color': '#ef4444', 'line-width': 3, 'line-dasharray': [4, 2], 'line-opacity': 0.7 }
            });

            // From-station commute line
            map.addSource(`${trainLayerPrefix}-from-station-source`, {
              type: 'geojson',
              data: {
                type: 'Feature' as const,
                properties: {},
                geometry: {
                  type: 'LineString' as const,
                  coordinates: [
                    trainRoute.stations.to.coordinates,
                    segment.geometry[segment.geometry.length - 1] // End point (place B)
                  ]
                }
              }
            });
            map.addLayer({
              id: `${trainLayerPrefix}-from-station`,
              type: 'line',
              source: `${trainLayerPrefix}-from-station-source`,
              layout: { 'line-join': 'round', 'line-cap': 'round' },
              paint: { 'line-color': '#3b82f6', 'line-width': 2, 'line-opacity': 0.7 }
            });
          } catch (e) {
            // Layer might already exist
          }
        }
      });
    });

    // Draw trip end route if trip is ended (cleanup handled by separate effect above)
    if (isTripEnded && tripEndRouteSegment && tripEndLocation) {
      
      const layerId = 'trip-end-route-line';
      const sourceId = 'trip-end-route-segment';
      const trainLayerPrefix = 'trip-end-train';
      
      const shouldDrawCarRoute = tripEndTransportMode === 'car' || (tripEndTransportMode === 'train' && !tripEndRouteSegment.trainRoute);
      
      if (shouldDrawCarRoute) {
        // Draw car route (blue solid line)
        const routeGeoJSON = {
          type: 'Feature' as const,
          properties: {},
          geometry: {
            type: 'LineString' as const,
            coordinates: tripEndRouteSegment.geometry
          }
        };

        try {
          map.addSource(sourceId, {
            type: 'geojson',
            data: routeGeoJSON
          });

          map.addLayer({
            id: layerId,
            type: 'line',
            source: sourceId,
            layout: {
              'line-join': 'round',
              'line-cap': 'round'
            },
            paint: {
              'line-color': '#3b82f6',
              'line-width': 3,
              'line-opacity': 0.7
            }
          });
        } catch (e) {
          // Layer might already exist
        }
      } else if (tripEndTransportMode === 'bus') {
        // Draw bus route (purple dashed straight line)
        const busGeoJSON = {
          type: 'Feature' as const,
          properties: {},
          geometry: {
            type: 'LineString' as const,
            coordinates: tripEndRouteSegment.geometry
          }
        };

        try {
          map.addSource(sourceId, {
            type: 'geojson',
            data: busGeoJSON
          });

          map.addLayer({
            id: layerId,
            type: 'line',
            source: sourceId,
            layout: {
              'line-join': 'round',
              'line-cap': 'round'
            },
            paint: {
              'line-color': '#9333ea',
              'line-width': 3,
              'line-dasharray': [2, 2],
              'line-opacity': 0.7
            }
          });
        } catch (e) {
          // Layer might already exist
        }
      } else if (tripEndTransportMode === 'train' && tripEndRouteSegment.trainRoute) {
        // Draw train route (red dashed line with blue commute lines)
        const trainRoute = tripEndRouteSegment.trainRoute;
        
        try {
          // To-station commute line
          map.addSource(`${trainLayerPrefix}-to-station-source`, {
            type: 'geojson',
            data: {
              type: 'Feature' as const,
              properties: {},
              geometry: {
                type: 'LineString' as const,
                coordinates: [
                  tripEndRouteSegment.geometry[0], // Start point
                  trainRoute.stations.from.coordinates
                ]
              }
            }
          });
          map.addLayer({
            id: `${trainLayerPrefix}-to-station`,
            type: 'line',
            source: `${trainLayerPrefix}-to-station-source`,
            layout: { 'line-join': 'round', 'line-cap': 'round' },
            paint: { 'line-color': '#3b82f6', 'line-width': 2, 'line-opacity': 0.7 }
          });

          // Train rail line (red dashed)
          map.addSource(`${trainLayerPrefix}-train-rail-source`, {
            type: 'geojson',
            data: {
              type: 'Feature' as const,
              properties: {},
              geometry: {
                type: 'LineString' as const,
                coordinates: trainRoute.geometry
              }
            }
          });
          map.addLayer({
            id: `${trainLayerPrefix}-train-rail`,
            type: 'line',
            source: `${trainLayerPrefix}-train-rail-source`,
            layout: { 'line-join': 'round', 'line-cap': 'round' },
            paint: {
              'line-color': '#ef4444',
              'line-width': 3,
              'line-dasharray': [3, 2],
              'line-opacity': 0.8
            }
          });

          // From-station commute line
          map.addSource(`${trainLayerPrefix}-from-station-source`, {
            type: 'geojson',
            data: {
              type: 'Feature' as const,
              properties: {},
              geometry: {
                type: 'LineString' as const,
                coordinates: [
                  trainRoute.stations.to.coordinates,
                  tripEndRouteSegment.geometry[tripEndRouteSegment.geometry.length - 1] // End point
                ]
              }
            }
          });
          map.addLayer({
            id: `${trainLayerPrefix}-from-station`,
            type: 'line',
            source: `${trainLayerPrefix}-from-station-source`,
            layout: { 'line-join': 'round', 'line-cap': 'round' },
            paint: { 'line-color': '#3b82f6', 'line-width': 2, 'line-opacity': 0.7 }
          });
        } catch (e) {
          // Layer might already exist
        }
      }
    } else if (!isTripEnded || !tripEndLocation) {
      // Explicitly clean up trip end layers when trip end is cleared
      const layerId = 'trip-end-route-line';
      const sourceId = 'trip-end-route-segment';
      const trainLayerPrefix = 'trip-end-train';
      
      try {
        if (map.getLayer(layerId)) {
          map.removeLayer(layerId);
        }
        if (map.getSource(sourceId)) {
          map.removeSource(sourceId);
        }
        
        ['to-station', 'train-rail', 'from-station'].forEach(suffix => {
          const trainLayerId = `${trainLayerPrefix}-${suffix}`;
          const trainSourceId = `${trainLayerPrefix}-${suffix}-source`;
          try {
            if (map.getLayer(trainLayerId)) {
              map.removeLayer(trainLayerId);
            }
            if (map.getSource(trainSourceId)) {
              map.removeSource(trainSourceId);
            }
          } catch (e) {
            // Ignore
          }
        });
      } catch (e) {
      }
    }

    // Cleanup function
    return () => {
      if (map) {
        try {
          map.getStyle();
          Array.from(finalizedDays.keys()).forEach(dayIndex => {
            const dayData = finalizedDays.get(dayIndex);
            if (!dayData) return;
            
            dayData.routeSegments.forEach((_, segmentIndex) => {
              const layerId = `itinerary-route-line-day${dayIndex}-seg${segmentIndex}`;
              const sourceId = `itinerary-route-segment-day${dayIndex}-seg${segmentIndex}`;
              const trainLayerPrefix = `itinerary-train-day${dayIndex}-seg${segmentIndex}`;
              
              try {
                if (map.getLayer(layerId)) map.removeLayer(layerId);
                if (map.getSource(sourceId)) map.removeSource(sourceId);
                
                ['to-station', 'train-rail', 'from-station'].forEach(suffix => {
                  const trainLayerId = `${trainLayerPrefix}-${suffix}`;
                  const trainSourceId = `${trainLayerPrefix}-${suffix}-source`;
                  try {
                    if (map.getLayer(trainLayerId)) map.removeLayer(trainLayerId);
                    if (map.getSource(trainSourceId)) map.removeSource(trainSourceId);
                  } catch (e) {
                    // Ignore
                  }
                });
              } catch (e) {
                // Ignore
              }
            });
          });
          
          // Cleanup trip end route layers (ALWAYS clean up, regardless of isTripEnded state)
          try {
            const layerId = 'trip-end-route-line';
            const sourceId = 'trip-end-route-segment';
            const trainLayerPrefix = 'trip-end-train';
            
            if (map.getLayer(layerId)) map.removeLayer(layerId);
            if (map.getSource(sourceId)) map.removeSource(sourceId);
            
            ['to-station', 'train-rail', 'from-station'].forEach(suffix => {
              const trainLayerId = `${trainLayerPrefix}-${suffix}`;
              const trainSourceId = `${trainLayerPrefix}-${suffix}-source`;
              try {
                if (map.getLayer(trainLayerId)) map.removeLayer(trainLayerId);
                if (map.getSource(trainSourceId)) map.removeSource(trainSourceId);
              } catch (e) {
                // Ignore
              }
            });
          } catch (e) {
            // Ignore
          }
        } catch (e) {
          // Map might be removed
        }
      }
    };
  }, [currentView, mapLoaded, finalizedDays, isTripEnded, tripEndRouteSegment, tripEndLocation, tripEndTransportMode]);

  // Handle start location selection on map
  useEffect(() => {
    if (!mapRef.current || !mapLoaded) return;
    
    const map = mapRef.current;
    
    if (isSelectingStartLocation) {
      // Change cursor style
      map.getCanvas().style.cursor = 'crosshair';
      
      // Create temporary marker at map center if not exists
      if (!tempStartMarkerRef.current) {
        const center = map.getCenter();
        const el = document.createElement('div');
        el.className = 'custom-marker';
        el.innerHTML = `
          <svg width="32" height="32" viewBox="0 0 24 24" style="filter: drop-shadow(0 2px 4px rgba(0,0,0,0.3));">
            <path d="M20 10c0 4.993-5.539 10.193-7.399 11.799a1 1 0 0 1-1.202 0C9.539 20.193 4 14.993 4 10a8 8 0 0 1 16 0" fill="#10b981" stroke="#059669" stroke-width="2"/>
            <circle cx="12" cy="10" r="3" fill="white"/>
            <path d="M10.5 10l1 1 2-2" stroke="#10b981" stroke-width="1.5" fill="none" stroke-linecap="round" stroke-linejoin="round"/>
          </svg>
        `;
        el.style.width = '32px';
        el.style.height = '32px';
        el.style.cursor = 'grab';
        
        const marker = new mapboxgl.Marker({
          element: el,
          draggable: true,
          anchor: 'bottom'
        })
          .setLngLat([center.lng, center.lat])
          .addTo(map);
        
        tempStartMarkerRef.current = marker;
        
        // Handle marker drag end
        marker.on('dragend', () => {
          const lngLat = marker.getLngLat();
        });
      }
      
      // Handle map click to position marker
      const handleMapClick = (e: mapboxgl.MapMouseEvent) => {
        if (tempStartMarkerRef.current) {
          tempStartMarkerRef.current.setLngLat(e.lngLat);
        }
      };
      
      map.on('click', handleMapClick);
      
      // Cleanup
      return () => {
        map.off('click', handleMapClick);
        map.getCanvas().style.cursor = '';
      };
    } else {
      // Not in selection mode - remove temporary marker
      if (tempStartMarkerRef.current) {
        tempStartMarkerRef.current.remove();
        tempStartMarkerRef.current = null;
      }
      map.getCanvas().style.cursor = '';
    }
  }, [isSelectingStartLocation, mapLoaded]);

  // Permanent start location marker
  useEffect(() => {
    if (!mapRef.current || !mapLoaded || currentView !== 'multi-day-trip-itinerary' || !multiDayStartLocation) return;
    
    const map = mapRef.current;
    let timeoutId: NodeJS.Timeout;
    
    // Remove old permanent marker if exists
    if (permanentStartMarkerRef.current) {
      permanentStartMarkerRef.current.remove();
    }
    
    // Create permanent green marker with check icon
    const el = document.createElement('div');
    el.className = 'permanent-start-marker';
    el.innerHTML = `
      <svg width="32" height="32" viewBox="0 0 24 24" fill="none" stroke="#10b981" stroke-width="2.5" stroke-linecap="round" stroke-linejoin="round" style="filter: drop-shadow(0 2px 6px rgba(0,0,0,0.4));">
        <path d="M20 10c0 4.993-5.539 10.193-7.399 11.799a1 1 0 0 1-1.202 0C9.539 20.193 4 14.993 4 10a8 8 0 0 1 16 0"/>
        <path d="m9 10 2 2 4-4" stroke="#10b981" stroke-width="2.5"/>
      </svg>
    `;
    el.style.width = '32px';
    el.style.height = '32px';
    el.style.cursor = 'pointer';
    
    const marker = new mapboxgl.Marker({
      element: el,
      anchor: 'bottom'
    })
      .setLngLat(multiDayStartLocation.coordinates)
      .addTo(map);
    
    permanentStartMarkerRef.current = marker;
    
    // Only center on start location if:
    // 1. No day location is currently being selected
    // 2. No days have been finalized yet (initial setup only)
    // This prevents overriding the focus when user is selecting a day's primary location
    // and prevents centering when returning to itinerary after finalizing days
    if (!selectedDayLocation && finalizedDays.size === 0) {
      // Add delay before centering to let drawer animation and map dimensions settle
      timeoutId = setTimeout(() => {
        // Center map on marker at position based on drawer state
        const offset = getDrawerAdjustedOffset();
        map.flyTo({
          center: multiDayStartLocation.coordinates,
          zoom: Math.max(map.getZoom(), 10),
          duration: 1000,
          offset: offset
        });
      }, 300);
    }
    
    // Cleanup on unmount or when dependencies change
    return () => {
      clearTimeout(timeoutId);
      if (permanentStartMarkerRef.current) {
        permanentStartMarkerRef.current.remove();
        permanentStartMarkerRef.current = null;
      }
    };
  }, [multiDayStartLocation, mapLoaded, drawerPosition, isDesktop, currentView, selectedDayLocation, finalizedDays]);

  // Reset map centering when navigating to trip selection pages
  // This ensures both day-trip and multi-day-trip centering effects are inactive
  useEffect(() => {
    if (!mapRef.current || !mapLoaded) return;
    
    // Only reset when on trip selection pages (home or trip-options)
    if (currentView !== 'home' && currentView !== 'trip-options') return;
    
    // Reset map to neutral state (fit to Sri Lanka bounds)
    mapRef.current.fitBounds([
      [79.3, 5.7],   // Southwest corner (extended)
      [82.2, 7.8]    // Northeast corner (extended)
    ], { padding: 80, duration: 800 });
  }, [currentView, mapLoaded]);

  // Handle day trip start location marker and map centering with drawer-aware offset
  useEffect(() => {
    if (!mapRef.current || !mapLoaded || currentView !== 'day-trip' || !startLocation) return;
    
    const map = mapRef.current;
    
    // Add marker
    if (startMarkerRef.current) {
      startMarkerRef.current.remove();
    }
    startMarkerRef.current = new mapboxgl.Marker({ color: '#22c55e' })
      .setLngLat(startLocation.coordinates)
      .addTo(map);
    
    // Add delay before centering to let drawer animation and map dimensions settle
    const timeoutId = setTimeout(() => {
      // Center map on marker - recalculates whenever drawer position changes
      const offset = getDrawerAdjustedOffset();
      map.flyTo({
        center: startLocation.coordinates,
        zoom: Math.max(map.getZoom(), 10),
        duration: 1000,
        offset: offset
      });
    }, 300);
    
    // Cleanup on unmount or when dependencies change
    return () => {
      clearTimeout(timeoutId);
      if (startMarkerRef.current) {
        startMarkerRef.current.remove();
        startMarkerRef.current = null;
      }
    };
  }, [startLocation, mapLoaded, drawerPosition, isDesktop, currentView]);

  // Create permanent markers for all day locations (supports multiple locations per day)
  useEffect(() => {
    if (!mapRef.current || !mapLoaded) return;
    
    const map = mapRef.current;
    
    // Remove all existing permanent day markers (now arrays)
    permanentDayMarkersRef.current.forEach(markers => {
      markers.forEach(marker => marker.remove());
    });
    permanentDayMarkersRef.current.clear();
    
    // Create markers for each day's locations
    dayLocations.forEach((locations, dayIndex) => {
      const dayMarkers: mapboxgl.Marker[] = [];
      
      // Create a marker for each primary location in this day
      // All markers for the same day show the same day number
      locations.forEach((location) => {
        // Validate coordinates before creating marker
        if (!location?.coordinates || 
            !Array.isArray(location.coordinates) || 
            location.coordinates.length !== 2 ||
            typeof location.coordinates[0] !== 'number' || 
            typeof location.coordinates[1] !== 'number' ||
            isNaN(location.coordinates[0]) || 
            isNaN(location.coordinates[1])) {
          return;
        }
        
        const el = document.createElement('div');
        el.className = 'permanent-day-marker';
        el.innerHTML = `
          <div style="position: relative; width: 32px; height: 32px; filter: drop-shadow(0 2px 4px rgba(0,0,0,0.3));">
            <svg width="32" height="32" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
              <path d="M12 2C8.13 2 5 5.13 5 9c0 5.25 7 13 7 13s7-7.75 7-13c0-3.87-3.13-7-7-7z" fill="#9333ea" stroke="#7e22ce" stroke-width="1.5"/>
              <circle cx="12" cy="9" r="4" fill="white"/>
              <text x="12" y="12" font-size="8" font-weight="bold" fill="#9333ea" text-anchor="middle">${dayIndex + 1}</text>
            </svg>
          </div>
        `;
        
        const marker = new mapboxgl.Marker({
          element: el,
          anchor: 'bottom'
        })
          .setLngLat(location.coordinates)
          .addTo(map);
        
        dayMarkers.push(marker);
      });
      
      permanentDayMarkersRef.current.set(dayIndex, dayMarkers);
    });
    
    // Cleanup on unmount
    return () => {
      permanentDayMarkersRef.current.forEach(markers => {
        markers.forEach(marker => marker.remove());
      });
      permanentDayMarkersRef.current.clear();
    };
  }, [dayLocations, mapLoaded]);

  // Center map on selected day location
  useEffect(() => {
    if (!mapRef.current || !mapLoaded || !selectedDayLocation) return;
    
    // Skip centering if user is browsing carousel in destination picker
    const isInDestinationPicker = currentView === 'multi-day-trip-day-destinations';
    if (isInDestinationPicker) {
      return;
    }
    
    // Also skip if in plan-day view to prevent re-centering when drawer position changes
    const isInPlanDay = currentView === 'multi-day-trip-plan-day';
    if (isInPlanDay) {
      return;
    }
    
    const map = mapRef.current;
    const offset = getDrawerAdjustedOffset();
    
    // Fly to the selected location with drawer-aware offset
    map.flyTo({
      center: selectedDayLocation.coordinates,
      zoom: Math.max(map.getZoom(), 12),
      duration: 1500,
      essential: true,
      offset: offset
    });
  }, [selectedDayLocation, mapLoaded, currentView, drawerPosition, isDesktop]);

  // Handle day location selection on map
  useEffect(() => {
    if (!mapRef.current || !mapLoaded) return;
    
    const map = mapRef.current;
    
    if (isSelectingDayLocation) {
      // Change cursor style
      map.getCanvas().style.cursor = 'crosshair';
      
      // Check if selecting trip end location or day location
      if (isSelectingTripEndLocation) {
        // Create temporary TRIP END marker (red X) if not exists
        if (!tempTripEndMarkerRef.current) {
          const center = map.getCenter();
          const el = document.createElement('div');
          el.innerHTML = `
            <svg width="36" height="36" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg" style="filter: drop-shadow(0 3px 6px rgba(0,0,0,0.4));">
              <path d="M20 10c0 4.993-5.539 10.193-7.399 11.799a1 1 0 0 1-1.202 0C9.539 20.193 4 14.993 4 10a8 8 0 0 1 16 0" fill="#ef4444" stroke="#dc2626" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round"/>
              <line x1="9" y1="7" x2="15" y2="13" stroke="white" stroke-width="2" stroke-linecap="round"/>
              <line x1="15" y1="7" x2="9" y2="13" stroke="white" stroke-width="2" stroke-linecap="round"/>
            </svg>
          `;
          el.style.width = '36px';
          el.style.height = '36px';
          el.style.cursor = 'grab';
          
          const marker = new mapboxgl.Marker({
            element: el,
            draggable: true,
            anchor: 'bottom'
          })
            .setLngLat([center.lng, center.lat])
            .addTo(map);
          
          tempTripEndMarkerRef.current = marker;
          
          // Handle marker drag end
          marker.on('dragend', () => {
            const lngLat = marker.getLngLat();
          });
        }
        
        // Handle map click to position trip end marker
        const handleMapClick = (e: mapboxgl.MapMouseEvent) => {
          if (tempTripEndMarkerRef.current) {
            tempTripEndMarkerRef.current.setLngLat(e.lngLat);
          }
        };
        
        map.on('click', handleMapClick);
        
        // Cleanup
        return () => {
          map.off('click', handleMapClick);
          map.getCanvas().style.cursor = '';
        };
      } else if (selectedDayIndex !== null) {
        // Create temporary DAY marker (calendar) if not exists
      if (!tempDayMarkerRef.current) {
        const center = map.getCenter();
        const el = document.createElement('div');
        el.className = 'custom-day-marker';
        el.innerHTML = `
          <div style="position: relative; width: 32px; height: 32px; filter: drop-shadow(0 2px 4px rgba(0,0,0,0.3));">
            <svg width="32" height="32" viewBox="0 0 24 24" fill="none" stroke="#8b5cf6" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
              <rect x="3" y="4" width="18" height="18" rx="2" ry="2"/>
              <line x1="16" y1="2" x2="16" y2="6"/>
              <line x1="8" y1="2" x2="8" y2="6"/>
              <line x1="3" y1="10" x2="21" y2="10"/>
            </svg>
            <div style="position: absolute; top: 50%; left: 50%; transform: translate(-50%, -30%); font-size: 10px; font-weight: bold; color: #8b5cf6;">
              ${selectedDayIndex + 1}
            </div>
          </div>
        `;
        el.style.width = '32px';
        el.style.height = '32px';
        el.style.cursor = 'grab';
        
        const marker = new mapboxgl.Marker({
          element: el,
          draggable: true,
          anchor: 'center'
        })
          .setLngLat([center.lng, center.lat])
          .addTo(map);
        
        tempDayMarkerRef.current = marker;
        
        // Handle marker drag end
        marker.on('dragend', () => {
          const lngLat = marker.getLngLat();
        });
      }
      
        // Handle map click to position day marker
      const handleMapClick = (e: mapboxgl.MapMouseEvent) => {
        if (tempDayMarkerRef.current) {
          tempDayMarkerRef.current.setLngLat(e.lngLat);
        }
      };
      
      map.on('click', handleMapClick);
      
      // Cleanup
      return () => {
        map.off('click', handleMapClick);
        map.getCanvas().style.cursor = '';
      };
      }
    } else {
      // Not in selection mode - remove temporary markers
      if (tempDayMarkerRef.current) {
        tempDayMarkerRef.current.remove();
        tempDayMarkerRef.current = null;
      }
      if (tempTripEndMarkerRef.current) {
        tempTripEndMarkerRef.current.remove();
        tempTripEndMarkerRef.current = null;
      }
      map.getCanvas().style.cursor = '';
    }
  }, [isSelectingDayLocation, isSelectingTripEndLocation, mapLoaded, selectedDayIndex]);

  // Load full details for a place from explore collection
  const handleLoadFullDetails = async (placeId: string) => {
    setIsLoadingPlaceDetails(true);
    try {
      const fullPlace = await exploreService.getPlaceById(placeId);
      setSelectedPlaceForDetail(fullPlace);
    } catch (error) {
      alert('Failed to load full details. Please try again.');
    } finally {
      setIsLoadingPlaceDetails(false);
    }
  };

  // Trip management functions
  // NOTE: isPlaceInTrip, addToTrip, removeFromTrip, categorizePlaces moved to usePlaceManagement hook

  const handleTrainRouteClick = async (segmentIndex: number) => {
    if (selectedDayIndex === null) return;

    // Determine start location:
    // - Day 1: Use trip's start location
    // - Day 2+: Use previous day's last place (accounting for merged blocks)
    let dayStartLocation: QuickLocation | null = null;
    
    if (selectedDayIndex === 0) {
      dayStartLocation = multiDayStartLocation;
    } else {
      // Find the actual previous day, accounting for merged blocks
      let previousDayData: FinalizedDayData | undefined;
      let previousDayPlaces: PlaceItem[] | undefined;
      
      // Check if the previous day is part of a merged block
      const previousDayIndex = selectedDayIndex - 1;
      let foundInBlock = false;
      
      for (const [blockStartIndex, blockIndices] of Array.from(combinedDayBlocks.entries())) {
        if (blockIndices.includes(previousDayIndex)) {
          // Previous day is in this merged block
          previousDayData = finalizedDays.get(blockStartIndex);
          previousDayPlaces = previousDayData?.places;
          foundInBlock = true;
          break;
        }
      }
      
      // If not in a merged block, check regular dayPlaces and finalizedDays
      if (!foundInBlock) {
        previousDayPlaces = dayPlaces.get(previousDayIndex);
        if (!previousDayPlaces) {
          previousDayData = finalizedDays.get(previousDayIndex);
          previousDayPlaces = previousDayData?.places;
        }
      }
      
      if (previousDayPlaces && previousDayPlaces.length > 0) {
        const lastPlace = previousDayPlaces[previousDayPlaces.length - 1];
        dayStartLocation = {
          name: lastPlace.name,
          coordinates: [
            lastPlace.geo_point?.lon || lastPlace.geo_point?.lng || 0,
            lastPlace.geo_point?.lat || 0
          ]
        };
      }
    }
    
    if (!dayStartLocation) {
      return;
    }

    // Build itinerary with consistent format
    const itinerary = [
      {
        name: dayStartLocation.name,
        lat: dayStartLocation.coordinates[1],
        lng: dayStartLocation.coordinates[0]
      },
      ...tripPlaces.map(place => ({
        name: place.name,
        lat: place.geo_point?.lat || 0,
        lng: place.geo_point?.lon || place.geo_point?.lng || 0
      }))
    ];

    const from = itinerary[segmentIndex];
    const to = itinerary[segmentIndex + 1];

    try {
      const response = await fetch(`${API_BASE_URL}/api/advanced-trip/train-route`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          from: { lat: from.lat, lng: from.lng },
          to: { lat: to.lat, lng: to.lng }
        })
      });

      const result = await response.json();

      if (result.available && result.route) {
        // Store train route data
        setActiveTrainRoutes(prev => {
          const newMap = new Map(prev);
          newMap.set(segmentIndex, result.route);
          return newMap;
        });

        // Update transportation mode to train
        setDayTransportation(prev => {
          const newMap = new Map(prev);
          const dayMap = new Map(newMap.get(selectedDayIndex!) || new Map());
          dayMap.set(segmentIndex, 'train');
          newMap.set(selectedDayIndex!, dayMap);
          return newMap;
        });

        // Update the route segment with train route data
        setDayRouteSegments(prev => {
          const newMap = new Map(prev);
          const segments = newMap.get(selectedDayIndex!) || [];
          if (segments[segmentIndex]) {
            segments[segmentIndex] = {
              ...segments[segmentIndex],
              trainRoute: result.route
            };
            newMap.set(selectedDayIndex!, segments);
          }
          return newMap;
        });

      }
    } catch (error) {
    }
  };

  // NOTE: toggleTripPlace, movePlace moved to usePlaceManagement hook
  // NOTE: AI review generation moved to useAIReview hook

  // Wrapper functions for AI review (calls hook functions with required params)
  const handleFinalizeConfirm = async () => {
    // Generate AI review first
    await hookHandleFinalizeConfirm(setShowFinalizeModal, setIsTripFinalized);
    
    // Auto-save the trip after finalization for registered users
    if (authState.user?.userId && currentView === 'my-day-trip') {
      // Wait a moment for the AI review to be set in state
      setTimeout(() => {
        handleSaveDayTrip();
      }, 500);
    }
  };

  const generateAIReview = () => {
    hookGenerateAIReview(setShowFinalizeModal);
  };

  // NOTE: Nearby places fetching moved to useNearbyPlaces hook

  // Handle map click for location selection
  useEffect(() => {
    if (!mapRef.current || !isMapSelectionMode) return;

    const handleMapClick = (e: mapboxgl.MapMouseEvent) => {
      const coords: [number, number] = [e.lngLat.lng, e.lngLat.lat];
      setStartLocation({ name: 'Selected Location', coordinates: coords });
      setTripPlaces([]);  // Clear any previously selected destinations
      
      // Add marker
      if (startMarkerRef.current) {
        startMarkerRef.current.remove();
      }
      startMarkerRef.current = new mapboxgl.Marker({ color: '#22c55e' })
        .setLngLat(coords)
        .addTo(mapRef.current!);
      
      // Center map on location with drawer-aware offset (drawer is already open)
      const offset = getMapOffsetForDrawer();
      mapRef.current!.flyTo({
        center: coords,
        zoom: Math.max(mapRef.current!.getZoom(), 12),
        duration: 800,
        offset: offset
      });
      
      // Exit map selection mode
      setIsMapSelectionMode(false);
    };

    mapRef.current.on('click', handleMapClick);
    
    // Change cursor
    if (mapRef.current.getCanvas()) {
      mapRef.current.getCanvas().style.cursor = 'crosshair';
    }

    return () => {
      if (mapRef.current) {
        mapRef.current.off('click', handleMapClick);
        if (mapRef.current.getCanvas()) {
          mapRef.current.getCanvas().style.cursor = '';
        }
      }
    };
  }, [isMapSelectionMode, isDesktop]);

  // Reset expandedDays when navigating away from itinerary view
  useEffect(() => {
    if (currentView !== 'multi-day-trip-itinerary') {
      setExpandedDays(new Set());
    }
  }, [currentView]);

  // Add markers for nearby places
  useEffect(() => {
    if (!mapRef.current || !mapLoaded) return;
    
    // Determine which places to show based on current view
    let placesToShow: any[] = [];
    
    if (currentView === 'my-day-trip' || currentView === 'multi-day-trip-plan-day') {
      placesToShow = tripPlaces;
    } else if (currentView === 'multi-day-trip-itinerary') {
      // Show all finalized places across all days
      placesToShow = Array.from(finalizedDays.values()).flatMap(day => day.places);
    } else {
      placesToShow = nearbyPlaces;
    }
    
    if (placesToShow.length === 0) {
      // Clear all markers if no places to show
      placeMarkersRef.current.forEach(marker => marker.remove());
      placeMarkersRef.current = [];
      return;
    }

    // IMPORTANT: Sort places by primaryLocationIndex for correct marker numbering
    // This ensures markers match the list view order (Location 1 places first, then Location 2, etc.)
    const sortedPlacesToShow = [...placesToShow].sort((a, b) => {
      const indexA = a.primaryLocationIndex || 0;
      const indexB = b.primaryLocationIndex || 0;
      return indexA - indexB;
    });

    // Clear existing place markers
    placeMarkersRef.current.forEach(marker => marker.remove());
    placeMarkersRef.current = [];

    // Add new markers
    sortedPlacesToShow.forEach((place, index) => {
      if (!place.geo_point) {
        return;
      }

      // Get coordinates (handle both lon/lng formats)
      const lng = place.geo_point.lon || place.geo_point.lng || place.geo_point.longitude;
      const lat = place.geo_point.lat || place.geo_point.latitude;

      if (!lng || !lat) {
        return;
      }

      // Get place type config
      const typeConfig = getPlaceTypeConfig(place.type as PlaceType);
      
      // Create custom marker element
      const el = document.createElement('div');
      el.className = 'custom-marker';
      el.style.width = '32px';
      el.style.height = '32px';
      el.style.borderRadius = '50%';
      el.style.cursor = 'pointer';
      el.style.display = 'flex';
      el.style.alignItems = 'center';
      el.style.justifyContent = 'center';
      el.style.fontWeight = 'bold';
      el.style.fontSize = '13px';
      el.style.border = '3px solid white';
      el.style.boxShadow = '0 2px 6px rgba(0,0,0,0.3)';
      
      // Show numbers in my-day-trip or plan day view, type codes otherwise
      if (currentView === 'my-day-trip' || currentView === 'multi-day-trip-plan-day') {
        el.textContent = String(index + 1);
        // Use blue for all numbered markers in trip view
        el.style.backgroundColor = '#3b82f6';
        el.style.color = 'white';
      } else {
        el.textContent = typeConfig.code;
        // Set background and text color based on type
        switch (place.type) {
          case 'attraction':
            el.style.backgroundColor = '#3b82f6'; // blue
            el.style.color = 'white';
            break;
          case 'food':
            el.style.backgroundColor = '#f97316'; // orange
            el.style.color = 'white';
            break;
          case 'experience':
            el.style.backgroundColor = '#8b5cf6'; // purple
            el.style.color = 'white';
            break;
          case 'hotel':
            el.style.backgroundColor = '#10b981'; // green
            el.style.color = 'white';
            break;
          default:
            el.style.backgroundColor = '#6b7280'; // gray
            el.style.color = 'white';
        }
      }

      // Create marker with center anchor
      const marker = new mapboxgl.Marker({ 
        element: el,
        anchor: 'center'
      })
        .setLngLat([lng, lat]);

      // Only use popups on desktop, on mobile we'll use the swipeable card
      if (isDesktop) {
        // Create beautiful popup card with all 5 photos
        const popupHTML = generatePlacePopupHTML({
          id: place.id,
          name: place.name,
          type: place.type as PlaceType,
          imageUrl: place.imageUrl,
          photo_1_cdn: place.photo_1_cdn,
          photo_2_cdn: place.photo_2_cdn,
          photo_3_cdn: place.photo_3_cdn,
          photo_4_cdn: place.photo_4_cdn,
          photo_5_cdn: place.photo_5_cdn,
          distance: place.distance,
          rating: place.rating,
          city: place.city,
          tags: place.tags,
          inTrip: isPlaceInTrip(place.id),
        });

        const popup = new mapboxgl.Popup({ 
          offset: 25,
          closeButton: true,
          closeOnClick: false,
          maxWidth: 'none',
          className: 'custom-popup'
        }).setHTML(popupHTML);

        // Initialize carousel functionality when popup opens
        popup.on('open', () => {
          // Close previous popup if one is open
          if (currentPopupRef.current && currentPopupRef.current !== popup) {
            currentPopupRef.current.remove();
          }
          // Track current popup
          currentPopupRef.current = popup;
          // Initialize carousel
          initializeCarousel();
          
          // Add click handlers
          setTimeout(() => {
            const popupElement = popup.getElement();
            if (popupElement) {
              // Handle "Add to Trip" button clicks
              const addToTripBtn = popupElement.querySelector('.add-to-trip-btn');
              if (addToTripBtn) {
                addToTripBtn.addEventListener('click', (e) => {
                  e.stopPropagation();
                  toggleTripPlace(place, currentPrimaryLocationIndex);
                  
                  // Update button appearance
                  const btn = e.target as HTMLElement;
                  const button = btn.tagName === 'BUTTON' ? btn : btn.closest('button');
                  if (button) {
                    const inTrip = isPlaceInTrip(place.id);
                    if (inTrip) {
                      (button as HTMLButtonElement).style.background = '#10b981';
                      (button as HTMLButtonElement).innerHTML = `
                        <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5" stroke-linecap="round" stroke-linejoin="round">
                          <polyline points="20 6 9 17 4 12"></polyline>
                        </svg>
                        Added to Trip
                      `;
                    } else {
                      (button as HTMLButtonElement).style.background = '#3b82f6';
                      (button as HTMLButtonElement).innerHTML = `
                        <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5" stroke-linecap="round" stroke-linejoin="round">
                          <line x1="12" y1="5" x2="12" y2="19"></line>
                          <line x1="5" y1="12" x2="19" y2="12"></line>
                        </svg>
                        Add to Trip
                      `;
                    }
                  }
                });
              }
              
              // Handle general click to open detail panel (but not when clicking the button or close button)
              popupElement.addEventListener('click', (e) => {
                const target = e.target as HTMLElement;
                if (!target.closest('.add-to-trip-btn') && !target.closest('.carousel-btn-prev') && !target.closest('.carousel-btn-next') && !target.closest('.carousel-dot') && !target.closest('.mapboxgl-popup-close-button')) {
                  setSelectedPlaceForDetail(place);
                }
              });
            }
          }, 0);
        });

        // Clear ref when popup closes
        popup.on('close', () => {
          if (currentPopupRef.current === popup) {
            currentPopupRef.current = null;
          }
        });

        marker.setPopup(popup);
      } else {
        // Mobile: click marker to center map and show card (user can then click card to open detail panel)
        el.addEventListener('click', () => {
          if (currentView === 'day-trip-destinations' || currentView === 'multi-day-trip-day-destinations') {
            const placeIndex = nearbyPlaces.findIndex(p => p.id === place.id);
            if (placeIndex !== -1) {
              navigateToCard(placeIndex);
            }
          } else if (currentView === 'multi-day-trip-plan-day') {
            const placeIndex = tripPlaces.findIndex(p => p.id === place.id);
            if (placeIndex !== -1) {
              setActiveCardIndex(placeIndex);
              // Center map on the selected place
              if (place?.geo_point && mapRef.current) {
                const lng = place.geo_point.lon || place.geo_point.lng || place.geo_point.longitude;
                const lat = place.geo_point.lat || place.geo_point.latitude;
                if (lng && lat) {
                  const offset = getMapOffsetForDrawer();
                  mapRef.current.flyTo({
                    center: [lng, lat],
                    zoom: 14,
                    duration: 1000,
                    offset: offset
                  });
                }
              }
            }
          }
        });
      }

      marker.addTo(mapRef.current!);
      placeMarkersRef.current.push(marker);
    });


    // Fit map to show all markers
    // For multi-day trip plan day view: use trip's start location for Day 1, day's primary location for other days
    // For other views: use day trip's start location or multi-day trip's start location
    // Determine location to fit bounds
    let locationToFit: QuickLocation | null = null;
    
    if (currentView === 'multi-day-trip-plan-day' && selectedDayIndex !== null) {
      if (selectedDayIndex === 0) {
        locationToFit = multiDayStartLocation;
      } else {
        // Find the actual previous day, accounting for merged blocks
        let previousDayPlaces: PlaceItem[] | undefined;
        const previousDayIndex = selectedDayIndex - 1;
        let foundInBlock = false;
        
        for (const [blockStartIndex, blockIndices] of Array.from(combinedDayBlocks.entries())) {
          if (blockIndices.includes(previousDayIndex)) {
            const previousDayData = finalizedDays.get(blockStartIndex);
            previousDayPlaces = previousDayData?.places;
            foundInBlock = true;
            break;
          }
        }
        
        if (!foundInBlock) {
          previousDayPlaces = dayPlaces.get(previousDayIndex);
          if (!previousDayPlaces) {
            const previousDayData = finalizedDays.get(previousDayIndex);
            previousDayPlaces = previousDayData?.places;
          }
        }
        
        if (previousDayPlaces && previousDayPlaces.length > 0) {
          const lastPlace = previousDayPlaces[previousDayPlaces.length - 1];
          locationToFit = {
            name: lastPlace.name,
            coordinates: [
              lastPlace.geo_point?.lon || lastPlace.geo_point?.lng || 0,
              lastPlace.geo_point?.lat || 0
            ]
          };
        }
      }
    } else {
      locationToFit = startLocation;
    }
    
    if (placeMarkersRef.current.length > 0 && locationToFit) {
      const bounds = new mapboxgl.LngLatBounds();
      
      // Include start/day location
      if (locationToFit.coordinates && Array.isArray(locationToFit.coordinates) && 
          locationToFit.coordinates.length === 2 &&
          typeof locationToFit.coordinates[0] === 'number' && 
          typeof locationToFit.coordinates[1] === 'number' &&
          !isNaN(locationToFit.coordinates[0]) && !isNaN(locationToFit.coordinates[1])) {
        bounds.extend(locationToFit.coordinates);
      }
      
      // Include all place markers
      placesToShow.forEach(place => {
        const lng = place.geo_point?.lon || place.geo_point?.lng || place.geo_point?.longitude;
        const lat = place.geo_point?.lat || place.geo_point?.latitude;
        if (lng && lat) {
          bounds.extend([lng, lat]);
        }
      });

      // For plan day view, also include all route geometry points for better fitting
      if (currentView === 'multi-day-trip-plan-day' && selectedDayIndex !== null) {
        const segments = dayRouteSegments.get(selectedDayIndex);
        if (segments) {
          segments.forEach(segment => {
            if (segment.geometry && Array.isArray(segment.geometry)) {
              segment.geometry.forEach(coord => {
                // Validate coordinate is [lng, lat] format with valid numbers
                if (Array.isArray(coord) && coord.length === 2 && 
                    typeof coord[0] === 'number' && typeof coord[1] === 'number' &&
                    !isNaN(coord[0]) && !isNaN(coord[1])) {
                  bounds.extend(coord);
                }
              });
            }
          });
        }
      }

      // Only fit bounds if we have at least one valid coordinate
      if (!bounds.isEmpty()) {
        mapRef.current!.fitBounds(bounds, {
          padding: { top: 50, bottom: 50, left: 50, right: isDesktop ? 450 : 50 }, // More padding on right for desktop panel
          maxZoom: 13,
          duration: 1000
        });
      }
    }

    return () => {
      // Clean up markers when component unmounts or places change
      placeMarkersRef.current.forEach(marker => marker.remove());
      placeMarkersRef.current = [];
      // Close any open popup
      if (currentPopupRef.current) {
        currentPopupRef.current.remove();
        currentPopupRef.current = null;
      }
    };
  }, [nearbyPlaces, tripPlaces, currentView, mapLoaded, startLocation, selectedDayLocation, selectedDayIndex, dayRouteSegments, isDesktop, multiDayStartLocation, dayPlaces, finalizedDays]);

  // Initialize carousel functionality for popups
  const initializeCarousel = () => {
    // Wait a bit for DOM to be ready
    setTimeout(() => {
      
      // Store current indices for each carousel
      if (typeof (window as any).carouselState === 'undefined') {
        (window as any).carouselState = {};
      }

      // Function to update carousel display
      const goToSlide = (carouselId: string, index: number) => {
        (window as any).carouselState[carouselId] = index;
        
        // Update images
        const images = document.querySelectorAll(`.carousel-img-${carouselId}`);
        images.forEach((img: any, i: number) => {
          img.style.display = i === index ? 'block' : 'none';
        });
        
        // Update dots
        const dots = document.querySelectorAll(`.carousel-dot[data-carousel-id="${carouselId}"]`);
        dots.forEach((dot: any, i: number) => {
          if (i === index) {
            dot.style.width = '24px';
            dot.style.background = 'rgba(255, 255, 255, 0.95)';
          } else {
            dot.style.width = '8px';
            dot.style.background = 'rgba(255, 255, 255, 0.5)';
          }
        });
      };

      // Attach event listeners to prev buttons
      const prevButtons = document.querySelectorAll('.carousel-btn-prev');
      prevButtons.forEach((btn: any) => {
        const carouselId = btn.dataset.carouselId;
        const total = parseInt(btn.dataset.total);
        
        if (!carouselId) return;
        
        // Initialize state if needed
        if (typeof (window as any).carouselState[carouselId] === 'undefined') {
          (window as any).carouselState[carouselId] = 0;
        }
        
        btn.onclick = (e: Event) => {
          e.stopPropagation();
          const currentIndex = (window as any).carouselState[carouselId];
          const newIndex = currentIndex === 0 ? total - 1 : currentIndex - 1;
          goToSlide(carouselId, newIndex);
        };

        // Hover effects
        btn.onmouseenter = () => {
          btn.style.background = 'rgba(255,255,255,1)';
          btn.style.transform = 'translateY(-50%) scale(1.1)';
        };
        btn.onmouseleave = () => {
          btn.style.background = 'rgba(255,255,255,0.9)';
          btn.style.transform = 'translateY(-50%) scale(1)';
        };
      });

      // Attach event listeners to next buttons
      const nextButtons = document.querySelectorAll('.carousel-btn-next');
      nextButtons.forEach((btn: any) => {
        const carouselId = btn.dataset.carouselId;
        const total = parseInt(btn.dataset.total);
        
        if (!carouselId) return;
        
        // Initialize state if needed
        if (typeof (window as any).carouselState[carouselId] === 'undefined') {
          (window as any).carouselState[carouselId] = 0;
        }
        
        btn.onclick = (e: Event) => {
          e.stopPropagation();
          const currentIndex = (window as any).carouselState[carouselId];
          const newIndex = (currentIndex + 1) % total;
          goToSlide(carouselId, newIndex);
        };

        // Hover effects
        btn.onmouseenter = () => {
          btn.style.background = 'rgba(255,255,255,1)';
          btn.style.transform = 'translateY(-50%) scale(1.1)';
        };
        btn.onmouseleave = () => {
          btn.style.background = 'rgba(255,255,255,0.9)';
          btn.style.transform = 'translateY(-50%) scale(1)';
        };
      });

      // Attach event listeners to dots
      const dots = document.querySelectorAll('.carousel-dot');
      dots.forEach((dot: any) => {
        const carouselId = dot.dataset.carouselId;
        const index = parseInt(dot.dataset.index);
        
        if (!carouselId) return;
        
        // Initialize state if needed
        if (typeof (window as any).carouselState[carouselId] === 'undefined') {
          (window as any).carouselState[carouselId] = 0;
        }
        
        dot.onclick = (e: Event) => {
          e.stopPropagation();
          goToSlide(carouselId, index);
        };
      });
    }, 100); // End setTimeout
  };

  // Get drawer header text based on current view
  const getDrawerHeader = () => {
    switch (currentView) {
      case 'home':
        return {
          title: 'Plan Your Trip',
          subtitle: 'Swipe up to expand • Swipe down to collapse'
        };
      case 'trip-options':
        return {
          title: 'Choose Your Trip Type',
          subtitle: 'Select how you\'d like to plan your adventure'
        };
      case 'day-trip':
        return {
          title: 'Day Trip Planning',
          subtitle: 'Where would you like to start your journey?'
        };
      case 'day-trip-destinations':
        return {
          title: 'Pick Destinations',
          subtitle: 'Select places to visit on your day trip'
        };
      case 'my-day-trip':
        return {
          title: 'My Day Trip',
          subtitle: `${tripPlaces.length} ${tripPlaces.length === 1 ? 'place' : 'places'} selected`
        };
      case 'multi-day-trip':
        return {
          title: 'Choose Your Style',
          subtitle: 'How would you like to plan your trip?'
        };
      case 'multi-day-trip-details':
        return {
          title: 'Trip Details',
          subtitle: 'When are you traveling and who\'s joining?'
        };
      case 'multi-day-trip-itinerary':
        const nights = multiDayStartDate && multiDayEndDate 
          ? Math.ceil((multiDayEndDate.getTime() - multiDayStartDate.getTime()) / (1000 * 60 * 60 * 24))
          : 0;
        return {
          title: 'Your Itinerary',
          subtitle: `${nights + 1} ${nights + 1 === 1 ? 'day' : 'days'}, ${nights} ${nights === 1 ? 'night' : 'nights'}`
        };
      case 'multi-day-trip-day-destinations':
        if (selectedDayIndex !== null) {
          // Check if this day is part of a merged block
          const blockIndices = combinedDayBlocks.get(selectedDayIndex) || 
                               finalizedDays.get(selectedDayIndex)?.blockDayIndices;
          if (blockIndices && blockIndices.length > 1) {
            return {
              title: `Days ${blockIndices[0] + 1}-${blockIndices[blockIndices.length - 1] + 1} Destinations`,
              subtitle: 'Select places to visit'
            };
          }
        }
        return {
          title: `Day ${selectedDayIndex !== null ? selectedDayIndex + 1 : ''} Destinations`,
          subtitle: 'Select places to visit'
        };
      case 'multi-day-trip-plan-day':
        if (selectedDayIndex !== null) {
          // Check if this day is part of a merged block
          const blockIndices = combinedDayBlocks.get(selectedDayIndex) || 
                               finalizedDays.get(selectedDayIndex)?.blockDayIndices;
          if (blockIndices && blockIndices.length > 1) {
            return {
              title: `Plan Days ${blockIndices[0] + 1}-${blockIndices[blockIndices.length - 1] + 1}`,
              subtitle: 'Build your itinerary'
            };
          }
        }
        return {
          title: `Plan Day ${selectedDayIndex !== null ? selectedDayIndex + 1 : ''}`,
          subtitle: 'Build your itinerary'
        };
      case 'saved-day-trips':
        return {
          title: 'Saved Day Trips',
          subtitle: 'Your saved day trip plans'
        };
      case 'saved-multi-day-trips':
        return {
          title: 'Saved Multi-Day Trips',
          subtitle: 'Your saved multi-day trip plans'
        };
      case 'quick-quotes':
        return {
          title: 'Quick Quotes',
          subtitle: 'Your quick quote requests'
        };
      default:
        return {
          title: 'Plan Your Trip',
          subtitle: 'Swipe up to expand • Swipe down to collapse'
        };
    }
  };

  // Helper function to check if save button should be shown
  const shouldShowSaveButton = () => {
    // Show save button in day trip views (except initial planning)
    if (currentView === 'day-trip-destinations' || currentView === 'my-day-trip') {
      return tripPlaces.length > 0;
    }
    // Show save button in multi-day trip views (except initial details)
    if (currentView === 'multi-day-trip-itinerary' || currentView === 'multi-day-trip-day-destinations' || currentView === 'multi-day-trip-plan-day') {
      return finalizedDays.size > 0 || dayPlaces.size > 0;
    }
    return false;
  };

  // Helper functions for transport icons and labels
  const getTransportIcon = (mode: TransportMode) => {
    switch (mode) {
      case 'car':
        return <Car className="w-4 h-4" />;
      case 'bus':
        return <Bus className="w-4 h-4" />;
      case 'train':
        return <TrainTrack className="w-4 h-4" />;
    }
  };

  const getTransportLabel = (mode: TransportMode) => {
    switch (mode) {
      case 'car':
        return 'Car';
      case 'bus':
        return 'Bus';
      case 'train':
        return 'Train';
    }
  };

  // NOTE: Helper functions and useSavedTrips hook moved earlier in file (after email states)

  // Wrapper for handleSaveTrip to pass all state
  const handleSaveTrip = async () => {
    if (!authState.user?.userId) {
      return;
    }
    
    try {
      const tripType = currentView.includes('multi-day') ? 'multi-day-trip' : 'day-trip';
      const tripName = tripType === 'day-trip' ? dayTripName : multiDayTripName;
      
      // Validate trip name
      if (!tripName || tripName.trim() === '') {
        alert('Please provide a name for your trip');
        return;
      }
      
      const tripData: SavedTrip = {
        id: currentTripId || '',
        userId: authState.user.userId,
        tripName: tripName,
        tripType: tripType,
        createdAt: null as any, // Will be set by service
        updatedAt: null as any,
      };
      
      if (tripData.tripType === 'day-trip') {
        
        // Extract only essential place data and remove undefined values
        const simplifiedPlaces = tripPlaces.map(place => {
          // Handle geo_point from multiple possible sources
          let geoPoint: any;
          
          if (place.geo_point) {
            // geo_point might be array [lng, lat] or object {lat, lon/lng}
            if (Array.isArray(place.geo_point)) {
              // Convert array to {lon, lat} object for Firestore
              geoPoint = { lon: place.geo_point[0], lat: place.geo_point[1] };
            } else if (typeof place.geo_point === 'object') {
              // Already an object, ensure it has lon/lat format
              geoPoint = {
                lat: place.geo_point.lat || place.geo_point.latitude,
                lon: place.geo_point.lon || place.geo_point.lng || place.geo_point.longitude
              };
            }
          } else if (place.coordinates) {
            // Fallback to coordinates field
            if (Array.isArray(place.coordinates)) {
              geoPoint = { lon: place.coordinates[0], lat: place.coordinates[1] };
            } else if (typeof place.coordinates === 'object') {
              geoPoint = {
                lat: place.coordinates.lat || place.coordinates.latitude,
                lon: place.coordinates.lon || place.coordinates.lng || place.coordinates.longitude
              };
            }
          }
          
          const simplified: any = {
            id: place.id,
            title: place.title || place.name,
            type: place.type,
            geo_point: geoPoint
          };
          
          // Only add fields if they have values
          if (place.photo_1_cdn || place.imageUrl) simplified.photo_1_cdn = place.photo_1_cdn || place.imageUrl;
          if (place.photo_2_cdn) simplified.photo_2_cdn = place.photo_2_cdn;
          if (place.photo_3_cdn) simplified.photo_3_cdn = place.photo_3_cdn;
          if (place.photo_4_cdn) simplified.photo_4_cdn = place.photo_4_cdn;
          if (place.photo_5_cdn) simplified.photo_5_cdn = place.photo_5_cdn;
          if (place.ideal_duration) simplified.ideal_duration = place.ideal_duration;
          if (place.typical_time_spent) simplified.typical_time_spent = place.typical_time_spent;
          if (place.city) simplified.city = place.city;
          if (place.rating) simplified.rating = place.rating;
          
          return simplified;
        });
        
        tripData.dayTrip = {
          startLocation: startLocation!,
          places: simplifiedPlaces,
          transportation: Object.fromEntries(
            Array.from(dayTransportation.get(0) || new Map())
          ),
          routeSegments: (dayRouteSegments.get(0) || []).map(convertRouteSegmentForFirestore),
          filters: { selectedTypes, maxDistance },
          aiReview: aiReview || undefined,
          isFinalized: isTripFinalized
        };
      } else {
        // Convert Maps to Records for Firestore and simplify place data
        
        const finalizedDaysRecord: Record<number, any> = {};
        finalizedDays.forEach((data, dayIndex) => {
          
          // Extract only essential place data and remove undefined values
          const simplifiedPlaces = data.places.map((place, placeIdx) => {
            
            // Handle geo_point from multiple possible sources
            let geoPoint: any;
            
            if (place.geo_point) {
              // geo_point might be array [lng, lat] or object {lat, lon/lng}
              if (Array.isArray(place.geo_point)) {
                // Convert array to {lon, lat} object for Firestore
                geoPoint = { lon: place.geo_point[0], lat: place.geo_point[1] };
              } else if (typeof place.geo_point === 'object') {
                // Already an object, ensure it has lon/lat format
                geoPoint = {
                  lat: place.geo_point.lat || place.geo_point.latitude,
                  lon: place.geo_point.lon || place.geo_point.lng || place.geo_point.longitude
                };
              }
            } else if (place.coordinates) {
              // Fallback to coordinates field
              if (Array.isArray(place.coordinates)) {
                geoPoint = { lon: place.coordinates[0], lat: place.coordinates[1] };
              } else if (typeof place.coordinates === 'object') {
                geoPoint = {
                  lat: place.coordinates.lat || place.coordinates.latitude,
                  lon: place.coordinates.lon || place.coordinates.lng || place.coordinates.longitude
                };
              }
            }
            
            
            const simplified: any = {
              id: place.id,
              title: place.title || place.name,
              type: place.type,
              geo_point: geoPoint,
              primaryLocationIndex: place.primaryLocationIndex || 0  // Include location index
            };
            
            // Only add fields if they have values
            if (place.photo_1_cdn || place.imageUrl) simplified.photo_1_cdn = place.photo_1_cdn || place.imageUrl;
            if (place.photo_2_cdn) simplified.photo_2_cdn = place.photo_2_cdn;
            if (place.photo_3_cdn) simplified.photo_3_cdn = place.photo_3_cdn;
            if (place.photo_4_cdn) simplified.photo_4_cdn = place.photo_4_cdn;
            if (place.photo_5_cdn) simplified.photo_5_cdn = place.photo_5_cdn;
            if (place.ideal_duration) simplified.ideal_duration = place.ideal_duration;
            if (place.typical_time_spent) simplified.typical_time_spent = place.typical_time_spent;
            if (place.city) simplified.city = place.city;
            if (place.rating) simplified.rating = place.rating;
            
            return simplified;
          });
          
          finalizedDaysRecord[dayIndex] = {
            places: simplifiedPlaces,
            transportation: Object.fromEntries(data.transportation),
            routeSegments: data.routeSegments.map(convertRouteSegmentForFirestore),
            primaryLocations: data.primaryLocations || (data.startLocation ? [data.startLocation] : []),  // Save as array
            startLocation: data.startLocation || data.primaryLocations?.[0],  // Keep for backwards compatibility
            // Only include multi-day block data if this is a merged block
            ...(data.isMultiDayBlock && {
              isMultiDayBlock: true,
              blockDayIndices: data.blockDayIndices,
              blockStartDate: data.blockStartDate?.toISOString(),
              blockEndDate: data.blockEndDate?.toISOString()
            })
          };
          
        });
        
        // Save dayLocations as arrays (multi-location support)
        const dayLocationsRecord: Record<number, Array<{ name: string; coordinates: [number, number] }>> = {};
        dayLocations.forEach((locs, idx) => {
          dayLocationsRecord[idx] = locs;
        });
        
        const multiDayTripData: any = {
          startDate: Timestamp.fromDate(multiDayStartDate!),
          endDate: Timestamp.fromDate(multiDayEndDate!),
          travelers: {
            adults: multiDayAdults,
            children: multiDayChildren,
            infants: multiDayInfants
          },
          startLocation: multiDayStartLocation!,
          dayLocations: dayLocationsRecord,
          finalizedDays: finalizedDaysRecord
        };
        
        // Only add tripEnd if it exists
        if (isTripEnded && tripEndRouteSegment && tripEndLocation) {
          multiDayTripData.tripEnd = {
            location: tripEndLocation,
            transportMode: tripEndTransportMode,
            routeSegment: convertRouteSegmentForFirestore(tripEndRouteSegment),
            trainAvailable: tripEndTrainAvailable
          };
        }
        
        tripData.multiDayTrip = multiDayTripData;
      }
      
      const tripId = await tripService.saveTrip(authState.user.userId, tripData);
      setCurrentTripId(tripId);
      setHasUnsavedChanges(false);
      
      // Refresh the trip in savedTrips to get the new updatedAt timestamp
      const updatedTrip = await tripService.loadTrip(authState.user.userId, tripId);
      if (updatedTrip) {
        setSavedTrips(prev => {
          const exists = prev.some(t => t.id === tripId);
          if (exists) {
            return prev.map(t => t.id === tripId ? updatedTrip : t);
          } else {
            return [...prev, updatedTrip];
          }
        });
      }
      
      showToast('Trip saved successfully!', 'success');
    } catch (error) {
      showToast('Error saving trip. Please try again.', 'error');
    }
  };

  // Check if trip summary is current (not stale)
  const isTripSummaryCurrent = (): boolean => {
    if (!currentTripId) return false;
    
    // If there are unsaved changes, summary is automatically stale
    if (hasUnsavedChanges) return false;
    
    const currentTrip = savedTrips.find(t => t.id === currentTripId);
    if (!currentTrip || !currentTrip.tripSummary || !currentTrip.tripSummary.generatedAt) {
      return false;
    }
    
    // Convert timestamps to Date objects for comparison
    const generatedAtDate = (currentTrip.tripSummary.generatedAt as any).toDate ? 
      (currentTrip.tripSummary.generatedAt as any).toDate() : 
      (currentTrip.tripSummary.generatedAt instanceof Date ? currentTrip.tripSummary.generatedAt : new Date());
    
    const updatedAtDate = (currentTrip.updatedAt as any).toDate ? 
      (currentTrip.updatedAt as any).toDate() : 
      (currentTrip.updatedAt instanceof Date ? currentTrip.updatedAt : new Date());
    
    // Summary is current if generatedAt is equal to or newer than updatedAt
    return generatedAtDate >= updatedAtDate;
  };

  // Generate trip summary handler
  const handleGenerateSummaryClick = async () => {
    if (!authState.user?.userId) {
      setShowEmailAuthPrompt(true);
      return;
    }
    
    if (!currentTripId) {
      alert('Please save your trip first before generating a summary.');
      return;
    }

    // Check if we have a current summary - if so, just view it
    if (isTripSummaryCurrent()) {
      // Load the existing summary from the saved trip
      const currentTrip = savedTrips.find(t => t.id === currentTripId);
      if (currentTrip?.tripSummary?.content) {
        setTripSummary(currentTrip.tripSummary.content);
        setPlacePhotos({}); // Photos would need to be cached separately if needed
        setShowSummaryModal(true);
      }
      return;
    }

    // Check for unsaved changes
    if (hasUnsavedChanges) {
      showToast('⚠️ Please save your changes before generating the trip summary', 'warning', 5000);
      return;
    }

    // Always show optimization dialog (user can choose to optimize or keep order)
    setShowOptimizationDialog(true);
  };

  // Handle optimization dialog confirmation
  const handleOptimizationConfirm = async (optimize: boolean) => {
    setShowOptimizationDialog(false);
    
    if (!currentTripId || !authState.user?.userId) {
      return;
    }

    // Calculate number of days in the trip
    const tripDaysCount = finalizedDays.size;

    // If trip has more than 5 days, use async generation
    if (tripDaysCount > 5) {
      // Show async modal and start generation
      setShowAsyncSummaryModal(true);
      const response = await asyncSummary.startGeneration(authState.user.userId, currentTripId, optimize);
      
      // Handle rate limit error
      if (!response.success && response.status === 429) {
        setShowAsyncSummaryModal(false);
        
        const errorData = response.error;
        const isVerified = authState.user?.whatsappNumberE164 || authState.user?.telegramNumberE164;
        
        // Determine variant based on user tier
        const variant = isVerified ? 'wait_only' : 'whatsapp_verify';
        
        setRateLimitWaitMinutes(errorData.waitMinutes);
        setRegistrationVariant(variant);
        setShowRateLimitPrompt(true);
        
        showToast(
          `⏱️ Trip summary limit reached. ${errorData.waitMinutes ? `Wait ${errorData.waitMinutes} minutes.` : 'Try again later.'}`,
          'warning',
          5000
        );
        return;
      }
      
      // Handle other errors
      if (!response.success) {
        setShowAsyncSummaryModal(false);
        showToast('Failed to start trip summary generation', 'error');
        return;
      }
    } else {
      // For shorter trips, use synchronous generation as before
      setShowSummaryModal(true);
      const result = await handleGenerateSummary(currentTripId, optimize, tripDaysCount);
      
      // If handleGenerateSummary returns useAsync signal, something went wrong
      // (shouldn't happen since we checked days count)
      if (result?.useAsync) {
        setShowAsyncSummaryModal(true);
        const response = await asyncSummary.startGeneration(authState.user.userId, currentTripId, optimize);
        
        // Handle rate limit error
        if (!response.success && response.status === 429) {
          setShowAsyncSummaryModal(false);
          
          const errorData = response.error;
          const isVerified = authState.user?.whatsappNumberE164 || authState.user?.telegramNumberE164;
          
          // Determine variant based on user tier
          const variant = isVerified ? 'wait_only' : 'whatsapp_verify';
          
          setRateLimitWaitMinutes(errorData.waitMinutes);
          setRegistrationVariant(variant);
          setShowRateLimitPrompt(true);
          
          showToast(
            `⏱️ Trip summary limit reached. ${errorData.waitMinutes ? `Wait ${errorData.waitMinutes} minutes.` : 'Try again later.'}`,
            'warning',
            5000
          );
          return;
        }
        
        // Handle other errors
        if (!response.success) {
          setShowAsyncSummaryModal(false);
          showToast('Failed to start trip summary generation', 'error');
          return;
        }
      }
    }
  };

  // Handle optimization dialog cancel
  const handleOptimizationCancel = () => {
    setShowOptimizationDialog(false);
  };

  // Close summary modal and clear state
  const handleCloseSummaryModal = () => {
    setShowSummaryModal(false);
    clearSummary();
  };

  // Retry generating summary
  const handleRetrySummary = async () => {
    if (currentTripId) {
      await handleGenerateSummary(currentTripId);
    }
  };

  // Handle location selection for "Add More Places" in multi-day trip
  const handleAddMorePlacesLocationSelection = async (locationIndex: number) => {
    if (selectedDayIndex === null) return;
    
    // Close dropdown
    setShowAddMorePlacesDropdown(false);
    
    // Set current primary location index
    setCurrentPrimaryLocationIndex(locationIndex);
    
    // Get selected location's coordinates
    const selectedLocation = dayLocations.get(selectedDayIndex)?.[locationIndex];
    if (!selectedLocation) return;
    
    // Load ALL existing places for correct route calculation
    // (The Continue handler will filter to current location's places before merging)
    const existingDayData = finalizedDays.get(selectedDayIndex);
    if (existingDayData) {
      setTripPlaces(existingDayData.places);  // Load ALL places for route calculation
      
      // Load transportation and route segments
      setDayTransportation(prev => {
        const newMap = new Map(prev);
        newMap.set(selectedDayIndex, existingDayData.transportation);
        return newMap;
      });
      
      setDayRouteSegments(prev => {
        const newMap = new Map(prev);
        newMap.set(selectedDayIndex, existingDayData.routeSegments);
        return newMap;
      });

      // Restore active train routes
      const restoredTrainRoutes = new Map<number, any>();
      existingDayData.routeSegments.forEach((segment, index) => {
        if (segment.trainRoute) {
          restoredTrainRoutes.set(index, segment.trainRoute);
        }
      });
      setActiveTrainRoutes(restoredTrainRoutes);
    } else {
      // No existing data, start fresh
      setTripPlaces([]);
    }
    
    // Set selectedDayLocation to trigger nearby places fetch via useNearbyPlaces hook
    setSelectedDayLocation(selectedLocation);
    
    // Navigate to destination picker view
    setCurrentView('multi-day-trip-day-destinations');
    
    // Center map on selected location
    if (mapRef.current) {
      mapRef.current.flyTo({
        center: selectedLocation.coordinates,
        zoom: 13,
        duration: 1000
      });
    }
  };

  // Save day trip handler
  const handleSaveDayTrip = async () => {
    if (!authState.user?.userId) {
      return;
    }

    // Validate trip has name and places
    if (!dayTripName.trim()) {
      showToast('Please enter a trip name', 'error');
      return;
    }

    if (tripPlaces.length === 0) {
      showToast('Please add at least one place to your trip', 'error');
      return;
    }

    try {
      // Simplify places data (same as in handleSaveTrip)
      const simplifiedPlaces = tripPlaces.map(place => {
        let geoPoint: any;
        
        if (place.geo_point) {
          if (Array.isArray(place.geo_point)) {
            geoPoint = { lon: place.geo_point[0], lat: place.geo_point[1] };
          } else if (typeof place.geo_point === 'object') {
            geoPoint = {
              lat: place.geo_point.lat || place.geo_point.latitude,
              lon: place.geo_point.lon || place.geo_point.lng || place.geo_point.longitude
            };
          }
        } else if (place.coordinates) {
          if (Array.isArray(place.coordinates)) {
            geoPoint = { lon: place.coordinates[0], lat: place.coordinates[1] };
          } else if (typeof place.coordinates === 'object') {
            geoPoint = {
              lat: place.coordinates.lat || place.coordinates.latitude,
              lon: place.coordinates.lon || place.coordinates.lng || place.coordinates.longitude
            };
          }
        }
        
        const simplified: any = {
          id: place.id,
          title: place.title || place.name,
          type: place.type,
          geo_point: geoPoint
        };
        
        if (place.photo_1_cdn || place.imageUrl) simplified.photo_1_cdn = place.photo_1_cdn || place.imageUrl;
        if (place.photo_2_cdn) simplified.photo_2_cdn = place.photo_2_cdn;
        if (place.photo_3_cdn) simplified.photo_3_cdn = place.photo_3_cdn;
        if (place.photo_4_cdn) simplified.photo_4_cdn = place.photo_4_cdn;
        if (place.photo_5_cdn) simplified.photo_5_cdn = place.photo_5_cdn;
        if (place.ideal_duration) simplified.ideal_duration = place.ideal_duration;
        if (place.typical_time_spent) simplified.typical_time_spent = place.typical_time_spent;
        if (place.city) simplified.city = place.city;
        if (place.rating) simplified.rating = place.rating;
        
        return simplified;
      });

      const tripData: SavedTrip = {
        id: currentDayTripId || '',
        userId: authState.user.userId,
        tripName: dayTripName,
        tripType: 'day-trip',
        createdAt: null as any,
        updatedAt: null as any,
        dayTrip: {
          startLocation: startLocation!,
          places: simplifiedPlaces,
          transportation: Object.fromEntries(
            Array.from(dayTransportation.get(0) || new Map())
          ),
          routeSegments: (dayRouteSegments.get(0) || []).map(convertRouteSegmentForFirestore),
          filters: { selectedTypes, maxDistance },
          aiReview: aiReview || undefined,
          isFinalized: isTripFinalized
        }
      };

      const tripId = await tripService.saveTrip(authState.user.userId, tripData);
      setCurrentDayTripId(tripId);
      setCurrentTripId(tripId);
      
      if (currentDayTripId) {
        showToast('Trip updated successfully!', 'success');
      } else {
        showToast('Trip saved successfully!', 'success');
      }
    } catch (error) {
      showToast('Failed to save trip. Please try again.', 'error');
    }
  };

  // Load trip handler
  const handleLoadTrip = async (tripId: string) => {
    if (!authState.user?.userId) return;
    
    try {
      const trip = await tripService.loadTrip(authState.user.userId, tripId);
      if (!trip) return;
      
      setCurrentTripId(trip.id);
      
      if (trip.tripType === 'day-trip' && trip.dayTrip) {
        const dt = trip.dayTrip;
        setDayTripName(trip.tripName);
        setStartLocation(dt.startLocation);
        // Normalize places to ensure they have 'name' field for display and recalculate distances
        const normalizedPlaces = dt.places.map((place: any) => {
          // Calculate distance from start location if not present
          let distance = place.distance;
          if (distance === undefined && dt.startLocation && place.geo_point) {
            const startLat = dt.startLocation.coordinates[1];
            const startLon = dt.startLocation.coordinates[0];
            const placeLat = place.geo_point.lat;
            const placeLon = place.geo_point.lon;
            if (startLat !== undefined && startLon !== undefined && placeLat !== undefined && placeLon !== undefined) {
              distance = calculateDistance(startLat, startLon, placeLat, placeLon);
            }
          }
          return {
            ...place,
            name: place.name || place.title, // Ensure name field exists for display
            distance, // Ensure distance is set
          };
        });
        setTripPlaces(normalizedPlaces);
        setSelectedTypes(dt.filters.selectedTypes);
        setMaxDistance(dt.filters.maxDistance);
        setAiReview(dt.aiReview || null);
        setIsTripFinalized(dt.isFinalized);
        setCurrentDayTripId(trip.id); // Set the day trip ID for updates
        
        // Restore transportation
        const transportMap = new Map<number, TransportMode>();
        Object.entries(dt.transportation).forEach(([idx, mode]) => {
          transportMap.set(Number(idx), mode as TransportMode);
        });
        setDayTransportation(new Map([[0, transportMap]]));
        // Convert route segments from Firestore format
        const convertedSegments = dt.routeSegments.map(convertRouteSegmentFromFirestore);
        setDayRouteSegments(new Map([[0, convertedSegments]]));
        
        setCurrentView('my-day-trip');
      } else if (trip.tripType === 'multi-day-trip' && trip.multiDayTrip) {
        const mt = trip.multiDayTrip;
        setMultiDayTripName(trip.tripName);
        setMultiDayStartDate(mt.startDate.toDate());
        setMultiDayEndDate(mt.endDate.toDate());
        setMultiDayAdults(mt.travelers.adults);
        setMultiDayChildren(mt.travelers.children);
        setMultiDayInfants(mt.travelers.infants);
        setMultiDayStartLocation(mt.startLocation);
        
        // Restore day locations (handle both old single-location and new array format)
        const locMap = new Map<number, Array<{ name: string; coordinates: [number, number] }>>();
        Object.entries(mt.dayLocations).forEach(([idx, loc]: [string, any]) => {
          // Handle backwards compatibility: old format was single object, new format is array
          if (Array.isArray(loc)) {
            locMap.set(Number(idx), loc);
          } else {
            // Migrate old single-location format to array
            locMap.set(Number(idx), [loc]);
          }
        });
        setDayLocations(locMap);
        
        // Restore finalized days
        const finalizedMap = new Map<number, any>();
        const combinedBlocksMap = new Map<number, number[]>();
        
        Object.entries(mt.finalizedDays).forEach(([idx, data]: [string, any]) => {
          const dayIndex = Number(idx);
          
          
          const transportMap = new Map<number, TransportMode>();
          Object.entries(data.transportation).forEach(([segIdx, mode]) => {
            transportMap.set(Number(segIdx), mode as TransportMode);
          });
          
          // Convert route segments from Firestore format
          const convertedSegments = data.routeSegments.map(convertRouteSegmentFromFirestore);
          
          // Normalize places to ensure they have 'name' field for display and recalculate distances
          const normalizedPlaces = data.places.map((place: any) => {
            // Calculate distance from start location if not present
            let distance = place.distance;
            if (distance === undefined && data.startLocation && place.geo_point) {
              const startLat = data.startLocation.coordinates[1];
              const startLon = data.startLocation.coordinates[0];
              const placeLat = place.geo_point.lat;
              const placeLon = place.geo_point.lon;
              if (startLat !== undefined && startLon !== undefined && placeLat !== undefined && placeLon !== undefined) {
                distance = calculateDistance(startLat, startLon, placeLat, placeLon);
              }
            }
            return {
              ...place,
              name: place.name || place.title, // Ensure name field exists for display
              distance, // Ensure distance is set
              primaryLocationIndex: place.primaryLocationIndex ?? 0, // Default to 0 for backwards compatibility
            };
          });
          
          // Handle primaryLocations (backwards compatibility: migrate from startLocation if needed)
          const primaryLocations = data.primaryLocations || (data.startLocation ? [data.startLocation] : []);
          
          finalizedMap.set(dayIndex, {
            ...data,
            places: normalizedPlaces,
            transportation: transportMap,
            routeSegments: convertedSegments,
            primaryLocations,  // Ensure this is always an array
            startLocation: data.startLocation || primaryLocations[0],  // Keep for backwards compatibility
            // Load multi-day block data
            ...(data.isMultiDayBlock && {
              isMultiDayBlock: true,
              blockDayIndices: data.blockDayIndices,
              blockStartDate: new Date(data.blockStartDate),
              blockEndDate: new Date(data.blockEndDate)
            })
          });
          
          // Build combinedDayBlocks map
          if (data.isMultiDayBlock && data.blockDayIndices) {
            combinedBlocksMap.set(dayIndex, data.blockDayIndices);
          }
        });
        
        // Set all state at once
        setFinalizedDays(finalizedMap);
        setCombinedDayBlocks(combinedBlocksMap);
        
        // Restore trip end
        if (mt.tripEnd) {
          setTripEndLocation(mt.tripEnd.location);
          setTripEndTransportMode(mt.tripEnd.transportMode);
          // Convert trip end route segment from Firestore format
          setTripEndRouteSegment(convertRouteSegmentFromFirestore(mt.tripEnd.routeSegment));
          setTripEndTrainAvailable(mt.tripEnd.trainAvailable);
          setIsTripEnded(true);
          // Add trip end marker
          addTripEndMarker(mt.tripEnd.location.coordinates);
        }
        
        // Decide which view to open based on trip summary status
        // If trip summary exists and is current (not stale), open summary view
        // Otherwise, open itinerary view
        let shouldOpenSummary = false;
        
        if (trip.tripSummary && trip.tripSummary.generatedAt && trip.updatedAt) {
          // Convert timestamps to Date objects for comparison
          const generatedAtDate = (trip.tripSummary.generatedAt as any).toDate ? 
            (trip.tripSummary.generatedAt as any).toDate() : 
            (trip.tripSummary.generatedAt instanceof Date ? trip.tripSummary.generatedAt : new Date());
          
          const updatedAtDate = (trip.updatedAt as any).toDate ? 
            (trip.updatedAt as any).toDate() : 
            (trip.updatedAt instanceof Date ? trip.updatedAt : new Date());
          
          // Summary is current if generatedAt is equal to or newer than updatedAt
          if (generatedAtDate >= updatedAtDate) {
            shouldOpenSummary = true;
            
            // Load the trip summary content into state
            setTripSummary(trip.tripSummary.content);
            
            // Load place photos if they exist (they're usually cached in the summary)
            // If not present, we'll set empty object
            setPlacePhotos({});
            
            console.log('[Plan] Opening trip summary view - summary is current', {
              generatedAt: generatedAtDate,
              updatedAt: updatedAtDate
            });
          } else {
            console.log('[Plan] Opening itinerary view - summary is stale', {
              generatedAt: generatedAtDate,
              updatedAt: updatedAtDate
            });
          }
        } else {
          console.log('[Plan] Opening itinerary view - no summary exists');
        }

        if (shouldOpenSummary) {
          // Open summary modal instead of itinerary view
          setCurrentView('multi-day-trip-itinerary'); // Still need to set the base view
          setShowSummaryModal(true); // Then open the summary modal
        } else {
          // Open itinerary view as usual
          setCurrentView('multi-day-trip-itinerary');
        }
      }
      
      setHasUnsavedChanges(false);
    } catch (error) {
    }
  };

  const handleDeleteTrip = async (tripId: string) => {
    if (!authState.user?.userId) return;
    
    try {
      await tripService.deleteTrip(authState.user.userId, tripId);
      setSavedTrips(prev => prev.filter(t => t.id !== tripId));
      alert('Trip deleted successfully');
    } catch (error) {
      alert('Error deleting trip');
    }
  };

  // Navigation handler with unsaved changes check
  const shouldPromptSave = (from: DrawerView, to: DrawerView): boolean => {
    const tripViews = ['my-day-trip', 'multi-day-trip-itinerary', 'multi-day-trip-plan-day', 'multi-day-trip-day-destinations'];
    return tripViews.includes(from) && !tripViews.includes(to);
  };

  const handleNavigation = (targetView: DrawerView) => {
    if (hasUnsavedChanges && shouldPromptSave(currentView, targetView)) {
      setPendingNavigation(targetView);
      setShowSavePrompt(true);
    } else {
      setCurrentView(targetView);
    }
  };

  // Multi-day block combining logic
  const checkCombineConflict = (startIndex: number): number[] | null => {
    const nextIndex = startIndex + 1;
    for (const [blockStart, blockIndices] of Array.from(combinedDayBlocks.entries())) {
      // Skip the current block (we're extending it, not conflicting with it)
      if (blockStart === startIndex) {
        continue;
      }
      // Check if next day is part of a different block
      if (blockIndices.includes(nextIndex)) {
        return blockIndices; // Return conflicting block
      }
    }
    return null;
  };

  const handleCombineDays = (dayIndex: number) => {
    const nextDayIndex = dayIndex + 1;
    
    // Check for conflict
    const conflict = checkCombineConflict(dayIndex);
    if (conflict) {
      const conflictStart = conflict[0];
      const conflictEnd = conflict[conflict.length - 1];
      showToast(
        `Please decombine Days ${conflictStart + 1}-${conflictEnd + 1} first before combining these days`,
        'warning',
        5000
      );
      return;
    }
    
    // Get existing block or create new one
    const existingBlock = combinedDayBlocks.get(dayIndex);
    let newBlockIndices: number[];
    
    if (existingBlock) {
      // Add next consecutive day to existing block
      const lastDayInBlock = existingBlock[existingBlock.length - 1];
      const nextConsecutiveDay = lastDayInBlock + 1;
      newBlockIndices = [...existingBlock, nextConsecutiveDay];
    } else {
      // Create new block with current and next day
      newBlockIndices = [dayIndex, nextDayIndex];
    }
    
    // Generate days array for date reference
    const generateDaysForCombine = () => {
      if (!multiDayStartDate || !multiDayEndDate) return [];
      const days = [];
      const currentDate = new Date(multiDayStartDate);
      while (currentDate <= multiDayEndDate) {
        days.push(new Date(currentDate));
        currentDate.setDate(currentDate.getDate() + 1);
      }
      return days;
    };
    const itineraryDays = generateDaysForCombine();
    
    // Get finalized data if it exists
    const currentDayData = finalizedDays.get(dayIndex);
    const nextDayData = finalizedDays.get(nextDayIndex);
    
    // Get places from both days (combine finalized places if they exist)
    const combinedPlaces = [
      ...(currentDayData?.places || []),
      ...(nextDayData?.places || [])
    ];
    
    // Merge transportation maps (recalculate indices)
    const combinedTransportation = new Map<number, TransportMode>();
    currentDayData?.transportation.forEach((mode, idx) => {
      combinedTransportation.set(idx, mode);
    });
    const currentPlaceCount = currentDayData?.places.length || 0;
    nextDayData?.transportation.forEach((mode, idx) => {
      combinedTransportation.set(idx + currentPlaceCount, mode);
    });
    
    // Merge route segments (update indices)
    const combinedSegments = [
      ...(currentDayData?.routeSegments || []),
      ...(nextDayData?.routeSegments?.map(seg => ({
        ...seg,
        fromIndex: seg.fromIndex + currentPlaceCount,
        toIndex: seg.toIndex + currentPlaceCount
      })) || [])
    ];
    
    // Determine primary locations (combine from both days)
    let primaryLocations: Array<{ name: string; coordinates: [number, number] }> = [];
    
    // Get primary locations from current day
    if (currentDayData?.primaryLocations) {
      primaryLocations = [...currentDayData.primaryLocations];
    } else if (currentDayData?.startLocation) {
      // Backwards compatibility: migrate single startLocation to array
      primaryLocations = [currentDayData.startLocation];
    } else {
      const dayLocs = dayLocations.get(dayIndex);
      if (dayLocs && dayLocs.length > 0) {
        primaryLocations = [...dayLocs];
      }
    }
    
    // Add primary locations from next day
    if (nextDayData?.primaryLocations) {
      primaryLocations = [...primaryLocations, ...nextDayData.primaryLocations];
    } else if (nextDayData?.startLocation) {
      primaryLocations.push(nextDayData.startLocation);
    } else {
      const nextDayLocs = dayLocations.get(nextDayIndex);
      if (nextDayLocs && nextDayLocs.length > 0) {
        primaryLocations = [...primaryLocations, ...nextDayLocs];
      }
    }
    
    // Fallback for first day
    if (primaryLocations.length === 0 && dayIndex === 0 && multiDayStartLocation) {
      primaryLocations = [multiDayStartLocation];
    }
    
    // Calculate block dates
    const blockStartDate = itineraryDays[newBlockIndices[0]];
    const blockEndDate = itineraryDays[newBlockIndices[newBlockIndices.length - 1]];
    
    // Create merged day data
    const mergedDayData: FinalizedDayData = {
      places: combinedPlaces,
      transportation: combinedTransportation,
      routeSegments: combinedSegments,
      primaryLocations: primaryLocations,
      startLocation: primaryLocations[0],  // Keep for backwards compatibility
      isMultiDayBlock: true,
      blockDayIndices: newBlockIndices,
      blockStartDate,
      blockEndDate
    };
    
    // Update states
    setCombinedDayBlocks(prev => {
      const newMap = new Map(prev);
      newMap.set(dayIndex, newBlockIndices);
      return newMap;
    });
    
    setFinalizedDays(prev => {
      const newMap = new Map(prev);
      newMap.set(dayIndex, mergedDayData);
      // Remove the absorbed day
      newMap.delete(nextDayIndex);
      return newMap;
    });
    
    // Mark as having unsaved changes
    setHasUnsavedChanges(true);
    
    showToast('Days combined successfully!', 'success', 3000);
  };

  const handleSplitDays = (blockStartIndex: number) => {
    const blockIndices = combinedDayBlocks.get(blockStartIndex);
    if (!blockIndices || blockIndices.length < 2) return;
    
    const removedDayIndex = blockIndices[blockIndices.length - 1];
    
    // Show warning about clearing subsequent days
    const confirmed = window.confirm(
      `Splitting days will remove all planned places from Day ${removedDayIndex + 1} and all following days. This is necessary to maintain route consistency. Are you sure?`
    );
    if (!confirmed) return;
    
    // Remove the last day from block
    const newBlockIndices = blockIndices.slice(0, -1);
    
    
    // Update or remove block
    setCombinedDayBlocks(prev => {
      const newMap = new Map(prev);
      if (newBlockIndices.length === 1) {
        // Only one day left, remove block entirely
        newMap.delete(blockStartIndex);
      } else {
        // Update block with remaining days
        newMap.set(blockStartIndex, newBlockIndices);
      }
      return newMap;
    });
    
    // Update finalized data - clear split-off day AND all subsequent days
    setFinalizedDays(prev => {
      const newMap = new Map(prev);
      
      // Clear all days from removedDayIndex onwards
      const allDayIndices = Array.from(newMap.keys()).sort((a, b) => a - b);
      allDayIndices.forEach(dayIndex => {
        if (dayIndex >= removedDayIndex) {
          newMap.delete(dayIndex);
        }
      });
      
      if (newBlockIndices.length === 1) {
        // If only one day remains, update it to be a regular single day
        const existingData = newMap.get(blockStartIndex);
        if (existingData) {
          newMap.set(blockStartIndex, {
            ...existingData,
            isMultiDayBlock: false,
            blockDayIndices: undefined,
            blockStartDate: undefined,
            blockEndDate: undefined
          });
        }
      } else {
        // Update the block with new indices and dates
        const existingData = newMap.get(blockStartIndex);
        if (existingData) {
          // Generate days array for date reference
          const generateDaysForSplit = () => {
            if (!multiDayStartDate || !multiDayEndDate) return [];
            const days = [];
            const currentDate = new Date(multiDayStartDate);
            while (currentDate <= multiDayEndDate) {
              days.push(new Date(currentDate));
              currentDate.setDate(currentDate.getDate() + 1);
            }
            return days;
          };
          const itineraryDays = generateDaysForSplit();
          
          newMap.set(blockStartIndex, {
            ...existingData,
            blockDayIndices: newBlockIndices,
            blockStartDate: itineraryDays[newBlockIndices[0]],
            blockEndDate: itineraryDays[newBlockIndices[newBlockIndices.length - 1]]
          });
        }
      }
      // Note: Already cleared all days >= removedDayIndex in the loop above
      return newMap;
    });
    
    // Clear places from the removed day AND all subsequent days
    setDayPlaces(prev => {
      const newMap = new Map(prev);
      const allDayIndices = Array.from(newMap.keys()).sort((a, b) => a - b);
      allDayIndices.forEach(dayIndex => {
        if (dayIndex >= removedDayIndex) {
          newMap.delete(dayIndex);
        }
      });
      return newMap;
    });
    
    // Clear transportation data for removed day and subsequent days
    setDayTransportation(prev => {
      const newMap = new Map(prev);
      const allDayIndices = Array.from(newMap.keys()).sort((a, b) => a - b);
      allDayIndices.forEach(dayIndex => {
        if (dayIndex >= removedDayIndex) {
          newMap.delete(dayIndex);
        }
      });
      return newMap;
    });
    
    // Clear route segments for removed day and subsequent days
    setDayRouteSegments(prev => {
      const newMap = new Map(prev);
      const allDayIndices = Array.from(newMap.keys()).sort((a, b) => a - b);
      allDayIndices.forEach(dayIndex => {
        if (dayIndex >= removedDayIndex) {
          newMap.delete(dayIndex);
        }
      });
      return newMap;
    });
    
    // Clear active train routes for removed day and subsequent days
    setActiveTrainRoutes(prev => {
      const newMap = new Map(prev);
      // activeTrainRoutes uses segment indices, not day indices
      // For safety, we'll clear all train routes when splitting
      newMap.clear();
      return newMap;
    });
    
    // Clear day locations (primary location markers on map) for removed day and subsequent days
    setDayLocations(prev => {
      const newMap = new Map(prev);
      const allDayIndices = Array.from(newMap.keys()).sort((a, b) => a - b);
      allDayIndices.forEach(dayIndex => {
        if (dayIndex >= removedDayIndex) {
          newMap.delete(dayIndex);
        }
      });
      return newMap;
    });
    
    // Clear trip end data if it was set (to maintain consistency)
    if (isTripEnded) {
      setIsTripEnded(false);
      setTripEndLocation(null);
      setTripEndRouteSegment(null);
      setTripEndTransportMode('car');
      removeTripEndMarker();
      setTripEndTrainAvailable(false);
    }
    
    // Mark as having unsaved changes
    setHasUnsavedChanges(true);
    
    showToast('Day split successfully. Please re-plan days from Day ' + (removedDayIndex + 1) + ' onwards.', 'info', 4000);
  };

  // Render drawer content based on current view
  const renderDrawerContent = () => {
    switch (currentView) {
      case 'home':
        return (
          <div className="space-y-2.5 md:space-y-4">
            {/* Getting Started Option */}
            <div 
              className="bg-gradient-to-br from-[#F0E9F8] via-[#F0E9F8] to-[#E0D7FA] rounded-xl md:rounded-lg p-3.5 md:p-5 cursor-pointer hover:shadow-md md:hover:shadow-lg transition-all duration-200 md:hover:scale-[1.02] border border-[#E0D7FA] md:border-2 md:border-transparent md:hover:border-[#D4C5ED] active:scale-95 md:active:scale-100"
              onClick={() => {
                setTripOptionsMode('create');
                navigateToView('trip-options');
              }}
            >
              <div className="flex items-start gap-2.5 md:gap-3">
                <div className="bg-gradient-to-br from-[#14b8a6] via-[#5C7FB8] to-[#663399] rounded-lg p-2.5 md:p-3 mr-0.5 flex-shrink-0 shadow-sm">
                  <Calendar className="w-5 h-5 md:w-6 md:h-6 text-white" />
                </div>
                <div className="flex-1 min-w-0">
                  <h4 className="font-semibold md:font-bold text-gray-900 mb-0.5 md:mb-1 text-sm md:text-base leading-tight">Getting Started</h4>
                  <p className="text-xs md:text-sm text-gray-600 leading-relaxed line-clamp-2 md:line-clamp-none">
                    Plan a new trip by selecting places
                  </p>
                </div>
                <svg className="w-4 h-4 md:w-5 md:h-5 text-[#663399] mt-0.5 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2.5} d="M9 5l7 7-7 7" />
                </svg>
              </div>
            </div>
            
            {/* I'm too lazy Option */}
            <div 
              className="bg-gradient-to-br from-purple-50 via-purple-50 to-purple-100 rounded-xl md:rounded-lg p-3.5 md:p-5 cursor-pointer hover:shadow-md md:hover:shadow-lg transition-all duration-200 md:hover:scale-[1.02] border border-purple-200 md:border-2 md:border-transparent md:hover:border-purple-300 active:scale-95 md:active:scale-100"
              onClick={() => {
                if (!authState.user?.userId) {
                  setShowEmailAuthPrompt(true);
                  return;
                }
                setShowLazyQuoteForm(true);
              }}
            >
              <div className="flex items-start gap-2.5 md:gap-3">
                <div className="bg-gradient-to-br from-[#663399] to-[#D946A6] rounded-lg p-2.5 md:p-3 mr-0.5 flex-shrink-0 shadow-sm">
                  <Zap className="w-5 h-5 md:w-6 md:h-6 text-white" />
                </div>
                <div className="flex-1 min-w-0">
                  <h4 className="font-semibold md:font-bold text-gray-900 mb-0.5 md:mb-1 text-sm md:text-base leading-tight">I'm Too Lazy</h4>
                  <p className="text-xs md:text-sm text-gray-600 leading-relaxed line-clamp-2 md:line-clamp-none">
                    Quote without planning - we'll help you figure it out
                  </p>
                </div>
                <svg className="w-4 h-4 md:w-5 md:h-5 text-purple-600 mt-0.5 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2.5} d="M9 5l7 7-7 7" />
                </svg>
              </div>
            </div>
            
            {/* Your Saved Trips Option */}
            <div 
              className="bg-gradient-to-br from-[#FAF9FC] via-[#FAF9FC] to-[#F5F3F7] rounded-xl md:rounded-lg p-3.5 md:p-5 cursor-pointer hover:shadow-md md:hover:shadow-lg transition-all duration-200 md:hover:scale-[1.02] border border-[#F0E9F8] md:border-2 md:border-transparent md:hover:border-[#E8E0F3] active:scale-95 md:active:scale-100"
              onClick={() => {
                if (!authState.user?.userId) {
                  setShowEmailAuthPrompt(true);
                  return;
                }
                setTripOptionsMode('saved');
                navigateToView('trip-options');
              }}
            >
              <div className="flex items-start gap-2.5 md:gap-3">
                <div className="bg-gradient-to-br from-[#663399] via-[#6B5BA8] to-[#5A4A97] rounded-lg p-2.5 md:p-3 mr-0.5 flex-shrink-0 shadow-sm">
                  <BookOpen className="w-5 h-5 md:w-6 md:h-6 text-white" />
              </div>
                <div className="flex-1 min-w-0">
                  <h4 className="font-semibold md:font-bold text-gray-900 mb-0.5 md:mb-1 text-sm md:text-base leading-tight">Your Saved Trips</h4>
                  <p className="text-xs md:text-sm text-gray-600 leading-relaxed line-clamp-2 md:line-clamp-none">
                    View and edit saved trips
                  </p>
                </div>
                <svg className="w-4 h-4 md:w-5 md:h-5 text-[#663399] mt-0.5 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2.5} d="M9 5l7 7-7 7" />
                </svg>
              </div>
            </div>
          </div>
        );

      case 'trip-options':
        return (
          <div className="space-y-3 md:space-y-6">
            {tripOptionsMode === 'saved' ? (
              /* Show Saved Trips Options */
              <div className="space-y-2.5 md:space-y-3">
                <h3 className="text-base md:text-lg font-semibold md:font-bold text-gray-900">Your Saved Trips</h3>
                
                {/* Saved Day Trips Option */}
                <div 
                  className="bg-gradient-to-br from-blue-50 via-blue-50 to-blue-100 rounded-xl md:rounded-lg p-3.5 md:p-5 cursor-pointer hover:shadow-md md:hover:shadow-lg transition-all duration-200 md:hover:scale-[1.02] border border-blue-200 md:border-2 md:border-transparent md:hover:border-blue-300 active:scale-95 md:active:scale-100"
                  onClick={() => navigateToView('saved-day-trips')}
                >
                  <div className="flex items-start gap-2.5 md:gap-3">
                    <div className="bg-gradient-to-br from-blue-500 to-blue-600 rounded-lg p-2.5 md:p-3 flex-shrink-0 shadow-sm">
                      <Calendar className="w-5 h-5 md:w-6 md:h-6 text-white" />
                    </div>
                    <div className="flex-1 min-w-0">
                      <h4 className="font-semibold md:font-bold text-gray-900 mb-0.5 md:mb-1 text-sm md:text-base leading-tight">Saved Day Trips</h4>
                      <p className="text-xs md:text-sm text-gray-600 leading-relaxed line-clamp-2 md:line-clamp-none">
                        View and edit saved day trips
                      </p>
                  </div>
                    <svg className="w-4 h-4 md:w-5 md:h-5 text-blue-600 mt-0.5 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2.5} d="M9 5l7 7-7 7" />
                    </svg>
                  </div>
                </div>

                {/* Saved Multi-Day Trips Option */}
                <div 
                  className="bg-gradient-to-br from-indigo-50 via-indigo-50 to-indigo-100 rounded-xl md:rounded-lg p-3.5 md:p-5 cursor-pointer hover:shadow-md md:hover:shadow-lg transition-all duration-200 md:hover:scale-[1.02] border border-indigo-200 md:border-2 md:border-transparent md:hover:border-indigo-300 active:scale-95 md:active:scale-100"
                  onClick={() => navigateToView('saved-multi-day-trips')}
                >
                  <div className="flex items-start gap-2.5 md:gap-3">
                    <div className="bg-gradient-to-br from-indigo-500 to-indigo-600 rounded-lg p-2.5 md:p-3 flex-shrink-0 shadow-sm">
                      <CalendarDays className="w-5 h-5 md:w-6 md:h-6 text-white" />
                    </div>
                    <div className="flex-1 min-w-0">
                      <h4 className="font-semibold md:font-bold text-gray-900 mb-0.5 md:mb-1 text-sm md:text-base leading-tight">Saved Multi-Day Trips</h4>
                      <p className="text-xs md:text-sm text-gray-600 leading-relaxed line-clamp-2 md:line-clamp-none">
                        View and edit saved multi-day trips
                      </p>
                  </div>
                    <svg className="w-4 h-4 md:w-5 md:h-5 text-indigo-600 mt-0.5 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2.5} d="M9 5l7 7-7 7" />
                    </svg>
              </div>
            </div>

            {/* Quick Quotes Option */}
                <div 
                  className="bg-gradient-to-br from-purple-50 via-purple-50 to-purple-100 rounded-xl md:rounded-lg p-3.5 md:p-5 cursor-pointer hover:shadow-md md:hover:shadow-lg transition-all duration-200 md:hover:scale-[1.02] border border-purple-200 md:border-2 md:border-transparent md:hover:border-purple-300 active:scale-95 md:active:scale-100"
                  onClick={() => navigateToView('quick-quotes')}
                >
                  <div className="flex items-start gap-2.5 md:gap-3">
                    <div className="bg-gradient-to-br from-purple-500 to-purple-600 rounded-lg p-2.5 md:p-3 flex-shrink-0 shadow-sm">
                      <Zap className="w-5 h-5 md:w-6 md:h-6 text-white" />
                    </div>
                    <div className="flex-1 min-w-0">
                      <h4 className="font-semibold md:font-bold text-gray-900 mb-0.5 md:mb-1 text-sm md:text-base leading-tight">Quick Quotes</h4>
                      <p className="text-xs md:text-sm text-gray-600 leading-relaxed line-clamp-2 md:line-clamp-none">
                        View your quick quote requests
                      </p>
                  </div>
                    <svg className="w-4 h-4 md:w-5 md:h-5 text-purple-600 mt-0.5 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2.5} d="M9 5l7 7-7 7" />
                    </svg>
              </div>
            </div>
          </div>
            ) : (
              /* Show Getting Started Options for new trip creation */
              <div className="space-y-2.5 md:space-y-3">
                <h3 className="text-base md:text-lg font-semibold md:font-bold text-gray-900">Plan a New Trip</h3>

            {/* Multi-Day Trip Option */}
            <div 
                  className="bg-gradient-to-br from-[#FAF9FC] via-[#FAF9FC] to-[#F5F3F7] rounded-xl md:rounded-lg p-3.5 md:p-5 cursor-pointer hover:shadow-md md:hover:shadow-lg transition-all duration-200 md:hover:scale-[1.02] border border-[#F0E9F8] md:border-2 md:border-transparent md:hover:border-[#E8E0F3] active:scale-95 md:active:scale-100"
              onClick={() => {
                    // Require authentication for multi-day trips
                if (!authState.user?.userId) {
                  setShowEmailAuthPrompt(true);
                  return;
                }
                    navigateToView('multi-day-trip');
              }}
            >
                  <div className="flex items-start gap-2.5 md:gap-3">
                    <div className="bg-gradient-to-br from-[#14b8a6] via-[#5C7FB8] to-[#663399] rounded-lg p-2.5 md:p-3 flex-shrink-0 shadow-sm">
                      <CalendarDays className="w-5 h-5 md:w-6 md:h-6 text-white" />
                </div>
                    <div className="flex-1 min-w-0">
                      <h4 className="font-semibold md:font-bold text-gray-900 mb-0.5 md:mb-1 text-sm md:text-base leading-tight">Plan a Multi-Day Trip</h4>
                      <p className="text-xs md:text-sm text-gray-600 leading-relaxed line-clamp-2 md:line-clamp-none">
                        Extended journey with multiple destinations
                  </p>
                </div>
                    <svg className="w-4 h-4 md:w-5 md:h-5 text-purple-600 mt-0.5 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2.5} d="M9 5l7 7-7 7" />
                </svg>
              </div>
            </div>

            {/* Day Trip Option */}
            <div 
                  className="bg-gradient-to-br from-purple-50 via-purple-50 to-purple-100 rounded-xl md:rounded-lg p-3.5 md:p-5 cursor-pointer hover:shadow-md md:hover:shadow-lg transition-all duration-200 md:hover:scale-[1.02] border border-purple-200 md:border-2 md:border-transparent md:hover:border-purple-300 active:scale-95 md:active:scale-100"
                  onClick={() => {
                // Require authentication for day trips
                    if (!authState.user?.userId) {
                      setShowEmailAuthPrompt(true);
                      return;
                    }
                navigateToView('day-trip');
                  }}
            >
                  <div className="flex items-start gap-2.5 md:gap-3">
                    <div className="bg-gradient-to-br from-purple-500 to-purple-600 rounded-lg p-2.5 md:p-3 flex-shrink-0 shadow-sm">
                      <Calendar className="w-5 h-5 md:w-6 md:h-6 text-white" />
                </div>
                    <div className="flex-1 min-w-0">
                      <h4 className="font-semibold md:font-bold text-gray-900 mb-0.5 md:mb-1 text-sm md:text-base leading-tight">Plan a Day Trip</h4>
                      <p className="text-xs md:text-sm text-gray-600 leading-relaxed line-clamp-2 md:line-clamp-none">
                        Explore multiple places in a day
                  </p>
                </div>
                    <svg className="w-4 h-4 md:w-5 md:h-5 text-[#663399] mt-0.5 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2.5} d="M9 5l7 7-7 7" />
                </svg>
              </div>
            </div>
              </div>
            )}
          </div>
        );

      case 'day-trip':
        return (
          <div className="space-y-2.5 md:space-y-4">
            {/* Trip Name Input */}
            <div className="bg-white rounded-lg md:rounded-xl border border-gray-200 md:border-2 p-3 md:p-4">
              <label className="block text-xs md:text-sm font-semibold text-gray-900 mb-1.5 md:mb-2">
                Trip Name
              </label>
              <input
                type="text"
                value={dayTripName}
                onChange={(e) => setDayTripName(e.target.value)}
                placeholder="e.g., Weekend Getaway"
                className="w-full px-3 md:px-4 py-1.5 md:py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent text-gray-600"
                style={{ fontSize: '16px' }}
              />
            </div>

            {/* Selected Location Display */}
            {startLocation && (
              <div className="bg-green-50 border border-green-200 md:border-2 md:border-green-200 rounded-lg md:rounded-lg p-2.5 md:p-4">
                <div className="flex items-center justify-between gap-2">
                  <div className="flex items-center gap-2 min-w-0 flex-shrink">
                    <MapPin className="w-4 h-4 md:w-5 md:h-5 text-[#663399] flex-shrink-0" />
                    <div className="min-w-0">
                      <p className="text-xs md:text-xs font-medium text-gray-900">Starting Point</p>
                      <p className="text-xs md:text-xs text-gray-600 truncate">
                        {startLocation.name === 'Selected Location' 
                          ? `${startLocation.coordinates[1].toFixed(4)}, ${startLocation.coordinates[0].toFixed(4)}`
                          : startLocation.name
                        }
                      </p>
                    </div>
                  </div>
                  <div className="flex items-center gap-1 md:gap-2 flex-shrink-0">
                    <button
                      onClick={handleClearLocation}
                      className="bg-red-50 hover:bg-red-100 text-red-600 hover:text-red-700 px-2 md:px-3 py-1 md:py-2 rounded-lg md:rounded-lg text-xs md:text-sm font-medium transition-colors whitespace-nowrap active:scale-95"
                    >
                      Reset
                    </button>
                  </div>
                </div>
              </div>
            )}

            {/* Option 1: Current Location */}
            <button
              onClick={handleUseCurrentLocation}
              disabled={locationLoading}
              className="w-full bg-blue-50 md:bg-gradient-to-br md:from-blue-50 md:to-blue-100 rounded-lg md:rounded-lg p-3 md:p-4 cursor-pointer md:hover:shadow-md hover:shadow-sm transition-all border border-blue-200 md:border-2 md:border-transparent md:hover:border-blue-300 disabled:opacity-50 disabled:cursor-not-allowed text-left active:scale-95"
            >
              <div className="flex items-center gap-2.5 md:gap-3">
                <div className="bg-blue-500 md:bg-gradient-to-br md:from-blue-500 md:to-blue-600 rounded-lg p-2 md:p-3 flex-shrink-0 shadow-sm">
                  <Navigation className="w-4 h-4 md:w-5 md:h-5 text-white" />
                </div>
                <div className="flex-1 min-w-0">
                  <h4 className="font-medium md:font-semibold text-gray-900 text-xs md:text-sm leading-tight">
                    {locationLoading ? 'Getting location...' : 'Use Current Location'}
                  </h4>
                  <p className="text-xs text-gray-600 hidden md:block">
                    Start from where you are
                  </p>
                </div>
              </div>
            </button>

            {/* Option 2: Quick Locations */}
            <div className="bg-purple-50 md:bg-gradient-to-br md:from-purple-50 md:to-purple-100 rounded-lg md:rounded-lg p-3 md:p-4 border border-purple-200 md:border-2 md:border-transparent">
              <div className="flex items-center gap-2.5 md:gap-3 mb-2.5 md:mb-3">
                <div className="bg-purple-500 md:bg-gradient-to-br md:from-purple-500 md:to-purple-600 rounded-lg p-2 md:p-3 flex-shrink-0 shadow-sm">
                  <ChevronDown className="w-4 h-4 md:w-5 md:h-5 text-white" />
                </div>
                <div className="flex-1 min-w-0">
                  <h4 className="font-medium md:font-semibold text-gray-900 text-xs md:text-sm leading-tight">Quick Locations</h4>
                  <p className="text-xs text-gray-600 hidden md:block">
                    Choose popular starting points
                  </p>
                </div>
              </div>
              
              {/* Quick Location Buttons */}
              <div className="grid grid-cols-2 gap-1.5 md:gap-2">
                {QUICK_LOCATIONS.map((location) => (
                  <button
                    key={location.name}
                    onClick={() => handleQuickLocation(location)}
                    className={`px-2 py-1 md:px-3 md:py-2 rounded-lg text-xs md:text-sm font-medium transition-all active:scale-95 ${
                      startLocation?.name === location.name
                        ? 'bg-purple-600 text-white shadow-sm'
                        : 'bg-white text-gray-700 hover:bg-purple-100 border border-purple-200'
                    }`}
                  >
                    {location.name}
                  </button>
                ))}
              </div>
            </div>

            {/* Option 3: Select on Map */}
            <button
              onClick={handleSelectOnMap}
              className={`w-full bg-[#F0E9F8] md:bg-gradient-to-br md:from-[#F0E9F8] md:to-[#E0D7FA] rounded-lg md:rounded-lg p-3 md:p-4 cursor-pointer md:hover:shadow-md hover:shadow-sm transition-all border text-left active:scale-95 ${
                isMapSelectionMode 
                  ? 'border-orange-500 md:ring-2 md:ring-orange-200' 
                  : 'border-orange-200 md:border-2 md:border-transparent md:hover:border-orange-300'
              }`}
            >
              <div className="flex items-center gap-2.5 md:gap-3">
                <div className="bg-[#663399] md:bg-gradient-to-br md:from-[#663399] md:via-[#6B5BA8] md:to-[#5A4A97] rounded-lg p-2 md:p-3 flex-shrink-0 shadow-sm">
                  <MapPin className="w-4 h-4 md:w-5 md:h-5 text-white" />
                </div>
                <div className="flex-1 min-w-0">
                  <h4 className="font-medium md:font-semibold text-gray-900 text-xs md:text-sm leading-tight">
                    {isMapSelectionMode ? 'Click map to select...' : 'Select on Map'}
                  </h4>
                  <p className="text-xs text-gray-600">
                    {isMapSelectionMode 
                      ? 'Tap anywhere on the map'
                      : 'Choose on map'
                    }
                  </p>
                </div>
              </div>
            </button>

            {/* Continue Button - Desktop Only */}
            {startLocation && (
              <button
                onClick={handleContinueToDestinations}
                className="hidden md:block w-full bg-gradient-to-r from-[#9A79BA] to-[#8A6BA4] hover:from-[#8A6BA4] hover:to-[#7A5B94] text-white font-semibold py-3 rounded-xl transition-all shadow-lg hover:shadow-xl text-base"
              >
                Continue to Destinations
              </button>
            )}

          </div>
        );

      case 'day-trip-destinations':
        const allTypes: PlaceType[] = ['attraction', 'food', 'experience', 'hotel'];
        
        return (
          <div className="space-y-3 md:space-y-4">
            {/* Filters */}
            <div className="space-y-2 md:space-y-3">
              {/* Type Filter */}
              <div>
                {/* Mobile: Horizontal Layout */}
                <div className="flex items-center gap-2 md:hidden">
                  <div className="flex items-center gap-1 text-xs font-medium text-gray-700 flex-shrink-0">
                    <Filter className="w-3.5 h-3.5" />
                    <span>Type</span>
                  </div>
                  <div className="flex-1 overflow-x-auto scrollbar-hide">
                    <div className="flex gap-1.5 py-0.5 px-0.5">
                      {allTypes.map((type) => {
                        const config = getPlaceTypeConfig(type);
                        const isSelected = selectedTypes.includes(type);
                        return (
                          <button
                            key={type}
                            onClick={() => toggleTypeFilter(type)}
                            className={`px-2.5 py-1.5 rounded text-xs font-medium transition-all whitespace-nowrap flex-shrink-0 ${
                              isSelected
                                ? `${config.bgColor} ${config.color} ring-1 ring-current`
                                : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
                            }`}
                          >
                            <span className="font-bold mr-1">{config.code}</span>
                            {config.label}
                          </button>
                        );
                      })}
                    </div>
                  </div>
                </div>

                {/* Desktop: Vertical Layout */}
                <div className="hidden md:block">
                  <label className="text-sm font-medium text-gray-700 mb-2 flex items-center">
                    <Filter className="w-4 h-4 mr-1.5" />
                    Type
                  </label>
                  <div className="flex flex-wrap gap-2">
                    {allTypes.map((type) => {
                      const config = getPlaceTypeConfig(type);
                      const isSelected = selectedTypes.includes(type);
                      return (
                        <button
                          key={type}
                          onClick={() => toggleTypeFilter(type)}
                          className={`px-3 py-2 rounded-lg text-sm font-medium transition-all ${
                            isSelected
                              ? `${config.bgColor} ${config.color} ring-1 ring-current`
                              : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
                          }`}
                        >
                          <span className="font-bold mr-1">{config.code}</span>
                          {config.label}
                        </button>
                      );
                    })}
                  </div>
                </div>
              </div>

              {/* Distance Filter */}
              <div>
                {/* Mobile: Horizontal Layout */}
                <div className="flex items-center gap-2 md:hidden">
                  <div className="text-xs font-medium text-gray-700 flex-shrink-0">
                    Distance within
                  </div>
                  <div className="flex gap-1.5">
                    {[10, 20, 30].map((distance) => (
                      <button
                        key={distance}
                        onClick={() => setMaxDistance(distance)}
                        className={`px-2.5 py-1.5 rounded text-xs font-medium transition-all whitespace-nowrap ${
                          maxDistance === distance
                            ? 'bg-blue-600 text-white'
                            : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
                        }`}
                      >
                        {distance}km
                      </button>
                    ))}
                  </div>
                </div>

                {/* Desktop: Vertical Layout */}
                <div className="hidden md:block">
                  <label className="text-sm font-medium text-gray-700 mb-2 block">
                    Distance: Within {maxDistance}km
                  </label>
                  <div className="flex gap-2">
                    {[10, 20, 30].map((distance) => (
                      <button
                        key={distance}
                        onClick={() => setMaxDistance(distance)}
                        className={`flex-1 px-3 py-2 rounded-lg text-sm font-medium transition-all ${
                          maxDistance === distance
                            ? 'bg-blue-600 text-white'
                            : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
                        }`}
                      >
                        {distance}km
                      </button>
                    ))}
                  </div>
                </div>
              </div>

              {/* Saved Only Toggle */}
              <div>
                <button
                  onClick={() => {
                    if (!authState.user) return; // Require authentication
                    setShowOnlySaved(!showOnlySaved);
                  }}
                  disabled={!authState.user}
                  className={`w-full px-3 py-2 md:py-2.5 rounded md:rounded-lg text-xs md:text-sm font-medium transition-all flex items-center justify-center ${
                    showOnlySaved
                      ? 'bg-yellow-100 text-yellow-800 ring-1 ring-yellow-300'
                      : authState.user
                      ? 'bg-gray-100 text-gray-600 hover:bg-gray-200'
                      : 'bg-gray-100 text-gray-400 cursor-not-allowed opacity-50'
                  }`}
                  title={!authState.user ? 'Login required to filter by saved places' : ''}
                >
                  <Bookmark className={`w-3.5 h-3.5 md:w-4 md:h-4 mr-1.5 ${showOnlySaved ? 'fill-current' : ''}`} />
                  {showOnlySaved ? 'Showing Saved Only' : authState.user ? 'Show Saved Only' : 'Show Saved Only (Login Required)'}
                </button>
              </div>
            </div>

            {/* View Mode Toggle */}
            <div className="flex items-center justify-between pb-2 border-b border-gray-200">
              <p className="text-xs md:text-sm font-medium text-gray-700">
                {nearbyPlaces.length} {nearbyPlaces.length === 1 ? 'place' : 'places'} found
              </p>
              <div className="flex gap-1">
                <button
                  onClick={() => setViewMode('list')}
                  className={`p-1.5 md:p-2 rounded transition-all ${
                    viewMode === 'list'
                      ? 'bg-blue-600 text-white'
                      : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
                  }`}
                  title="List view"
                >
                  <List className="w-4 h-4" />
                </button>
                <button
                  onClick={() => setViewMode('grid')}
                  className={`p-1.5 md:p-2 rounded transition-all ${
                    viewMode === 'grid'
                      ? 'bg-blue-600 text-white'
                      : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
                  }`}
                  title="Grid view"
                >
                  <Grid3x3 className="w-4 h-4" />
                </button>
              </div>
            </div>

            {/* Places List/Grid */}
            <div className={viewMode === 'grid' ? 'grid grid-cols-3 gap-2 md:gap-3' : 'space-y-2 md:space-y-3'}>
              {placesLoading ? (
                <div className={`text-center py-8 ${viewMode === 'grid' ? 'col-span-3' : ''}`}>
                  <div className="inline-block animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600 mb-2"></div>
                  <p className="text-xs md:text-sm text-gray-600">Loading places...</p>
                </div>
              ) : nearbyPlaces.length === 0 ? (
                <div className={`text-center py-8 bg-gray-50 rounded-md md:rounded-lg ${viewMode === 'grid' ? 'col-span-3' : ''}`}>
                  <p className="text-xs md:text-sm text-gray-600">
                    No places found nearby. Try adjusting your filters.
                  </p>
                </div>
              ) : viewMode === 'list' ? (
                // List View
                nearbyPlaces.map((place, index) => {
                  const typeConfig = getPlaceTypeConfig(place.type as PlaceType);
                  const inTrip = isPlaceInTrip(place.id);

                  return (
                    <div
                      key={place.id}
                      className="bg-white border border-gray-200 rounded md:rounded-lg p-2 md:p-3 hover:shadow-md transition-all"
                    >
                      <div className="flex items-center gap-2 md:gap-3">
                        {/* Place Image */}
                        <div 
                          className="w-14 h-14 md:w-16 md:h-16 rounded md:rounded-lg overflow-hidden flex-shrink-0 bg-gray-100 cursor-pointer"
                          onClick={() => {
                            setActiveCardIndex(index);
                            setSelectedPlaceForDetail(place);
                          }}
                        >
                          {place.imageUrl || place.photo_1_cdn ? (
                            <img
                              src={(() => {
                                const variants = getImageVariants(place.photo_1_cdn || place.imageUrl);
                                return variants.tiny || variants.thumb || variants.original || place.imageUrl || place.photo_1_cdn;
                              })()}
                              alt={place.name}
                              className="w-full h-full object-cover"
                            />
                          ) : (
                            <div className="w-full h-full flex items-center justify-center">
                              <MapPin className="w-6 h-6 text-gray-400" />
                            </div>
                          )}
                        </div>

                        {/* Place Info */}
                        <div 
                          className="flex-1 min-w-0 cursor-pointer"
                          onClick={() => {
                            setActiveCardIndex(index);
                            setSelectedPlaceForDetail(place);
                          }}
                        >
                            <h4 className="font-semibold text-sm md:text-base text-gray-900 truncate">
                              {place.name}
                            </h4>
                          <p className="text-xs text-gray-500 mt-0.5">
                            {formatDistance(place.distance)} away
                          </p>
                        </div>

                        {/* Type Badge */}
                            <span
                              className={`${typeConfig.bgColor} ${typeConfig.color} px-1.5 py-0.5 rounded text-xs font-bold flex-shrink-0`}
                            >
                              {typeConfig.code}
                            </span>

                        {/* Add to Trip Button */}
                        <button
                          onClick={(e) => {
                            e.stopPropagation();
                            toggleTripPlace(place, currentPrimaryLocationIndex);
                          }}
                          className={`flex-shrink-0 p-2 rounded-lg transition-all ${
                            inTrip
                              ? 'bg-green-500 text-white'
                              : 'bg-blue-500 text-white hover:bg-blue-600'
                          }`}
                          title={inTrip ? 'Remove from trip' : 'Add to trip'}
                        >
                          {inTrip ? (
                            <Check className="w-4 h-4 md:w-5 md:h-5" />
                          ) : (
                            <Plus className="w-4 h-4 md:w-5 md:h-5" />
                          )}
                        </button>
                      </div>
                    </div>
                  );
                })
              ) : (
                // Grid View (3 columns)
                nearbyPlaces.map((place, index) => {
                  const typeConfig = getPlaceTypeConfig(place.type as PlaceType);
                  const variants = getImageVariants(place.photo_1_cdn || place.imageUrl);
                  const imageUrl = variants.tiny || variants.thumb || variants.original || place.imageUrl || place.photo_1_cdn;
                  const inTrip = isPlaceInTrip(place.id);

                  return (
                    <div
                      key={place.id}
                      className="bg-white border border-gray-200 rounded md:rounded-lg overflow-hidden hover:shadow-md transition-all group relative cursor-pointer"
                      onMouseEnter={() => setActiveCardIndex(index)}
                    >
                      {/* Image */}
                      <div className="relative aspect-square bg-gray-100">
                        {imageUrl ? (
                          <img
                            src={imageUrl}
                            alt={place.name}
                            className="w-full h-full object-cover"
                            loading="lazy"
                          />
                        ) : (
                          <div className="w-full h-full flex items-center justify-center">
                            <MapPin className="w-8 h-8 text-gray-400" />
                          </div>
                        )}
                        {/* Type Badge */}
                        <div className={`absolute top-1 right-1 ${typeConfig.bgColor} ${typeConfig.color} px-1.5 py-0.5 rounded text-[10px] font-bold`}>
                          {typeConfig.code}
                        </div>
                        
                        {/* Hover Overlay with Actions */}
                        <div className="absolute inset-0 bg-black bg-opacity-0 group-hover:bg-opacity-60 transition-all duration-200 flex items-center justify-center gap-2 opacity-0 group-hover:opacity-100">
                          <button
                            onClick={(e) => {
                              e.stopPropagation();
                              setSelectedPlaceForDetail(place);
                            }}
                            className="bg-white text-gray-900 px-3 py-1.5 rounded-lg text-xs font-medium hover:bg-gray-100 transition-colors flex items-center gap-1"
                          >
                            <Eye className="w-3 h-3" />
                            <span className="hidden md:inline">View</span>
                          </button>
                          <button
                            onClick={(e) => {
                              e.stopPropagation();
                              toggleTripPlace(place, currentPrimaryLocationIndex);
                            }}
                            className={`px-3 py-1.5 rounded-lg text-xs font-medium transition-colors flex items-center gap-1 ${
                              inTrip
                                ? 'bg-green-500 text-white hover:bg-green-600'
                                : 'bg-blue-500 text-white hover:bg-blue-600'
                            }`}
                          >
                            {inTrip ? (
                              <>
                                <Check className="w-3 h-3" />
                                <span className="hidden md:inline">Added</span>
                              </>
                            ) : (
                              <>
                                <Plus className="w-3 h-3" />
                                <span className="hidden md:inline">Add</span>
                              </>
                            )}
                          </button>
                        </div>
                      </div>
                      
                      {/* Content */}
                      <div className="p-2">
                        <h4 className="font-semibold text-xs md:text-sm text-gray-900 line-clamp-2 mb-1">
                          {place.name}
                        </h4>
                        <p className="text-[10px] md:text-xs text-gray-500">
                          {formatDistance(place.distance)} away
                        </p>
                      </div>
                    </div>
                  );
                })
              )}
            </div>
          </div>
        );

      case 'my-day-trip':
        return (
          <div className="space-y-4">
            {tripPlaces.length === 0 ? (
              <div className="text-center py-12 bg-gray-50 rounded-lg">
                <p className="text-gray-600 mb-4">No places selected yet</p>
                <button
                  onClick={() => handleNavigation('day-trip-destinations')}
                  className="bg-blue-600 hover:bg-blue-700 text-white px-6 py-2 rounded-lg transition-colors"
                >
                  Add Places
                </button>
              </div>
            ) : (
              <>
                {/* Start Location */}
                {startLocation && (
                  <div className="bg-green-50 border border-green-200 rounded-lg p-2 md:p-3">
                    <div className="flex items-center gap-2">
                      <div className="w-6 h-6 md:w-8 md:h-8 bg-green-500 rounded-full flex items-center justify-center flex-shrink-0">
                        <Navigation className="w-3 h-3 md:w-4 md:h-4 text-white" />
                      </div>
                      <div className="flex-1">
                        <p className="text-[10px] md:text-xs text-[#663399] font-medium">Start Location</p>
                        <p className="text-xs md:text-sm font-semibold text-gray-900">{startLocation.name}</p>
                      </div>
                    </div>
                  </div>
                )}

                {/* View Mode Toggle */}
                <div className="flex items-center justify-between">
                  <h3 className="text-xs md:text-sm font-semibold text-gray-700">Your Route</h3>
                  <div className="flex gap-1 bg-gray-100 p-1 rounded-lg">
                    <button
                      onClick={() => setViewMode('list')}
                      className={`p-1.5 md:p-2 rounded transition-all ${
                        viewMode === 'list'
                          ? 'bg-blue-600 text-white'
                          : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
                      }`}
                      title="List view"
                    >
                      <List className="w-4 h-4" />
                    </button>
                    <button
                      onClick={() => setViewMode('grid')}
                      className={`p-1.5 md:p-2 rounded transition-all ${
                        viewMode === 'grid'
                          ? 'bg-blue-600 text-white'
                          : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
                      }`}
                      title="Grid view"
                    >
                      <Grid3x3 className="w-4 h-4" />
                    </button>
                  </div>
                </div>

                {/* Places List/Grid */}
                <div className={viewMode === 'grid' ? 'grid grid-cols-3 gap-2 md:gap-3' : 'space-y-2 md:space-y-3'}>
                  {viewMode === 'list' ? (
                    // List View
                    tripPlaces.map((place, index) => {
                      const typeConfig = getPlaceTypeConfig(place.type as PlaceType);

                      return (
                        <div
                          key={place.id}
                          className="bg-white border border-gray-200 rounded md:rounded-lg p-2 md:p-3 hover:shadow-md transition-all"
                        >
                          <div className="flex items-center gap-2 md:gap-3">
                            {/* Number Badge */}
                            <div className="w-6 h-6 md:w-8 md:h-8 bg-blue-500 rounded-full flex items-center justify-center flex-shrink-0">
                              <span className="text-white font-bold text-xs md:text-sm">{index + 1}</span>
                            </div>

                            {/* Type Badge */}
                            <span
                              className={`${typeConfig.bgColor} ${typeConfig.color} px-1 md:px-1.5 py-0.5 rounded text-[10px] md:text-xs font-bold flex-shrink-0`}
                            >
                              {typeConfig.code}
                            </span>

                            {/* Place Image */}
                            <div 
                              className="w-12 h-12 md:w-16 md:h-16 rounded md:rounded-lg overflow-hidden flex-shrink-0 bg-gray-100 cursor-pointer"
                              onClick={() => setSelectedPlaceForDetail(place)}
                            >
                              {place.imageUrl || place.photo_1_cdn ? (
                                <img
                                  src={(() => {
                                    const variants = getImageVariants(place.photo_1_cdn || place.imageUrl);
                                    return variants.tiny || variants.thumb || variants.original || place.imageUrl || place.photo_1_cdn;
                                  })()}
                                  alt={place.name}
                                  className="w-full h-full object-cover"
                                />
                              ) : (
                                <div className="w-full h-full flex items-center justify-center">
                                  <MapPin className="w-5 h-5 md:w-6 md:h-6 text-gray-400" />
                                </div>
                              )}
                            </div>

                            {/* Place Info */}
                            <div 
                              className="flex-1 min-w-0 cursor-pointer"
                              onClick={() => setSelectedPlaceForDetail(place)}
                            >
                                <h4 className="font-semibold text-xs md:text-base text-gray-900 truncate">
                                  {place.name}
                                </h4>
                              <p className="text-[10px] md:text-xs text-gray-500 mt-0.5">
                                {formatDistance(place.distance)} away
                              </p>
                            </div>

                            {/* Move Up/Down Buttons */}
                            {!isTripFinalized && (
                              <>
                                <div className="flex flex-col gap-1 flex-shrink-0">
                                  <button
                                    onClick={(e) => {
                                      e.stopPropagation();
                                      movePlace(index, 'up');
                                    }}
                                    disabled={index === 0}
                                    className={`p-1 rounded transition-all ${
                                      index === 0
                                        ? 'bg-gray-100 text-gray-300 cursor-not-allowed'
                                        : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
                                    }`}
                                    title="Move up"
                                  >
                                    <MoveUp className="w-4 h-4" />
                                  </button>
                                  <button
                                    onClick={(e) => {
                                      e.stopPropagation();
                                      movePlace(index, 'down');
                                    }}
                                    disabled={index === tripPlaces.length - 1}
                                    className={`p-1 rounded transition-all ${
                                      index === tripPlaces.length - 1
                                        ? 'bg-gray-100 text-gray-300 cursor-not-allowed'
                                        : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
                                    }`}
                                    title="Move down"
                                  >
                                    <MoveDown className="w-4 h-4" />
                                  </button>
                                </div>

                                {/* Remove Button */}
                                <button
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    removeFromTrip(place.id);
                                  }}
                                  className="flex-shrink-0 p-2 rounded-lg transition-all bg-red-100 text-red-600 hover:bg-red-200"
                                  title="Remove from trip"
                                >
                                  <X className="w-4 h-4 md:w-5 md:h-5" />
                                </button>
                              </>
                            )}
                          </div>
                        </div>
                      );
                    })
                  ) : (
                    // Grid View
                    tripPlaces.map((place, index) => {
                      const typeConfig = getPlaceTypeConfig(place.type as PlaceType);
                      const variants = getImageVariants(place.photo_1_cdn || place.imageUrl);
                      const imageUrl = variants.tiny || variants.thumb || variants.original || place.imageUrl || place.photo_1_cdn;

                      return (
                        <div
                          key={place.id}
                          className="bg-white border border-gray-200 rounded md:rounded-lg overflow-hidden hover:shadow-md transition-all group relative"
                        >
                          {/* Image */}
                          <div className="relative aspect-square bg-gray-100">
                            {imageUrl ? (
                              <img
                                src={imageUrl}
                                alt={place.name}
                                className="w-full h-full object-cover"
                                loading="lazy"
                              />
                            ) : (
                              <div className="w-full h-full flex items-center justify-center">
                                <MapPin className="w-8 h-8 text-gray-400" />
                              </div>
                            )}
                            
                            {/* Number Badge */}
                            <div className="absolute top-1 left-1 w-6 h-6 bg-blue-500 rounded-full flex items-center justify-center">
                              <span className="text-white font-bold text-xs">{index + 1}</span>
                            </div>
                            
                            {/* Type Badge */}
                            <div className={`absolute top-1 right-1 ${typeConfig.bgColor} ${typeConfig.color} px-1.5 py-0.5 rounded text-[10px] font-bold`}>
                              {typeConfig.code}
                            </div>
                            
                            {/* Hover Overlay with Actions */}
                            <div className="absolute inset-0 bg-black bg-opacity-0 group-hover:bg-opacity-60 transition-all duration-200 flex flex-col items-center justify-center gap-2 opacity-0 group-hover:opacity-100">
                              {!isTripFinalized ? (
                                <>
                                  {/* Move Left/Right Buttons */}
                                  <div className="flex gap-2">
                                    <button
                                      onClick={(e) => {
                                        e.stopPropagation();
                                        movePlace(index, 'left');
                                      }}
                                      disabled={index === 0}
                                      className={`p-2 rounded-lg text-xs font-medium transition-colors ${
                                        index === 0
                                          ? 'bg-gray-400 text-gray-200 cursor-not-allowed'
                                          : 'bg-white text-gray-900 hover:bg-gray-100'
                                      }`}
                                      title="Move left"
                                    >
                                      <MoveLeft className="w-3 h-3" />
                                    </button>
                                    <button
                                      onClick={(e) => {
                                        e.stopPropagation();
                                        movePlace(index, 'right');
                                      }}
                                      disabled={index === tripPlaces.length - 1}
                                      className={`p-2 rounded-lg text-xs font-medium transition-colors ${
                                        index === tripPlaces.length - 1
                                          ? 'bg-gray-400 text-gray-200 cursor-not-allowed'
                                          : 'bg-white text-gray-900 hover:bg-gray-100'
                                      }`}
                                      title="Move right"
                                    >
                                      <MoveRight className="w-3 h-3" />
                                    </button>
                                  </div>
                                  
                                  {/* View and Remove Buttons */}
                                  <div className="flex gap-2">
                                    <button
                                      onClick={(e) => {
                                        e.stopPropagation();
                                        setSelectedPlaceForDetail(place);
                                      }}
                                      className="bg-white text-gray-900 px-2 py-1 rounded-lg text-xs font-medium hover:bg-gray-100 transition-colors flex items-center gap-1"
                                    >
                                      <Eye className="w-3 h-3" />
                                    </button>
                                    <button
                                      onClick={(e) => {
                                        e.stopPropagation();
                                        removeFromTrip(place.id);
                                      }}
                                      className="bg-red-500 text-white hover:bg-red-600 px-2 py-1 rounded-lg text-xs font-medium transition-colors flex items-center gap-1"
                                    >
                                      <X className="w-3 h-3" />
                                    </button>
                                  </div>
                                </>
                              ) : (
                                // When finalized, only show View button
                                <button
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    setSelectedPlaceForDetail(place);
                                  }}
                                  className="bg-white text-gray-900 px-3 py-2 rounded-lg text-sm font-medium hover:bg-gray-100 transition-colors flex items-center gap-1"
                                >
                                  <Eye className="w-4 h-4" />
                                  <span>View Details</span>
                                </button>
                              )}
                            </div>
                          </div>
                          
                          {/* Content */}
                          <div className="p-2">
                            <h4 className="font-semibold text-xs md:text-sm text-gray-900 line-clamp-2 mb-1">
                              {place.name}
                            </h4>
                            <p className="text-[10px] md:text-xs text-gray-500">
                              {formatDistance(place.distance)} away
                            </p>
                          </div>
                        </div>
                      );
                    })
                  )}
                </div>

                {/* Action Buttons */}
                <div className="flex gap-2 pt-3 md:pt-4 border-t border-gray-200">
                  {/* Add More Places */}
                  <button
                    onClick={() => {
                      if (isTripFinalized) {
                        // Reopen for editing (keep AI review visible)
                        setIsTripFinalized(false);
                      } else {
                        setCurrentView('day-trip-destinations');
                      }
                    }}
                    className="flex-1 bg-gray-100 hover:bg-gray-200 text-gray-700 font-semibold px-2 md:px-4 py-1.5 md:py-2.5 rounded-lg transition-colors text-xs md:text-sm"
                  >
                    {isTripFinalized ? 'Reopen for Editing' : 'Add More Places'}
                  </button>
                  
                  {/* Save Trip - NEW */}
                  <button
                    onClick={() => {
                      if (!authState.user?.userId) {
                        setShowEmailAuthPrompt(true);
                        return;
                      }
                      handleSaveDayTrip();
                    }}
                    className="flex-1 bg-blue-600 hover:bg-blue-700 text-white font-semibold px-2 md:px-4 py-1.5 md:py-2.5 rounded-lg transition-colors text-xs md:text-sm"
                  >
                    {currentDayTripId ? 'Update Trip' : 'Save Trip'}
                  </button>
                  
                  {/* AI Review - Modified visibility */}
                  {authState.user?.userId && (
                    <button
                      onClick={() => {
                        if (!authState.user?.whatsappNumberE164 && !authState.user?.telegramNumberE164) {
                          setShowWhatsAppVerifyPrompt(true);
                          return;
                        }
                        generateAIReview();
                      }}
                    disabled={isGeneratingReview || isTripFinalized}
                    className="flex-1 bg-blue-600 hover:bg-blue-700 disabled:bg-gray-400 disabled:cursor-not-allowed text-white font-semibold px-2 md:px-4 py-1.5 md:py-2.5 rounded-lg transition-colors text-xs md:text-sm"
                  >
                    {isGeneratingReview ? 'Generating Review...' : (isTripFinalized && aiReview ? '✓ Review Complete' : 'AI Review')}
                  </button>
                  )}
                </div>

                {/* AI Review Display */}
                {aiReview && (
                  <div ref={aiReviewRef} className="mt-4 p-4 bg-blue-50 border border-blue-200 rounded-lg">
                    <h3 className="font-bold text-lg mb-3 text-blue-900">AI Trip Review</h3>
                    <div className="space-y-3 text-sm text-gray-800 whitespace-pre-line">
                      {aiReview.split('\n\n').map((section, index) => {
                        // Check if this is a section header
                        if (section.startsWith('**') && section.includes('**:')) {
                          const [header, ...contentParts] = section.split(':');
                          const headerText = header.replace(/\*\*/g, '').trim();
                          const content = contentParts.join(':').trim();
                          
                          return (
                            <div key={index} className="space-y-1">
                              <h4 className="font-semibold text-blue-900">{headerText}</h4>
                              <p className="text-gray-700 leading-relaxed">{content}</p>
                            </div>
                          );
                        }
                        // Regular paragraph
                        return section.trim() && (
                          <p key={index} className="text-gray-700 leading-relaxed">{section}</p>
                        );
                      })}
                    </div>
                  </div>
                )}
              </>
            )}
          </div>
        );

      case 'multi-day-trip':
        return (
          <div className="space-y-2.5 md:space-y-4">
            {/* Recommended Trips Option */}
            <div 
              className="bg-gradient-to-br from-purple-50 via-pink-50 to-purple-100 rounded-xl md:rounded-lg p-3.5 md:p-6 cursor-pointer hover:shadow-md md:hover:shadow-lg transition-all duration-200 md:hover:scale-[1.02] border border-purple-200 md:border-2 md:border-transparent md:hover:border-purple-300 active:scale-95 md:active:scale-100"
              onClick={() => {
                showToast('Coming soon! 🎉', 'info', 3000);
              }}
            >
              <div className="flex items-start gap-2.5 md:gap-4">
                <div className="bg-gradient-to-br from-purple-500 to-pink-500 rounded-lg md:rounded-xl p-2.5 md:p-3 flex-shrink-0 shadow-sm">
                  <Sparkles className="w-5 h-5 md:w-7 md:h-7 text-white" />
                </div>
                <div className="flex-1">
                  <h4 className="font-semibold md:font-bold text-gray-900 text-sm md:text-lg mb-1 md:mb-2 leading-tight">Try Our Picks</h4>
                  <p className="text-xs md:text-sm text-gray-600 leading-relaxed line-clamp-2 md:line-clamp-none">
                    Discover expertly crafted itineraries based on your dates and group size. Perfect for first-time visitors.
                  </p>
                  <div className="mt-2 md:mt-3 flex items-center gap-2">
                    <span className="text-[10px] md:text-xs font-medium text-purple-700 bg-purple-100 px-2 md:px-2.5 py-0.5 md:py-1 rounded-full">
                      Recommended
                    </span>
                    <span className="text-[10px] md:text-xs text-gray-500">
                      Curated by locals
                    </span>
                  </div>
                </div>
              </div>
            </div>
            
            {/* Custom Trip Option */}
            <div 
              className="bg-gradient-to-br from-blue-50 via-cyan-50 to-blue-100 rounded-xl md:rounded-lg p-3.5 md:p-6 cursor-pointer hover:shadow-md md:hover:shadow-lg transition-all duration-200 md:hover:scale-[1.02] border border-blue-200 md:border-2 md:border-transparent md:hover:border-blue-300 active:scale-95 md:active:scale-100"
              onClick={() => handleNavigation('multi-day-trip-details')}
            >
              <div className="flex items-start gap-2.5 md:gap-4">
                <div className="bg-gradient-to-br from-blue-500 to-cyan-500 rounded-lg md:rounded-xl p-2.5 md:p-3 flex-shrink-0 shadow-sm">
                  <Route className="w-5 h-5 md:w-7 md:h-7 text-white" />
                </div>
                <div className="flex-1">
                  <h4 className="font-semibold md:font-bold text-gray-900 text-sm md:text-lg mb-1 md:mb-2 leading-tight">Build My Own</h4>
                  <p className="text-xs md:text-sm text-gray-600 leading-relaxed line-clamp-2 md:line-clamp-none">
                    Create a personalized itinerary from scratch. Choose your own destinations, activities, and pace.
                  </p>
                  <div className="mt-2 md:mt-3 flex items-center gap-2">
                    <span className="text-[10px] md:text-xs font-medium text-blue-700 bg-blue-100 px-2 md:px-2.5 py-0.5 md:py-1 rounded-full">
                      Flexible
                    </span>
                    <span className="text-[10px] md:text-xs text-gray-500">
                      Full control
                    </span>
                  </div>
                </div>
              </div>
            </div>
          </div>
        );
      
      case 'multi-day-trip-details':
        const today = new Date();
        today.setHours(0, 0, 0, 0);
        
        const isDateDisabled = (date: Date) => date < today;
        
        const isDateSelected = (date: Date) => {
          if (!multiDayStartDate) return false;
          if (!multiDayEndDate) return date.getTime() === multiDayStartDate.getTime();
          return date >= multiDayStartDate && date <= multiDayEndDate;
        };
        
        const isDateInRange = (date: Date) => {
          if (!multiDayStartDate || !multiDayEndDate) return false;
          return date > multiDayStartDate && date < multiDayEndDate;
        };
        
        const handleDateClick = (date: Date) => {
          if (isDateDisabled(date)) return;
          
          if (!multiDayStartDate || (multiDayStartDate && multiDayEndDate)) {
            setMultiDayStartDate(date);
            setMultiDayEndDate(null);
          } else if (multiDayStartDate && !multiDayEndDate) {
            if (date < multiDayStartDate) {
              setMultiDayStartDate(date);
              setMultiDayEndDate(multiDayStartDate);
            } else {
              setMultiDayEndDate(date);
            }
          }
        };
        
        const getDaysInMonth = (date: Date) => {
          const year = date.getFullYear();
          const month = date.getMonth();
          const firstDay = new Date(year, month, 1);
          const lastDay = new Date(year, month + 1, 0);
          const daysInMonth = lastDay.getDate();
          const startingDayOfWeek = firstDay.getDay();
          const days = [];
          
          for (let i = 0; i < startingDayOfWeek; i++) {
            days.push(null);
          }
          
          for (let day = 1; day <= daysInMonth; day++) {
            days.push(new Date(year, month, day));
          }
          
          return days;
        };
        
        const formatDateRange = () => {
          if (!multiDayStartDate) return 'Select dates';
          if (!multiDayEndDate) return multiDayStartDate.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
          const nights = Math.ceil((multiDayEndDate.getTime() - multiDayStartDate.getTime()) / (1000 * 60 * 60 * 24));
          return `${multiDayStartDate.toLocaleDateString('en-US', { month: 'short', day: 'numeric' })} - ${multiDayEndDate.toLocaleDateString('en-US', { month: 'short', day: 'numeric' })} • ${nights} ${nights === 1 ? 'night' : 'nights'}`;
        };
        
        const days = getDaysInMonth(currentMonth);
        const monthNames = ['January', 'February', 'March', 'April', 'May', 'June', 'July', 'August', 'September', 'October', 'November', 'December'];
        
        const totalTravelers = multiDayAdults + multiDayChildren + multiDayInfants;
        
        return (
          <div className="space-y-2.5 md:space-y-4">
            {/* Trip Name - Horizontal Layout */}
            <div className="flex items-center gap-2 md:gap-3 bg-white rounded-lg md:rounded-xl border border-gray-200 md:border-2 p-2.5 md:p-4">
              <label className="text-xs md:text-sm font-medium text-gray-700 whitespace-nowrap flex-shrink-0">
                Trip Name
              </label>
                <input
                  type="text"
                  value={multiDayTripName}
                  onChange={(e) => setMultiDayTripName(e.target.value)}
                placeholder="e.g., Sri Lanka Adventure"
                className="flex-1 px-2 md:px-3 py-1 md:py-1.5 border border-gray-300 rounded min-w-0 text-gray-600"
                style={{ fontSize: '16px' }}
                />
            </div>

            {/* When - Collapsible Date Picker */}
            <div className="bg-white rounded-lg md:rounded-xl border border-gray-200 md:border-2 p-2.5 md:p-4">
              <div 
                className="flex items-center justify-between cursor-pointer"
                onClick={() => setShowCalendarPicker(!showCalendarPicker)}
              >
                <div className="flex items-center gap-2">
                  <Calendar className="w-4 h-4 md:w-5 md:h-5 text-purple-600" />
                  <span className="text-xs md:text-sm font-medium text-gray-700">When</span>
                </div>
                <div className="flex items-center gap-2">
                  <span className="text-xs md:text-sm text-gray-600">{formatDateRange()}</span>
                  <ChevronDown className={`w-4 h-4 transition-transform ${showCalendarPicker ? 'rotate-180' : ''}`} />
              </div>
                </div>
                    </div>
            
            {/* Who - Collapsible Traveler Picker */}
            <div className="bg-white rounded-lg md:rounded-xl border border-gray-200 md:border-2 p-2.5 md:p-4">
              <div 
                className="flex items-center justify-between cursor-pointer"
                onClick={() => setShowTravelerPicker(!showTravelerPicker)}
              >
                <div className="flex items-center gap-2">
                  <Users className="w-4 h-4 md:w-5 md:h-5 text-blue-600" />
                  <span className="text-xs md:text-sm font-medium text-gray-700">Who</span>
                </div>
                <div className="flex items-center gap-2">
                  <span className="text-xs md:text-sm text-gray-600">
                  {totalTravelers} {totalTravelers === 1 ? 'traveler' : 'travelers'}
                  </span>
                  <ChevronDown className={`w-4 h-4 transition-transform ${showTravelerPicker ? 'rotate-180' : ''}`} />
              </div>
                  </div>
                </div>
                
            {/* Continue Button - Desktop Only */}
            {multiDayStartDate && multiDayEndDate && (
              <button
                onClick={() => {
                  // Validate trip name
                  if (!multiDayTripName.trim()) {
                    const roasts = [
                      "Hey! 👋 Your epic adventure needs an epic name first!",
                      "Slow down! 🐌 Can't plan a legendary trip without a legendary name!",
                      "Nope! ❌ Your trip name field is lonelier than a beach in monsoon season!",
                      "Wait up! ⏸️ Name your trip - future you will thank present you!",
                      "Stop right there! 🛑 Give this journey a name worthy of your Instagram stories!"
                    ];
                    const randomRoast = roasts[Math.floor(Math.random() * roasts.length)];
                    showToast(randomRoast, 'warning', 4000);
                    return;
                  }
                  handleNavigation('multi-day-trip-itinerary');
                }}
                className="hidden md:block w-full bg-gradient-to-r from-purple-600 to-pink-600 text-white font-semibold py-4 rounded-xl hover:from-purple-700 hover:to-pink-700 transition-all shadow-lg hover:shadow-xl text-base"
              >
                Continue
              </button>
            )}
          </div>
        );
      
      case 'multi-day-trip-itinerary':
        // Generate days array from date range
        const generateDays = () => {
          if (!multiDayStartDate || !multiDayEndDate) return [];
          
          const days = [];
          const currentDate = new Date(multiDayStartDate);
          
          while (currentDate <= multiDayEndDate) {
            days.push(new Date(currentDate));
            currentDate.setDate(currentDate.getDate() + 1);
          }
          
          return days;
        };
        
        const itineraryDays = generateDays();
        const totalTravelersItinerary = multiDayAdults + multiDayChildren + multiDayInfants;
        
        return (
          <div className="space-y-4">
            {/* Editable Trip Summary */}
            <div className="bg-gradient-to-br from-purple-50 to-pink-50 rounded-xl border-2 border-purple-200 p-2 md:p-4">
              {!isEditingTripDetails ? (
                <div>
                  {/* Mobile: Collapsed Thin Block */}
                  <div className="md:hidden flex items-center justify-between gap-2">
                    <div className="flex items-center gap-2 flex-1 min-w-0">
                      <Calendar className="w-3.5 h-3.5 text-purple-600 flex-shrink-0" />
                      <span className="text-xs font-medium text-gray-900 truncate">
                        {multiDayStartDate?.toLocaleDateString('en-US', { month: 'short', day: 'numeric' })} - {multiDayEndDate?.toLocaleDateString('en-US', { month: 'short', day: 'numeric' })}
                      </span>
                      <span className="text-xs text-gray-500 flex-shrink-0">• {itineraryDays.length}d</span>
                    </div>
                    <div className="flex items-center gap-1">
                      <button
                        onClick={() => setShowTripDetailsModal(true)}
                        className="text-purple-600 hover:text-purple-700 text-xs font-medium px-2 py-0.5 rounded hover:bg-purple-100 transition-colors"
                      >
                        View
                      </button>
                      <button
                        onClick={() => setIsEditingTripDetails(true)}
                        className="text-purple-600 hover:text-purple-700 text-xs font-medium px-2 py-0.5 rounded hover:bg-purple-100 transition-colors"
                      >
                        Edit
                      </button>
                    </div>
                  </div>

                  {/* Desktop: Full Display */}
                  <div className="hidden md:block">
                  <div className="flex items-start justify-between mb-3">
                    <div className="flex-1">
                      <div className="flex items-center gap-2 mb-2">
                        <Calendar className="w-4 h-4 text-purple-600" />
                        <p className="text-sm font-semibold text-gray-900">
                          {multiDayStartDate?.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })} - {multiDayEndDate?.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })}
                        </p>
                      </div>
                      <div className="flex items-center gap-2">
                        <Users className="w-4 h-4 text-purple-600" />
                        <p className="text-sm text-gray-700">
                          {multiDayAdults} {multiDayAdults === 1 ? 'Adult' : 'Adults'}
                          {multiDayChildren > 0 && `, ${multiDayChildren} ${multiDayChildren === 1 ? 'Child' : 'Children'}`}
                          {multiDayInfants > 0 && `, ${multiDayInfants} ${multiDayInfants === 1 ? 'Infant' : 'Infants'}`}
                        </p>
                      </div>
                    </div>
                    <button
                      onClick={() => setIsEditingTripDetails(true)}
                      className="text-purple-600 hover:text-purple-700 text-sm font-medium px-3 py-1.5 rounded-lg hover:bg-purple-100 transition-colors"
                    >
                      Edit
                    </button>
                  </div>
                  <div className="text-xs text-gray-500 flex items-center gap-1">
                    <span className="bg-purple-100 text-purple-700 px-2 py-0.5 rounded-full font-medium">
                      {itineraryDays.length} {itineraryDays.length === 1 ? 'day' : 'days'}
                    </span>
                    <span className="bg-blue-100 text-blue-700 px-2 py-0.5 rounded-full font-medium">
                      {totalTravelersItinerary} {totalTravelersItinerary === 1 ? 'traveler' : 'travelers'}
                    </span>
                    </div>
                  </div>
                </div>
              ) : (
                <div className="space-y-3">
                  <p className="text-sm font-semibold text-gray-900 mb-2">Edit Trip Details</p>
                  
                  {/* Quick date edit info */}
                  <div className="text-xs text-gray-600 bg-white rounded-lg p-2">
                    <p className="font-medium mb-1">Current dates:</p>
                    <p>{multiDayStartDate?.toLocaleDateString('en-US', { month: 'short', day: 'numeric' })} - {multiDayEndDate?.toLocaleDateString('en-US', { month: 'short', day: 'numeric' })}</p>
                  </div>
                  
                  {/* Travelers adjustment */}
                  <div className="space-y-2">
                    <div className="flex items-center justify-between bg-white rounded-lg p-2">
                      <span className="text-xs font-medium text-gray-700">Adults</span>
                      <div className="flex items-center gap-2">
                        <button
                          onClick={() => setMultiDayAdults(Math.max(1, multiDayAdults - 1))}
                          className="w-6 h-6 rounded-full border border-gray-300 flex items-center justify-center hover:border-purple-500 text-sm"
                        >
                          −
                        </button>
                        <span className="w-6 text-center text-sm font-medium">{multiDayAdults}</span>
                        <button
                          onClick={() => setMultiDayAdults(Math.min(10, multiDayAdults + 1))}
                          className="w-6 h-6 rounded-full border border-gray-300 flex items-center justify-center hover:border-purple-500 text-sm"
                        >
                          +
                        </button>
                      </div>
                    </div>
                    
                    <div className="flex items-center justify-between bg-white rounded-lg p-2">
                      <span className="text-xs font-medium text-gray-700">Children (2-12)</span>
                      <div className="flex items-center gap-2">
                        <button
                          onClick={() => setMultiDayChildren(Math.max(0, multiDayChildren - 1))}
                          className="w-6 h-6 rounded-full border border-gray-300 flex items-center justify-center hover:border-purple-500 text-sm"
                        >
                          −
                        </button>
                        <span className="w-6 text-center text-sm font-medium">{multiDayChildren}</span>
                        <button
                          onClick={() => setMultiDayChildren(Math.min(10, multiDayChildren + 1))}
                          className="w-6 h-6 rounded-full border border-gray-300 flex items-center justify-center hover:border-purple-500 text-sm"
                        >
                          +
                        </button>
                      </div>
                    </div>
                    
                    <div className="flex items-center justify-between bg-white rounded-lg p-2">
                      <span className="text-xs font-medium text-gray-700">Infants (Under 2)</span>
                      <div className="flex items-center gap-2">
                        <button
                          onClick={() => setMultiDayInfants(Math.max(0, multiDayInfants - 1))}
                          className="w-6 h-6 rounded-full border border-gray-300 flex items-center justify-center hover:border-purple-500 text-sm"
                        >
                          −
                        </button>
                        <span className="w-6 text-center text-sm font-medium">{multiDayInfants}</span>
                        <button
                          onClick={() => setMultiDayInfants(Math.min(5, multiDayInfants + 1))}
                          className="w-6 h-6 rounded-full border border-gray-300 flex items-center justify-center hover:border-purple-500 text-sm"
                        >
                          +
                        </button>
                      </div>
                    </div>
                  </div>
                  
                  <div className="flex gap-2 pt-2">
                    <button
                      onClick={() => setIsEditingTripDetails(false)}
                      className="flex-1 bg-purple-600 hover:bg-purple-700 text-white text-sm font-medium py-2 rounded-lg transition-colors"
                    >
                      Done
                    </button>
                    <button
                      onClick={() => {
                        setIsEditingTripDetails(false);
                        setCurrentView('multi-day-trip-details');
                      }}
                      className="px-4 bg-gray-100 hover:bg-gray-200 text-gray-700 text-sm font-medium py-2 rounded-lg transition-colors"
                    >
                      Change Dates
                    </button>
                  </div>
                </div>
              )}
            </div>
            
            {/* Holiday Warning */}
            {multiDayStartDate && multiDayEndDate && (() => {
              const holidays = getHolidaysInRange(multiDayStartDate, multiDayEndDate);
              if (holidays.length === 0) return null;
              
              return (
                <div className="bg-gradient-to-br from-orange-50 to-amber-50 rounded-xl border-2 border-orange-200 overflow-hidden">
                  {/* Header - Always visible */}
                  <button
                    onClick={() => setIsHolidayAlertExpanded(!isHolidayAlertExpanded)}
                    className="w-full p-3 md:p-4 flex items-center gap-3 hover:bg-orange-100/50 transition-colors"
                  >
                    <div className="flex-shrink-0 w-7 h-7 md:w-8 md:h-8 rounded-full bg-orange-500 flex items-center justify-center">
                      <Calendar className="w-3.5 h-3.5 md:w-4 md:h-4 text-white" />
                    </div>
                    <div className="flex-1 text-left min-w-0">
                      <h4 className="text-xs md:text-sm font-bold text-orange-900">
                        Holiday Alert {holidays.length > 1 && `(${holidays.length} holidays)`}
                      </h4>
                      <p className="text-[10px] md:text-xs text-orange-700 mt-0.5">
                        {holidays.map(h => h.name).join(', ')}
                      </p>
                    </div>
                    <ChevronDown className={`w-4 h-4 md:w-5 md:h-5 text-[#663399] flex-shrink-0 transition-transform ${
                      isHolidayAlertExpanded ? 'rotate-180' : ''
                    }`} />
                  </button>
                  
                  {/* Expanded Content - Desktop: always shown, Mobile: collapsible */}
                  <div className={`${
                    isHolidayAlertExpanded ? 'block' : 'hidden md:block'
                  } px-3 pb-3 md:px-4 md:pb-4 pt-0`}>
                    <div className="space-y-2">
                      {holidays.map((holiday, idx) => (
                        <div key={idx} className="bg-white rounded-lg p-2.5 md:p-3 border border-orange-200">
                          <div className="flex items-center gap-2 mb-1">
                            <span className="text-[10px] md:text-xs font-bold text-orange-700">
                              {new Date(holiday.date).toLocaleDateString('en-US', { month: 'short', day: 'numeric' })}
                            </span>
                            <span className="text-[10px] md:text-xs font-semibold text-gray-900">
                              {holiday.name}
                            </span>
                          </div>
                          <p className="text-[10px] md:text-xs text-gray-700 leading-relaxed">
                            ⚠️ {holiday.fareImpact}
                          </p>
                        </div>
                      ))}
                    </div>
                    <p className="text-[10px] md:text-xs text-orange-800 mt-2 md:mt-3 font-medium">
                      💡 Plan ahead: Some attractions may have modified hours or be closed. Transportation costs may be higher.
                    </p>
                  </div>
                </div>
              );
            })()}
            
            {/* Days List */}
            <div className="space-y-2 md:space-y-3">
              <div className="flex items-center justify-between">
                <h3 className="text-xs md:text-sm font-semibold text-gray-900">Your Trip</h3>
                <span className="text-[10px] md:text-xs text-gray-500">{itineraryDays.length} {itineraryDays.length === 1 ? 'day' : 'days'}</span>
              </div>
              
              {/* Start Location Block */}
              <div className="bg-white rounded-lg md:rounded-xl border-2 border-gray-200 p-2.5 md:p-4 relative">
                <div className="flex items-center gap-2 md:gap-3">
                  {/* Icon Column */}
                  <div className="flex flex-col items-center flex-shrink-0">
                    <div className="w-8 h-8 md:w-10 md:h-10 rounded-full bg-gradient-to-br from-[#9A79BA] to-[#8A6BA4] flex items-center justify-center text-white">
                      <MapPin className="w-4 h-4 md:w-5 md:h-5" />
                    </div>
                    <span className="text-[10px] md:text-xs text-gray-500 mt-0.5 md:mt-1">Start</span>
                  </div>
                  
                  {/* Content Column */}
                  <div className="flex-1 min-w-0 flex items-start justify-between gap-2">
                    <div className="flex-1 min-w-0">
                      <h4 className="font-semibold text-gray-900 text-xs md:text-sm">Starting Location</h4>
                      {multiDayStartLocation ? (
                        <p className="text-xs md:text-sm text-gray-600 truncate">{multiDayStartLocation.name}</p>
                      ) : (
                      <div className="flex flex-wrap gap-1 mt-1">
                        {/* Current Location */}
                        <button
                          onClick={async () => {
                            setLocationLoading(true);
                            if (navigator.geolocation) {
                              navigator.geolocation.getCurrentPosition(
                                (position) => {
                                  const coords: [number, number] = [position.coords.longitude, position.coords.latitude];
                                  setMultiDayStartLocation({ name: 'Current Location', coordinates: coords });
                                  setLocationLoading(false);
                                },
                                (error) => {
                                  setLocationLoading(false);
                                  alert('Could not get your location. Please try another option.');
                                }
                              );
                            }
                          }}
                          className="bg-blue-50 hover:bg-blue-100 border border-blue-200 rounded px-2 py-0.5 md:py-1 text-[10px] md:text-xs font-medium text-gray-700 transition-colors flex items-center gap-1"
                        >
                          <Navigation className="w-3 h-3 text-blue-600" />
                          <span>Current</span>
                        </button>
                        
                        {/* Quick Locations Dropdown */}
                        <div className="relative">
                          <button
                            onClick={() => setShowQuickLocationsDropdown(!showQuickLocationsDropdown)}
                            className="bg-purple-50 hover:bg-purple-100 border border-purple-200 rounded px-2 py-0.5 md:py-1 text-[10px] md:text-xs font-medium text-gray-700 transition-colors flex items-center gap-1"
                          >
                            <MapPin className="w-3 h-3 text-purple-600" />
                            <span>Quick Pick</span>
                            <ChevronDown className={`w-3 h-3 transition-transform ${showQuickLocationsDropdown ? 'rotate-180' : ''}`} />
                          </button>
                          
                          {/* Dropdown Menu */}
                          {showQuickLocationsDropdown && (
                            <div className="absolute top-full left-0 mt-1 bg-white rounded-lg shadow-lg border border-gray-200 z-10 min-w-[140px] max-h-48 overflow-y-auto">
                              {QUICK_LOCATIONS.map((location, idx) => (
                                <button
                                  key={idx}
                                  onClick={() => {
                                    setMultiDayStartLocation(location);
                                    setShowQuickLocationsDropdown(false);
                                  }}
                                  className="w-full text-left px-3 py-2 hover:bg-purple-50 text-[10px] md:text-xs text-gray-700 hover:text-purple-900 transition-colors border-b border-gray-100 last:border-b-0"
                                >
                                  {location.name}
                                </button>
                              ))}
                            </div>
                          )}
                        </div>
                        
                  {/* Pick in Map */}
                  <button
                    onClick={() => {
                      setIsSelectingStartLocation(true);
                      setShowQuickLocationsDropdown(false);
                      // Collapse drawer on mobile
                      if (!isDesktop) {
                        collapse();
                      }
                    }}
                    className="bg-orange-50 hover:bg-orange-100 border border-orange-200 rounded px-2 py-0.5 md:py-1 text-[10px] md:text-xs font-medium text-gray-700 transition-colors flex items-center gap-1"
                  >
                    <MapPin className="w-3 h-3 text-[#663399]" />
                    <span>On Map</span>
                  </button>
                      </div>
                    )}
                    </div>
                    
                    {/* Edit Button - appears when location is selected */}
                    {multiDayStartLocation && (
                      <button
                        onClick={() => {
                          // Check if any days have places
                          if (hasAnyDayWithPlaces()) {
                            // Show confirmation modal
                            setShowResetItineraryModal(true);
                            return;
                          }
                          // Allow editing if no places added
                          setMultiDayStartLocation(null);
                        }}
                        className="text-purple-600 hover:text-purple-700 text-[10px] md:text-xs font-medium px-2 py-0.5 md:py-1 rounded hover:bg-purple-50 transition-colors flex-shrink-0"
                      >
                        Edit
                      </button>
                    )}
                  </div>
                </div>
              </div>
              
              {itineraryDays.map((day, index) => {
                // Skip days that are absorbed into a multi-day block (but not the first day of the block)
                const isAbsorbedDay = Array.from(combinedDayBlocks.values()).some(blockIndices => 
                  blockIndices.includes(index) && blockIndices[0] !== index
                );
                if (isAbsorbedDay) {
                  return null;
                }
                
                const dayName = day.toLocaleDateString('en-US', { weekday: 'short' });
                const monthDay = day.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
                const isFirstDay = index === 0;
                const finalizedDay = finalizedDays.get(index);
                const isExpanded = expandedDays.has(index);
                
                // Check if this day is part of a combined block (whether finalized or not)
                const combinedBlock = combinedDayBlocks.get(index);
                const isMultiDayBlock = (finalizedDay?.isMultiDayBlock) || (combinedBlock && combinedBlock.length > 1);
                const blockIndices = (finalizedDay?.blockDayIndices) || combinedBlock || [index];
                
                // Check if this day/block contains the actual last day
                const actualLastDayIndex = itineraryDays.length - 1;
                const isLastDay = blockIndices.includes(actualLastDayIndex);
                
                return (
                  <div
                    key={index}
                    className={`bg-white rounded-lg md:rounded-xl p-2.5 md:p-4 relative transition-colors ${
                      selectedDayIndex === index
                        ? 'border-4 border-purple-600'
                        : 'border-2 border-gray-200 hover:border-purple-300'
                    }`}
                  >
                    <div className="flex items-start justify-between gap-2 md:gap-3">
                      <div className="flex items-start gap-2 md:gap-3 flex-1 min-w-0">
                        {/* Day Number Circle */}
                        <div className="flex flex-col items-center flex-shrink-0">
                          <div className="w-8 h-8 md:w-10 md:h-10 rounded-full bg-gradient-to-br from-purple-500 to-pink-500 flex items-center justify-center text-white font-bold text-xs md:text-sm">
                            {index + 1}
                          </div>
                          <span className="text-[10px] md:text-xs text-gray-500 mt-0.5 md:mt-1">{dayName}</span>
                        </div>
                        
                        {/* Day Content */}
                        <div className="flex-1 min-w-0">
                          {/* Header */}
                          <div className="flex items-center gap-2 mb-1">
                            <h4 className="font-semibold text-gray-900 text-xs md:text-sm">
                              {isMultiDayBlock ? (
                                <>Days {blockIndices[0] + 1}-{blockIndices[blockIndices.length - 1] + 1}</>
                              ) : (
                                <>Day {index + 1}</>
                              )}
                            </h4>
                            {isFirstDay && <span className="text-[10px] md:text-xs bg-green-100 text-green-700 px-1.5 md:px-2 py-0.5 rounded-full">Start</span>}
                            {isLastDay && <span className="text-[10px] md:text-xs bg-blue-100 text-blue-700 px-1.5 md:px-2 py-0.5 rounded-full">End</span>}
                          </div>
                          <div className="flex items-center gap-2">
                            <p className="text-xs md:text-sm text-gray-600 mb-1 md:mb-2">
                              {isMultiDayBlock ? (
                                <>
                                  {itineraryDays[blockIndices[0]].toLocaleDateString('en-US', { month: 'short', day: 'numeric' })} - 
                                  {itineraryDays[blockIndices[blockIndices.length - 1]].toLocaleDateString('en-US', { month: 'short', day: 'numeric' })}
                                  <span className="text-[10px] md:text-xs text-gray-500 ml-2">
                                    • {(() => {
                                      const nights = blockIndices.length - 1;
                                      const days = blockIndices.length;
                                      return `${nights} ${nights === 1 ? 'night' : 'nights'}, ${days} ${days === 1 ? 'day' : 'days'}`;
                                    })()}
                                  </span>
                                </>
                              ) : (
                                <>{monthDay}</>
                              )}
                            </p>
                          </div>
                          
                          {finalizedDay ? (
                            <>
                              {/* Collapsed View - Summary */}
                              {!isExpanded && (
                                <div className="space-y-1 md:space-y-2">
                                  {/* Place Type Summary */}
                                  <div className="flex items-center gap-2 flex-wrap">
                                    {(() => {
                                      const counts = categorizePlaces(finalizedDay.places);
                                      const summary: string[] = [];
                                      if (counts.attraction > 0) summary.push(`${counts.attraction} ${counts.attraction === 1 ? 'attraction' : 'attractions'}`);
                                      if (counts.experience > 0) summary.push(`${counts.experience} ${counts.experience === 1 ? 'experience' : 'experiences'}`);
                                      if (counts.hotel > 0) summary.push(`${counts.hotel} ${counts.hotel === 1 ? 'hotel' : 'hotels'}`);
                                      if (counts.food > 0) summary.push(`${counts.food} ${counts.food === 1 ? 'food place' : 'food places'}`);
                                      
                                      return summary.length > 0 ? (
                                        <p className="text-[10px] md:text-xs text-gray-700 font-medium">
                                          {summary.join(', ')}
                                        </p>
                                      ) : (
                                        <p className="text-[10px] md:text-xs text-gray-400">No places added</p>
                                      );
                                    })()}
                                  </div>
                                  
                                  {/* Transport Icons */}
                                  <div className="flex items-center gap-1">
                                    {Array.from(new Set(Array.from(finalizedDay.transportation.values()))).map((mode, idx) => (
                                      <span key={idx} className="inline-flex items-center justify-center w-5 h-5 md:w-6 md:h-6 rounded-full bg-blue-50 text-blue-600" title={mode}>
                                        {mode === 'car' && <Car className="w-3 h-3 md:w-3.5 md:h-3.5" />}
                                        {mode === 'bus' && <Bus className="w-3 h-3 md:w-3.5 md:h-3.5" />}
                                        {mode === 'train' && <TrainTrack className="w-3 h-3 md:w-3.5 md:h-3.5" />}
                                      </span>
                                    ))}
                                  </div>
                                </div>
                              )}
                              
                              {/* Expanded View - Place List (grouped by primary location) */}
                              {isExpanded && (
                                <div className="space-y-3 md:space-y-4 mt-2 md:mt-3">
                                  {(() => {
                                    // Group places by primaryLocationIndex
                                    const locationIndices = Array.from(new Set(finalizedDay.places.map(p => p.primaryLocationIndex || 0))).sort((a, b) => a - b);
                                    const primaryLocs = finalizedDay.primaryLocations || (finalizedDay.startLocation ? [finalizedDay.startLocation] : []);
                                    
                                    return locationIndices.map(locIndex => {
                                      const locationPlaces = finalizedDay.places.filter(p => (p.primaryLocationIndex || 0) === locIndex);
                                      const locationName = primaryLocs[locIndex]?.name || `Location ${locIndex + 1}`;
                                      
                                      return (
                                        <div key={locIndex}>
                                          {/* Location Header - only show if multiple locations */}
                                          {locationIndices.length > 1 && (
                                            <div className="flex items-center gap-1.5 mb-1.5">
                                              <MapPin className="w-3 h-3 text-purple-600" />
                                              <span className="text-[10px] md:text-xs font-semibold text-purple-700">{locationName}</span>
                                            </div>
                                          )}
                                          
                                          {/* Places for this location */}
                                          <div className="space-y-1.5 md:space-y-2">
                                            {locationPlaces.map((place, placeIdx) => {
                                              const variants = getImageVariants(place.photo_1_cdn || place.imageUrl || null);
                                              const imageUrl = variants.tiny || variants.thumb || variants.original || place.photo_1_cdn || place.imageUrl;
                                              const typeConfig = getPlaceTypeConfig(place.type as PlaceType);
                                              
                                              return (
                                                <div 
                                                  key={placeIdx}
                                                  onClick={() => setSelectedPlaceForDetail(place)}
                                                  className="flex items-center gap-2 md:gap-2.5 p-1.5 md:p-2 rounded-lg hover:bg-gray-50 transition-colors cursor-pointer"
                                                >
                                                  {/* Tiny Image */}
                                                  {imageUrl ? (
                                                    <img 
                                                      src={imageUrl} 
                                                      alt={place.name}
                                                      className="w-10 h-10 md:w-12 md:h-12 rounded object-cover flex-shrink-0"
                                                    />
                                                  ) : (
                                                    <div className="w-10 h-10 md:w-12 md:h-12 rounded bg-gray-100 flex-shrink-0" />
                                                  )}
                                                  
                                                  {/* Type Badge */}
                                                  <div className={`${typeConfig.bgColor} w-7 h-7 md:w-8 md:h-8 rounded flex items-center justify-center flex-shrink-0`}>
                                                    <span className={`text-xs md:text-sm font-bold ${typeConfig.color}`}>
                                                      {typeConfig.code}
                                                    </span>
                                                  </div>
                                                  
                                                  {/* Place Name */}
                                                  <p className="text-xs md:text-sm font-medium text-gray-900 flex-1 min-w-0 truncate">
                                                    {place.name}
                                                  </p>
                                                </div>
                                              );
                                            })}
                                          </div>
                                        </div>
                                      );
                                    });
                                  })()}
                                </div>
                              )}
                            </>
                          ) : (
                            <p className="text-[10px] md:text-xs text-gray-400">No activities yet</p>
                          )}
                          
                          {/* Trip End Section - Only for last day when trip is ended */}
                          {isLastDay && isTripEnded && tripEndLocation && (
                            <div className="mt-3 pt-3 border-t border-gray-200">
                              <div className="flex items-center justify-between mb-2">
                                <div className="flex items-center gap-2">
                                  <Flag className="w-4 h-4 text-green-600" />
                                  <span className="text-xs font-semibold text-gray-900">Trip Ends</span>
                                </div>
                                <button
                                  onClick={() => {
                                    setIsTripEnded(false);
                                    setTripEndLocation(null);
                                    setTripEndTransportMode('car');
                                    setTripEndRouteSegment(null);
                                    setTripEndTrainAvailable(false);
                                  }}
                                  className="text-gray-400 hover:text-red-600 transition-colors"
                                  title="Remove trip end"
                                >
                                  <X className="w-4 h-4" />
                                </button>
                              </div>
                              <p className="text-xs text-gray-600 mb-2">{tripEndLocation.name}</p>
                              
                              {/* Transport Mode Dropdown */}
                              <div className="flex items-center gap-2">
                                <div className="relative inline-block">
                                  <button
                                    onClick={() => setExpandedTransportIndex(expandedTransportIndex === -1 ? null : -1)}
                                    className="flex items-center gap-2 px-3 py-1.5 bg-gray-50 hover:bg-gray-100 rounded-lg transition-colors text-xs"
                                  >
                                    {getTransportIcon(tripEndTransportMode)}
                                    <span className="font-medium text-gray-700">{getTransportLabel(tripEndTransportMode)}</span>
                                    <ChevronDown className="w-3 h-3 text-gray-500" />
                                  </button>
                                
                                {expandedTransportIndex === -1 && (
                                  <div className="absolute top-full left-0 mt-1 bg-white rounded-lg shadow-lg border border-gray-200 z-10 py-1 min-w-[120px]">
                                    {((): TransportMode[] => {
                                      const modes: TransportMode[] = ['car', 'bus'];
                                      if (tripEndTrainAvailable) {
                                        modes.push('train');
                                      }
                                      return modes;
                                    })().map((mode) => (
                                      <button
                                        key={mode}
                                        onClick={() => {
                                          setTripEndTransportMode(mode);
                                          setExpandedTransportIndex(null);
                                        }}
                                        className={`w-full flex items-center gap-2 px-3 py-2 hover:bg-gray-50 transition-colors ${
                                          tripEndTransportMode === mode ? 'bg-blue-50' : ''
                                        }`}
                                      >
                                        {getTransportIcon(mode)}
                                        <span className="text-sm font-medium text-gray-700">
                                          {getTransportLabel(mode)}
                                        </span>
                                        {tripEndTransportMode === mode && (
                                          <Check className="w-4 h-4 text-blue-600 ml-auto" />
                                        )}
                                      </button>
                                    ))}
                                  </div>
                                )}
                                </div>
                                
                                {/* Distance and Time - shown after transport dropdown */}
                                {tripEndRouteSegment && tripEndTransportMode !== 'train' && (() => {
                                  return (
                                    <div className="flex items-center gap-1 text-xs text-gray-600">
                                      <span className="text-gray-400">•</span>
                                      <span>{tripEndRouteSegment.distance.toFixed(1)} km</span>
                                      <span className="text-gray-400">•</span>
                                      <span>{Math.round(tripEndRouteSegment.duration)} min</span>
                                    </div>
                                  );
                                })()}
                              </div>
                            </div>
                          )}
                        </div>
                      </div>
                      
                      {/* Action Buttons */}
                      <div className="flex items-center gap-1 md:gap-2 flex-shrink-0">
                        {/* Expand/Collapse Button */}
                        {finalizedDay && finalizedDay.places.length > 0 && (
                          <button
                            onClick={() => {
                              setExpandedDays(prev => {
                                const newSet = new Set(prev);
                                if (newSet.has(index)) {
                                  newSet.delete(index);
                                } else {
                                  newSet.add(index);
                                }
                                return newSet;
                              });
                            }}
                            className="text-gray-600 hover:bg-gray-100 p-1.5 md:p-2 rounded-lg transition-colors"
                            title={isExpanded ? 'Collapse' : 'Expand'}
                          >
                            {isExpanded ? <ChevronUp className="w-4 h-4 md:w-5 md:h-5" /> : <ChevronDown className="w-4 h-4 md:w-5 md:h-5" />}
                          </button>
                        )}
                        
                        
                        {/* Edit/Add Button */}
                        <button 
                          onClick={() => {
                            // Check 1: Must have start location first
                            if (!multiDayStartLocation) {
                              showToast('⚠️ Please add a start location first to begin planning your itinerary', 'warning', 4000);
                              return;
                            }
                            
                            // Check 2: If trip has ended (end location exists), show toast and prevent adding places to any day
                            if (isTripEnded) {
                              showToast('Remove the end location first to pick more places', 'warning', 4000);
                              return;
                            }
                            
                            // Check 3: Must plan days in sequential order
                            const nextDayToPlan = getNextDayToPlan();
                            
                            // Check if this day/block is already finalized (allow editing)
                            const isAlreadyFinalized = finalizedDay && finalizedDay.places && finalizedDay.places.length > 0;
                            
                            // For merged blocks, check if ANY day in the block matches nextDayToPlan
                            const isNextDayBlock = blockIndices.includes(nextDayToPlan);
                            const isCurrentDay = index === nextDayToPlan;
                            
                            // Allow if: it's the next day/block to plan OR it's already finalized
                            const shouldAllow = isCurrentDay || isNextDayBlock || isAlreadyFinalized;
                            
                            if (!shouldAllow) {
                              // Block access and show guidance
                              const clickedLabel = isMultiDayBlock 
                                ? `Days ${blockIndices[0] + 1}-${blockIndices[blockIndices.length - 1] + 1}`
                                : `Day ${index + 1}`;
                              const nextDayLabel = nextDayToPlan + 1;
                              showToast(`⚠️ Please plan days in order. Next day to plan is Day ${nextDayLabel}. Complete Day ${nextDayLabel} before planning ${clickedLabel}.`, 'warning', 5000);
                              return;
                            }
                            
                            setSelectedDayIndex(index);
                            
                            // If day has places, load existing data to edit
                            if (finalizedDay && finalizedDay.places.length > 0) {
                              // Load places
                              setTripPlaces(finalizedDay.places);
                              
                              // Load transportation
                              setDayTransportation(prev => {
                                const newMap = new Map(prev);
                                newMap.set(index, new Map(finalizedDay.transportation));
                                return newMap;
                              });
                              
                              // Load route segments
                              setDayRouteSegments(prev => {
                                const newMap = new Map(prev);
                                newMap.set(index, finalizedDay.routeSegments);
                                return newMap;
                              });
                              
                              // Restore active train routes from route segments
                              const newActiveTrainRoutes = new Map<number, any>();
                              finalizedDay.routeSegments.forEach((segment, segmentIndex) => {
                                if (segment.trainRoute) {
                                  newActiveTrainRoutes.set(segmentIndex, segment.trainRoute);
                                }
                              });
                              setActiveTrainRoutes(newActiveTrainRoutes);
                              
                              // Load trip end data if this is the last day
                              if (finalizedDay.endLocation) {
                                setTripEndLocation(finalizedDay.endLocation);
                                setIsTripEnded(true);
                                if (finalizedDay.endTransportMode) {
                                  setTripEndTransportMode(finalizedDay.endTransportMode);
                                }
                                if (finalizedDay.endRouteSegment) {
                                  setTripEndRouteSegment(finalizedDay.endRouteSegment);
                                }
                              }
                              
                              // Set the day location (use first primary location for nearby places)
                              const dayLocs = dayLocations.get(index);
                              if (dayLocs && dayLocs.length > 0) {
                                setSelectedDayLocation(dayLocs[0]);
                                // Set to -1 meaning "viewing all locations" - prevents merge logic from duplicating
                                setCurrentPrimaryLocationIndex(-1);
                              }
                              
                              // Go directly to plan day view
                              setCurrentView('multi-day-trip-plan-day');
                            } else {
                              // New day - clear places and show location picker
                              setTripPlaces([]);
                              setShowDayLocationSlider(true);
                              setSelectedDayLocation(null);
                            }
                          }}
                          className={`p-1.5 md:p-2 rounded-lg transition-colors ${
                            (() => {
                              if (!multiDayStartLocation || isTripEnded) {
                                return 'text-gray-400 bg-gray-100 cursor-not-allowed opacity-50';
                              }
                              const nextDay = getNextDayToPlan();
                              const isAlreadyFinalized = finalizedDay && finalizedDay.places && finalizedDay.places.length > 0;
                              const isNextDayBlock = blockIndices.includes(nextDay);
                              const isCurrentDay = index === nextDay;
                              const shouldAllow = isCurrentDay || isNextDayBlock || isAlreadyFinalized;
                              return shouldAllow ? 'text-purple-600 hover:bg-purple-50 cursor-pointer' : 'text-gray-400 bg-gray-100 cursor-not-allowed opacity-50';
                            })()
                          }`}
                          title={
                            (() => {
                              if (!multiDayStartLocation) return 'Add start location first';
                              if (isTripEnded) return 'Remove end location first to add places';
                              const nextDay = getNextDayToPlan();
                              const isAlreadyFinalized = finalizedDay && finalizedDay.places && finalizedDay.places.length > 0;
                              const isNextDayBlock = blockIndices.includes(nextDay);
                              const isCurrentDay = index === nextDay;
                              const shouldAllow = isCurrentDay || isNextDayBlock || isAlreadyFinalized;
                              if (!shouldAllow) {
                                return `Plan Day ${nextDay + 1} first`;
                              }
                              return '';
                            })()
                          }
                        >
                          {finalizedDay && finalizedDay.places.length > 0 ? <Eye className="w-4 h-4 md:w-5 md:h-5" /> : <Plus className="w-4 h-4 md:w-5 md:h-5" />}
                        </button>
                      </div>
                    </div>
                    
                    {/* Combine/Split Buttons */}
                    <div className="mt-2 md:mt-3 pt-2 md:pt-3 border-t border-gray-200 flex items-center gap-1.5 md:gap-2">
                      {/* Add Location Slider - Shows when adding additional primary location */}
                      {selectedDayIndex === index && showAddLocationSlider && (
                        <div className="flex items-center gap-1.5 flex-1 animate-slide-in-right">
                          {/* Show location picker OR selected location with Change/Search buttons */}
                          {!selectedDayLocation ? (
                            <>
                              <span className="text-[10px] md:text-xs text-gray-600 whitespace-nowrap">Add location:</span>
                              {/* Recommended Dropdown */}
                              <div className="relative">
                                <button
                                  onClick={() => setShowRecommendedDropdown(!showRecommendedDropdown)}
                                  className="bg-gradient-to-br from-purple-50 to-pink-50 hover:from-purple-100 hover:to-pink-100 text-purple-700 font-medium px-2 md:px-3 py-1 md:py-1.5 rounded-full text-[10px] md:text-xs transition-colors flex items-center gap-1 whitespace-nowrap shadow-sm hover:shadow-md"
                                >
                                  <span>Recommended</span>
                                  <ChevronDown className={`w-3 h-3 transition-transform ${showRecommendedDropdown ? 'rotate-180' : ''}`} />
                                </button>
                                
                                {showRecommendedDropdown && (
                                  <div className="absolute top-full left-0 mt-1 bg-white rounded-lg shadow-lg border border-gray-200 z-50 min-w-[140px] max-h-48 overflow-y-auto">
                                    {QUICK_LOCATIONS.map((location, idx) => (
                                      <button
                                        key={idx}
                                        onClick={() => {
                                          setSelectedDayLocation(location);
                                          setShowRecommendedDropdown(false);
                                          // Add to dayLocations array
                                          if (selectedDayIndex !== null) {
                                            const existingLocs = dayLocations.get(selectedDayIndex) || [];
                                            setDayLocations(prev => {
                                              const newMap = new Map(prev);
                                              newMap.set(selectedDayIndex, [...existingLocs, location]);
                                              return newMap;
                                            });
                                            setCurrentPrimaryLocationIndex(existingLocs.length);
                                          }
                                          setShowAddLocationSlider(false);
                                          // Clear nearbyPlaces and markers before navigating (same as first location selection)
                                          setNearbyPlaces([]);
                                          placeMarkersRef.current.forEach(marker => marker.remove());
                                          placeMarkersRef.current = [];
                                          setActiveCardIndex(0);
                                          
                                          // Zoom map to the new location (same behavior as selecting first location for a new day)
                                          if (mapRef.current) {
                                            const offset = getDrawerAdjustedOffset();
                                            mapRef.current.flyTo({
                                              center: location.coordinates,
                                              zoom: Math.max(mapRef.current.getZoom(), 12),
                                              duration: 1500,
                                              essential: true,
                                              offset: offset
                                            });
                                          }
                                          
                                          // Navigate to destination picker
                                          setCurrentView('multi-day-trip-day-destinations');
                                        }}
                                        className="w-full text-left px-3 py-2 hover:bg-purple-50 text-xs text-gray-700 hover:text-purple-900 transition-colors border-b border-gray-100 last:border-b-0"
                                      >
                                        {location.name}
                                      </button>
                                    ))}
                                  </div>
                                )}
                              </div>
                              
                              {/* Pick from Map */}
                              <button
                                onClick={() => {
                                  setIsSelectingDayLocation(true);
                                  setShowRecommendedDropdown(false);
                                  // Clear nearbyPlaces and markers before map selection
                                  setNearbyPlaces([]);
                                  placeMarkersRef.current.forEach(marker => marker.remove());
                                  placeMarkersRef.current = [];
                                  if (!isDesktop) {
                                    collapse();
                                  }
                                }}
                                className="bg-gradient-to-br from-orange-50 to-amber-50 hover:from-orange-100 hover:to-amber-100 text-orange-700 font-medium px-2 md:px-3 py-1 md:py-1.5 rounded-full text-[10px] md:text-xs transition-colors flex items-center gap-1 whitespace-nowrap shadow-sm hover:shadow-md"
                              >
                                <MapPin className="w-3 h-3" />
                                <span>On Map</span>
                              </button>
                              
                              {/* Cancel */}
                              <button
                                onClick={() => {
                                  setShowAddLocationSlider(false);
                                  setShowRecommendedDropdown(false);
                                }}
                                className="text-gray-400 hover:text-gray-600 p-1"
                              >
                                <X className="w-4 h-4" />
                              </button>
                            </>
                          ) : (
                            <>
                              {/* Location selected from map - show Change/Search Places buttons */}
                              <div className="flex items-center gap-1 flex-1 min-w-0">
                                <MapPin className="w-3 h-3 text-purple-600 flex-shrink-0" />
                                <span className="text-[10px] md:text-xs text-gray-700 truncate">{selectedDayLocation.name}</span>
                              </div>
                              <button
                                onClick={() => {
                                  // Remove the location that was added from dayLocations
                                  if (selectedDayIndex !== null) {
                                    setDayLocations(prev => {
                                      const newMap = new Map(prev);
                                      const locations = newMap.get(selectedDayIndex) || [];
                                      if (locations.length > 0) {
                                        const updatedLocations = locations.slice(0, -1);
                                        if (updatedLocations.length > 0) {
                                          newMap.set(selectedDayIndex, updatedLocations);
                                        } else {
                                          newMap.delete(selectedDayIndex);
                                        }
                                      }
                                      return newMap;
                                    });
                                  }
                                  setSelectedDayLocation(null);
                                }}
                                className="text-[10px] md:text-xs text-gray-500 hover:text-gray-700 font-medium px-2"
                              >
                                Change
                              </button>
                              <button
                                onClick={() => {
                                  setShowAddLocationSlider(false);
                                  // Clear nearbyPlaces and markers before navigating
                                  setNearbyPlaces([]);
                                  placeMarkersRef.current.forEach(marker => marker.remove());
                                  placeMarkersRef.current = [];
                                  setActiveCardIndex(0);
                                  
                                  // Zoom map to the new location
                                  if (mapRef.current) {
                                    const offset = getDrawerAdjustedOffset();
                                    mapRef.current.flyTo({
                                      center: selectedDayLocation.coordinates,
                                      zoom: Math.max(mapRef.current.getZoom(), 12),
                                      duration: 1000,
                                      offset: offset
                                    });
                                  }
                                  
                                  setCurrentView('multi-day-trip-day-destinations');
                                }}
                                className="bg-gradient-to-r from-purple-600 to-pink-600 hover:from-purple-700 hover:to-pink-700 text-white font-medium px-2 md:px-3 py-1 md:py-1.5 rounded-lg transition-colors text-[10px] md:text-xs"
                              >
                                Search Places
                              </button>
                              {/* Cancel */}
                              <button
                                onClick={() => {
                                  // Remove the location that was added from dayLocations
                                  if (selectedDayIndex !== null) {
                                    setDayLocations(prev => {
                                      const newMap = new Map(prev);
                                      const locations = newMap.get(selectedDayIndex) || [];
                                      if (locations.length > 0) {
                                        const updatedLocations = locations.slice(0, -1);
                                        if (updatedLocations.length > 0) {
                                          newMap.set(selectedDayIndex, updatedLocations);
                                        } else {
                                          newMap.delete(selectedDayIndex);
                                        }
                                      }
                                      return newMap;
                                    });
                                  }
                                  setShowAddLocationSlider(false);
                                  setSelectedDayLocation(null);
                                  setShowRecommendedDropdown(false);
                                }}
                                className="text-gray-400 hover:text-gray-600 p-1"
                              >
                                <X className="w-4 h-4" />
                              </button>
                            </>
                          )}
                        </div>
                      )}
                      
                      {/* Add Location Button - Show if day has at least one location and slider not showing */}
                      {!showAddLocationSlider && (dayLocations.get(index)?.length || 0) > 0 && !isTripEnded && (
                        <button
                          onClick={() => {
                            setSelectedDayIndex(index);
                            // Clear selectedDayLocation so slider shows Recommended/On Map options
                            setSelectedDayLocation(null);
                            
                            // Load ALL existing places for correct route calculation
                            // The Continue handler will filter tripPlaces to only current location's places before merging
                            const existingDayData = finalizedDays.get(index);
                            if (existingDayData && existingDayData.places.length > 0) {
                              setTripPlaces(existingDayData.places);
                            } else {
                              setTripPlaces([]);
                            }
                            
                            // Also restore transportation and route segments for display
                            if (existingDayData && existingDayData.places.length > 0) {
                              setDayTransportation(prev => {
                                const newMap = new Map(prev);
                                newMap.set(index, new Map(existingDayData.transportation));
                                return newMap;
                              });
                              setDayRouteSegments(prev => {
                                const newMap = new Map(prev);
                                newMap.set(index, existingDayData.routeSegments);
                                return newMap;
                              });
                              // Restore active train routes from route segments
                              const newActiveTrainRoutes = new Map<number, any>();
                              existingDayData.routeSegments.forEach((segment, segmentIndex) => {
                                if (segment.trainRoute) {
                                  newActiveTrainRoutes.set(segmentIndex, segment.trainRoute);
                                }
                              });
                              setActiveTrainRoutes(newActiveTrainRoutes);
                            }
                            
                            setShowAddLocationSlider(true);
                            setShowRecommendedDropdown(false);
                          }}
                          className="flex items-center gap-1 md:gap-1.5 px-2 md:px-3 py-1 md:py-1.5 bg-blue-50 hover:bg-blue-100 text-blue-700 rounded-lg transition-colors text-[10px] md:text-xs font-medium"
                        >
                          <MapPin className="w-3 h-3 md:w-3.5 md:h-3.5" />
                          <span>Add Location</span>
                        </button>
                      )}
                      
                      {/* End Trip Button - Only for last day */}
                      {isLastDay && !isTripEnded && !showAddLocationSlider && (
                        <button
                          onClick={() => {
                            setShowTripEndLocationPicker(true);
                            setSelectedDayIndex(index);
                          }}
                          className="flex items-center gap-1 md:gap-1.5 px-2 md:px-3 py-1 md:py-1.5 bg-green-50 hover:bg-green-100 text-green-700 rounded-lg transition-colors text-[10px] md:text-xs font-medium"
                        >
                          <svg className="w-3 h-3 md:w-3.5 md:h-3.5" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                            <path d="M4 15s1-1 4-1 5 2 8 2 4-1 4-1V3s-1 1-4 1-5-2-8-2-4 1-4 1z" />
                            <line x1="4" y1="22" x2="4" y2="15" />
                          </svg>
                          <span>Select end location</span>
                        </button>
                      )}
                      
                      {/* Combine Button - Show if not last day and not already at max of block */}
                      {!showAddLocationSlider && !isLastDay && (!finalizedDay?.isMultiDayBlock || 
                        (finalizedDay.blockDayIndices && finalizedDay.blockDayIndices[finalizedDay.blockDayIndices.length - 1] !== itineraryDays.length - 1)
                      ) && (
                        <button
                          onClick={() => handleCombineDays(index)}
                          className="flex items-center gap-1 md:gap-1.5 px-2 md:px-3 py-1 md:py-1.5 bg-purple-50 hover:bg-purple-100 text-purple-700 rounded-lg transition-colors text-[10px] md:text-xs font-medium"
                        >
                          <Plus className="w-3 h-3 md:w-3.5 md:h-3.5" />
                          <span>Combine Next Day</span>
                        </button>
                      )}
                      
                      {/* Split Button - Show if multi-day block with 2+ days */}
                      {!showAddLocationSlider && finalizedDay?.isMultiDayBlock && finalizedDay.blockDayIndices && finalizedDay.blockDayIndices.length >= 2 && (
                        <button
                          onClick={() => handleSplitDays(index)}
                          className="flex items-center gap-1 md:gap-1.5 px-2 md:px-3 py-1 md:py-1.5 bg-orange-50 hover:bg-orange-100 text-orange-700 rounded-lg transition-colors text-[10px] md:text-xs font-medium"
                        >
                          <X className="w-3 h-3 md:w-3.5 md:h-3.5" />
                          <span>Split Off Last Day</span>
                        </button>
                      )}
                    </div>
                    
                    {/* Location Selection Slider */}
                    {selectedDayIndex === index && showDayLocationSlider && (
                      <div className="absolute -inset-[2px] bg-white rounded-lg md:rounded-xl border-2 border-gray-200 z-10 overflow-visible">
                        {!selectedDayLocation ? (
                          <>
                            {/* Desktop Layout - horizontal */}
                            <div className="hidden md:flex items-center h-full py-2">
                              {/* Keep existing day info on left - static, no animation */}
                              <div className="flex items-center gap-2 flex-1 pl-3">
                                <div className="flex flex-col items-center flex-shrink-0">
                                  <div className="w-8 h-8 rounded-full bg-gradient-to-br from-purple-500 to-pink-500 flex items-center justify-center text-white font-bold text-xs">
                                    {index + 1}
                                  </div>
                                  <span className="text-[10px] text-gray-500 mt-0.5">{dayName}</span>
                                </div>
                                <div>
                                  <h4 className="font-semibold text-gray-900 text-xs">
                                    {isMultiDayBlock ? (
                                      <>Days {blockIndices[0] + 1}-{blockIndices[blockIndices.length - 1] + 1}</>
                                    ) : (
                                      <>Day {index + 1}</>
                                    )}
                                    {isFirstDay && <span className="text-[10px] ml-2 bg-green-100 text-green-700 px-1.5 py-0.5 rounded-full">Start</span>}
                                    {isLastDay && <span className="text-[10px] ml-2 bg-blue-100 text-blue-700 px-1.5 py-0.5 rounded-full">End</span>}
                                  </h4>
                                  <p className="text-[10px] text-gray-600">Select primary location</p>
                                </div>
                              </div>
                              
                              {/* Horizontal pill-shaped buttons on right - animated slide in */}
                              <div className="flex gap-1.5 pr-2 animate-slide-in-right">
                                {/* Recommended Dropdown */}
                                <div className="relative">
                                  <button
                                    onClick={() => setShowRecommendedDropdown(!showRecommendedDropdown)}
                                    className="bg-gradient-to-br from-purple-50 to-pink-50 hover:from-purple-100 hover:to-pink-100 text-purple-700 font-medium px-3 py-1.5 rounded-full text-xs transition-colors flex items-center gap-1.5 whitespace-nowrap shadow-sm hover:shadow-md"
                                  >
                                    <span>Recommended</span>
                                    <ChevronDown className={`w-3.5 h-3.5 transition-transform ${showRecommendedDropdown ? 'rotate-180' : ''}`} />
                                  </button>
                                  
                                  {showRecommendedDropdown && (
                                    <div className="absolute top-full right-0 mt-1 bg-white rounded-lg shadow-lg border border-gray-200 z-50 min-w-[160px] max-h-48 overflow-y-auto">
                                      {QUICK_LOCATIONS.map((location, idx) => (
                                        <button
                                          key={idx}
                                          onClick={() => {
                                            setSelectedDayLocation(location);
                                            setShowRecommendedDropdown(false);
                                            // Save to dayLocations map as first primary location
                                            if (selectedDayIndex !== null) {
                                              setDayLocations(prev => {
                                                const newMap = new Map(prev);
                                                newMap.set(selectedDayIndex, [location]);
                                                return newMap;
                                              });
                                              setCurrentPrimaryLocationIndex(0);
                                            }
                                          }}
                                          className="w-full text-left px-3 py-2 hover:bg-purple-50 text-xs text-gray-700 hover:text-purple-900 transition-colors border-b border-gray-100 last:border-b-0"
                                        >
                                          {location.name}
                                        </button>
                                      ))}
                                    </div>
                                  )}
                                </div>
                                
                                {/* Pick from Map */}
                                <button
                                  onClick={() => {
                                    setIsSelectingDayLocation(true);
                                    setShowRecommendedDropdown(false);
                                    if (!isDesktop) {
                                      collapse();
                                    }
                                  }}
                                  className="bg-gradient-to-br from-orange-50 to-amber-50 hover:from-orange-100 hover:to-amber-100 text-orange-700 font-medium px-3 py-1.5 rounded-full text-xs transition-colors flex items-center gap-1.5 whitespace-nowrap shadow-sm hover:shadow-md"
                                >
                                  <MapPin className="w-3.5 h-3.5" />
                                  <span>On Map</span>
                                </button>
                              </div>
                            </div>

                            {/* Mobile Layout - vertical */}
                            <div className="md:hidden flex flex-col p-2 h-full justify-between">
                              {/* Header text at top */}
                              <div className="flex items-center justify-between mb-1.5">
                                <div className="flex items-center gap-1.5">
                                  <span className="text-xs font-bold text-gray-900">
                                    {isMultiDayBlock ? (
                                      <>Days {blockIndices[0] + 1}-{blockIndices[blockIndices.length - 1] + 1}</>
                                    ) : (
                                      <>Day {index + 1}</>
                                    )}
                                  </span>
                                  <span className="text-[10px] text-gray-600">Select primary location</span>
                                </div>
                                <button
                                  onClick={() => {
                                    setShowDayLocationSlider(false);
                                    setSelectedDayIndex(null);
                                    setShowRecommendedDropdown(false);
                                  }}
                                  className="text-gray-400 hover:text-gray-600"
                                >
                                  <X className="w-3.5 h-3.5" />
                                </button>
                              </div>
                              
                              {/* Buttons stacked horizontally - animated slide in */}
                              <div className="flex gap-1.5 animate-slide-in-right">
                                {/* Recommended Dropdown */}
                                <div className="relative flex-1">
                                  <button
                                    onClick={() => setShowRecommendedDropdown(!showRecommendedDropdown)}
                                    className="w-full bg-gradient-to-br from-purple-50 to-pink-50 hover:from-purple-100 hover:to-pink-100 text-purple-700 font-medium px-3 py-1.5 rounded-full text-xs transition-colors flex items-center justify-center gap-1.5 shadow-sm hover:shadow-md"
                                  >
                                    <span>Recommended</span>
                                    <ChevronDown className={`w-3.5 h-3.5 transition-transform ${showRecommendedDropdown ? 'rotate-180' : ''}`} />
                                  </button>
                                  
                                  {showRecommendedDropdown && (
                                    <div className="absolute top-full left-0 right-0 mt-1 bg-white rounded-lg shadow-lg border border-gray-200 z-50 max-h-48 overflow-y-auto">
                                      {QUICK_LOCATIONS.map((location, idx) => (
                                        <button
                                          key={idx}
                                          onClick={() => {
                                            setSelectedDayLocation(location);
                                            setShowRecommendedDropdown(false);
                                            // Save to dayLocations map as first primary location
                                            if (selectedDayIndex !== null) {
                                              setDayLocations(prev => {
                                                const newMap = new Map(prev);
                                                newMap.set(selectedDayIndex, [location]);
                                                return newMap;
                                              });
                                              setCurrentPrimaryLocationIndex(0);
                                            }
                                          }}
                                          className="w-full text-left px-3 py-2 hover:bg-purple-50 text-xs text-gray-700 hover:text-purple-900 transition-colors border-b border-gray-100 last:border-b-0"
                                        >
                                          {location.name}
                                        </button>
                                      ))}
                                    </div>
                                  )}
                                </div>
                                
                                {/* Pick from Map */}
                                <button
                                  onClick={() => {
                                    setIsSelectingDayLocation(true);
                                    setShowRecommendedDropdown(false);
                                    if (!isDesktop) {
                                      collapse();
                                    }
                                  }}
                                  className="flex-1 bg-gradient-to-br from-orange-50 to-amber-50 hover:from-orange-100 hover:to-amber-100 text-orange-700 font-medium px-3 py-1.5 rounded-full text-xs transition-colors flex items-center justify-center gap-1.5 shadow-sm hover:shadow-md"
                                >
                                  <MapPin className="w-3.5 h-3.5" />
                                  <span>On Map</span>
                                </button>
                              </div>
                            </div>
                            
                            {/* Close Button - Desktop only (mobile has it in header) */}
                            <button
                              onClick={() => {
                                setShowDayLocationSlider(false);
                                setSelectedDayIndex(null);
                                setShowRecommendedDropdown(false);
                              }}
                              className="hidden md:block text-gray-400 hover:text-gray-600 absolute top-2 right-2"
                            >
                              <X className="w-4 h-4" />
                            </button>
                          </>
                        ) : (
                          <div className="flex items-center h-full py-2">
                            {/* Keep day info on left */}
                            <div className="flex items-center gap-2 flex-1 pl-3">
                              <div className="flex flex-col items-center flex-shrink-0">
                                <div className="w-8 h-8 rounded-full bg-gradient-to-br from-purple-500 to-pink-500 flex items-center justify-center text-white font-bold text-xs">
                                  {index + 1}
                                </div>
                                <span className="text-[10px] text-gray-500 mt-0.5">{dayName}</span>
                              </div>
                              <div className="flex-1 min-w-0">
                                <h4 className="font-semibold text-gray-900 text-xs">
                                  {isMultiDayBlock ? (
                                    <>Days {blockIndices[0] + 1}-{blockIndices[blockIndices.length - 1] + 1}</>
                                  ) : (
                                    <>Day {index + 1}</>
                                  )}
                                  {isFirstDay && <span className="text-[10px] ml-1.5 bg-green-100 text-green-700 px-1.5 py-0.5 rounded-full">Start</span>}
                                  {isLastDay && <span className="text-[10px] ml-1.5 bg-blue-100 text-blue-700 px-1.5 py-0.5 rounded-full">End</span>}
                                </h4>
                                <div className="flex items-center gap-1 mt-0.5">
                                  <MapPin className="w-3 h-3 text-purple-600 flex-shrink-0" />
                                  <span className="text-[10px] text-gray-700 truncate">{selectedDayLocation.name}</span>
                                </div>
                              </div>
                            </div>
                            
                            {/* Action buttons on right */}
                            <div className="flex gap-1.5 pr-2">
                              <button
                                onClick={() => setSelectedDayLocation(null)}
                                className="text-[10px] text-gray-500 hover:text-gray-700 font-medium px-2"
                              >
                                Change
                              </button>
                              <button
                                onClick={() => {
                                  setShowDayLocationSlider(false);
                                  // Clear trip places for new day (unless already finalized)
                                  if (!finalizedDay) {
                                    setTripPlaces([]);
                                  }
                                  // CRITICAL FOR MOBILE: Clear nearbyPlaces BEFORE transitioning to destinations view
                                  // On mobile, the carousel centering effect runs immediately when currentView changes
                                  // If nearbyPlaces still has the previous day's places, it will center on the old location
                                  // Clearing it here prevents the wrong location from being shown
                                  setNearbyPlaces([]);
                                  setActiveCardIndex(0);
                                  setCurrentView('multi-day-trip-day-destinations');
                                }}
                                className="bg-gradient-to-r from-purple-600 to-pink-600 hover:from-purple-700 hover:to-pink-700 text-white font-medium px-3 py-1.5 rounded-lg transition-colors text-xs"
                              >
                                Search Places
                              </button>
                            </div>
                            
                            {/* Close Button */}
                            <button
                              onClick={() => {
                                setShowDayLocationSlider(false);
                                setSelectedDayIndex(null);
                                setShowRecommendedDropdown(false);
                              }}
                              className="text-gray-400 hover:text-gray-600 absolute top-2 right-2"
                            >
                              <X className="w-3.5 h-3.5" />
                            </button>
                          </div>
                        )}
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
            
            {/* Generate Trip Summary and Get Quote Buttons */}
            {currentTripId && currentView === 'multi-day-trip-itinerary' && (
              <div className="flex flex-col gap-2">
              <button
                  onClick={handleGenerateSummaryClick}
                  disabled={isGeneratingSummary}
                  className="w-full bg-gradient-to-r from-orange-500 to-orange-600 hover:from-orange-600 hover:to-orange-700 disabled:from-gray-400 disabled:to-gray-500 text-white font-medium py-2 md:py-2.5 px-3 md:px-4 rounded-lg md:rounded-xl shadow-sm hover:shadow-md transition-all duration-200 flex items-center justify-center gap-1.5 md:gap-2 text-xs md:text-sm"
              >
                  {isGeneratingSummary ? (
                    <>
                      <svg className="w-4 h-4 md:w-5 md:h-5 animate-spin" fill="none" viewBox="0 0 24 24">
                        <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                        <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                </svg>
                      Generating...
                    </>
                  ) : isTripSummaryCurrent() ? (
                    <>
                      <Eye className="w-4 h-4 md:w-5 md:h-5" />
                      View Trip Summary
                    </>
                  ) : (
                    <>
                      <Sparkles className="w-4 h-4 md:w-5 md:h-5" />
                      Generate Trip Summary
                    </>
                  )}
              </button>
                <button
                  onClick={handleGenerateSummaryClick}
                  className="w-full bg-gradient-to-r from-purple-600 to-blue-600 hover:from-purple-700 hover:to-blue-700 text-white font-medium py-2 md:py-2.5 px-3 md:px-4 rounded-lg md:rounded-xl shadow-sm hover:shadow-md transition-all duration-200 flex items-center justify-center gap-1.5 md:gap-2 text-xs md:text-sm"
                >
                  <MessageSquare className="w-4 h-4 md:w-5 md:h-5" />
                  Get Quote
                </button>
              </div>
            )}
          </div>
        );
      
      case 'multi-day-trip-day-destinations':
        const allTypesMultiDay: PlaceType[] = ['attraction', 'food', 'experience', 'hotel'];
        
        return (
          <div className="space-y-3 md:space-y-4">
            {/* Day Location Header */}
            <div className="bg-gradient-to-br from-purple-50 to-pink-50 rounded-lg md:rounded-xl border border-purple-200 md:border-2 p-3 md:p-4">
              {/* Desktop: Show full header with Day X Destinations */}
              <div className="hidden md:flex items-center justify-between gap-3 mb-1">
                <div className="flex items-center gap-2 flex-1">
                  <Calendar className="w-4 h-4 md:w-5 md:h-5 text-purple-600" />
                  <h3 className="font-semibold text-sm md:text-base text-gray-900">
                    Day {selectedDayIndex !== null ? selectedDayIndex + 1 : ''} Destinations
                  </h3>
                </div>
                {tripPlaces.length > 0 && (
                  <button
                    onClick={() => handleNavigation('multi-day-trip-plan-day')}
                    className="bg-gradient-to-r from-purple-600 to-pink-600 hover:from-purple-700 hover:to-pink-700 text-white font-semibold px-4 py-1.5 rounded-lg text-xs md:text-sm transition-colors whitespace-nowrap"
                  >
                    Continue
                  </button>
                )}
              </div>
              
              {/* Mobile: Show only Places around */}
              {selectedDayLocation && (
                <div className="md:hidden flex items-center gap-1.5">
                  <MapPin className="w-3.5 h-3.5 text-purple-600 flex-shrink-0" />
                  <p className="text-xs text-gray-700 truncate">
                    Places around: <span className="font-medium">{selectedDayLocation.name}</span>
                  </p>
                </div>
              )}
              
              {/* Desktop: Show Places around below header */}
              {selectedDayLocation && (
                <div className="hidden md:flex items-center gap-1.5">
                  <MapPin className="w-3.5 h-3.5 md:w-4 md:h-4 text-purple-600" />
                  <p className="text-xs md:text-sm text-gray-700">
                    Places around: <span className="font-medium">{selectedDayLocation.name}</span>
                  </p>
                </div>
              )}
            </div>

            {/* Filters */}
            <div className="space-y-2 md:space-y-3">
              {/* Type Filter */}
              <div>
                {/* Mobile: Horizontal Layout */}
                <div className="flex items-center gap-2 md:hidden">
                  <div className="flex items-center gap-1 text-xs font-medium text-gray-700 flex-shrink-0">
                    <Filter className="w-3.5 h-3.5" />
                    <span>Type</span>
                  </div>
                  <div className="flex-1 overflow-x-auto scrollbar-hide">
                    <div className="flex gap-1.5 py-0.5 px-0.5">
                      {allTypesMultiDay.map((type) => {
                        const config = getPlaceTypeConfig(type);
                        const isSelected = selectedTypes.includes(type);
                        return (
                          <button
                            key={type}
                            onClick={() => toggleTypeFilter(type)}
                            className={`px-2.5 py-1.5 rounded text-xs font-medium transition-all whitespace-nowrap flex-shrink-0 ${
                              isSelected
                                ? `${config.bgColor} ${config.color} ring-1 ring-current`
                                : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
                            }`}
                          >
                            <span className="font-bold mr-1">{config.code}</span>
                            {config.label}
                          </button>
                        );
                      })}
                    </div>
                  </div>
                </div>

                {/* Desktop: Vertical Layout */}
                <div className="hidden md:block">
                  <label className="text-sm font-medium text-gray-700 mb-2 flex items-center">
                    <Filter className="w-4 h-4 mr-1.5" />
                    Type
                  </label>
                  <div className="flex flex-wrap gap-2">
                    {allTypesMultiDay.map((type) => {
                      const config = getPlaceTypeConfig(type);
                      const isSelected = selectedTypes.includes(type);
                      return (
                        <button
                          key={type}
                          onClick={() => toggleTypeFilter(type)}
                          className={`px-3 py-2 rounded-lg text-sm font-medium transition-all ${
                            isSelected
                              ? `${config.bgColor} ${config.color} ring-1 ring-current`
                              : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
                          }`}
                        >
                          <span className="font-bold mr-1">{config.code}</span>
                          {config.label}
                        </button>
                      );
                    })}
                  </div>
                </div>
              </div>

              {/* Distance Filter */}
              <div>
                {/* Mobile: Horizontal Layout */}
                <div className="flex items-center gap-2 md:hidden">
                  <div className="text-xs font-medium text-gray-700 flex-shrink-0">
                    Distance within
                  </div>
                  <div className="flex gap-1.5">
                    {[10, 20, 30].map((distance) => (
                      <button
                        key={distance}
                        onClick={() => setMaxDistance(distance)}
                        className={`px-2.5 py-1.5 rounded text-xs font-medium transition-all whitespace-nowrap ${
                          maxDistance === distance
                            ? 'bg-blue-600 text-white'
                            : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
                        }`}
                      >
                        {distance}km
                      </button>
                    ))}
                  </div>
                </div>

                {/* Desktop: Vertical Layout */}
                <div className="hidden md:block">
                  <label className="text-sm font-medium text-gray-700 mb-2 block">
                    Distance: Within {maxDistance}km
                  </label>
                  <div className="flex gap-2">
                    {[10, 20, 30].map((distance) => (
                      <button
                        key={distance}
                        onClick={() => setMaxDistance(distance)}
                        className={`flex-1 px-3 py-2 rounded-lg text-sm font-medium transition-all ${
                          maxDistance === distance
                            ? 'bg-blue-600 text-white'
                            : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
                        }`}
                      >
                        {distance}km
                      </button>
                    ))}
                  </div>
                </div>
              </div>

              {/* Saved Only Toggle */}
              <div>
                <button
                  onClick={() => {
                    if (!authState.user) return;
                    setShowOnlySaved(!showOnlySaved);
                  }}
                  disabled={!authState.user}
                  className={`w-full px-3 py-2 md:py-2.5 rounded md:rounded-lg text-xs md:text-sm font-medium transition-all flex items-center justify-center ${
                    showOnlySaved
                      ? 'bg-yellow-100 text-yellow-800 ring-1 ring-yellow-300'
                      : authState.user
                      ? 'bg-gray-100 text-gray-600 hover:bg-gray-200'
                      : 'bg-gray-100 text-gray-400 cursor-not-allowed opacity-50'
                  }`}
                  title={!authState.user ? 'Login required to filter by saved places' : ''}
                >
                  <Bookmark className={`w-3.5 h-3.5 md:w-4 md:h-4 mr-1.5 ${showOnlySaved ? 'fill-current' : ''}`} />
                  {showOnlySaved ? 'Showing Saved Only' : authState.user ? 'Show Saved Only' : 'Show Saved Only (Login Required)'}
                </button>
              </div>
            </div>

            {/* View Mode Toggle */}
            <div className="flex items-center justify-between pb-2 border-b border-gray-200">
              <p className="text-xs md:text-sm font-medium text-gray-700">
                {nearbyPlaces.length} {nearbyPlaces.length === 1 ? 'place' : 'places'} found
              </p>
              <div className="flex gap-1">
                <button
                  onClick={() => setViewMode('list')}
                  className={`p-1.5 md:p-2 rounded transition-all ${
                    viewMode === 'list'
                      ? 'bg-blue-600 text-white'
                      : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
                  }`}
                  title="List view"
                >
                  <List className="w-4 h-4" />
                </button>
                <button
                  onClick={() => setViewMode('grid')}
                  className={`p-1.5 md:p-2 rounded transition-all ${
                    viewMode === 'grid'
                      ? 'bg-blue-600 text-white'
                      : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
                  }`}
                  title="Grid view"
                >
                  <Grid3x3 className="w-4 h-4" />
                </button>
              </div>
            </div>

            {/* Places List/Grid */}
            <div className={viewMode === 'grid' ? 'grid grid-cols-3 gap-2 md:gap-3' : 'space-y-2 md:space-y-3'}>
              {placesLoading ? (
                <div className={`text-center py-8 ${viewMode === 'grid' ? 'col-span-3' : ''}`}>
                  <div className="inline-block animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600 mb-2"></div>
                  <p className="text-xs md:text-sm text-gray-600">Loading places...</p>
                </div>
              ) : nearbyPlaces.length === 0 ? (
                <div className={`text-center py-8 bg-gray-50 rounded-md md:rounded-lg ${viewMode === 'grid' ? 'col-span-3' : ''}`}>
                  <p className="text-xs md:text-sm text-gray-600">
                    No places found nearby. Try adjusting your filters.
                  </p>
                </div>
              ) : viewMode === 'list' ? (
                // List View
                nearbyPlaces.map((place, index) => {
                  const typeConfig = getPlaceTypeConfig(place.type as PlaceType);
                  const inTrip = isPlaceInTrip(place.id);

                  return (
                    <div
                      key={place.id}
                      className="bg-white border border-gray-200 rounded md:rounded-lg p-2 md:p-3 hover:shadow-md transition-all"
                    >
                      <div className="flex items-center gap-2 md:gap-3">
                        {/* Place Image */}
                        <div 
                          className="w-14 h-14 md:w-16 md:h-16 rounded md:rounded-lg overflow-hidden flex-shrink-0 bg-gray-100 cursor-pointer"
                          onClick={() => {
                            setActiveCardIndex(index);
                            setSelectedPlaceForDetail(place);
                          }}
                        >
                          {place.imageUrl || place.photo_1_cdn ? (
                            <img
                              src={(() => {
                                const variants = getImageVariants(place.photo_1_cdn || place.imageUrl);
                                return variants.tiny || variants.thumb || variants.original || place.imageUrl || place.photo_1_cdn;
                              })()}
                              alt={place.name}
                              className="w-full h-full object-cover"
                            />
                          ) : (
                            <div className="w-full h-full flex items-center justify-center">
                              <MapPin className="w-6 h-6 text-gray-400" />
                            </div>
                          )}
                        </div>

                        {/* Place Info */}
                        <div 
                          className="flex-1 min-w-0 cursor-pointer"
                          onClick={() => {
                            setActiveCardIndex(index);
                            setSelectedPlaceForDetail(place);
                          }}
                        >
                            <h4 className="font-semibold text-sm md:text-base text-gray-900 truncate">
                              {place.name}
                            </h4>
                          <p className="text-xs text-gray-500 mt-0.5">
                            {formatDistance(place.distance)} away
                          </p>
                        </div>

                        {/* Type Badge */}
                            <span
                              className={`${typeConfig.bgColor} ${typeConfig.color} px-1.5 py-0.5 rounded text-xs font-bold flex-shrink-0`}
                            >
                              {typeConfig.code}
                            </span>

                        {/* Add to Trip Button */}
                        <button
                          onClick={(e) => {
                            e.stopPropagation();
                            toggleTripPlace(place, currentPrimaryLocationIndex);
                          }}
                          className={`flex-shrink-0 p-2 rounded-lg transition-all ${
                            inTrip
                              ? 'bg-green-500 text-white'
                              : 'bg-blue-500 text-white hover:bg-blue-600'
                          }`}
                          title={inTrip ? 'Remove from trip' : 'Add to trip'}
                        >
                          {inTrip ? (
                            <Check className="w-4 h-4 md:w-5 md:h-5" />
                          ) : (
                            <Plus className="w-4 h-4 md:w-5 md:h-5" />
                          )}
                        </button>
                      </div>
                    </div>
                  );
                })
              ) : (
                // Grid View (3 columns)
                nearbyPlaces.map((place, index) => {
                  const typeConfig = getPlaceTypeConfig(place.type as PlaceType);
                  const variants = getImageVariants(place.photo_1_cdn || place.imageUrl);
                  const imageUrl = variants.tiny || variants.thumb || variants.original || place.imageUrl || place.photo_1_cdn;
                  const inTrip = isPlaceInTrip(place.id);

                  return (
                    <div
                      key={place.id}
                      className="bg-white border border-gray-200 rounded md:rounded-lg overflow-hidden hover:shadow-md transition-all group relative cursor-pointer"
                      onMouseEnter={() => setActiveCardIndex(index)}
                    >
                      {/* Image */}
                      <div className="relative aspect-square bg-gray-100">
                        {imageUrl ? (
                          <img
                            src={imageUrl}
                            alt={place.name}
                            className="w-full h-full object-cover"
                            loading="lazy"
                          />
                        ) : (
                          <div className="w-full h-full flex items-center justify-center">
                            <MapPin className="w-8 h-8 text-gray-400" />
                          </div>
                        )}
                        {/* Type Badge */}
                        <div className={`absolute top-1 right-1 ${typeConfig.bgColor} ${typeConfig.color} px-1.5 py-0.5 rounded text-[10px] font-bold`}>
                          {typeConfig.code}
                        </div>
                        
                        {/* Hover Overlay with Actions */}
                        <div className="absolute inset-0 bg-black bg-opacity-0 group-hover:bg-opacity-60 transition-all duration-200 flex items-center justify-center gap-2 opacity-0 group-hover:opacity-100">
                          <button
                            onClick={(e) => {
                              e.stopPropagation();
                              setSelectedPlaceForDetail(place);
                            }}
                            className="bg-white text-gray-900 px-3 py-1.5 rounded-lg text-xs font-medium hover:bg-gray-100 transition-colors flex items-center gap-1"
                          >
                            <Eye className="w-3 h-3" />
                            <span className="hidden md:inline">View</span>
                          </button>
                          <button
                            onClick={(e) => {
                              e.stopPropagation();
                              toggleTripPlace(place, currentPrimaryLocationIndex);
                            }}
                            className={`px-3 py-1.5 rounded-lg text-xs font-medium transition-colors flex items-center gap-1 ${
                              inTrip
                                ? 'bg-green-500 text-white hover:bg-green-600'
                                : 'bg-blue-500 text-white hover:bg-blue-600'
                            }`}
                          >
                            {inTrip ? (
                              <>
                                <Check className="w-3 h-3" />
                                <span className="hidden md:inline">Added</span>
                              </>
                            ) : (
                              <>
                                <Plus className="w-3 h-3" />
                                <span className="hidden md:inline">Add</span>
                              </>
                            )}
                          </button>
                        </div>
                      </div>
                      
                      {/* Content */}
                      <div className="p-2">
                        <h4 className="font-semibold text-xs md:text-sm text-gray-900 line-clamp-2 mb-1">
                          {place.name}
                        </h4>
                        <p className="text-[10px] md:text-xs text-gray-500">
                          {formatDistance(place.distance)} away
                        </p>
                      </div>
                    </div>
                  );
                })
              )}
            </div>

          </div>
        );

      case 'multi-day-trip-plan-day':
        // Get transportation icon and label
        // Get current day's transportation map
        const currentDayTransport = dayTransportation.get(selectedDayIndex!) || new Map();
        
        // Get route segments for this day
        const currentDaySegments = dayRouteSegments.get(selectedDayIndex!) || [];

        // Build itinerary: start location + selected places
        const dayItinerary: Array<{ name: string; coordinates: [number, number]; isStart?: boolean; place?: PlaceItem }> = [];
        
        // Add start location
        // For Day 1, use the trip's start location; for Day 2+, use previous day's last place
        let dayStartLocation: QuickLocation | null = null;
        
        if (selectedDayIndex === 0) {
          dayStartLocation = multiDayStartLocation;
        } else if (selectedDayIndex !== null) {
          // Find the actual previous day, accounting for merged blocks
          // If the previous day is absorbed in a merged block, we need to find that block
          let previousDayData: FinalizedDayData | undefined;
          let previousDayPlaces: PlaceItem[] | undefined;
          
          // Check if the previous day is part of a merged block
          const previousDayIndex = selectedDayIndex - 1;
          let foundInBlock = false;
          
          for (const [blockStartIndex, blockIndices] of Array.from(combinedDayBlocks.entries())) {
            if (blockIndices.includes(previousDayIndex)) {
              // Previous day is in this merged block
              previousDayData = finalizedDays.get(blockStartIndex);
              previousDayPlaces = previousDayData?.places;
              foundInBlock = true;
              break;
            }
          }
          
          // If not in a merged block, check regular dayPlaces and finalizedDays
          if (!foundInBlock) {
            previousDayPlaces = dayPlaces.get(previousDayIndex);
            if (!previousDayPlaces) {
              previousDayData = finalizedDays.get(previousDayIndex);
              previousDayPlaces = previousDayData?.places;
            }
          }
          
          if (previousDayPlaces && previousDayPlaces.length > 0) {
            const lastPlace = previousDayPlaces[previousDayPlaces.length - 1];
            dayStartLocation = {
              name: lastPlace.name,
              coordinates: [
                lastPlace.geo_point?.lon || lastPlace.geo_point?.lng || 0,
                lastPlace.geo_point?.lat || 0
              ]
            };
          }
        }
        
        
        if (dayStartLocation) {
          dayItinerary.push({
            name: dayStartLocation.name,
            coordinates: dayStartLocation.coordinates,
            isStart: true
          });
        }

        // Add selected places for this day
        tripPlaces.forEach(place => {
          const lng = place.geo_point?.lon || place.geo_point?.lng || place.geo_point?.longitude || 0;
          const lat = place.geo_point?.lat || place.geo_point?.latitude || 0;
          dayItinerary.push({
            name: place.name,
            coordinates: [lng, lat],
            place: place
          });
        });

        return (
          <div className="space-y-3 md:space-y-4">
            {/* Day Header - Desktop Only */}
            <div className="hidden md:block bg-gradient-to-br from-purple-50 to-pink-50 rounded-lg md:rounded-xl border border-purple-200 md:border-2 p-3 md:p-4">
              <div className="flex items-center justify-between mb-2">
              <div className="flex items-center gap-2">
                <Calendar className="w-4 h-4 md:w-5 md:h-5 text-purple-600" />
                <h3 className="font-semibold text-sm md:text-base text-gray-900">
                  Day {selectedDayIndex !== null ? selectedDayIndex + 1 : ''} Itinerary
                </h3>
              </div>
                <button
                  onClick={() => {
                    if (tripPlaces.length > 0) {
                      showToast(`Remove all existing places from Day ${(selectedDayIndex !== null ? selectedDayIndex + 1 : '')} to change primary location`, 'warning', 4000);
                      return;
                    }
                    // Clear day data before resetting location
                    setDayRouteSegments(prev => {
                      const newMap = new Map(prev);
                      if (selectedDayIndex !== null) {
                        newMap.delete(selectedDayIndex);
                      }
                      return newMap;
                    });
                    setDayTransportation(prev => {
                      const newMap = new Map(prev);
                      if (selectedDayIndex !== null) {
                        newMap.delete(selectedDayIndex);
                      }
                      return newMap;
                    });
                    setTrainAvailability(prev => {
                      const newMap = new Map(prev);
                      if (selectedDayIndex !== null) {
                        newMap.delete(selectedDayIndex);
                      }
                      return newMap;
                    });
                    // Clear the selected day location to allow re-selection
                    setSelectedDayLocation(null);
                    // Also clear from dayLocations map
                    if (selectedDayIndex !== null) {
                      setDayLocations(prev => {
                        const newMap = new Map(prev);
                        newMap.delete(selectedDayIndex);
                        return newMap;
                      });
                    }
                    // Open location picker
                    setIsResettingDayLocation(true);
                    // Navigate back to itinerary view so the location picker can be displayed
                    setCurrentView('multi-day-trip-itinerary');
                    // Show the location picker slider after a short delay
                    setTimeout(() => {
                      setShowDayLocationSlider(true);
                    }, 100);
                  }}
                  className={`flex items-center gap-1.5 px-2.5 py-1.5 rounded-lg text-xs font-medium transition-colors ${
                    tripPlaces.length > 0
                      ? 'bg-gray-100 text-gray-400 cursor-not-allowed opacity-50'
                      : 'bg-purple-50 text-purple-700 hover:bg-purple-100 cursor-pointer'
                  }`}
                  title={tripPlaces.length > 0 ? 'Remove all places first' : 'Change starting location'}
                >
                  <MapPin className="w-3.5 h-3.5" />
                  <span className="hidden sm:inline">Reset Location</span>
                </button>
              </div>
              <p className="text-xs md:text-sm text-gray-600">
                {dayItinerary.length} {dayItinerary.length === 1 ? 'stop' : 'stops'}
              </p>
            </div>

            {/* Itinerary List - Grouped by primary location */}
            <div className="space-y-4 md:space-y-5">
              {(() => {
                // Group places by primaryLocationIndex
                const locationIndices = Array.from(new Set(tripPlaces.map(p => p.primaryLocationIndex || 0))).sort((a, b) => a - b);
                const dayLocs = selectedDayIndex !== null ? dayLocations.get(selectedDayIndex) || [] : [];
                const hasMultipleLocations = locationIndices.length > 1 || dayLocs.length > 1;
                
                // Build items for rendering
                const allItems: Array<{
                  type: 'start' | 'location-header' | 'place';
                  name: string;
                  coordinates?: [number, number];
                  place?: any;
                  locationIndex?: number;
                  placeIndex?: number;
                  globalIndex?: number;
                }> = [];
                
                // Add start location
                if (dayStartLocation) {
                  allItems.push({
                    type: 'start',
                    name: dayStartLocation.name,
                    coordinates: dayStartLocation.coordinates
                  });
                }
                
                // Add location headers and places
                let globalPlaceIndex = 0;
                locationIndices.forEach(locIndex => {
                  const locationPlaces = tripPlaces.filter(p => (p.primaryLocationIndex || 0) === locIndex);
                  const locationName = dayLocs[locIndex]?.name || `Location ${locIndex + 1}`;
                  
                  // Add location header if multiple locations
                  if (hasMultipleLocations) {
                    allItems.push({
                      type: 'location-header',
                      name: locationName,
                      locationIndex: locIndex
                    });
                  }
                  
                  // Add places for this location
                  locationPlaces.forEach((place, idx) => {
                    allItems.push({
                      type: 'place',
                      name: place.name,
                      place: place,
                      locationIndex: locIndex,
                      placeIndex: idx,
                      globalIndex: globalPlaceIndex++
                    });
                  });
                });
                
                // Helper to render transport selector between items
                // segmentIndex: 0 = Start→Place[0], 1 = Place[0]→Place[1], etc.
                const renderTransportSelector = (segmentIndex: number, fromCoords?: [number, number], toCoords?: [number, number]) => {
                  if (segmentIndex >= tripPlaces.length) return null; // No transport after last place
                  
                  return (
                    <div className="flex justify-between items-center gap-2 py-1 md:py-1.5 px-2 md:px-4 my-1">
                      {/* Left: Transport Mode Dropdown */}
                      <div className="relative">
                        <button
                          onClick={() => setExpandedTransportIndex(expandedTransportIndex === segmentIndex ? null : segmentIndex)}
                          className={`flex items-center gap-1.5 md:gap-2 px-2 md:px-3 py-1 md:py-1.5 rounded-full border md:border-2 transition-all ${
                            expandedTransportIndex === segmentIndex
                              ? 'bg-blue-50 border-blue-400'
                              : 'bg-white border-gray-300 hover:border-gray-400'
                          }`}
                        >
                          {getTransportIcon(currentDayTransport.get(segmentIndex) || 'car')}
                          <span className="text-[10px] md:text-xs font-medium text-gray-700">
                            {getTransportLabel(currentDayTransport.get(segmentIndex) || 'car')}
                          </span>
                          <ChevronDown className={`w-2.5 h-2.5 md:w-3 md:h-3 text-gray-500 transition-transform ${
                            expandedTransportIndex === segmentIndex ? 'rotate-180' : ''
                          }`} />
                        </button>

                        {/* Transport Options Dropdown */}
                        {expandedTransportIndex === segmentIndex && (
                          <div className="absolute top-full left-0 mt-1 md:mt-2 bg-white rounded-lg shadow-lg border border-gray-200 z-10 py-0.5 md:py-1 min-w-[100px] md:min-w-[120px]">
                            {((): TransportMode[] => {
                              const modes: TransportMode[] = ['car', 'bus'];
                              if (trainAvailability.get(segmentIndex)) {
                                modes.push('train');
                              }
                              return modes;
                            })().map((mode) => (
                              <button
                                key={mode}
                                onClick={() => {
                                  if (mode === 'train' && trainAvailability.get(segmentIndex)) {
                                    handleTrainRouteClick(segmentIndex);
                                  } else {
                                    setDayTransportation(prev => {
                                      const newMap = new Map(prev);
                                      const dayMap = new Map(newMap.get(selectedDayIndex!) || new Map());
                                      dayMap.set(segmentIndex, mode);
                                      newMap.set(selectedDayIndex!, dayMap);
                                      return newMap;
                                    });
                                  }
                                  setExpandedTransportIndex(null);
                                }}
                                className={`w-full flex items-center gap-1.5 md:gap-2 px-2 md:px-3 py-1.5 md:py-2 hover:bg-gray-50 transition-colors ${
                                  currentDayTransport.get(segmentIndex) === mode ? 'bg-blue-50' : ''
                                }`}
                              >
                                {getTransportIcon(mode)}
                                <span className="text-xs md:text-sm font-medium text-gray-700">
                                  {getTransportLabel(mode)}
                                </span>
                                {currentDayTransport.get(segmentIndex) === mode && (
                                  <Check className="w-3 h-3 md:w-4 md:h-4 text-blue-600 ml-auto" />
                                )}
                              </button>
                            ))}
                          </div>
                        )}
                      </div>

                      {/* Center-Left: Distance/Time Info */}
                      {currentDaySegments[segmentIndex] && (currentDayTransport.get(segmentIndex) || 'car') !== 'train' && (
                        <div className="flex items-center text-[9px] md:text-[10px] text-gray-600 font-medium">
                          <span>
                            {currentDaySegments[segmentIndex].isApproximate && '~'}
                            {currentDaySegments[segmentIndex].distance.toFixed(1)}km • {currentDaySegments[segmentIndex].duration}min
                          </span>
                        </div>
                      )}

                      {/* Center: Train Available Badge */}
                      {trainAvailability.get(segmentIndex) && (
                        <div className="flex items-center gap-1.5 px-2 md:px-3 py-1 md:py-1.5 bg-amber-50 border border-amber-300 rounded-full">
                          <TrainTrack className="w-3 h-3 md:w-4 md:h-4 text-amber-700" />
                          <span className="text-[10px] md:text-xs font-medium text-amber-700">Train Available</span>
                        </div>
                      )}

                      {/* Right: Google Maps Button */}
                      {fromCoords && toCoords && (
                        <button
                          onClick={() => {
                            const mapsUrl = `https://www.google.com/maps/dir/?api=1&origin=${fromCoords[1]},${fromCoords[0]}&destination=${toCoords[1]},${toCoords[0]}&travelmode=driving`;
                            window.open(mapsUrl, '_blank');
                          }}
                          className="p-2 rounded-full transition-all border-2 hover:shadow-md flex-shrink-0"
                          style={{
                            background: 'linear-gradient(135deg, #4285F4 25%, #EA4335 25%, #EA4335 50%, #FBBC05 50%, #FBBC05 75%, #34A853 75%)',
                            borderColor: '#e0e0e0'
                          }}
                          title="Open in Google Maps"
                        >
                          <ExternalLink className="w-4 h-4 text-white drop-shadow-md" />
                        </button>
                      )}
                    </div>
                  );
                };

                return allItems.map((item, index) => {
                  if (item.type === 'start') {
                    // Get first place coordinates for Google Maps link
                    const firstPlace = tripPlaces[0];
                    const firstPlaceCoords: [number, number] | undefined = firstPlace?.geo_point ? [
                      firstPlace.geo_point.lon || firstPlace.geo_point.lng || 0,
                      firstPlace.geo_point.lat || 0
                    ] : undefined;
                    
                    return (
                      <div key="start">
                        <div className="bg-white rounded-lg md:rounded-xl border border-gray-200 p-2 md:p-4 hover:shadow-md transition-shadow">
                          <div className="flex items-center gap-2 md:gap-3">
                            <div className="flex-shrink-0 w-5 h-5 md:w-8 md:h-8 rounded-full flex items-center justify-center bg-purple-500 text-white font-bold text-xs md:text-sm">
                              <MapPin className="w-2.5 h-2.5 md:w-4 md:h-4" />
                            </div>
                            <div className="flex-1 min-w-0">
                              <h4 className="font-semibold text-xs md:text-base text-gray-900 truncate">
                                {item.name}
                                <span className="ml-1.5 md:ml-2 text-[10px] md:text-xs bg-purple-100 text-purple-700 px-1.5 md:px-2 py-0.5 rounded-full">
                                  Start
                                </span>
                              </h4>
                            </div>
                          </div>
                        </div>
                        {/* Transport from Start to first place (segment 0) */}
                        {tripPlaces.length > 0 && renderTransportSelector(0, item.coordinates, firstPlaceCoords)}
                      </div>
                    );
                  }
                  
                  if (item.type === 'location-header') {
                    return (
                      <div key={`loc-header-${item.locationIndex}`} className="flex items-center gap-2 pt-2 pb-1">
                        <MapPin className="w-4 h-4 text-purple-600" />
                        <span className="text-sm font-semibold text-purple-700">{item.name}</span>
                        <div className="flex-1 h-px bg-purple-200"></div>
                      </div>
                    );
                  }
                  
                  // Place card
                  const place = item.place!;
                  const placeGlobalIndex = item.globalIndex!;
                  
                  return (
                    <div key={`place-${placeGlobalIndex}`}>
                      <div className="bg-white rounded-lg md:rounded-xl border border-gray-200 p-2 md:p-4 hover:shadow-md transition-shadow">
                        <div className="flex items-center gap-2 md:gap-3">
                          <div className="flex-shrink-0 w-6 h-6 md:w-8 md:h-8 rounded-full flex items-center justify-center text-white font-bold text-xs md:text-sm bg-blue-500">
                            {placeGlobalIndex + 1}
                          </div>

                          {/* Type Badge */}
                          <span className={`flex-shrink-0 ${getPlaceTypeConfig(place.type as PlaceType).bgColor} ${getPlaceTypeConfig(place.type as PlaceType).color} px-1 md:px-1.5 py-0.5 rounded text-[10px] md:text-xs font-bold`}>
                            {getPlaceTypeConfig(place.type as PlaceType).code}
                          </span>

                          {/* Place Info */}
                          <div 
                            className="flex-1 min-w-0 cursor-pointer"
                            onClick={() => setSelectedPlaceForDetail(place)}
                          >
                            <h4 className="font-semibold text-xs md:text-base text-gray-900 truncate">
                              {place.name}
                            </h4>
                            <span className="text-[10px] md:text-xs text-gray-500 truncate block mt-0.5">
                              {place.distance ? formatDistance(place.distance) : ''} from previous
                            </span>
                          </div>

                          {/* Move Up/Down and Delete Buttons */}
                          <div className="flex flex-col gap-0.5 md:gap-1 flex-shrink-0">
                            <button
                              onClick={(e) => {
                                e.stopPropagation();
                                if (placeGlobalIndex > 0) {
                                  movePlace(placeGlobalIndex, 'up');
                                }
                              }}
                              disabled={placeGlobalIndex === 0}
                              className={`w-6 h-6 md:w-8 md:h-8 flex items-center justify-center rounded transition-all ${
                                placeGlobalIndex === 0
                                  ? 'bg-gray-100 text-gray-300 cursor-not-allowed'
                                  : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
                              }`}
                              title="Move up"
                            >
                              <MoveUp className="w-3 h-3 md:w-4 md:h-4" />
                            </button>
                            <button
                              onClick={(e) => {
                                e.stopPropagation();
                                if (placeGlobalIndex < tripPlaces.length - 1) {
                                  movePlace(placeGlobalIndex, 'down');
                                }
                              }}
                              disabled={placeGlobalIndex === tripPlaces.length - 1}
                              className={`w-6 h-6 md:w-8 md:h-8 flex items-center justify-center rounded transition-all ${
                                placeGlobalIndex === tripPlaces.length - 1
                                  ? 'bg-gray-100 text-gray-300 cursor-not-allowed'
                                  : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
                              }`}
                              title="Move down"
                            >
                              <MoveDown className="w-3 h-3 md:w-4 md:h-4" />
                            </button>
                          </div>

                          {/* Remove Button */}
                          <button
                            onClick={(e) => {
                              e.stopPropagation();
                              removeFromTrip(place.id);
                            }}
                            className="flex-shrink-0 w-6 h-6 md:w-8 md:h-8 flex items-center justify-center rounded-lg transition-all bg-red-100 text-red-600 hover:bg-red-200"
                            title="Remove from trip"
                          >
                            <X className="w-4 h-4 md:w-5 md:h-5" />
                          </button>
                        </div>
                      </div>
                      
                      {/* Transportation Selector (between places) - segment index is placeGlobalIndex + 1 since segment 0 is Start→Place[0] */}
                      {placeGlobalIndex < tripPlaces.length - 1 && (() => {
                        const segmentIndex = placeGlobalIndex + 1; // +1 because segment 0 is Start→Place[0]
                        const fromPlace = tripPlaces[placeGlobalIndex];
                        const toPlace = tripPlaces[placeGlobalIndex + 1];
                        const fromCoords: [number, number] = [
                          fromPlace.geo_point?.lon || fromPlace.geo_point?.lng || 0,
                          fromPlace.geo_point?.lat || 0
                        ];
                        const toCoords: [number, number] = [
                          toPlace.geo_point?.lon || toPlace.geo_point?.lng || 0,
                          toPlace.geo_point?.lat || 0
                        ];
                        return renderTransportSelector(segmentIndex, fromCoords, toCoords);
                      })()}
                    </div>
                  );
                });
              })()}
            </div>

              {/* Trip End Section - Only for last day */}
              {selectedDayIndex !== null && selectedDayIndex === Array.from(dayLocations.keys()).reduce((max, idx) => Math.max(max, idx), -1) && isTripEnded && tripEndLocation && (
                <div className="mt-2 md:mt-4 pt-2 md:pt-4 border-t-2 border-dashed border-gray-300">
                  <div className="flex items-center gap-1.5 md:gap-2 mb-2 md:mb-3">
                    <Flag className="w-4 h-4 md:w-5 md:h-5 text-green-600" />
                    <h4 className="text-xs md:text-sm font-bold text-gray-900">Trip Ends</h4>
                  </div>
                  <p className="text-xs md:text-sm text-gray-700 mb-2 md:mb-3">{tripEndLocation.name}</p>
                  
                  {/* Transport Mode Section */}
                  <div className="flex justify-between items-center gap-2 py-1.5 md:py-2 px-2 md:px-4">
                    {/* Left: Transport Mode Dropdown */}
                    <div className="relative">
                      <button
                        onClick={() => setExpandedTransportIndex(expandedTransportIndex === -1 ? null : -1)}
                        className={`flex items-center gap-1.5 md:gap-2 px-2 md:px-3 py-1 md:py-1.5 rounded-full border md:border-2 transition-all ${
                          expandedTransportIndex === -1
                            ? 'bg-blue-50 border-blue-400'
                            : 'bg-white border-gray-300 hover:border-gray-400'
                        }`}
                      >
                        {getTransportIcon(tripEndTransportMode)}
                        <span className="text-[10px] md:text-xs font-medium text-gray-700">
                            {getTransportLabel(tripEndTransportMode)}
                          </span>
                        <ChevronDown className={`w-2.5 h-2.5 md:w-3 md:h-3 text-gray-500 transition-transform ${
                          expandedTransportIndex === -1 ? 'rotate-180' : ''
                        }`} />
                      </button>

                      {/* Transport Options Dropdown */}
                      {expandedTransportIndex === -1 && (
                        <div className="absolute top-full left-0 mt-1 md:mt-2 bg-white rounded-lg shadow-lg border border-gray-200 z-10 py-0.5 md:py-1 min-w-[100px] md:min-w-[120px]">
                          {((): TransportMode[] => {
                            const modes: TransportMode[] = ['car', 'bus'];
                            if (tripEndTrainAvailable) {
                              modes.push('train');
                            }
                            return modes;
                          })().map((mode) => (
                            <button
                              key={mode}
                              onClick={() => {
                                setTripEndTransportMode(mode);
                                setExpandedTransportIndex(null);
                              }}
                              className={`w-full flex items-center gap-1.5 md:gap-2 px-2 md:px-3 py-1.5 md:py-2 hover:bg-gray-50 transition-colors ${
                                tripEndTransportMode === mode ? 'bg-blue-50' : ''
                              }`}
                            >
                              {getTransportIcon(mode)}
                              <span className="text-xs md:text-sm font-medium text-gray-700">
                                {getTransportLabel(mode)}
                              </span>
                              {tripEndTransportMode === mode && (
                                <Check className="w-3 h-3 md:w-4 md:h-4 text-blue-600 ml-auto" />
                              )}
                            </button>
                          ))}
                        </div>
                      )}
                    </div>

                    {/* Center-Left: Distance/Time Info (for car mode only) */}
                    {tripEndRouteSegment && tripEndTransportMode === 'car' && (
                      <div className="flex items-center text-[9px] md:text-[10px] text-gray-600 font-medium">
                        <span>
                          {tripEndRouteSegment.isApproximate && '~'}
                          {tripEndRouteSegment.distance.toFixed(1)}km • {tripEndRouteSegment.duration}min
                        </span>
                      </div>
                    )}

                    {/* Center: Train Available Badge */}
                    {tripEndTrainAvailable && (
                      <div className="flex items-center gap-1.5 px-2 md:px-3 py-1 md:py-1.5 bg-amber-50 border border-amber-300 rounded-full">
                        <TrainTrack className="w-3 h-3 md:w-4 md:h-4 text-amber-700" />
                        <span className="text-[10px] md:text-xs font-medium text-amber-700">Train Available</span>
                      </div>
                    )}
                  </div>
                </div>
              )}

            {/* Action Buttons */}
            <div className="pt-2.5 md:pt-4 border-t border-gray-200">
              {/* Mobile: Only Add More Places */}
              <div className="relative w-full md:hidden add-more-places-dropdown-container">
                <button
                  onClick={() => {
                    const locations = dayLocations.get(selectedDayIndex!) || [];
                    if (locations.length > 1) {
                      setShowAddMorePlacesDropdown(!showAddMorePlacesDropdown);
                    } else {
                      setCurrentView('multi-day-trip-day-destinations');
                    }
                  }}
                  className="w-full flex items-center justify-center gap-1 bg-gray-100 hover:bg-gray-200 text-gray-700 font-semibold px-2 py-1.5 rounded-lg text-xs transition-colors"
                >
                  <span>Add More Places</span>
                  {(dayLocations.get(selectedDayIndex!) || []).length > 1 && (
                    <ChevronDown className={`w-3 h-3 transition-transform ${showAddMorePlacesDropdown ? 'rotate-180' : ''}`} />
                  )}
                </button>
                
                {/* Dropdown for multiple locations */}
                {showAddMorePlacesDropdown && (dayLocations.get(selectedDayIndex!) || []).length > 1 && (
                  <div className="absolute top-full left-0 right-0 mt-1 bg-white rounded-lg shadow-lg border border-gray-200 z-50 max-h-48 overflow-y-auto">
                    {(dayLocations.get(selectedDayIndex!) || []).map((location, index) => (
                      <button
                        key={index}
                        onClick={() => handleAddMorePlacesLocationSelection(index)}
                        className="w-full text-left px-3 py-2 hover:bg-gray-50 transition-colors border-b border-gray-100 last:border-b-0"
                      >
                        <span className="font-medium text-gray-900 text-xs">Location {index + 1}:</span>{' '}
                        <span className="text-gray-600 text-xs">
                          {location.name || `[${location.coordinates[0].toFixed(4)}, ${location.coordinates[1].toFixed(4)}]`}
                        </span>
                      </button>
                    ))}
                  </div>
                )}
              </div>
              
              {/* Desktop: Add More Places + Continue */}
              <div className="hidden md:flex gap-2">
                <div className="relative flex-1 add-more-places-dropdown-container">
                  <button
                    onClick={() => {
                      const locations = dayLocations.get(selectedDayIndex!) || [];
                      if (locations.length > 1) {
                        setShowAddMorePlacesDropdown(!showAddMorePlacesDropdown);
                      } else {
                        setCurrentView('multi-day-trip-day-destinations');
                      }
                    }}
                    className="w-full flex items-center justify-center gap-1.5 bg-gray-100 hover:bg-gray-200 text-gray-700 font-semibold px-4 py-2.5 rounded-lg text-base transition-colors"
                  >
                    <span>Add More Places</span>
                    {(dayLocations.get(selectedDayIndex!) || []).length > 1 && (
                      <ChevronDown className={`w-4 h-4 transition-transform ${showAddMorePlacesDropdown ? 'rotate-180' : ''}`} />
                    )}
                  </button>
                  
                  {/* Dropdown for multiple locations */}
                  {showAddMorePlacesDropdown && (dayLocations.get(selectedDayIndex!) || []).length > 1 && (
                    <div className="absolute top-full left-0 right-0 mt-1 bg-white rounded-lg shadow-lg border border-gray-200 z-50 max-h-60 overflow-y-auto">
                      {(dayLocations.get(selectedDayIndex!) || []).map((location, index) => (
                        <button
                          key={index}
                          onClick={() => handleAddMorePlacesLocationSelection(index)}
                          className="w-full text-left px-4 py-2.5 hover:bg-gray-50 transition-colors border-b border-gray-100 last:border-b-0"
                        >
                          <span className="font-medium text-gray-900 text-sm">Location {index + 1}:</span>{' '}
                          <span className="text-gray-600 text-sm">
                            {location.name || `[${location.coordinates[0].toFixed(4)}, ${location.coordinates[1].toFixed(4)}]`}
                          </span>
                        </button>
                      ))}
                    </div>
                  )}
                </div>
              <button
                onClick={() => {
                  if (selectedDayIndex === null) return;
                  
                  // Save finalized day data
                  const currentDayTransport = dayTransportation.get(selectedDayIndex) || new Map();
                  const currentDaySegments = dayRouteSegments.get(selectedDayIndex) || [];
                  
                  // Determine start location for this day
                  let dayStartLocation: QuickLocation | null = null;
                  if (selectedDayIndex === 0) {
                    dayStartLocation = multiDayStartLocation;
                  } else {
                    // Find the actual previous day, accounting for merged blocks
                    let previousDayData: FinalizedDayData | undefined;
                    let previousDayPlaces: PlaceItem[] | undefined;
                    
                    // Check if the previous day is part of a merged block
                    const previousDayIndex = selectedDayIndex - 1;
                    let foundInBlock = false;
                    
                    for (const [blockStartIndex, blockIndices] of Array.from(combinedDayBlocks.entries())) {
                      if (blockIndices.includes(previousDayIndex)) {
                        // Previous day is in this merged block
                        previousDayData = finalizedDays.get(blockStartIndex);
                        previousDayPlaces = previousDayData?.places;
                        foundInBlock = true;
                        break;
                      }
                    }
                    
                    // If not in a merged block, check regular dayPlaces and finalizedDays
                    if (!foundInBlock) {
                      previousDayPlaces = dayPlaces.get(previousDayIndex);
                      if (!previousDayPlaces) {
                        previousDayData = finalizedDays.get(previousDayIndex);
                        previousDayPlaces = previousDayData?.places;
                      }
                    }
                    
                    if (previousDayPlaces && previousDayPlaces.length > 0) {
                      const lastPlace = previousDayPlaces[previousDayPlaces.length - 1];
                      dayStartLocation = {
                        name: lastPlace.name,
                        coordinates: [
                          lastPlace.geo_point?.lon || lastPlace.geo_point?.lng || 0,
                          lastPlace.geo_point?.lat || 0
                        ]
                      };
                    }
                  }
                  
                  if (!dayStartLocation) {
                    return;
                  }
                  
                  // Save finalized day data
                  const isLastDay = selectedDayIndex === Array.from(dayLocations.keys()).reduce((max, idx) => Math.max(max, idx), -1);
                  
                  // Get existing data before updating
                  const existingData = finalizedDays.get(selectedDayIndex);
                  
                  // Merge places: keep places from other locations + updated places from current location
                  // BUT if currentPrimaryLocationIndex is -1 (viewing all), use tripPlaces directly without merging
                  let mergedPlaces: any[];
                  if (currentPrimaryLocationIndex === -1) {
                    // Viewing all locations - tripPlaces already has all places, no merge needed
                    mergedPlaces = [...tripPlaces];
                  } else {
                    // Editing a specific location - merge with places from other locations
                    // IMPORTANT: Filter tripPlaces to only include current location's places to avoid duplicates
                    // (tripPlaces may contain ALL places for route calculation purposes)
                    const existingPlacesFromOtherLocations = existingData?.places.filter(
                      p => (p.primaryLocationIndex || 0) !== currentPrimaryLocationIndex
                    ) || [];
                    const tripPlacesForCurrentLocation = tripPlaces.filter(
                      p => (p.primaryLocationIndex || 0) === currentPrimaryLocationIndex
                    );
                    mergedPlaces = [...existingPlacesFromOtherLocations, ...tripPlacesForCurrentLocation];
                  }
                  
                  // CRITICAL: Sort merged places by primaryLocationIndex to match UI/transport order
                  // This ensures transportation segment indices align with place order in backend
                  mergedPlaces.sort((a, b) => {
                    const indexA = a.primaryLocationIndex || 0;
                    const indexB = b.primaryLocationIndex || 0;
                    return indexA - indexB;
                  });
                  
                  setFinalizedDays(prev => {
                    const newMap = new Map(prev);
                    
                    // Get primary locations for this day (supports multiple locations)
                    const dayPrimaryLocations = dayLocations.get(selectedDayIndex) || (dayStartLocation ? [dayStartLocation] : []);
                    
                    const dayData: FinalizedDayData = {
                      places: mergedPlaces,  // Use merged places to preserve all locations
                      transportation: new Map(currentDayTransport),
                      routeSegments: [...currentDaySegments],
                      primaryLocations: dayPrimaryLocations,  // Store all primary locations
                      startLocation: dayPrimaryLocations[0] || dayStartLocation,  // Keep for backwards compatibility
                      // Preserve merge metadata if it exists
                      ...(existingData?.isMultiDayBlock && {
                        isMultiDayBlock: existingData.isMultiDayBlock,
                        blockDayIndices: existingData.blockDayIndices,
                        blockStartDate: existingData.blockStartDate,
                        blockEndDate: existingData.blockEndDate
                      })
                    };
                    
                    // If this is the last day and trip is ended, include trip end data
                    if (isLastDay && isTripEnded && tripEndLocation) {
                      dayData.endLocation = tripEndLocation;
                      dayData.endTransportMode = tripEndTransportMode;
                      dayData.endRouteSegment = tripEndRouteSegment || undefined;
                    }
                    
                    newMap.set(selectedDayIndex, dayData);
                    return newMap;
                  });
                  
                  // Mark as having unsaved changes
                  setHasUnsavedChanges(true);
                  
                  // If there's a next day already finalized, update its first route segment
                  // to start from this day's new last place (async fetch for accurate route)
                  const nextDayIndex = selectedDayIndex + 1;
                  const nextDayData = finalizedDays.get(nextDayIndex);
                  if (nextDayData && nextDayData.places.length > 0 && tripPlaces.length > 0) {
                    const newLastPlace = tripPlaces[tripPlaces.length - 1];
                    const nextFirstPlace = nextDayData.places[0];
                    
                    // Check if the connection needs updating
                    const oldLastPlace = existingData?.places[existingData.places.length - 1];
                    const lastPlaceChanged = !oldLastPlace || 
                      oldLastPlace.id !== newLastPlace.id ||
                      oldLastPlace.name !== newLastPlace.name;
                    
                    if (lastPlaceChanged && nextDayData.routeSegments.length > 0) {
                      // Recalculate route from new last place to next day's first place
                      (async () => {
                        try {
                          const fromLng = newLastPlace.geo_point?.lon || newLastPlace.geo_point?.lng || 0;
                          const fromLat = newLastPlace.geo_point?.lat || 0;
                          const toLng = nextFirstPlace.geo_point?.lon || nextFirstPlace.geo_point?.lng || 0;
                          const toLat = nextFirstPlace.geo_point?.lat || 0;
                          
                          const response = await fetch(
                            `${API_BASE_URL}/api/mapbox/directions?fromLng=${fromLng}&fromLat=${fromLat}&toLng=${toLng}&toLat=${toLat}`
                          );
                          const data = await response.json();
                          
                          if (data.routes && data.routes[0]) {
                            const route = data.routes[0];
                            const newSegment: RouteSegment = {
                              fromIndex: 0,
                              toIndex: 1,
                              geometry: route.geometry.coordinates,
                              distance: route.distance / 1000,
                              duration: Math.round(route.duration / 60),
                              isApproximate: false
                            };
                            
                            // Update the next day's Segment 0
                            setFinalizedDays(prev => {
                              const newMap = new Map(prev);
                              const nextDay = newMap.get(nextDayIndex);
                              if (nextDay) {
                                const updatedSegments = [...nextDay.routeSegments];
                                updatedSegments[0] = newSegment;
                                newMap.set(nextDayIndex, {
                                  ...nextDay,
                                  routeSegments: updatedSegments
                                });
                              }
                              return newMap;
                            });
                            // Mark as having unsaved changes
                            setHasUnsavedChanges(true);
                          }
                        } catch (error) {
                        }
                      })();
                    }
                  }
                  
                  const dayLabel = existingData?.blockDayIndices && existingData.blockDayIndices.length > 1
                    ? `Days ${existingData.blockDayIndices[0] + 1}-${existingData.blockDayIndices[existingData.blockDayIndices.length - 1] + 1}`
                    : `Day ${selectedDayIndex + 1}`;
                  
                    // Clear tripPlaces for next day
                    setTripPlaces([]);
                  setSelectedDayIndex(null);
                    
                    // Navigate back to itinerary (will trigger Sri Lanka zoom effect)
                    handleNavigation('multi-day-trip-itinerary');
                }}
                  className="flex-1 bg-gradient-to-r from-purple-600 to-pink-600 hover:from-purple-700 hover:to-pink-700 text-white font-semibold px-4 py-2.5 rounded-lg transition-colors text-base"
              >
                Continue
              </button>
              </div>
            </div>
          </div>
        );

      case 'saved-day-trips':
        return (
          <div className="space-y-4">
            {loadingTrips ? (
              <div className="text-center py-12">
                <p className="text-gray-600">Loading trips...</p>
              </div>
            ) : savedTrips.filter(t => t.tripType === 'day-trip').length === 0 ? (
              <div className="text-center py-12 bg-gray-50 rounded-lg">
                <p className="text-gray-600 mb-4">No saved day trips yet</p>
                <button
                  onClick={() => handleNavigation('trip-options')}
                  className="bg-blue-600 hover:bg-blue-700 text-white px-6 py-2 rounded-lg transition-colors"
                >
                  Plan Your First Trip
                </button>
              </div>
            ) : (
              <div className="space-y-3">
                {savedTrips
                  .filter(t => t.tripType === 'day-trip')
                  .sort((a, b) => b.updatedAt.seconds - a.updatedAt.seconds)
                  .map(trip => (
                    <div
                      key={trip.id}
                      className="bg-white rounded-lg border-2 border-gray-200 p-4 hover:border-blue-300 transition-all cursor-pointer"
                      onClick={() => handleLoadTrip(trip.id)}
                    >
                      <div className="flex items-start justify-between">
                        <div className="flex-1">
                          <h4 className="font-semibold text-gray-900 mb-1">
                            {trip.tripName}
                          </h4>
                          <p className="text-xs text-gray-600">
                            {trip.dayTrip?.places.length || 0} places
                          </p>
                          <p className="text-xs text-gray-500 mt-1">
                            Last updated: {new Date(trip.updatedAt.seconds * 1000).toLocaleDateString()}
                          </p>
                        </div>
                        <button
                          onClick={(e) => {
                            e.stopPropagation();
                            if (window.confirm('Delete this trip?')) {
                              handleDeleteTrip(trip.id);
                            }
                          }}
                          className="flex items-center justify-center w-8 h-8 rounded-lg bg-red-50 text-red-600 hover:bg-red-100 hover:text-red-700 transition-all duration-200 flex-shrink-0"
                        >
                          <X className="w-4 h-4" />
                        </button>
                      </div>
                    </div>
                  ))}
              </div>
            )}
          </div>
        );

      case 'saved-multi-day-trips':
        return (
          <div className="space-y-4">
            {loadingTrips ? (
              <div className="text-center py-12">
                <p className="text-gray-600">Loading trips...</p>
              </div>
            ) : savedTrips.filter(t => t.tripType === 'multi-day-trip').length === 0 ? (
              <div className="text-center py-12 bg-gray-50 rounded-lg">
                <p className="text-gray-600 mb-4">No saved multi-day trips yet</p>
                <button
                  onClick={() => handleNavigation('trip-options')}
                  className="bg-purple-600 hover:bg-purple-700 text-white px-6 py-2 rounded-lg transition-colors"
                >
                  Plan Your First Trip
                </button>
              </div>
            ) : (
              <div className="space-y-3">
                {savedTrips
                  .filter(t => t.tripType === 'multi-day-trip')
                  .sort((a, b) => b.updatedAt.seconds - a.updatedAt.seconds)
                  .map(trip => (
                    <div
                      key={trip.id}
                      className="bg-white rounded-lg border-2 border-gray-200 p-4 hover:border-purple-300 transition-all cursor-pointer"
                      onClick={() => handleLoadTrip(trip.id)}
                    >
                      <div className="flex items-start justify-between">
                        <div className="flex-1">
                          <h4 className="font-semibold text-gray-900 mb-1">
                            {trip.tripName}
                          </h4>
                          {trip.multiDayTrip && (
                            <>
                              <p className="text-xs text-gray-600">
                                {trip.multiDayTrip.startDate.toDate().toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })} - {trip.multiDayTrip.endDate.toDate().toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })}
                              </p>
                              <p className="text-xs text-gray-600 mt-0.5">
                                {Object.keys(trip.multiDayTrip.finalizedDays || {}).length} days planned
                              </p>
                            </>
                          )}
                          <p className="text-xs text-gray-500 mt-1">
                            Last updated: {new Date(trip.updatedAt.seconds * 1000).toLocaleDateString()}
                          </p>
                        </div>
                        <button
                          onClick={(e) => {
                            e.stopPropagation();
                            if (window.confirm('Delete this trip?')) {
                              handleDeleteTrip(trip.id);
                            }
                          }}
                          className="flex items-center justify-center w-8 h-8 rounded-lg bg-red-50 text-red-600 hover:bg-red-100 hover:text-red-700 transition-all duration-200 flex-shrink-0"
                        >
                          <X className="w-4 h-4" />
                        </button>
                      </div>
                    </div>
                  ))}
              </div>
            )}
          </div>
        );

      case 'quick-quotes':
        return (
          <div className="space-y-4">
            {loadingTrips ? (
              <div className="text-center py-12">
                <p className="text-gray-600">Loading quotes...</p>
              </div>
            ) : savedTrips.filter(t => t.isLazyQuote).length === 0 ? (
              <div className="text-center py-12 bg-gray-50 rounded-lg">
                <p className="text-gray-600 mb-4">No quick quotes yet</p>
                <button
                  onClick={() => setShowLazyQuoteForm(true)}
                  className="bg-gradient-to-r from-purple-600 to-blue-600 hover:from-purple-700 hover:to-blue-700 text-white px-6 py-2 rounded-lg transition-colors"
                >
                  Request a Quote
                </button>
              </div>
            ) : (
              <div className="space-y-3">
                {savedTrips
                  .filter(t => t.isLazyQuote)
                  .sort((a, b) => b.updatedAt.seconds - a.updatedAt.seconds)
                  .map(quote => {
                    // Determine service type badge
                    const serviceType = quote.quickQuote?.serviceType || 'Unknown';
                    const serviceBadge = serviceType === 'full_trip' ? 
                      { label: 'Full Trip Planning', color: 'bg-purple-100 text-purple-700' } : 
                      { label: 'Driver Only', color: 'bg-blue-100 text-blue-700' };
                    
                    return (
                      <div
                        key={quote.id}
                        className="bg-white rounded-lg border-2 border-gray-200 p-4 hover:border-purple-300 transition-all cursor-pointer"
                        onClick={() => {
                          setSelectedQuickQuote(quote);
                        }}
                      >
                        <div className="flex items-start justify-between">
                          <div className="flex-1">
                            <div className="flex items-center gap-2 mb-2">
                              <h4 className="font-semibold text-gray-900">
                                {quote.tripName}
                              </h4>
                              <span className={`text-xs font-medium px-2 py-1 rounded-full ${serviceBadge.color}`}>
                                {serviceBadge.label}
                              </span>
                            </div>
                            {quote.quickQuote?.dates && (
                              <p className="text-xs text-gray-600">
                                {quote.quickQuote.dates.arrivalDate || quote.quickQuote.dates.pickupDate} - {quote.quickQuote.dates.departureDate || quote.quickQuote.dates.dropOffDate}
                              </p>
                            )}
                            {quote.quickQuote?.travelers && (
                              <p className="text-xs text-gray-600 mt-0.5">
                                {quote.quickQuote.travelers.adults} Adults
                                {quote.quickQuote.travelers.children > 0 && `, ${quote.quickQuote.travelers.children} Children`}
                                {quote.quickQuote.travelers.infants > 0 && `, ${quote.quickQuote.travelers.infants} Infants`}
                              </p>
                            )}
                            <p className="text-xs text-gray-500 mt-1">
                              Requested: {new Date(quote.createdAt.seconds * 1000).toLocaleDateString()}
                            </p>
                          </div>
                          <button
                            onClick={(e) => {
                              e.stopPropagation();
                              if (window.confirm('Delete this quote?')) {
                                handleDeleteTrip(quote.id);
                              }
                            }}
                            className="flex items-center justify-center w-8 h-8 rounded-lg bg-red-50 text-red-600 hover:bg-red-100 hover:text-red-700 transition-all duration-200 flex-shrink-0"
                          >
                            <X className="w-4 h-4" />
                          </button>
                        </div>
                      </div>
                    );
                  })}
              </div>
            )}
          </div>
        );

      default:
        return null;
    }
  };

  return (
    <div className="flex h-full bg-gray-50">
      {/* Inject custom popup styles */}
      <style>{popupStyles}</style>
      
      {/* Desktop Layout: Left Panel (50%) + Right Map (50%) */}
      <div className="hidden md:flex w-full h-full">
        {/* Left Panel */}
        <div className="w-1/2 border-r border-gray-200 bg-white flex flex-col overflow-hidden">
          {/* Panel Header */}
          <div className="bg-white border-b border-gray-200 p-4 shadow-sm">
            <div className="flex items-center justify-between">
              <div className="flex-1">
                <h1 className="text-2xl font-bold text-gray-900">{getDrawerHeader().title}</h1>
                <p className="text-sm text-gray-600 mt-1">
                  {getDrawerHeader().subtitle}
                </p>
                {authState.user && (
                  <div className="text-sm text-gray-500 mt-2">
                    Welcome, {authState.user.profile?.firstName || authState.user.profile?.username || authState.user.email}
                  </div>
                )}
              </div>
              <div className="flex items-center gap-3">
                {/* Unselect All Button - for destination pickers */}
                {(currentView === 'day-trip-destinations' || currentView === 'multi-day-trip-day-destinations') && tripPlaces.length > 0 && (
                  <button
                    onClick={() => setTripPlaces([])}
                    className="bg-red-50 hover:bg-red-100 text-red-600 hover:text-red-700 font-semibold px-5 py-2.5 rounded-lg transition-colors border border-red-200"
                  >
                    Unselect All
                  </button>
                )}
                {/* Save Button */}
                {shouldShowSaveButton() && authState.user && (
                  <button
                    onClick={handleSaveTrip}
                    className="bg-green-600 hover:bg-green-700 text-white font-semibold px-2 py-1.5 lg:px-5 lg:py-2.5 rounded-lg transition-colors flex items-center gap-1 lg:gap-2 text-sm lg:text-base whitespace-nowrap"
                  >
                    <Save className="w-3.5 h-3.5 lg:w-4 lg:h-4" />
                    <span className="hidden lg:inline">Save Trip</span>
                    <span className="lg:hidden">Save</span>
                  </button>
                )}
                {/* Generate Trip Summary Button - Desktop Header */}
                {currentView === 'multi-day-trip-itinerary' && currentTripId && authState.user && (
                  <button
                    onClick={handleGenerateSummaryClick}
                    disabled={isGeneratingSummary}
                    className="bg-gradient-to-r from-orange-500 to-orange-600 hover:from-orange-600 hover:to-orange-700 disabled:from-gray-400 disabled:to-gray-500 text-white font-semibold px-2 py-1.5 lg:px-5 lg:py-2.5 rounded-lg transition-colors flex items-center gap-1 lg:gap-2 shadow-md hover:shadow-lg text-sm lg:text-base whitespace-nowrap"
                  >
                    {isGeneratingSummary ? (
                      <>
                        <svg className="w-3.5 h-3.5 lg:w-4 lg:h-4 animate-spin" fill="none" viewBox="0 0 24 24">
                          <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                          <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                        </svg>
                        <span className="hidden lg:inline">Generating...</span>
                        <span className="lg:hidden">Gen...</span>
                      </>
                    ) : isTripSummaryCurrent() ? (
                      <>
                        <Eye className="w-3.5 h-3.5 lg:w-4 lg:h-4" />
                        <span className="hidden lg:inline">View Trip Summary</span>
                        <span className="lg:hidden">View</span>
                      </>
                    ) : (
                      <>
                        <Sparkles className="w-3.5 h-3.5 lg:w-4 lg:h-4" />
                        <span className="hidden lg:inline">Generate Trip Summary</span>
                        <span className="lg:hidden">Generate</span>
                      </>
                    )}
                  </button>
                )}
                {/* Get Quote Button - Desktop Header */}
                {currentView === 'multi-day-trip-itinerary' && currentTripId && authState.user && (
                  <button
                    onClick={handleGenerateSummaryClick}
                    className="bg-gradient-to-r from-purple-600 to-blue-600 hover:from-purple-700 hover:to-blue-700 text-white font-semibold px-2 py-1.5 lg:px-5 lg:py-2.5 rounded-lg transition-colors flex items-center gap-1 lg:gap-2 shadow-md hover:shadow-lg text-sm lg:text-base whitespace-nowrap"
                  >
                    <MessageSquare className="w-3.5 h-3.5 lg:w-4 lg:h-4" />
                    <span className="hidden lg:inline">Get Quote</span>
                    <span className="lg:hidden">Quote</span>
                  </button>
                )}
                {/* Continue Button */}
                {currentView === 'day-trip-destinations' && tripPlaces.length > 0 && (
                  <button
                    onClick={() => handleNavigation('my-day-trip')}
                    className="bg-blue-600 hover:bg-blue-700 text-white font-semibold px-6 py-2.5 rounded-lg transition-colors flex items-center gap-2"
                  >
                    Continue
                    <span className="bg-white text-blue-600 px-2 py-0.5 rounded-full text-sm font-bold">
                      {tripPlaces.length}
                    </span>
                  </button>
                )}
              </div>
            </div>
          </div>

          {/* Panel Content */}
          <div className="flex-1 overflow-y-auto p-4">
            {/* Back button (shown when not on home view) */}
            {currentView !== 'home' && (
              <button
                onClick={navigateBack}
                className="flex items-center text-gray-600 hover:text-gray-900 mb-4 transition-colors"
              >
                <ArrowLeft className="w-5 h-5 mr-2" />
                <span className="font-medium">Back</span>
              </button>
            )}
            
            {renderDrawerContent()}
          </div>
        </div>

        {/* Right Map */}
        <div className="w-1/2 relative">
          {error ? (
            <div className="flex items-center justify-center h-full">
              <div className="text-center p-6">
                <div className="text-red-500 text-lg font-semibold mb-2">
                  {error}
                </div>
                <p className="text-gray-600 text-sm">
                  Please check your configuration and try again.
                </p>
              </div>
            </div>
          ) : (
            <>
              <div
                ref={mapContainerDesktopRef}
                className="absolute inset-0 w-full h-full"
              />
              
              {!mapLoaded && (
                <div className="absolute inset-0 flex items-center justify-center bg-gray-100 bg-opacity-75">
                  <div className="text-center">
                    <div className="inline-block animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mb-4"></div>
                    <p className="text-gray-600">Loading map...</p>
                  </div>
                </div>
              )}

              {/* Day/Trip End Location Selection Overlay - Desktop */}
              {isSelectingDayLocation && (isSelectingTripEndLocation || selectedDayIndex !== null) && (
                <div className="absolute bottom-6 left-6 right-auto z-30 flex gap-2 max-w-md">
                  <button
                    onClick={() => {
                      setIsSelectingDayLocation(false);
                      if (isSelectingTripEndLocation) {
                        setIsSelectingTripEndLocation(false);
                      }
                    }}
                    className="bg-white hover:bg-gray-50 text-gray-700 font-medium py-2 px-4 rounded-lg shadow-lg border-2 border-gray-200 transition-colors text-sm"
                  >
                    Cancel
                  </button>
                  <button
                    onClick={() => {
                      // Get the correct marker based on selection type
                      const marker = isSelectingTripEndLocation ? tempTripEndMarkerRef.current : tempDayMarkerRef.current;
                      
                      if (marker) {
                        const lngLat = marker.getLngLat();
                        
                        // Validate location is within Sri Lanka
                        const lat = lngLat.lat;
                        const lng = lngLat.lng;
                        
                        // Sri Lanka boundaries (approximate)
                        const SRI_LANKA_BOUNDS = {
                          minLat: 5.9,
                          maxLat: 9.9,
                          minLng: 79.4,
                          maxLng: 82.0
                        };
                        
                        if (lat < SRI_LANKA_BOUNDS.minLat || lat > SRI_LANKA_BOUNDS.maxLat ||
                            lng < SRI_LANKA_BOUNDS.minLng || lng > SRI_LANKA_BOUNDS.maxLng) {
                          showToast('Please select a location within Sri Lanka', 'error', 4000);
                          return;
                        }
                        
                        const location = {
                          name: `${lngLat.lat.toFixed(4)}, ${lngLat.lng.toFixed(4)}`,
                          coordinates: [lngLat.lng, lngLat.lat] as [number, number]
                        };
                        
                        if (isSelectingTripEndLocation) {
                          // Set trip end location
                          setTripEndLocation(location);
                          setIsTripEnded(true);
                          setIsSelectingTripEndLocation(false);
                          // Clear old route state before new calculation
                          setTripEndRouteSegment(null);
                          setTripEndTrainAvailable(false);
                          // Add trip end marker
                          addTripEndMarker(location.coordinates);
                          // Clear selected day index so the day card border returns to grey
                          setSelectedDayIndex(null);
                        } else if (selectedDayIndex !== null) {
                          // Set day location
                          setSelectedDayLocation(location);
                          
                          // Check if adding additional location or first location
                          if (showAddLocationSlider) {
                            // Adding additional primary location - append to array
                            setDayLocations(prev => {
                              const newMap = new Map(prev);
                              const existing = newMap.get(selectedDayIndex) || [];
                              newMap.set(selectedDayIndex, [...existing, location]);
                              return newMap;
                            });
                            setCurrentPrimaryLocationIndex((dayLocations.get(selectedDayIndex)?.length || 0));
                            // Keep showAddLocationSlider true - the slider will now show Change/Search Places buttons
                          } else {
                            // First primary location - initialize array
                            setDayLocations(prev => {
                              const newMap = new Map(prev);
                              newMap.set(selectedDayIndex, [location]);
                              return newMap;
                            });
                            setCurrentPrimaryLocationIndex(0);
                          }
                          
                          // If resetting location, auto-open destination picker
                          if (isResettingDayLocation) {
                            setIsResettingDayLocation(false);
                            // Automatically navigate to destination picker view
                            setTimeout(() => {
                              setCurrentView('multi-day-trip-day-destinations');
                            }, 100);
                          }
                        }
                        setIsSelectingDayLocation(false);
                      }
                    }}
                    className="bg-gradient-to-r from-purple-600 to-pink-600 hover:from-purple-700 hover:to-pink-700 text-white font-medium py-2 px-4 rounded-lg shadow-lg transition-colors text-sm"
                  >
                    Confirm Location
                  </button>
                </div>
              )}

              {/* Start Location Selection Overlay - Desktop */}
              {isSelectingStartLocation && (
                <div className="absolute bottom-6 left-6 right-auto z-30 flex gap-2 max-w-md">
                  <button
                    onClick={() => setIsSelectingStartLocation(false)}
                    className="bg-white hover:bg-gray-50 text-gray-700 font-medium py-2 px-4 rounded-lg shadow-lg border-2 border-gray-200 transition-colors text-sm"
                  >
                    Cancel
                  </button>
                  <button
                    onClick={() => {
                      if (tempStartMarkerRef.current) {
                        const lngLat = tempStartMarkerRef.current.getLngLat();
                        
                        // Validate location is within Sri Lanka
                        const lat = lngLat.lat;
                        const lng = lngLat.lng;
                        
                        // Sri Lanka boundaries (approximate)
                        const SRI_LANKA_BOUNDS = {
                          minLat: 5.9,
                          maxLat: 9.9,
                          minLng: 79.4,
                          maxLng: 82.0
                        };
                        
                        if (lat < SRI_LANKA_BOUNDS.minLat || lat > SRI_LANKA_BOUNDS.maxLat ||
                            lng < SRI_LANKA_BOUNDS.minLng || lng > SRI_LANKA_BOUNDS.maxLng) {
                          showToast('Please select a location within Sri Lanka', 'error', 4000);
                          return;
                        }
                        
                        setMultiDayStartLocation({
                          name: `${lngLat.lat.toFixed(4)}, ${lngLat.lng.toFixed(4)}`,
                          coordinates: [lngLat.lng, lngLat.lat]
                        });
                        setIsSelectingStartLocation(false);
                      }
                    }}
                    className="bg-gradient-to-r from-purple-600 to-pink-600 hover:from-purple-700 hover:to-pink-700 text-white font-medium py-2 px-4 rounded-lg shadow-lg transition-colors text-sm"
                  >
                    Confirm Location
                  </button>
                </div>
              )}
            </>
          )}
        </div>
      </div>

      {/* Mobile Layout: Full Map + Bottom Drawer */}
      <div className="md:hidden flex flex-col w-full h-full relative">
        {null}
        {/* Full Screen Map - Fixed position, not affected by drawer */}
        <div className="fixed inset-0 bottom-16 z-0">
          {error ? (
            <div className="flex items-center justify-center h-full">
              <div className="text-center p-6">
                <div className="text-red-500 text-lg font-semibold mb-2">
                  {error}
                </div>
                <p className="text-gray-600 text-sm">
                  Please check your configuration and try again.
                </p>
              </div>
            </div>
          ) : (
            <>
              <div
                ref={mapContainerMobileRef}
                className="absolute inset-0 w-full h-full"
              />
              
              {/* Place Cards Overlay - Mobile */}
              {!mapLoaded && (
                <div className="absolute inset-0 flex items-center justify-center bg-gray-100 bg-opacity-75">
                  <div className="text-center">
                    <div className="inline-block animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mb-4"></div>
                    <p className="text-gray-600">Loading map...</p>
                  </div>
                </div>
              )}

              {/* Day/Trip End Location Selection Overlay */}
              {isSelectingDayLocation && (isSelectingTripEndLocation || selectedDayIndex !== null) && (
                <div className="absolute top-4 left-4 right-auto z-30 flex gap-2 max-w-md">
                  <button
                    onClick={() => {
                      setIsSelectingDayLocation(false);
                      if (isSelectingTripEndLocation) {
                        setIsSelectingTripEndLocation(false);
                      }
                      if (!isDesktop) {
                        setHalf();
                      }
                    }}
                    className="bg-white hover:bg-gray-50 text-gray-700 font-medium py-2 px-4 rounded-lg shadow-lg border-2 border-gray-200 transition-colors text-sm"
                  >
                    Cancel
                  </button>
                  <button
                    onClick={() => {
                      // Get the correct marker based on selection type
                      const marker = isSelectingTripEndLocation ? tempTripEndMarkerRef.current : tempDayMarkerRef.current;
                      
                      if (marker) {
                        const lngLat = marker.getLngLat();
                        
                        // Validate location is within Sri Lanka
                        const lat = lngLat.lat;
                        const lng = lngLat.lng;
                        
                        // Sri Lanka boundaries (approximate)
                        const SRI_LANKA_BOUNDS = {
                          minLat: 5.9,
                          maxLat: 9.9,
                          minLng: 79.4,
                          maxLng: 82.0
                        };
                        
                        if (lat < SRI_LANKA_BOUNDS.minLat || lat > SRI_LANKA_BOUNDS.maxLat ||
                            lng < SRI_LANKA_BOUNDS.minLng || lng > SRI_LANKA_BOUNDS.maxLng) {
                          showToast('Please select a location within Sri Lanka', 'error', 4000);
                          return;
                        }
                        
                        const location = {
                          name: `${lngLat.lat.toFixed(4)}, ${lngLat.lng.toFixed(4)}`,
                          coordinates: [lngLat.lng, lngLat.lat] as [number, number]
                        };
                        
                        if (isSelectingTripEndLocation) {
                          // Set trip end location
                          setTripEndLocation(location);
                          setIsTripEnded(true);
                          setIsSelectingTripEndLocation(false);
                          // Clear old route state before new calculation
                          setTripEndRouteSegment(null);
                          setTripEndTrainAvailable(false);
                          // Add trip end marker
                          addTripEndMarker(location.coordinates);
                          // Clear selected day index so the day card border returns to grey
                          setSelectedDayIndex(null);
                        } else if (selectedDayIndex !== null) {
                          // Set day location
                          setSelectedDayLocation(location);
                          
                          // Check if adding additional location or first location
                          if (showAddLocationSlider) {
                            // Adding additional primary location - append to array
                            setDayLocations(prev => {
                              const newMap = new Map(prev);
                              const existing = newMap.get(selectedDayIndex) || [];
                              newMap.set(selectedDayIndex, [...existing, location]);
                              return newMap;
                            });
                            setCurrentPrimaryLocationIndex((dayLocations.get(selectedDayIndex)?.length || 0));
                            // Keep showAddLocationSlider true - the slider will now show Change/Search Places buttons
                          } else {
                            // First primary location - initialize array
                            setDayLocations(prev => {
                              const newMap = new Map(prev);
                              newMap.set(selectedDayIndex, [location]);
                              return newMap;
                            });
                            setCurrentPrimaryLocationIndex(0);
                          }
                          
                          // If resetting location, auto-open destination picker
                          if (isResettingDayLocation) {
                            setIsResettingDayLocation(false);
                            // Automatically navigate to destination picker view
                            setTimeout(() => {
                              setCurrentView('multi-day-trip-day-destinations');
                            }, 100);
                          }
                        }
                        setIsSelectingDayLocation(false);
                        if (!isDesktop) {
                          setHalf();
                        }
                      }
                    }}
                    className="bg-gradient-to-r from-purple-600 to-pink-600 hover:from-purple-700 hover:to-pink-700 text-white font-medium py-2 px-4 rounded-lg shadow-lg transition-colors text-sm"
                  >
                    Confirm Location
                  </button>
                </div>
              )}

              {/* Start Location Selection Overlay */}
              {isSelectingStartLocation && (
                <div className="absolute top-4 left-4 right-auto z-30 flex gap-2 max-w-md">
                  <button
                    onClick={() => {
                      setIsSelectingStartLocation(false);
                      if (!isDesktop) {
                        setHalf();
                      }
                    }}
                    className="bg-white hover:bg-gray-50 text-gray-700 font-medium py-2 px-4 rounded-lg shadow-lg border-2 border-gray-200 transition-colors text-sm"
                  >
                    Cancel
                  </button>
                  <button
                    onClick={() => {
                      if (tempStartMarkerRef.current) {
                        const lngLat = tempStartMarkerRef.current.getLngLat();
                        
                        // Validate location is within Sri Lanka
                        const lat = lngLat.lat;
                        const lng = lngLat.lng;
                        
                        // Sri Lanka boundaries (approximate)
                        const SRI_LANKA_BOUNDS = {
                          minLat: 5.9,
                          maxLat: 9.9,
                          minLng: 79.4,
                          maxLng: 82.0
                        };
                        
                        if (lat < SRI_LANKA_BOUNDS.minLat || lat > SRI_LANKA_BOUNDS.maxLat ||
                            lng < SRI_LANKA_BOUNDS.minLng || lng > SRI_LANKA_BOUNDS.maxLng) {
                          showToast('Please select a location within Sri Lanka', 'error', 4000);
                          return;
                        }
                        
                        setMultiDayStartLocation({
                          name: `${lngLat.lat.toFixed(4)}, ${lngLat.lng.toFixed(4)}`,
                          coordinates: [lngLat.lng, lngLat.lat]
                        });
                        setIsSelectingStartLocation(false);
                        if (!isDesktop) {
                          setHalf();
                        }
                      }
                    }}
                    className="bg-gradient-to-r from-purple-600 to-pink-600 hover:from-purple-700 hover:to-pink-700 text-white font-medium py-2 px-4 rounded-lg shadow-lg transition-colors text-sm"
                  >
                    Confirm Location
                  </button>
                </div>
              )}

              {/* Mobile Swipeable Place Cards Carousel - Day Trip */}
              {currentView === 'day-trip-destinations' && nearbyPlaces.length > 0 && drawerPosition !== 'expanded' && (() => {
                // Create carousel array with duplicates for infinite loop effect
                // [last card, ...all cards, first card]
                const carouselPlaces = nearbyPlaces.length > 1 
                  ? [nearbyPlaces[nearbyPlaces.length - 1], ...nearbyPlaces, nearbyPlaces[0]]
                  : nearbyPlaces;
                
                return (
                  <div className="absolute top-4 left-0 right-0 z-20 overflow-x-clip pointer-events-none">
                    <div 
                      ref={cardContainerRef}
                      className="flex gap-3 pointer-events-auto"
                      style={{
                        paddingLeft: 'calc(50vw - 140px)',
                        paddingRight: 'calc(50vw - 140px)',
                        // Offset by 1 card (292px) to account for duplicate last card at start
                        transform: nearbyPlaces.length > 1
                          ? `translateX(calc(-${(activeCardIndex + 1) * 292}px + ${cardSwipeOffset}px))`
                          : `translateX(${cardSwipeOffset}px)`,
                        transition: isCardSwiping ? 'none' : 'transform 0.3s ease-out'
                      }}
                      onTouchStart={handleCardTouchStart}
                      onTouchMove={handleCardTouchMove}
                      onTouchEnd={handleCardTouchEnd}
                    >
                      {carouselPlaces.map((place, carouselIndex) => {
                        // Calculate the actual index in the original array
                        const actualIndex = nearbyPlaces.length > 1
                          ? (carouselIndex === 0 ? nearbyPlaces.length - 1 
                            : carouselIndex === carouselPlaces.length - 1 ? 0 
                            : carouselIndex - 1)
                          : carouselIndex;
                        
                        const typeConfig = getPlaceTypeConfig(place.type as PlaceType);
                        const variants = getImageVariants(place.photo_1_cdn || null);
                        const imageUrl = variants.thumb || variants.original || place.photo_1_cdn;
                        const isActive = actualIndex === activeCardIndex;
                        const inTrip = isPlaceInTrip(place.id);

                        return (
                          <div
                            key={`${place.id}-${carouselIndex}`}
                            className={`flex-shrink-0 bg-white rounded-lg shadow-lg overflow-hidden transition-all duration-300 ${
                              isActive ? 'scale-100 opacity-100' : 'scale-95 opacity-70'
                            }`}
                            style={{ 
                              width: '280px'
                            }}
                          >
                          <div className="flex h-24">
                            {/* Image on Left */}
                            <div 
                              className="relative w-24 flex-shrink-0 bg-gray-200 cursor-pointer"
                              onClick={(e) => {
                                e.stopPropagation();
                                
                                // If not active card, just navigate to it
                                if (!isActive) {
                                  setActiveCardIndex(actualIndex);
                                  return;
                                }
                                
                                // Center map on the place with drawer-aware offset
                                if (place?.geo_point && mapRef.current) {
                                  const lng = place.geo_point.lon || place.geo_point.lng || place.geo_point.longitude;
                                  const lat = place.geo_point.lat || place.geo_point.latitude;
                                  if (lng && lat) {
                                    const offset = getDrawerAdjustedOffset();
                                    mapRef.current.flyTo({
                                      center: [lng, lat],
                                      zoom: 14,
                                      duration: 1000,
                                      offset: offset
                                    });
                                  }
                                } else {
                                }
                                // Open detail panel
                                setSelectedPlaceForDetail(place);
                              }}
                            >
                              {imageUrl && (
                                <img 
                                  src={imageUrl} 
                                  alt={place.name}
                                  className="w-full h-full object-cover"
                                  onError={(e) => {
                                    (e.target as HTMLImageElement).style.display = 'none';
                                  }}
                                />
                              )}
                              {/* Type Badge */}
                              <div className={`absolute bottom-1 left-1 ${typeConfig.bgColor} px-1.5 py-0.5 rounded-full flex items-center gap-0.5`}>
                                <span className={`text-[10px] font-bold ${typeConfig.color}`}>{typeConfig.code}</span>
                              </div>
                            </div>

                            {/* Content on Right */}
                            <div className="flex-1 p-2.5 flex flex-col justify-between min-w-0 cursor-pointer" onClick={(e) => {
                              e.stopPropagation();
                              
                              // If not active card, just navigate to it
                              if (!isActive) {
                                setActiveCardIndex(actualIndex);
                                return;
                              }
                              
                              // Center map on the place
                              if (place?.geo_point && mapRef.current) {
                                const lng = place.geo_point.lon || place.geo_point.lng || place.geo_point.longitude;
                                const lat = place.geo_point.lat || place.geo_point.latitude;
                                if (lng && lat) {
                                  const offset = getMapOffsetForDrawer();
                                  mapRef.current.flyTo({
                                    center: [lng, lat],
                                    zoom: 14,
                                    duration: 1000,
                                    offset: offset
                                  });
                                }
                              } else {
                              }
                              // Open detail panel
                              setSelectedPlaceForDetail(place);
                            }}>
                              {/* Top: Title, Rating and Add Button */}
                              <div>
                                <div className="flex items-start justify-between gap-1.5 mb-1">
                                  <h3 className="font-bold text-gray-900 text-sm leading-tight line-clamp-2 flex-1">
                                    {place.name}
                                  </h3>
                                  <div className="flex items-center gap-1 flex-shrink-0">
                                  {place.rating && (
                                      <div className="flex items-center gap-0.5 bg-yellow-50 px-1.5 py-0.5 rounded">
                                      <span className="text-yellow-500 text-xs">★</span>
                                      <span className="text-xs font-semibold text-gray-900">
                                        {typeof place.rating === 'number' ? place.rating.toFixed(1) : place.rating}
                                      </span>
                                    </div>
                                  )}
                                    <button
                                      onClick={(e) => {
                                        e.stopPropagation();
                                        toggleTripPlace(place, currentPrimaryLocationIndex);
                                      }}
                                      className={`p-1.5 rounded-lg transition-all ${
                                        inTrip
                                          ? 'bg-green-500 text-white'
                                          : 'bg-blue-500 text-white hover:bg-blue-600'
                                      }`}
                                      title={inTrip ? 'Remove from trip' : 'Add to trip'}
                                    >
                                      {inTrip ? (
                                        <Check className="w-3.5 h-3.5" />
                                      ) : (
                                        <Plus className="w-3.5 h-3.5" />
                                      )}
                                    </button>
                                  </div>
                                </div>

                                {/* Location and Distance */}
                                <div className="flex items-center gap-2 text-xs text-gray-600">
                                  <div className="flex items-center gap-1">
                                    <MapPin className="w-3 h-3" />
                                    <span className="truncate">{place.city || 'Sri Lanka'}</span>
                                  </div>
                                  {place.distance !== undefined && (
                                    <>
                                      <span className="text-gray-400">•</span>
                                      <span className="font-medium">{formatDistance(place.distance)}</span>
                                    </>
                                  )}
                                </div>
                              </div>

                              {/* Bottom: Counter and Type */}
                              <div className="flex items-center justify-between">
                                <span className={`text-[10px] font-medium ${typeConfig.color}`}>
                                  {typeConfig.label}
                                </span>
                                <span className="text-[10px] text-gray-500 font-medium">
                                  {actualIndex + 1} / {nearbyPlaces.length}
                                </span>
                              </div>
                            </div>
                          </div>
                        </div>
                      );
                    })}
                  </div>

                  {/* Swipe Indicator Dots */}
                  {nearbyPlaces.length > 1 && (
                    <div className="flex items-center justify-center gap-1.5 mt-2 pointer-events-none">
                      {nearbyPlaces.map((_, index) => (
                        <div
                          key={index}
                          className={`h-1 rounded-full transition-all ${
                            index === activeCardIndex 
                              ? 'w-6 bg-blue-600' 
                              : 'w-1.5 bg-gray-400'
                          }`}
                        />
                      ))}
                    </div>
                  )}
                </div>
              );
              })()}
            </>
          )}
        </div>

        {/* Mobile Swipeable Place Cards Carousel - Multi-Day Trip Day Destinations View Only */}
        {null}
        {currentView === 'multi-day-trip-day-destinations' && drawerPosition !== 'expanded' && (() => {
          // Use nearbyPlaces for destinations view only
          const placesToDisplay = nearbyPlaces;
          
          if (placesToDisplay.length === 0) return null;
          
          // Create carousel array with duplicates for infinite loop effect
          // [last card, ...all cards, first card]
          const carouselPlaces = placesToDisplay.length > 1 
            ? [placesToDisplay[placesToDisplay.length - 1], ...placesToDisplay, placesToDisplay[0]]
            : placesToDisplay;
          
          return (
            <div className="absolute top-4 left-0 right-0 z-20 overflow-x-clip pointer-events-none">
              <div 
                ref={cardContainerRef}
                className="flex gap-3 pointer-events-auto"
                style={{
                  paddingLeft: 'calc(50vw - 140px)',
                  paddingRight: 'calc(50vw - 140px)',
                  // Offset by 1 card (292px) to account for duplicate last card at start
                  transform: placesToDisplay.length > 1
                    ? `translateX(calc(-${(activeCardIndex + 1) * 292}px + ${cardSwipeOffset}px))`
                    : `translateX(${cardSwipeOffset}px)`,
                  transition: isCardSwiping ? 'none' : 'transform 0.3s ease-out'
                }}
                onTouchStart={handleCardTouchStart}
                onTouchMove={handleCardTouchMove}
                onTouchEnd={handleCardTouchEnd}
              >
                {carouselPlaces.map((place, carouselIndex) => {
                  // Calculate the actual index in the original array
                  const actualIndex = placesToDisplay.length > 1
                    ? (carouselIndex === 0 ? placesToDisplay.length - 1 
                      : carouselIndex === carouselPlaces.length - 1 ? 0 
                      : carouselIndex - 1)
                    : carouselIndex;
                  
                  const typeConfig = getPlaceTypeConfig(place.type as PlaceType);
                  const variants = getImageVariants(place.photo_1_cdn || place.imageUrl || null);
                  const imageUrl = variants.thumb || variants.original || place.photo_1_cdn || place.imageUrl;
                  const isActive = actualIndex === activeCardIndex;
                  const inTrip = isPlaceInTrip(place.id);

                  return (
                    <div
                      key={`${place.id}-${carouselIndex}`}
                      className={`flex-shrink-0 bg-white rounded-lg shadow-lg overflow-hidden transition-all duration-300 ${
                        isActive ? 'scale-100 opacity-100' : 'scale-95 opacity-70'
                      }`}
                      style={{ 
                        width: '280px'
                      }}
                    >
                    <div className="flex h-24">
                      {/* Image on Left */}
                      <div 
                        className="relative w-24 flex-shrink-0 bg-gray-200 cursor-pointer"
                        onClick={(e) => {
                          e.stopPropagation();
                          
                          // If not active card, just navigate to it
                          if (!isActive) {
                            setActiveCardIndex(actualIndex);
                            return;
                          }
                          
                          // Center map on the place
                          if (place?.geo_point && mapRef.current) {
                            const lng = place.geo_point.lon || place.geo_point.lng || place.geo_point.longitude;
                            const lat = place.geo_point.lat || place.geo_point.latitude;
                            if (lng && lat) {
                              const offset = getMapOffsetForDrawer();
                              mapRef.current.flyTo({
                                center: [lng, lat],
                                zoom: 14,
                                duration: 1000,
                                offset: offset
                              });
                            }
                          } else {
                          }
                          // Open detail panel
                          setSelectedPlaceForDetail(place);
                        }}
                      >
                        {imageUrl && (
                          <img 
                            src={imageUrl} 
                            alt={place.name}
                            className="w-full h-full object-cover"
                            onError={(e) => {
                              (e.target as HTMLImageElement).style.display = 'none';
                            }}
                          />
                        )}
                        {/* Type Badge */}
                        <div className={`absolute bottom-1 left-1 ${typeConfig.bgColor} px-1.5 py-0.5 rounded-full flex items-center gap-0.5`}>
                          <span className={`text-[10px] font-bold ${typeConfig.color}`}>{typeConfig.code}</span>
                        </div>
                      </div>

                      {/* Content on Right */}
                      <div className="flex-1 p-2.5 flex flex-col justify-between min-w-0 cursor-pointer" onClick={(e) => {
                        e.stopPropagation();
                        
                        // If not active card, just navigate to it
                        if (!isActive) {
                          setActiveCardIndex(actualIndex);
                          return;
                        }
                        
                        // Center map on the place
                        if (place?.geo_point && mapRef.current) {
                          const lng = place.geo_point.lon || place.geo_point.lng || place.geo_point.longitude;
                          const lat = place.geo_point.lat || place.geo_point.latitude;
                          if (lng && lat) {
                            const offset = getMapOffsetForDrawer();
                            mapRef.current.flyTo({
                              center: [lng, lat],
                              zoom: 14,
                              duration: 1000,
                              offset: offset
                            });
                          }
                        } else {
                        }
                        // Open detail panel
                        setSelectedPlaceForDetail(place);
                      }}>
                        {/* Top: Title, Rating and Add Button */}
                        <div>
                          <div className="flex items-start justify-between gap-1.5 mb-1">
                            <h3 className="font-bold text-gray-900 text-sm leading-tight line-clamp-2 flex-1">
                              {place.name}
                            </h3>
                            <div className="flex items-center gap-1 flex-shrink-0">
                            {place.rating && (
                                <div className="flex items-center gap-0.5 bg-yellow-50 px-1.5 py-0.5 rounded">
                                <span className="text-yellow-500 text-xs">★</span>
                                <span className="text-xs font-semibold text-gray-900">
                                  {typeof place.rating === 'number' ? place.rating.toFixed(1) : place.rating}
                                </span>
                              </div>
                            )}
                              {currentView === 'multi-day-trip-day-destinations' && !inTrip && (
                            <button
                              onClick={(e) => {
                                e.stopPropagation();
                                addToTrip(place, currentPrimaryLocationIndex);
                              }}
                                  className={`p-1.5 rounded-lg transition-all bg-blue-500 text-white hover:bg-blue-600`}
                                  title="Add to trip"
                            >
                                  <Plus className="w-3.5 h-3.5" />
                            </button>
                          )}
                              {inTrip && (
                                <div className="p-1.5 rounded-lg bg-green-500 text-white">
                                  <Check className="w-3.5 h-3.5" />
                        </div>
                              )}
                            </div>
                          </div>

                          {/* Location and Distance */}
                          <div className="flex items-center gap-2 text-xs text-gray-600">
                            <div className="flex items-center gap-1">
                              <MapPin className="w-3 h-3" />
                              <span className="truncate">{place.city || 'Sri Lanka'}</span>
                            </div>
                            {place.distance !== undefined && (
                              <>
                                <span className="text-gray-400">•</span>
                                <span className="font-medium">{formatDistance(place.distance)}</span>
                              </>
                            )}
                          </div>
                        </div>

                        {/* Bottom: Counter and Type */}
                        <div className="flex items-center justify-between">
                          <span className={`text-[10px] font-medium ${typeConfig.color}`}>
                            {typeConfig.label}
                          </span>
                          <span className="text-[10px] text-gray-500 font-medium">
                            {actualIndex + 1} / {placesToDisplay.length}
                          </span>
                        </div>
                      </div>
                    </div>
                    </div>
                  );
                })}
              </div>

              {/* Carousel Indicators */}
              {placesToDisplay.length > 1 && (
                <div className="flex justify-center gap-1.5 mt-2">
                  {placesToDisplay.map((_, index) => (
                    <div 
                      key={index}
                      className={`h-1 rounded-full transition-all ${
                        index === activeCardIndex 
                          ? 'w-6 bg-blue-600' 
                          : 'w-1.5 bg-gray-400'
                      }`}
                    />
                  ))}
                </div>
              )}
            </div>
          );
        })()}

        {/* Bottom Drawer */}
        <div
          ref={drawerRef}
          className={`absolute bottom-0 left-0 right-0 bg-white rounded-t-2xl shadow-2xl z-30 flex flex-col ${
            isDragging ? '' : 'transition-all duration-200 ease-out'
          }`}
          style={{
            height: `${drawerHeight}vh`,
            maxHeight: 'calc(100vh - 64px)', // Account for bottom nav (64px = h-16)
          }}
        >
          {/* Drawer Handle */}
          <div
            className="w-full py-3 cursor-grab active:cursor-grabbing touch-none"
            {...dragHandlers}
            onClick={() => {
              if (drawerPosition === 'collapsed') {
                expand();
              }
            }}
          >
            <div className="w-12 h-1.5 bg-gray-300 rounded-full mx-auto"></div>
          </div>

          {/* Drawer Header */}
          <div
            className="px-4 pb-3 border-b border-gray-200 cursor-grab active:cursor-grabbing touch-none"
            {...dragHandlers}
          >
            <div className="flex items-center justify-between gap-2">
              <div className="flex-1 min-w-0">
                <h2 className="text-lg font-bold text-gray-900">{getDrawerHeader().title}</h2>
                <p className="text-xs text-gray-600 mt-1">
                  {getDrawerHeader().subtitle}
                </p>
              </div>
              <div className="flex items-center gap-1 flex-shrink-0">
                {/* Back Button */}
                {currentView !== 'home' && (
                  <button
                    onClick={navigateBack}
                    className="flex items-center justify-center text-gray-600 hover:text-gray-900 hover:bg-gray-100 transition-colors p-2 rounded-lg"
                  >
                    <ArrowLeft className="w-5 h-5" />
                  </button>
                )}
                {/* Unselect All Button - for destination pickers */}
                {(currentView === 'day-trip-destinations' || currentView === 'multi-day-trip-day-destinations') && tripPlaces.length > 0 && (
                  <button
                    onClick={() => setTripPlaces([])}
                    className="flex items-center justify-center bg-red-50 hover:bg-red-100 text-red-600 p-2 rounded-lg transition-colors border border-red-200"
                    title="Clear all selected places"
                  >
                    <X className="w-5 h-5" />
                  </button>
                )}
                {/* Continue Button for Day Trip - after location is selected */}
                {currentView === 'day-trip' && startLocation && (
                  <button
                    onClick={handleContinueToDestinations}
                    className="flex items-center justify-center bg-green-600 hover:bg-green-700 text-white p-2 rounded-lg transition-colors"
                  >
                    <ArrowRight className="w-5 h-5" />
                  </button>
                )}
                {/* Save Button - Icon only */}
                {shouldShowSaveButton() && authState.user && (
                  <button
                    onClick={handleSaveTrip}
                    className="flex items-center justify-center bg-green-600 hover:bg-green-700 text-white p-2 rounded-lg transition-colors"
                  >
                    <Save className="w-5 h-5" />
                  </button>
                )}
                {/* Generate Trip Summary Button - Icon only (for itinerary view) */}
                {currentView === 'multi-day-trip-itinerary' && currentTripId && (
                  <button
                    onClick={handleGenerateSummaryClick}
                    disabled={isGeneratingSummary}
                    className="flex items-center justify-center bg-orange-500 hover:bg-orange-600 disabled:bg-gray-400 text-white p-2 rounded-lg transition-colors"
                    title={isTripSummaryCurrent() ? "View Trip Summary" : "Generate Trip Summary"}
                  >
                    {isGeneratingSummary ? (
                      <svg className="w-5 h-5 animate-spin" fill="none" viewBox="0 0 24 24">
                        <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                        <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                      </svg>
                    ) : isTripSummaryCurrent() ? (
                      <Eye className="w-5 h-5" />
                    ) : (
                      <Sparkles className="w-5 h-5" />
                    )}
                  </button>
                )}
                {/* Get Quote Button - Next to Sparkle */}
                {currentView === 'multi-day-trip-itinerary' && currentTripId && (
                  <button
                    onClick={handleGenerateSummaryClick}
                    className="flex items-center justify-center bg-gradient-to-r from-purple-600 to-blue-600 hover:from-purple-700 hover:to-blue-700 text-white p-2 rounded-lg transition-colors shadow-md"
                    title="Get Quote"
                  >
                    <MessageSquare className="w-5 h-5" />
                  </button>
                )}
                {/* Continue Button - Next arrow icon with badge */}
                {currentView === 'day-trip-destinations' && tripPlaces.length > 0 && (
                  <button
                    onClick={() => handleNavigation('my-day-trip')}
                    className="flex items-center justify-center bg-blue-600 hover:bg-blue-700 text-white p-2 rounded-lg transition-colors relative"
                  >
                    <ArrowRight className="w-5 h-5" />
                    {tripPlaces.length > 0 && (
                      <span className="absolute -top-1 -right-1 bg-white text-blue-600 px-1.5 py-0.5 rounded-full text-xs font-bold min-w-[20px] text-center">
                        {tripPlaces.length}
                      </span>
                    )}
                  </button>
                )}
                {/* Continue Button for Multi-Day Trip Details - Mobile Only */}
                {currentView === 'multi-day-trip-details' && multiDayStartDate && multiDayEndDate && !isDesktop && (
                  <button
                    onClick={() => {
                      // Validate trip name
                      if (!multiDayTripName.trim()) {
                        const roasts = [
                          "Hey! 👋 Your epic adventure needs an epic name first!",
                          "Slow down! 🐌 Can't plan a legendary trip without a legendary name!",
                          "Nope! ❌ Your trip name field is lonelier than a beach in monsoon season!",
                          "Wait up! ⏸️ Name your trip - future you will thank present you!",
                          "Stop right there! 🛑 Give this journey a name worthy of your Instagram stories!"
                        ];
                        const randomRoast = roasts[Math.floor(Math.random() * roasts.length)];
                        showToast(randomRoast, 'warning', 4000);
                        return;
                      }
                      handleNavigation('multi-day-trip-itinerary');
                    }}
                    className="flex items-center justify-center bg-gradient-to-r from-purple-600 to-pink-600 hover:from-purple-700 hover:to-pink-700 text-white p-2 rounded-lg transition-colors"
                  >
                    <ArrowRight className="w-5 h-5" />
                  </button>
                )}
                {/* Continue Button for Day Destinations - Mobile Only */}
                {currentView === 'multi-day-trip-day-destinations' && tripPlaces.length > 0 && !isDesktop && (
                  <button
                    onClick={() => handleNavigation('multi-day-trip-plan-day')}
                    className="flex items-center justify-center bg-gradient-to-r from-purple-600 to-pink-600 hover:from-purple-700 hover:to-pink-700 text-white p-2 rounded-lg transition-colors"
                  >
                    <ArrowRight className="w-5 h-5" />
                  </button>
                )}
                {/* Edit Primary Location Button for Plan Day - Mobile Only */}
                {currentView === 'multi-day-trip-plan-day' && !isDesktop && (
                  <button
                    onClick={() => {
                      if (tripPlaces.length > 0) {
                        showToast(`Remove all existing places from Day ${(selectedDayIndex !== null ? selectedDayIndex + 1 : '')} to change primary location`, 'warning', 4000);
                        return;
                      }
                      // Clear day data before resetting location
                      setDayRouteSegments(prev => {
                        const newMap = new Map(prev);
                        if (selectedDayIndex !== null) {
                          newMap.delete(selectedDayIndex);
                        }
                        return newMap;
                      });
                      setDayTransportation(prev => {
                        const newMap = new Map(prev);
                        if (selectedDayIndex !== null) {
                          newMap.delete(selectedDayIndex);
                        }
                        return newMap;
                      });
                      setTrainAvailability(prev => {
                        const newMap = new Map(prev);
                        if (selectedDayIndex !== null) {
                          newMap.delete(selectedDayIndex);
                        }
                        return newMap;
                      });
                      // Clear the selected day location to allow re-selection
                      setSelectedDayLocation(null);
                      // Also clear from dayLocations map
                      if (selectedDayIndex !== null) {
                        setDayLocations(prev => {
                          const newMap = new Map(prev);
                          newMap.delete(selectedDayIndex);
                          return newMap;
                        });
                      }
                      // Open location picker
                      setIsResettingDayLocation(true);
                      // Navigate back to itinerary view so the location picker can be displayed
                      setCurrentView('multi-day-trip-itinerary');
                      // Show the location picker slider after a short delay
                      setTimeout(() => {
                        setShowDayLocationSlider(true);
                      }, 100);
                    }}
                    className={`flex items-center justify-center p-2 rounded-lg transition-colors ${
                      tripPlaces.length > 0
                        ? 'bg-gray-100 text-gray-400 cursor-not-allowed opacity-50'
                        : 'text-gray-600 hover:text-gray-900 hover:bg-gray-100'
                    }`}
                    title={tripPlaces.length > 0 ? 'Remove all places first' : 'Change starting location'}
                  >
                    <Edit2 className="w-5 h-5" />
                  </button>
                )}
                {/* Continue Button for Plan Day - Mobile Only */}
                {currentView === 'multi-day-trip-plan-day' && !isDesktop && (
                  <button
                    onClick={() => {
                      if (selectedDayIndex === null) return;
                      
                      // Save finalized day data
                      const currentDayTransport = dayTransportation.get(selectedDayIndex) || new Map();
                      const currentDaySegments = dayRouteSegments.get(selectedDayIndex) || [];
                      
                      // Determine start location for this day
                      let dayStartLocation: QuickLocation | null = null;
                      if (selectedDayIndex === 0) {
                        dayStartLocation = multiDayStartLocation;
                      } else {
                        // Find the actual previous day, accounting for merged blocks
                        let previousDayData: FinalizedDayData | undefined;
                        let previousDayPlaces: PlaceItem[] | undefined;
                        
                        // Check if the previous day is part of a merged block
                        const previousDayIndex = selectedDayIndex - 1;
                        let foundInBlock = false;
                        
                        for (const [blockStartIndex, blockIndices] of Array.from(combinedDayBlocks.entries())) {
                          if (blockIndices.includes(previousDayIndex)) {
                            // Previous day is in this merged block
                            previousDayData = finalizedDays.get(blockStartIndex);
                            previousDayPlaces = previousDayData?.places;
                            foundInBlock = true;
                            break;
                          }
                        }
                        
                        // If not in a merged block, check regular dayPlaces and finalizedDays
                        if (!foundInBlock) {
                          previousDayPlaces = dayPlaces.get(previousDayIndex);
                          if (!previousDayPlaces) {
                            previousDayData = finalizedDays.get(previousDayIndex);
                            previousDayPlaces = previousDayData?.places;
                          }
                        }
                        
                        if (previousDayPlaces && previousDayPlaces.length > 0) {
                          const lastPlace = previousDayPlaces[previousDayPlaces.length - 1];
                          dayStartLocation = {
                            name: lastPlace.name,
                            coordinates: [
                              lastPlace.geo_point?.lon || lastPlace.geo_point?.lng || 0,
                              lastPlace.geo_point?.lat || 0
                            ]
                          };
                        }
                      }
                      
                      if (!dayStartLocation) {
                        return;
                      }
                      
                      // Save finalized day data
                      const isLastDay = selectedDayIndex === Array.from(dayLocations.keys()).reduce((max, idx) => Math.max(max, idx), -1);
                      
                      // Get existing data before updating
                      const existingData = finalizedDays.get(selectedDayIndex);
                      
                      // Merge places: keep places from other locations + updated places from current location
                      // BUT if currentPrimaryLocationIndex is -1 (viewing all), use tripPlaces directly without merging
                      let mergedPlaces: any[];
                      if (currentPrimaryLocationIndex === -1) {
                        // Viewing all locations - tripPlaces already has all places, no merge needed
                        mergedPlaces = [...tripPlaces];
                      } else {
                        // Editing a specific location - merge with places from other locations
                        // IMPORTANT: Filter tripPlaces to only include current location's places to avoid duplicates
                        // (tripPlaces may contain ALL places for route calculation purposes)
                        const existingPlacesFromOtherLocations = existingData?.places.filter(
                          p => (p.primaryLocationIndex || 0) !== currentPrimaryLocationIndex
                        ) || [];
                        const tripPlacesForCurrentLocation = tripPlaces.filter(
                          p => (p.primaryLocationIndex || 0) === currentPrimaryLocationIndex
                        );
                        mergedPlaces = [...existingPlacesFromOtherLocations, ...tripPlacesForCurrentLocation];
                      }
                      
                      // CRITICAL: Sort merged places by primaryLocationIndex to match UI/transport order
                      // This ensures transportation segment indices align with place order in backend
                      mergedPlaces.sort((a, b) => {
                        const indexA = a.primaryLocationIndex || 0;
                        const indexB = b.primaryLocationIndex || 0;
                        return indexA - indexB;
                      });
                      
                      setFinalizedDays(prev => {
                        const newMap = new Map(prev);
                        
                        // Get primary locations for this day (supports multiple locations)
                        const dayPrimaryLocations = dayLocations.get(selectedDayIndex) || (dayStartLocation ? [dayStartLocation] : []);
                        
                        const dayData: FinalizedDayData = {
                          places: mergedPlaces,  // Use merged places to preserve all locations
                          transportation: new Map(currentDayTransport),
                          routeSegments: [...currentDaySegments],
                          primaryLocations: dayPrimaryLocations,  // Store all primary locations
                          startLocation: dayPrimaryLocations[0] || dayStartLocation,  // Keep for backwards compatibility
                          // Preserve merge metadata if it exists
                          ...(existingData?.isMultiDayBlock && {
                            isMultiDayBlock: existingData.isMultiDayBlock,
                            blockDayIndices: existingData.blockDayIndices,
                            blockStartDate: existingData.blockStartDate,
                            blockEndDate: existingData.blockEndDate
                          })
                        };
                        
                        // If this is the last day and trip is ended, include trip end data
                        if (isLastDay && isTripEnded && tripEndLocation) {
                          dayData.endLocation = tripEndLocation;
                          dayData.endTransportMode = tripEndTransportMode;
                          dayData.endRouteSegment = tripEndRouteSegment || undefined;
                        }
                        
                        newMap.set(selectedDayIndex, dayData);
                        return newMap;
                      });
                      
                      // Mark as having unsaved changes
                      setHasUnsavedChanges(true);
                      
                      const dayLabel = existingData?.blockDayIndices && existingData.blockDayIndices.length > 1
                        ? `Days ${existingData.blockDayIndices[0] + 1}-${existingData.blockDayIndices[existingData.blockDayIndices.length - 1] + 1}`
                        : `Day ${selectedDayIndex + 1}`;
                      
                      // If this day's last place changed, update the next day's incoming route (Segment 0)
                      const nextDayIndex = selectedDayIndex + 1;
                      const lastDayIndexForCheck = Array.from(dayLocations.keys()).reduce((max, idx) => Math.max(max, idx), -1);
                      const nextDayData = finalizedDays.get(nextDayIndex);
                      
                      if (nextDayData && nextDayData.places.length > 0 && tripPlaces.length > 0) {
                        const newLastPlace = tripPlaces[tripPlaces.length - 1];
                        const nextFirstPlace = nextDayData.places[0];
                        
                        // Check if the connection needs updating
                        const oldLastPlace = existingData?.places[existingData.places.length - 1];
                        const lastPlaceChanged = !oldLastPlace || 
                          oldLastPlace.place_id !== newLastPlace.place_id ||
                          oldLastPlace.name !== newLastPlace.name;
                        
                        if (lastPlaceChanged && nextDayData.routeSegments.length > 0) {
                          
                          // Recalculate route from new last place to next day's first place
                          (async () => {
                            try {
                              const fromLng = newLastPlace.geo_point?.lon || newLastPlace.geo_point?.lng || newLastPlace.geo_point?.longitude || 0;
                              const fromLat = newLastPlace.geo_point?.lat || newLastPlace.geo_point?.latitude || 0;
                              const toLng = nextFirstPlace.geo_point?.lon || nextFirstPlace.geo_point?.lng || nextFirstPlace.geo_point?.longitude || 0;
                              const toLat = nextFirstPlace.geo_point?.lat || nextFirstPlace.geo_point?.latitude || 0;
                              
                              const response = await fetch(
                                `${API_BASE_URL}/api/mapbox/directions?fromLng=${fromLng}&fromLat=${fromLat}&toLng=${toLng}&toLat=${toLat}`
                              );
                              const data = await response.json();
                              
                              if (data.routes && data.routes[0]) {
                                const route = data.routes[0];
                                const newSegment: RouteSegment = {
                                  fromIndex: 0,
                                  toIndex: 1,
                                  geometry: route.geometry.coordinates,
                                  distance: route.distance / 1000,
                                  duration: Math.round(route.duration / 60),
                                  isApproximate: false
                                };
                                
                                // Update the next day's Segment 0
                                setFinalizedDays(prev => {
                                  const newMap = new Map(prev);
                                  const nextDay = newMap.get(nextDayIndex);
                                  if (nextDay) {
                                    const updatedSegments = [...nextDay.routeSegments];
                                    updatedSegments[0] = newSegment;
                                    
                                    const updatedNextDay = {
                                      ...nextDay,
                                      routeSegments: updatedSegments
                                    };
                                    newMap.set(nextDayIndex, updatedNextDay);
                                  } else {
                                  }
                                  return newMap;
                                });
                                // Mark as having unsaved changes
                                setHasUnsavedChanges(true);
                              } else {
                              }
                            } catch (error) {
                            }
                          })();
                        }
                      }
                      
                      // If primary location was reset, mark for cleanup
                      if (isResettingDayLocation || (existingData && existingData.startLocation?.name !== dayStartLocation?.name)) {
                      }
                      
                      // Clear trip places to avoid stale data affecting trip end calculations
                      setTripPlaces([]);
                      
                      // Navigate back to itinerary
                      setCurrentView('multi-day-trip-itinerary');
                      setSelectedDayIndex(null);
                      setSelectedDayLocation(null);
                    }}
                    className="flex items-center justify-center bg-gradient-to-r from-purple-600 to-pink-600 hover:from-purple-700 hover:to-pink-700 text-white p-2 rounded-lg transition-colors"
                  >
                    <ArrowRight className="w-5 h-5" />
                  </button>
                )}
              </div>
            </div>
          </div>

          {/* Drawer Content */}
          <div 
            className="overflow-y-auto flex-1 overscroll-y-contain"
            ref={drawerContentRef}
          >
            <div className="p-4 pb-32">
              {renderDrawerContent()}
            </div>
          </div>
        </div>
      </div>
      
      {/* Place Detail Panel */}
      {selectedPlaceForDetail && (
        <div className="relative z-50 md:z-auto">
          <PlaceDetailPanel
            place={selectedPlaceForDetail}
            onClose={() => setSelectedPlaceForDetail(null)}
            onLoadFullDetails={handleLoadFullDetails}
            isLoadingDetails={isLoadingPlaceDetails}
          />
        </div>
      )}

      {/* Save Prompt Modal */}
      {showSavePrompt && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-lg shadow-xl max-w-sm md:max-w-md w-full p-4 md:p-6 animate-fade-in">
            <h3 className="text-base md:text-xl font-bold text-gray-900 mb-2 md:mb-3">Unsaved Changes</h3>
            <p className="text-sm md:text-base text-gray-600 mb-4 md:mb-6">
              You have unsaved changes. Would you like to save your trip before leaving?
            </p>
            
            <div className="flex flex-col gap-2 md:gap-3">
              <button
                onClick={async () => {
                  await handleSaveTrip();
                  setShowSavePrompt(false);
                  if (pendingNavigation) setCurrentView(pendingNavigation);
                  setPendingNavigation(null);
                }}
                className="w-full bg-blue-600 hover:bg-blue-700 text-white px-3 md:px-4 py-2 md:py-2.5 rounded-lg font-semibold transition-colors text-sm md:text-base"
              >
                Save Trip
              </button>
              <button
                onClick={() => {
                  setShowSavePrompt(false);
                  setHasUnsavedChanges(false);
                  if (pendingNavigation) setCurrentView(pendingNavigation);
                  setPendingNavigation(null);
                }}
                className="w-full bg-gray-200 hover:bg-gray-300 text-gray-700 px-3 md:px-4 py-2 md:py-2.5 rounded-lg font-semibold transition-colors text-sm md:text-base"
              >
                Don't Save
              </button>
              <button
                onClick={() => {
                  setShowSavePrompt(false);
                  setPendingNavigation(null);
                }}
                className="w-full px-3 md:px-4 py-2 md:py-2.5 text-gray-600 hover:text-gray-900 font-medium transition-colors text-sm md:text-base"
              >
                Cancel
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Finalize Trip Confirmation Modal */}
      {/* Trip End Location Picker Overlay */}
      {showTripEndLocationPicker && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-lg shadow-xl max-w-sm md:max-w-md w-full p-4 md:p-6 animate-fade-in">
            <h3 className="text-base md:text-xl font-bold text-gray-900 mb-2 md:mb-3">Select Trip End Location</h3>
            <p className="text-sm md:text-base text-gray-600 mb-4 md:mb-6">
              Choose where your trip ends
            </p>
            
            <div className="flex flex-col gap-2 md:gap-3">
              {/* Recommended Dropdown */}
              <div className="relative">
                <button
                  onClick={() => setShowRecommendedDropdown(!showRecommendedDropdown)}
                  className="w-full bg-gradient-to-br from-purple-50 to-pink-50 hover:from-purple-100 hover:to-pink-100 text-purple-700 font-medium px-3 md:px-4 py-2 md:py-3 rounded-lg text-xs md:text-sm transition-colors flex items-center justify-between shadow-sm hover:shadow-md"
                >
                  <span>Recommended</span>
                  <ChevronDown className={`w-4 h-4 transition-transform ${showRecommendedDropdown ? 'rotate-180' : ''}`} />
                </button>
                
                {showRecommendedDropdown && (
                  <div className="absolute top-full left-0 right-0 mt-2 bg-white rounded-lg shadow-lg border border-gray-200 z-50 max-h-48 overflow-y-auto">
                    {QUICK_LOCATIONS.map((location, idx) => (
                      <button
                        key={idx}
                        onClick={() => {
                          setTripEndLocation(location);
                          setShowRecommendedDropdown(false);
                          setShowTripEndLocationPicker(false);
                          setIsTripEnded(true);
                          // Clear old route state before new calculation
                          setTripEndRouteSegment(null);
                          setTripEndTrainAvailable(false);
                          // Add trip end marker
                          addTripEndMarker(location.coordinates);
                          // Clear selected day index so the day card border returns to grey
                          setSelectedDayIndex(null);
                          // Will trigger route calculation in useEffect
                        }}
                        className="w-full text-left px-3 md:px-4 py-2 md:py-2.5 hover:bg-purple-50 text-xs md:text-sm text-gray-700 hover:text-purple-900 transition-colors border-b border-gray-100 last:border-b-0"
                      >
                        {location.name}
                      </button>
                    ))}
                  </div>
                )}
              </div>
              
              {/* Pick from Map */}
              <button
                onClick={() => {
                  setShowTripEndLocationPicker(false);
                  setIsSelectingDayLocation(true);
                  setIsSelectingTripEndLocation(true);
                  setShowRecommendedDropdown(false);
                  if (!isDesktop) {
                    collapse();
                  }
                }}
                className="w-full bg-gradient-to-br from-orange-50 to-amber-50 hover:from-orange-100 hover:to-amber-100 text-orange-700 font-medium px-3 md:px-4 py-2 md:py-3 rounded-lg text-xs md:text-sm transition-colors flex items-center justify-center gap-2 shadow-sm hover:shadow-md"
              >
                <MapPin className="w-4 h-4" />
                <span>On Map</span>
              </button>
              
              {/* Cancel */}
              <button
                onClick={() => setShowTripEndLocationPicker(false)}
                className="w-full px-3 md:px-4 py-2 md:py-2.5 border border-gray-300 text-gray-700 font-semibold rounded-lg hover:bg-gray-50 transition-colors text-xs md:text-sm"
              >
                Cancel
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Reset Itinerary Confirmation Modal */}
      {showResetItineraryModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-lg shadow-xl max-w-sm md:max-w-md w-full p-4 md:p-6 animate-fade-in">
            <h3 className="text-base md:text-xl font-bold text-red-600 mb-2 md:mb-3">⚠️ Reset Entire Itinerary?</h3>
            <p className="text-sm md:text-base text-gray-600 mb-4 md:mb-6">
              Changing the start location will <strong>reset all your trip progress</strong>. All planned days and places will be lost. Are you sure you want to continue?
            </p>
            <div className="flex gap-2 md:gap-3">
              <button
                onClick={() => setShowResetItineraryModal(false)}
                className="flex-1 px-3 md:px-4 py-2 md:py-2.5 border border-gray-300 text-gray-700 font-semibold rounded-lg hover:bg-gray-50 transition-colors text-xs md:text-sm"
              >
                Cancel
              </button>
              <button
                onClick={() => {
                  // Reset everything
                  setMultiDayStartLocation(null);
                  setFinalizedDays(new Map());
                  setDayPlaces(new Map());
                  setDayLocations(new Map());
                  setDayRouteSegments(new Map());
                  setDayTransportation(new Map());
                  setCombinedDayBlocks(new Map());
                  setTripPlaces([]);
                  setSelectedDayIndex(null);
                  setActiveTrainRoutes(new Map());
                  setExpandedDays(new Set());
                  
                  // Clear trip end data
                  setIsTripEnded(false);
                  setTripEndLocation(null);
                  setTripEndTransportMode('car');
                  setTripEndRouteSegment(null);
                  if (tripEndMarkerRef.current) {
                    tripEndMarkerRef.current.remove();
                    tripEndMarkerRef.current = null;
                  }
                  
                  // Close modal
                  setShowResetItineraryModal(false);
                  
                  // Show success toast
                  showToast('✅ Itinerary reset. You can now select a new start location.', 'success', 3000);
                }}
                className="flex-1 px-3 md:px-4 py-2 md:py-2.5 bg-red-600 text-white font-semibold rounded-lg hover:bg-red-700 transition-colors text-xs md:text-sm"
              >
                Yes, Reset All
              </button>
            </div>
          </div>
        </div>
      )}

      {showFinalizeModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-lg shadow-xl max-w-sm md:max-w-md w-full p-4 md:p-6 animate-fade-in">
            <h3 className="text-base md:text-xl font-bold text-gray-900 mb-2 md:mb-3">Finalize Your Trip?</h3>
            <p className="text-sm md:text-base text-gray-600 mb-4 md:mb-6">
              Are you sure your trip is finalized? This will lock your itinerary for review.
            </p>
            <div className="flex gap-2 md:gap-3">
              <button
                onClick={() => setShowFinalizeModal(false)}
                className="flex-1 px-3 md:px-4 py-2 md:py-2.5 border border-gray-300 text-gray-700 font-semibold rounded-lg hover:bg-gray-50 transition-colors text-xs md:text-sm"
              >
                Cancel
              </button>
              <button
                onClick={handleFinalizeConfirm}
                className="flex-1 px-3 md:px-4 py-2 md:py-2.5 bg-blue-600 text-white font-semibold rounded-lg hover:bg-blue-700 transition-colors text-xs md:text-sm"
              >
                Yes, Finalize
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Registration Prompt Modal */}
      <RegistrationPrompt
        isOpen={showEmailAuthPrompt}
        onClose={() => setShowEmailAuthPrompt(false)}
        variant="google_auth"
        trigger="trip_create"
      />

      {/* WhatsApp/Telegram Verification Prompt */}
      <RegistrationPrompt
        isOpen={showWhatsAppVerifyPrompt}
        onClose={() => setShowWhatsAppVerifyPrompt(false)}
        variant="whatsapp_verify"
        trigger="trip_create"
      />

      {/* Rate Limit Prompt for Trip Summary */}
      <RegistrationPrompt
        isOpen={showRateLimitPrompt}
        onClose={() => setShowRateLimitPrompt(false)}
        variant={registrationVariant}
        trigger="trip_summary_limit"
        waitMinutes={rateLimitWaitMinutes}
      />


      {/* Calendar Picker Modal */}
      {showCalendarPicker && (() => {
        const days = (() => {
          const year = currentMonth.getFullYear();
          const month = currentMonth.getMonth();
          const firstDay = new Date(year, month, 1);
          const lastDay = new Date(year, month + 1, 0);
          const daysInMonth = lastDay.getDate();
          const startingDayOfWeek = firstDay.getDay();
          const days = [];
          
          for (let i = 0; i < startingDayOfWeek; i++) {
            days.push(null);
          }
          
          for (let day = 1; day <= daysInMonth; day++) {
            days.push(new Date(year, month, day));
          }
          
          return days;
        })();
        
        const monthNames = ['January', 'February', 'March', 'April', 'May', 'June', 'July', 'August', 'September', 'October', 'November', 'December'];
        
        const today = new Date();
        today.setHours(0, 0, 0, 0);
        
        const isDateDisabled = (date: Date) => date < today;
        
        const isDateSelected = (date: Date) => {
          if (!multiDayStartDate) return false;
          if (!multiDayEndDate) return date.getTime() === multiDayStartDate.getTime();
          return date >= multiDayStartDate && date <= multiDayEndDate;
        };
        
        const isDateInRange = (date: Date) => {
          if (!multiDayStartDate || !multiDayEndDate) return false;
          return date > multiDayStartDate && date < multiDayEndDate;
        };
        
        const handleDateClick = (date: Date) => {
          if (isDateDisabled(date)) return;
          
          if (!multiDayStartDate || (multiDayStartDate && multiDayEndDate)) {
            setMultiDayStartDate(date);
            setMultiDayEndDate(null);
          } else if (multiDayStartDate && !multiDayEndDate) {
            if (date < multiDayStartDate) {
              setMultiDayStartDate(date);
              setMultiDayEndDate(multiDayStartDate);
            } else {
              setMultiDayEndDate(date);
              // Close modal after end date is selected
              setShowCalendarPicker(false);
            }
          }
        };

        return (
          <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
            <div className="bg-white rounded-lg shadow-xl max-w-sm md:max-w-md w-full p-4 md:p-6 animate-fade-in">
              <div className="flex items-center justify-between mb-4">
                <h3 className="text-base md:text-lg font-semibold text-gray-900">Select Dates</h3>
                <button
                  onClick={() => setShowCalendarPicker(false)}
                  className="p-1 hover:bg-gray-100 rounded-full transition-colors"
                >
                  <X className="w-5 h-5 text-gray-500" />
                </button>
    </div>
              
              {/* Month Navigation */}
              <div className="flex items-center justify-between mb-4">
                <button
                  onClick={() => setCurrentMonth(new Date(currentMonth.getFullYear(), currentMonth.getMonth() - 1))}
                  className="p-2 hover:bg-gray-100 rounded-full transition-colors"
                >
                  <ChevronLeft className="w-5 h-5" />
                </button>
                <h4 className="font-semibold text-gray-900 text-sm md:text-base">
                  {monthNames[currentMonth.getMonth()]} {currentMonth.getFullYear()}
                </h4>
                <button
                  onClick={() => setCurrentMonth(new Date(currentMonth.getFullYear(), currentMonth.getMonth() + 1))}
                  className="p-2 hover:bg-gray-100 rounded-full transition-colors"
                >
                  <ChevronRight className="w-5 h-5" />
                </button>
              </div>
              
              {/* Calendar Grid */}
              <div className="grid grid-cols-7 gap-1">
                {['Su', 'Mo', 'Tu', 'We', 'Th', 'Fr', 'Sa'].map((day) => (
                  <div key={day} className="text-center text-xs font-medium text-gray-500 py-2">
                    {day}
                  </div>
                ))}
                
                {days.map((date, index) => {
                  if (!date) {
                    return <div key={`empty-${index}`} />;
                  }
                  
                  const disabled = isDateDisabled(date);
                  const selected = isDateSelected(date);
                  const inRange = isDateInRange(date);
                  const isStart = multiDayStartDate && date.getTime() === multiDayStartDate.getTime();
                  const isEnd = multiDayEndDate && date.getTime() === multiDayEndDate.getTime();
                  const holiday = getHolidayForDate(date);
                  
                  return (
                    <button
                      key={index}
                      onClick={() => handleDateClick(date)}
                      disabled={disabled}
                      className={`
                        aspect-square p-2 text-xs md:text-sm rounded-lg transition-all relative
                        ${disabled ? 'text-gray-300 cursor-not-allowed' : 'hover:bg-purple-50 cursor-pointer'}
                        ${selected && !inRange ? 'bg-purple-600 text-white font-semibold hover:bg-purple-700' : ''}
                        ${inRange ? 'bg-purple-100 text-purple-900' : ''}
                        ${isStart || isEnd ? 'bg-purple-600 text-white font-semibold' : ''}
                      `}
                      title={holiday ? `${holiday.name} - ${holiday.fareImpact}` : ''}
                    >
                      {date.getDate()}
                      {holiday && !disabled && (
                        <div className={`absolute bottom-0.5 left-1/2 transform -translate-x-1/2 w-1 h-1 rounded-full ${
                          selected || isStart || isEnd ? 'bg-white' : 'bg-orange-500'
                        }`} />
                      )}
                    </button>
                  );
                })}
              </div>
            </div>
          </div>
        );
      })()}

      {/* Traveler Picker Modal */}
      {showTravelerPicker && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-lg shadow-xl max-w-sm md:max-w-md w-full p-4 md:p-6 animate-fade-in">
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-base md:text-lg font-semibold text-gray-900">Select Travelers</h3>
              <button
                onClick={() => setShowTravelerPicker(false)}
                className="p-1 hover:bg-gray-100 rounded-full transition-colors"
              >
                <X className="w-5 h-5 text-gray-500" />
              </button>
            </div>
            
            <div className="space-y-4">
              {/* Adults */}
              <div className="flex items-center justify-between">
                <div>
                  <p className="font-medium text-gray-900 text-sm md:text-base">Adults</p>
                  <p className="text-xs md:text-sm text-gray-500">Age 13+</p>
                </div>
                <div className="flex items-center gap-3">
                  <button
                    onClick={() => setMultiDayAdults(Math.max(1, multiDayAdults - 1))}
                    disabled={multiDayAdults <= 1}
                    className="w-8 h-8 rounded-full border-2 border-gray-300 flex items-center justify-center hover:border-purple-500 disabled:opacity-30 disabled:cursor-not-allowed transition-colors"
                  >
                    <span className="text-lg font-medium">−</span>
                  </button>
                  <span className="w-8 text-center font-medium text-sm md:text-base">{multiDayAdults}</span>
                  <button
                    onClick={() => setMultiDayAdults(Math.min(10, multiDayAdults + 1))}
                    disabled={multiDayAdults >= 10}
                    className="w-8 h-8 rounded-full border-2 border-gray-300 flex items-center justify-center hover:border-purple-500 disabled:opacity-30 disabled:cursor-not-allowed transition-colors"
                  >
                    <span className="text-lg font-medium">+</span>
                  </button>
                </div>
              </div>
              
              {/* Children */}
              <div className="flex items-center justify-between">
                <div>
                  <p className="font-medium text-gray-900 text-sm md:text-base">Children</p>
                  <p className="text-xs md:text-sm text-gray-500">Ages 2-12</p>
                </div>
                <div className="flex items-center gap-3">
                  <button
                    onClick={() => setMultiDayChildren(Math.max(0, multiDayChildren - 1))}
                    disabled={multiDayChildren <= 0}
                    className="w-8 h-8 rounded-full border-2 border-gray-300 flex items-center justify-center hover:border-purple-500 disabled:opacity-30 disabled:cursor-not-allowed transition-colors"
                  >
                    <span className="text-lg font-medium">−</span>
                  </button>
                  <span className="w-8 text-center font-medium text-sm md:text-base">{multiDayChildren}</span>
                  <button
                    onClick={() => setMultiDayChildren(Math.min(10, multiDayChildren + 1))}
                    disabled={multiDayChildren >= 10}
                    className="w-8 h-8 rounded-full border-2 border-gray-300 flex items-center justify-center hover:border-purple-500 disabled:opacity-30 disabled:cursor-not-allowed transition-colors"
                  >
                    <span className="text-lg font-medium">+</span>
                  </button>
                </div>
              </div>
              
              {/* Infants */}
              <div className="flex items-center justify-between">
                <div>
                  <p className="font-medium text-gray-900 text-sm md:text-base">Infants</p>
                  <p className="text-xs md:text-sm text-gray-500">Under 2</p>
                </div>
                <div className="flex items-center gap-3">
                  <button
                    onClick={() => setMultiDayInfants(Math.max(0, multiDayInfants - 1))}
                    disabled={multiDayInfants <= 0}
                    className="w-8 h-8 rounded-full border-2 border-gray-300 flex items-center justify-center hover:border-purple-500 disabled:opacity-30 disabled:cursor-not-allowed transition-colors"
                  >
                    <span className="text-lg font-medium">−</span>
                  </button>
                  <span className="w-8 text-center font-medium text-sm md:text-base">{multiDayInfants}</span>
                  <button
                    onClick={() => setMultiDayInfants(Math.min(5, multiDayInfants + 1))}
                    disabled={multiDayInfants >= 5}
                    className="w-8 h-8 rounded-full border-2 border-gray-300 flex items-center justify-center hover:border-purple-500 disabled:opacity-30 disabled:cursor-not-allowed transition-colors"
                  >
                    <span className="text-lg font-medium">+</span>
                  </button>
                </div>
              </div>
            </div>
            
            <button
              onClick={() => setShowTravelerPicker(false)}
            className="w-full mt-6 bg-blue-600 hover:bg-blue-700 text-white font-semibold py-2.5 rounded-lg transition-colors text-sm md:text-base"
          >
            Done
          </button>
        </div>
      </div>
    )}

    {/* Trip Details View Modal - Mobile Only */}
    {showTripDetailsModal && (() => {
      // Calculate days count
      const calculateDaysCount = () => {
        if (!multiDayStartDate || !multiDayEndDate) return 0;
        const days = [];
        const currentDate = new Date(multiDayStartDate);
        while (currentDate <= multiDayEndDate) {
          days.push(new Date(currentDate));
          currentDate.setDate(currentDate.getDate() + 1);
        }
        return days.length;
      };
      
      const daysCount = calculateDaysCount();
      const totalTravelers = multiDayAdults + multiDayChildren + multiDayInfants;
      
      return (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-[70] p-4 md:hidden">
          <div className="bg-white rounded-lg shadow-xl max-w-sm w-full p-4 animate-fade-in">
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-base font-semibold text-gray-900">Trip Details</h3>
              <button
                onClick={() => setShowTripDetailsModal(false)}
                className="p-1 hover:bg-gray-100 rounded-full transition-colors"
              >
                <X className="w-5 h-5 text-gray-500" />
              </button>
            </div>
            
            <div className="space-y-3">
              {/* Date Range */}
              <div className="bg-gradient-to-br from-purple-50 to-pink-50 rounded-lg p-3 border border-purple-200">
                <div className="flex items-center gap-2 mb-1">
                  <Calendar className="w-4 h-4 text-purple-600" />
                  <span className="text-xs font-medium text-gray-500">Dates</span>
                </div>
                <p className="text-sm font-semibold text-gray-900">
                  {multiDayStartDate?.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })} - {multiDayEndDate?.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })}
                </p>
              </div>

              {/* Travelers */}
              <div className="bg-gradient-to-br from-blue-50 to-purple-50 rounded-lg p-3 border border-blue-200">
                <div className="flex items-center gap-2 mb-1">
                  <Users className="w-4 h-4 text-blue-600" />
                  <span className="text-xs font-medium text-gray-500">Travelers</span>
                </div>
                <p className="text-sm font-semibold text-gray-900">
                  {multiDayAdults} {multiDayAdults === 1 ? 'Adult' : 'Adults'}
                  {multiDayChildren > 0 && `, ${multiDayChildren} ${multiDayChildren === 1 ? 'Child' : 'Children'}`}
                  {multiDayInfants > 0 && `, ${multiDayInfants} ${multiDayInfants === 1 ? 'Infant' : 'Infants'}`}
                </p>
              </div>

              {/* Summary Badges */}
              <div className="flex items-center gap-2 pt-2">
                <span className="bg-purple-100 text-purple-700 px-3 py-1 rounded-full font-medium text-xs">
                  {daysCount} {daysCount === 1 ? 'day' : 'days'}
                </span>
                <span className="bg-blue-100 text-blue-700 px-3 py-1 rounded-full font-medium text-xs">
                  {totalTravelers} {totalTravelers === 1 ? 'traveler' : 'travelers'}
                </span>
              </div>
            </div>

            <button
              onClick={() => setShowTripDetailsModal(false)}
              className="w-full mt-4 bg-purple-600 hover:bg-purple-700 text-white font-semibold py-2.5 rounded-lg transition-colors text-sm"
            >
              Close
            </button>
          </div>
        </div>
      );
    })()}

    {/* Optimization Dialog */}
    <OptimizationDialog
      isOpen={showOptimizationDialog}
      onConfirm={handleOptimizationConfirm}
      onCancel={handleOptimizationCancel}
    />

    {/* Trip Summary Modal */}
    {(() => {
      const currentTrip = savedTrips.find(t => t.id === currentTripId);
      return (
        <TripSummaryModal
      isOpen={showSummaryModal}
      onClose={handleCloseSummaryModal}
      summary={tripSummary}
      placePhotos={placePhotos}
      loading={isGeneratingSummary}
      error={summaryError}
      onRetry={handleRetrySummary}
      onEmailSummary={async (additionalEmail) => {
        if (!currentTripId) return;
        await hookHandleEmailItinerary(currentTripId, authState.user?.email || '', additionalEmail || '', !!additionalEmail);
      }}
      onRequestQuote={async (formData) => {
        return await handleRequestQuote(formData);
      }}
      tripId={currentTripId || undefined}
      tripName={currentTrip?.tripName}
      startDate={currentTrip?.multiDayTrip?.startDate?.toDate ? currentTrip.multiDayTrip.startDate.toDate() : (currentTrip?.multiDayTrip?.startDate instanceof Date ? currentTrip.multiDayTrip.startDate : undefined)}
      endDate={currentTrip?.multiDayTrip?.endDate?.toDate ? currentTrip.multiDayTrip.endDate.toDate() : (currentTrip?.multiDayTrip?.endDate instanceof Date ? currentTrip.multiDayTrip.endDate : undefined)}
      numAdults={currentTrip?.multiDayTrip?.travelers?.adults}
      numChildren={currentTrip?.multiDayTrip?.travelers?.children}
        />
      );
    })()}

    {/* Async Summary Generation Modal (for long trips >5 days) */}
    <SummaryGenerationModal
      isOpen={showAsyncSummaryModal}
      progress={asyncSummary.progress}
      status={asyncSummary.jobStatus?.status || 'pending'}
      error={asyncSummary.error}
      onCancel={() => {
        asyncSummary.cancelGeneration();
        setShowAsyncSummaryModal(false);
      }}
      onClose={() => {
        // Close async modal
        setShowAsyncSummaryModal(false);
        
        // If generation completed successfully, show the summary modal
        if (asyncSummary.isCompleted && currentTripId) {
          setShowSummaryModal(true);
        }
        
        // Reset async state
        asyncSummary.cancelGeneration();
      }}
    />

    {/* Lazy Quote Form */}
    {showLazyQuoteForm && authState.user?.userId && (
      <QuoteRequestForm
        isLazyMode={true}
        onClose={() => setShowLazyQuoteForm(false)}
        onSubmit={async (formData) => {
          if (!authState.user?.userId) return;
          await handleRequestQuote(formData);
          setShowLazyQuoteForm(false);
        }}
      />
    )}

    {/* Quick Quote Detail Modal */}
    {selectedQuickQuote && (
      <QuickQuoteDetail
        quote={selectedQuickQuote}
        onClose={() => setSelectedQuickQuote(null)}
      />
    )}
  </div>
  );
}

